import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { uploadDocument, deleteDocument } from "./firebase";
import { storage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "./firebase";
import ReferralApp from "./ReferralApp";
import { MEDICATION_DATABASE, MEDICATION_NAMES, getDosesForMed } from "./medications";

// ── Constants ─────────────────────────────────────────────────────────────────
const FACILITIES = ["Dorcus 1 - 801", "Dorcus 2 - 24th St"];

const DORCUS_LOGO_URL = "https://firebasestorage.googleapis.com/v0/b/dorcusstep.firebasestorage.app/o/_DH%20Logo%20Main%20-%20Brand%20Colors.png?alt=media&token=38f9ca53-879c-45b3-b74f-492846dc2b2c";

const FACILITY_ADDRESS = {
  "Dorcus 1 - 801": "801 Shenandoah Ave NW, Roanoke, VA 24016",
  "Dorcus 2 - 24th St": "23 24th Street NW, Roanoke, VA 24017",
};

// ── NOA Document Generator ─────────────────────────────────────────────────────
async function fetchLogoBase64() {
  try {
    const resp = await fetch(DORCUS_LOGO_URL);
    const blob = await resp.blob();
    return await new Promise((res) => {
      const reader = new FileReader();
      reader.onloadend = () => res(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch(e) { return null; }
}

function generateNOADocument(resident, noaData, logoBase64=null) {
  const items = noaData.items || {};
  const ITEMS = [
    { key:"noticeAcceptance",  num:1, label:"Does resident have a Dorcus Notice of Acceptance & Signed program rules?" },
    { key:"certIncarceration", num:2, label:"Does resident have a certificate of incarceration?" },
    { key:"clothing",          num:3, label:"Does resident have at least 2 sets of clothing?" },
    { key:"referralMedRecords",num:4, label:"Were referral and medical records provided for resident?" },
    { key:"money",             num:5, label:"Did resident arrive with money?", isAmount:true },
    { key:"ssCard",            num:6, label:"Does resident have a SS Card?", hasPhoto:true },
    { key:"birthCert",         num:7, label:"Does resident have a birth certificate?", hasPhoto:true },
    { key:"unexpiredId",       num:8, label:"Does resident have an unexpired ID?", hasPhoto:true },
    { key:"requiresMed",       num:9, label:"Does resident require medication?" },
    { key:"broughtMed",        num:10, label:"Did resident bring medication?", isMed:true },
    { key:"hygieneKit",        num:11, label:"Did resident arrive with VADOC issued Hygiene Kit?" },
  ];

  const rowHTML = ITEMS.map(item => {
    const val = items[item.key];
    let answerHTML = "";
    if (item.isAmount) {
      answerHTML = val ? `<td class="ans amt">$${noaData.moneyAmount||val}</td>` : `<td class="ans ans-no">NO</td>`;
    } else if (val === true) {
      const photoBadge = item.hasPhoto && noaData[`${item.key}Photo`] ? `<span class="badge-photo">📎 photo</span>` : (item.hasPhoto ? `<span class="badge-upload">📎 upload later</span>` : "");
      const medBadge = item.isMed ? `<span class="badge-med">→ added to med log</span>` : "";
      answerHTML = `<td class="ans ans-yes">YES ${photoBadge}${medBadge}</td>`;
    } else if (val === false) {
      const photoBadge = item.hasPhoto ? `<span class="badge-upload">📎 upload later</span>` : "";
      answerHTML = `<td class="ans ans-no">NO ${photoBadge}</td>`;
    } else {
      answerHTML = `<td class="ans ans-blank">—</td>`;
    }
    return `<tr><td class="num">${item.num}</td><td class="lbl">${item.label}</td>${answerHTML}</tr>`;
  }).join("");

  const uaSection = `
    <div class="section-hdr">Date of Initial Urine Screen</div>
    <div class="ua-grid">
      <div class="field-row"><span class="field-lbl">Date:</span><span class="field-val">${noaData.uaDate||"—"}</span></div>
      <div class="field-row"><span class="field-lbl">Results:</span><span class="field-val">${noaData.uaResult||"—"}</span></div>
      <div class="field-row"><span class="field-lbl">Substance:</span><span class="field-val">${noaData.uaSubstance||"—"}</span></div>
    </div>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Notice of Arrival — ${resident.name}</title>
<style>
  body { font-family: 'Arial', sans-serif; font-size: 12px; color: #1A2B45; margin: 0; padding: 24px 32px; }
  .doc-hdr { display: flex; align-items: flex-start; justify-content: space-between; padding-bottom: 12px; margin-bottom: 4px; border-bottom: 2px solid #1A2B45; }
  .logo { height: 52px; width: auto; }
  .hdr-right { font-size: 11px; color: #4A5568; text-align: right; line-height: 1.5; }
  .doc-title { font-size: 17px; font-weight: 700; text-align: center; letter-spacing: 0.04em; margin: 14px 0 18px; color: #1A2B45; }
  .field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 28px; margin-bottom: 16px; }
  .field-row { display: flex; gap: 4px; align-items: baseline; font-size: 12px; }
  .field-lbl { color: #4A5568; white-space: nowrap; }
  .field-val { font-weight: 500; border-bottom: 0.5px solid #A0AEC0; flex: 1; padding-bottom: 1px; }
  .field-full { grid-column: span 2; }
  .section-hdr { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; margin: 14px 0 8px; border-top: 0.5px solid #D8DCE3; padding-top: 10px; }
  table.chk { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  table.chk td { padding: 6px 8px; border: 0.5px solid #D8DCE3; vertical-align: middle; line-height: 1.4; }
  .num { width: 24px; text-align: center; color: #4A5568; background: #F7F9FC; font-size: 11px; }
  .lbl { color: #1A2B45; }
  .ans { width: 90px; text-align: center; font-weight: 600; font-size: 12px; }
  .ans-yes { color: #1A6B35; background: #E6F4EC; }
  .ans-no { color: #8B1A1A; background: #FDECEA; }
  .amt { color: #1B5EBE; background: #E8F0FB; }
  .ans-blank { color: #718096; background: #F7F9FC; }
  .badge-photo { font-size: 9px; background: #E8F0FB; color: #1B5EBE; border: 0.5px solid #B5D4F4; border-radius: 3px; padding: 1px 5px; margin-left: 4px; }
  .badge-upload { font-size: 9px; background: #E8F0FB; color: #1B5EBE; border: 0.5px solid #B5D4F4; border-radius: 3px; padding: 1px 5px; margin-left: 4px; }
  .badge-med { font-size: 9px; background: #FFF3CD; color: #7A4F00; border: 0.5px solid #F5C200; border-radius: 3px; padding: 1px 5px; margin-left: 4px; }
  .ua-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; margin-bottom: 10px; }
  .note-box { border: 0.5px solid #D8DCE3; border-radius: 4px; min-height: 36px; padding: 6px 8px; color: #718096; background: #FAFAFA; margin-bottom: 12px; }
  .sig-row { display: flex; gap: 40px; border-top: 0.5px solid #D8DCE3; padding-top: 10px; margin-top: 4px; }
  .sig-line { border-bottom: 0.5px solid #1A2B45; height: 18px; margin-top: 2px; flex: 1; }
  .cc { font-size: 10px; color: #718096; margin-top: 8px; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <div class="doc-hdr">
    <img src="${logoBase64||DORCUS_LOGO_URL}" class="logo" alt="Dorcus Home" onerror="this.style.display='none'"/>
    <div class="hdr-right">Dorcus Home, LLC<br><span style="font-size:10px;">Mailing Address: ${FACILITY_ADDRESS[resident.facility]||""}</span></div>
  </div>

  <div class="doc-title">NOTICE OF ARRIVAL</div>

  <div class="field-grid">
    <div class="field-row"><span class="field-lbl">Facility:&nbsp;</span><span class="field-val">${resident.facility||""}</span></div>
    <div></div>
    <div class="field-row field-full"><span class="field-lbl">Facility Address:&nbsp;</span><span class="field-val">${FACILITY_ADDRESS[resident.facility]||""}</span></div>
    <div class="field-row"><span class="field-lbl">Offender Name:&nbsp;</span><span class="field-val">${resident.name||""}</span></div>
    <div class="field-row"><span class="field-lbl">DOC #:&nbsp;</span><span class="field-val">${resident.doc||""}</span></div>
    <div class="field-row"><span class="field-lbl">Date of Arrival:&nbsp;</span><span class="field-val">${noaData.arrivalDate||resident.entryDate||""}</span></div>
    <div class="field-row"><span class="field-lbl">Arrival From:&nbsp;</span><span class="field-val">${noaData.arrivalFrom||""}</span></div>
    <div class="field-row"><span class="field-lbl">Probation Officer:&nbsp;</span><span class="field-val">${noaData.ppOfficer||""}</span></div>
    <div class="field-row"><span class="field-lbl">Classification:&nbsp;</span><span class="field-val">${noaData.classification||resident.classification||""}</span></div>
    <div class="field-row"><span class="field-lbl">Scheduled Release:&nbsp;</span><span class="field-val">${noaData.scheduledRelease||resident.releaseDate||""}</span></div>
    <div class="field-row"><span class="field-lbl">Race:&nbsp;</span><span class="field-val">${resident.race||""}</span></div>
  </div>

  <div class="section-hdr">This offender arrived at our facility with the following:</div>
  <table class="chk"><tbody>${rowHTML}</tbody></table>

  ${uaSection}

  <div style="font-weight:600;margin-bottom:4px;">Comments:</div>
  <div class="note-box">${noaData.comments||""}</div>

  <div class="sig-row">
    <div style="flex:1;"><div style="color:#4A5568;">NAME/TITLE:</div><div class="sig-line"></div></div>
    <div style="flex:0 0 160px;"><div style="color:#4A5568;">Date:</div><div class="sig-line"></div></div>
  </div>
  <div class="cc">Original:&nbsp;&nbsp;&nbsp;&nbsp; cc:</div>
</body>
</html>`;
}

// ── NOD Document Generator ─────────────────────────────────────────────────────
function generateNODDocument(resident, dischargeMeetingData, adminName, logoBase64=null) {
  const dm = dischargeMeetingData || {};
  const dischargeAddress = dm.forwardingAddress || dm.proposedAddress || "";
  const dischargePhone = dm.dischargePhone || "";
  const ec = resident.intakeForms?.intakeForm?.ec1 || {};
  const isSuccessful = !["violation","abscond"].includes(resident.dischargeType);
  const dischargeStatusLabel = isSuccessful ? "Successful" : "Unsuccessful";

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Notice of Discharge — ${resident.name}</title>
<style>
  body { font-family: 'Arial', sans-serif; font-size: 12px; color: #1A2B45; margin: 0; padding: 24px 32px; }
  .doc-hdr { display: flex; align-items: center; justify-content: space-between; padding-bottom: 12px; border-bottom: 2px solid #1A2B45; }
  .logo { height: 52px; width: auto; }
  .hdr-right { font-size: 11px; color: #4A5568; }
  .doc-title { font-size: 17px; font-weight: 700; text-align: center; letter-spacing: 0.04em; margin: 14px 0 20px; color: #1A2B45; border-bottom: 2px solid #1A2B45; padding-bottom: 12px; }
  .to-grid { display: grid; grid-template-columns: 60px 1fr; gap: 7px; margin-bottom: 18px; align-items: baseline; }
  .to-lbl { font-weight: 600; }
  .to-val { border-bottom: 0.5px solid #A0AEC0; padding-bottom: 1px; color: #1A2B45; }
  .date-box { display: inline-block; background: #FFF59D; color: #5D4037; border: 0.5px solid #F9A825; border-radius: 3px; padding: 1px 8px; font-weight: 500; }
  .info-card { border: 0.5px solid #D8DCE3; border-radius: 6px; padding: 12px 14px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px 28px; margin-bottom: 14px; background: #F7F9FC; }
  .info-lbl { font-size: 10px; color: #4A5568; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px; }
  .info-val { font-weight: 500; border-bottom: 0.5px solid #A0AEC0; }
  .info-full { grid-column: span 2; }
  .status-ok { display: inline-block; background: #E6F4EC; color: #1A6B35; border: 0.5px solid #A3D4B4; border-radius: 4px; padding: 2px 10px; font-weight: 600; }
  .status-bad { display: inline-block; background: #FDECEA; color: #8B1A1A; border: 0.5px solid #F5A8A8; border-radius: 4px; padding: 2px 10px; font-weight: 600; }
  .note-box { border: 0.5px solid #D8DCE3; border-radius: 4px; min-height: 36px; padding: 6px 8px; color: #718096; background: #FAFAFA; margin-bottom: 12px; }
  .inline-pair { display: grid; grid-template-columns: 220px 1fr; gap: 6px; align-items: center; margin-bottom: 6px; }
  .inline-lbl { font-weight: 600; }
  .inline-val { background: #FFF59D; color: #5D4037; border: 0.5px solid #F9A825; border-radius: 3px; padding: 2px 8px; }
  .sig-row { display: flex; gap: 40px; border-top: 0.5px solid #D8DCE3; padding-top: 14px; margin-top: 14px; }
  .sig-line { border-bottom: 0.5px solid #1A2B45; height: 18px; margin-top: 2px; flex: 1; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <div class="doc-hdr">
    <img src="${logoBase64||DORCUS_LOGO_URL}" class="logo" alt="Dorcus Home" onerror="this.style.display='none'"/>
    <div class="hdr-right">Dorcus Home, LLC</div>
  </div>

  <div class="doc-title">Notice of Discharge</div>

  <div class="to-grid">
    <div class="to-lbl">To:</div><div class="to-val">${dm.noaSentTo||"[District Personnel / P&amp;P Officer]"}</div>
    <div class="to-lbl">From:</div><div class="to-val">${adminName||"Dorcus Home, LLC"}</div>
  </div>

  <div style="margin-bottom:16px;font-size:12px;"><strong>Date:</strong> <span class="date-box">${TODAY}</span></div>

  <div class="info-card">
    <div><div class="info-lbl">Resident Name</div><div class="info-val">${resident.name||""}</div></div>
    <div><div class="info-lbl">DOC #</div><div class="info-val">${resident.doc||""}</div></div>
    <div><div class="info-lbl">Date of Arrival</div><div class="info-val">${resident.entryDate||""}</div></div>
    <div><div class="info-lbl">Date of Discharge</div><div class="info-val"><span class="date-box">${resident.actualDischargeDate||TODAY}</span></div></div>
    <div class="info-full"><div class="info-lbl">Discharge Status</div><div style="margin-top:4px;"><span class="${isSuccessful?"status-ok":"status-bad"}">${dischargeStatusLabel}</span></div></div>
  </div>

  <div style="margin-bottom:12px;">
    <div style="font-weight:600;margin-bottom:4px;">Reason for Unsuccessful Discharge:</div>
    <div class="note-box">${isSuccessful?"N/A":(resident.dischargeNotes||dm.notes||"")}</div>
  </div>

  <div style="margin-bottom:14px;">
    <div class="inline-pair"><div class="inline-lbl">Discharge Address Provided:</div><div class="inline-val">${dischargeAddress||"—"}</div></div>
    <div class="inline-pair"><div class="inline-lbl">Discharge Phone Number Provided:</div><div class="inline-val">${dischargePhone||"—"}</div></div>
    <div class="inline-pair"><div class="inline-lbl">Emergency Contact:</div><div class="inline-val">${ec.name||"—"}${ec.phone?" — "+ec.phone:""}</div></div>
    <div class="inline-pair"><div class="inline-lbl">Emergency Contact Address Provided:</div><div class="inline-val">${ec.address||"—"}</div></div>
  </div>

  <div style="margin-bottom:14px;">
    <div style="font-weight:600;margin-bottom:4px;">Additional Comments:</div>
    <div class="note-box"></div>
  </div>

  <div class="sig-row">
    <div style="flex:1;"><div style="color:#4A5568;">NAME/TITLE:</div><div class="sig-line"></div></div>
    <div style="flex:0 0 160px;"><div style="color:#4A5568;">Date:</div><div class="sig-line"></div></div>
  </div>
</body>
</html>`;
}
const TZ = "America/New_York";
const NOW_NY = () => new Date(new Date().toLocaleString("en-US", {timeZone:TZ}));
const toNYDateStr = (d) => d.toLocaleDateString("en-CA", {timeZone:TZ}); // en-CA gives YYYY-MM-DD
const TODAY = toNYDateStr(new Date());
const daysFromToday = n => { const d = new Date(); d.setDate(d.getDate()+n); return toNYDateStr(d); };
const SHIFT_TYPES = ["Morning (6AM–2PM)", "Afternoon (2PM–10PM)", "Overnight (10PM–6AM)"];
const CHECKLIST_ITEMS = [
  { label:"Consent to Release Information to Dorcus", required:true },
  { label:"VA DOC Institutional Disciplinary Action", required:true },
  { label:"Criminal History Narrative / VA DOC Sentencing Summary", required:true },
  { label:"Pre/Post Sentence Report / Trial Disposition", required:true },
  { label:"Medical Discharge Summary (incl. TB Test)", required:true },
  { label:"Offender Progress Report", required:true },
  { label:"Mental Health Records/Summary", required:false, condition:"mh" },
  { label:"Probation/Parole Conditions", required:false, condition:"pp" },
  { label:"Substance Abuse History/Treatment", required:false },
  { label:"Medication List", required:false },
  { label:"Medicaid/Medicare Transfer Confirmed", required:false },
];

// Virginia General District Court Districts 1–43
// Virginia P&P Districts (VADOC Probation & Parole)
const VA_DISTRICTS = [
  {n:1,  label:"1 – Richmond"},
  {n:2,  label:"2 – Norfolk"},
  {n:3,  label:"3 – Portsmouth"},
  {n:4,  label:"4 – Accomac"},
  {n:5,  label:"5 – Gloucester"},
  {n:6,  label:"6 – Suffolk"},
  {n:7,  label:"7 – Petersburg"},
  {n:8,  label:"8 – South Boston"},
  {n:9,  label:"9 – Charlottesville"},
  {n:10, label:"10 – Arlington"},
  {n:11, label:"11 – Winchester"},
  {n:12, label:"12 – Staunton"},
  {n:13, label:"13 – Lynchburg"},
  {n:14, label:"14 – Danville"},
  {n:15, label:"15 – Roanoke"},
  {n:16, label:"16 – Wytheville"},
  {n:17, label:"17 – Abingdon"},
  {n:18, label:"18 – Norton"},
  {n:19, label:"19 – Newport News"},
  {n:20, label:"20 – Bedford"},
  {n:21, label:"21 – Fredericksburg"},
  {n:22, label:"22 – Martinsville"},
  {n:23, label:"23 – Virginia Beach"},
  {n:24, label:"24 – Farmville"},
  {n:25, label:"25 – Leesburg"},
  {n:26, label:"26 – Culpeper"},
  {n:27, label:"27 – Chesterfield"},
  {n:28, label:"28 – Radford"},
  {n:29, label:"29 – Fairfax"},
  {n:30, label:"30 – Hampton"},
  {n:31, label:"31 – Chesapeake"},
  {n:32, label:"32 – Henrico"},
  {n:33, label:"33 – Warsaw"},
  {n:34, label:"34 – Williamsburg"},
  {n:35, label:"35 – Manassas"},
  {n:36, label:"36 – Alexandria"},
  {n:37, label:"37 – Rocky Mount"},
  {n:38, label:"38 – Emporia"},
  {n:39, label:"39 – Harrisonburg"},
  {n:40, label:"40 – Fincastle"},
  {n:41, label:"41 – Ashland"},
  {n:42, label:"42 – Franklin"},
  {n:43, label:"43 – Tazewell"},
];

// Virginia DOC Correctional Facilities (active male facilities, 2024)
const VA_INSTITUTIONS = [
  // Western Region
  "Bland Correctional Center",
  "Cold Springs Correctional Unit",
  "Green Rock Correctional Center",
  "Keen Mountain Correctional Center",
  "Marion Correctional Treatment Center",
  "Patrick Henry Correctional Unit",
  "Pocahontas State Correctional Center",
  "Red Onion State Prison",
  "River North Correctional Center",
  "Wallens Ridge State Prison",
  "Wise Correctional Unit",
  // Central Region
  "Baskerville Correctional Center",
  "Beaumont Correctional Center",
  "Buckingham Correctional Center",
  "Central Virginia Correctional Unit",
  "Coffeewood Correctional Center",
  "Dillwyn Correctional Center",
  "Halifax Correctional Unit",
  "Lunenburg Correctional Center",
  "Nottoway Correctional Center",
  "Rustburg Correctional Unit",
  "State Farm Correctional Center",
  // Eastern Region
  "Caroline Correctional Unit",
  "Deerfield Correctional Center",
  "Greensville Correctional Center",
  "Haynesville Correctional Center",
  "Indian Creek Correctional Center",
  "Lawrenceville Correctional Center",
  "St. Brides Correctional Center",
  "Sussex I State Prison",
  // Other
  "Other",
].sort((a,b) => a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b));

// Race options
const RACE_OPTIONS = [
  "Black / African American",
  "White / Caucasian",
  "Hispanic / Latino",
  "Asian / Pacific Islander",
  "Native American / Alaska Native",
  "Multiracial",
  "Other",
  "Prefer not to say",
];

// Virginia felony offense list
const OFFENSE_OPTIONS = [
  "Aggravated Malicious Wounding",
  "Strangulation (Major Felony)",
  "Robbery",
  "Murder",
  "Assault (Various Categories)",
  "Grand Larceny",
  "Burglary",
  "Arson",
  "Embezzlement",
  "Possession with Intent to Distribute",
  "Manufacturing Controlled Substances",
  "Multiple Marijuana Possession Offenses",
  "Third-Offense DUI",
  "Forgery",
  "Money Laundering",
  "Reckless Driving",
  "Rape (including Statutory)",
  "Object Sexual Penetration",
  "Aggravated Sexual Battery",
  "Forcible Sodomy",
  "Taking Indecent Liberties with a Child",
  "Child Pornography Charges",
  "Infected Sexual Battery",
  "Abduction for Immoral Purpose",
  "Other",
];

const ROLES = {
  admin:   { label:"Admin",                dot:"#f87171", activeBg:"#3b1a1a", activeBorder:"#b45252", activeColor:"#fca5a5" },
  pm:      { label:"Program Coordinator",  dot:"#a78bfa", activeBg:"#2d1a4a", activeBorder:"#8b5cf6", activeColor:"#c4b5fd" },
  rc:      { label:"Referral Coordinator", dot:"#fb923c", activeBg:"#2a1a0c", activeBorder:"#f97316", activeColor:"#fdba74" },
  ra_a:    { label:"RA – Dorcus 1",        dot:"#38bdf8", activeBg:"#0c2a3a", activeBorder:"#38bdf8", activeColor:"#7dd3fc" },
  ra_b:    { label:"RA – Dorcus 2",        dot:"#2dd4bf", activeBg:"#0c3030", activeBorder:"#2dd4bf", activeColor:"#5eead4" },
};

const PERMISSIONS = {
  admin: { canSeeAllFacilities:true,  assignedFacility:null,               canEditHighLevel:true,  canEditHealth:true,  canEditDocs:true,  canEditMeds:true,  canLeaveCaseNotes:true,  canCreateReferral:true,  canViewApplicants:true,  canUseRATools:true,  canManageReferrals:true,  canManageDirectory:true,  canViewResidents:true  },
  pm:    { canSeeAllFacilities:true,  assignedFacility:null,               canEditHighLevel:false, canEditHealth:true,  canEditDocs:true,  canEditMeds:true,  canLeaveCaseNotes:true,  canCreateReferral:true,  canViewApplicants:true,  canUseRATools:false, canManageReferrals:false, canManageDirectory:false, canViewResidents:true  },
  rc:    { canSeeAllFacilities:false, assignedFacility:null,               canEditHighLevel:false, canEditHealth:false, canEditDocs:false, canEditMeds:false, canLeaveCaseNotes:false, canCreateReferral:true,  canViewApplicants:true,  canUseRATools:false, canManageReferrals:true,  canManageDirectory:false, canViewResidents:false },
  ra_a:  { canSeeAllFacilities:false, assignedFacility:"Dorcus 1 - 801",  canEditHighLevel:false, canEditHealth:false, canEditDocs:false, canEditMeds:true,  canLeaveCaseNotes:false, canCreateReferral:false, canViewApplicants:false, canUseRATools:true,  canManageReferrals:false, canManageDirectory:false, canViewResidents:true, raOnly:true },
  ra_b:          { canSeeAllFacilities:false, assignedFacility:"Dorcus 2 - 24th St", canEditHighLevel:false, canEditHealth:false, canEditDocs:false, canEditMeds:true,  canLeaveCaseNotes:false, canCreateReferral:false, canViewApplicants:false, canUseRATools:true,  canManageReferrals:false, canManageDirectory:false, canViewResidents:true, raOnly:true },
  house_computer:{ canSeeAllFacilities:false, assignedFacility:null,                canEditHighLevel:false, canEditHealth:false, canEditDocs:false, canEditMeds:false, canLeaveCaseNotes:false, canCreateReferral:false, canViewApplicants:false, canUseRATools:true,  canManageReferrals:false, canManageDirectory:false, canViewResidents:false },
};

const MOCK_RESIDENTS = [
  { id:1, status:"active", facility:"Dorcus 1 - 801", name:"Marcus J. Thompson", doc:"1092847", dob:"1988-03-14", releaseDate:"2024-11-01", sentencingDistrict:"Richmond", classification:"Non-Violent", referredBy:"James Holloway", referralInstitution:"Pocahontas State Correctional", referralDate:"2024-10-15", currentOffenses:"Possession with Intent to Distribute", priorOffenses:"Simple Possession (2018)", detainer:false, icot:false, priorDorcus:"No", mentalHealth:false, mentalHealthNotes:"", physicalHealth:false, physicalHealthNotes:"", substanceAbuse:true, substanceAbuseNotes:"History of opioid use disorder, completed in-facility MAT program.", employed:false, employmentNotes:"", trainingPrograms:true, trainingNotes:"Completed GED, Vocational Welding Certificate", noContact:false, noContactList:"", medications:["Buprenorphine 8mg daily","Clonidine 0.1mg nightly"], checklist:[true,true,true,true,true,true,false,true,false,true,true,true,false], inStatus:"in", caseNotes:[{date:"2024-11-05",author:"Program Coordinator",note:"Initial intake completed. Resident oriented to house rules."}] },
  { id:2, status:"active", facility:"Dorcus 1 - 801", name:"Terrell A. Banks", doc:"1938472", dob:"1991-06-09", releaseDate:"2024-10-20", sentencingDistrict:"Roanoke", classification:"Non-Violent", referredBy:"Kevin Osei", referralInstitution:"Augusta Correctional Center", referralDate:"2024-10-05", currentOffenses:"Grand Larceny", priorOffenses:"Shoplifting (2019)", detainer:false, icot:false, priorDorcus:"No", mentalHealth:false, mentalHealthNotes:"", physicalHealth:true, physicalHealthNotes:"Diabetes Type 2 — requires monitoring.", substanceAbuse:false, substanceAbuseNotes:"", employed:true, employmentNotes:"Warehouse associate at Richmond Logistics.", trainingPrograms:false, trainingNotes:"", noContact:false, noContactList:"", medications:["Metformin 500mg daily"], checklist:[true,true,true,true,false,true,true,true,false,true,true,true,true], inStatus:"in", caseNotes:[] },
  { id:3, status:"active", facility:"Dorcus 2 - 24th St", name:"Kevin D. Harris", doc:"2103948", dob:"1985-11-30", releaseDate:"2024-09-15", sentencingDistrict:"Hampton Roads", classification:"Violent", referredBy:"Dana Morris", referralInstitution:"Greensville Correctional Center", referralDate:"2024-09-01", currentOffenses:"Malicious Wounding", priorOffenses:"Simple Assault (2016), Disorderly Conduct (2012)", detainer:false, icot:false, priorDorcus:"Yes — 2021 — Unsuccessful", mentalHealth:true, mentalHealthNotes:"Diagnosed PTSD. Active counseling recommended.", physicalHealth:false, physicalHealthNotes:"", substanceAbuse:true, substanceAbuseNotes:"Alcohol and cannabis use disorder.", employed:false, employmentNotes:"", trainingPrograms:true, trainingNotes:"HVAC certification in progress.", noContact:true, noContactList:"Brenda Harris (victim — protective order active through 2026)", medications:["Buprenorphine 4mg daily","Prazosin 1mg nightly"], checklist:[true,true,true,true,true,true,true,false,true,true,true,true,false], inStatus:"out", caseNotes:[{date:"2024-09-20",author:"Program Coordinator",note:"Reviewed no-contact order. Confirmed understanding of terms."},{date:"2024-10-10",author:"Program Coordinator",note:"Check-in: progressing well. HVAC attendance confirmed."}] },
  { id:4, status:"active", facility:"Dorcus 2 - 24th St", name:"Michael B. Wright", doc:"1847362", dob:"1996-02-14", releaseDate:"2024-12-01", sentencingDistrict:"Northern Virginia", classification:"Non-Violent", referredBy:"Patricia Lee", referralInstitution:"Deerfield Correctional Center", referralDate:"2024-11-15", currentOffenses:"Fraud — Identity Theft", priorOffenses:"None", detainer:false, icot:false, priorDorcus:"No", mentalHealth:true, mentalHealthNotes:"Anxiety and depression. Prescribed Sertraline and Trazodone.", physicalHealth:false, physicalHealthNotes:"", substanceAbuse:false, substanceAbuseNotes:"", employed:false, employmentNotes:"", trainingPrograms:true, trainingNotes:"Associate's Degree in Business Administration.", noContact:false, noContactList:"", medications:["Sertraline 100mg daily","Trazodone 50mg nightly"], checklist:[true,true,true,false,true,true,true,true,true,true,false,true,false], inStatus:"in", caseNotes:[] },
  { id:5, status:"applicant", facility:null, name:"Devon R. Simmons", doc:"2034561", dob:"1993-07-22", releaseDate:"2025-01-15", sentencingDistrict:"Norfolk", classification:"Violent", referredBy:"Sarah Nguyen", referralInstitution:"Greensville Correctional Center", referralDate:"2024-12-01", currentOffenses:"Assault & Battery – Serious Injury", priorOffenses:"Disorderly Conduct (2015)", detainer:false, icot:true, priorDorcus:"No", mentalHealth:true, mentalHealthNotes:"Anxiety disorder.", physicalHealth:false, physicalHealthNotes:"", substanceAbuse:true, substanceAbuseNotes:"Alcohol use disorder, no formal treatment.", employed:false, employmentNotes:"", trainingPrograms:false, trainingNotes:"", noContact:true, noContactList:"Angela Simmons (victim — restraining order in effect)", medications:["Sertraline 50mg daily"], checklist:[true,false,true,true,true,false,false,false,true,true,true,false,false], inStatus:null, caseNotes:[] },
];

const EXIT_CHECKLIST_ITEMS = [
  "Exit interview completed",
  "Room inspected and cleared",
  "House keys returned",
  "Medication supply reconciled",
  "Probation/Parole officer notified",
  "Next housing confirmed",
  "Transportation arranged",
  "Benefits/ID documents returned to resident",
  "MAT provider transfer arranged (if applicable)",
  "Case summary completed by Program Coordinator",
  "Resident signed exit acknowledgment",
];

// Property return checklist — detailed itemized list
const PROPERTY_RETURN_ITEMS = [
  { id:"state_id",      label:"State-Issued ID / Driver's License", notes:"" },
  { id:"ssn_card",      label:"Social Security Card",               notes:"" },
  { id:"birth_cert",    label:"Birth Certificate",                  notes:"" },
  { id:"phone",         label:"Phone / Electronics",                notes:"" },
  { id:"clothing_bags", label:"Clothing & Personal Bags",           notes:"" },
  { id:"medications",   label:"Medications (reconciled w/ MAR)",    notes:"" },
  { id:"keys",          label:"Personal Keys",                      notes:"" },
  { id:"wallet_cash",   label:"Wallet / Cash / Debit Cards",        notes:"" },
  { id:"jewelry",       label:"Jewelry / Watch",                    notes:"" },
  { id:"documents",     label:"Other Personal Documents",           notes:"" },
  { id:"other",         label:"Other Valuables",                    notes:"" },
];

function add90Days(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 90);
  return d.toISOString().split("T")[0];
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date(TODAY);
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const emptyForm = { status:"applicant",facility:"",room:"",name:"",doc:"",dob:"",releaseDate:"",sentencingDistrict:"",classification:"Non-Violent",gangAffiliated:false,race:"",referredBy:"",counselorEmail:"",referralSource:"institution",referralInstitution:"",ppDistrict:"",referralDate:TODAY,currentOffenses:[],priorOffenses:[],detainer:false,icot:false,priorDorcus:"",mentalHealth:false,mhLevel:"MH-0",mentalHealthNotes:"",physicalHealth:false,physicalHealthNotes:"",substanceAbuse:false,substanceAbuseNotes:"",employed:false,employer:"",trainingDecisionPoints:false,trainingAngerManagement:false,trainingParenting:false,trainingNotes:"",noContact:false,noContactList:"",medications:[],otcLog:[],checklist:Array(CHECKLIST_ITEMS.length).fill(false),exitChecklist:Array(EXIT_CHECKLIST_ITEMS.length).fill(false),inStatus:null,caseNotes:[],entryDate:"",exitDate:"",actualDischargeDate:"",dischargeNotes:"",archived:false,dpLevel:"",dpHistory:[],intakeForms:{},dietaryNeeds:{hasRestrictions:false,restrictions:"",reason:"",allergies:"",completedDate:""},virtualInitials:"",virtualInitialsLockedAt:"" };

const statusColors = {
  active:          { text:"#1A6B35", dot:"#1A6B35",  label:"Active" },
  pending_arrival: { text:"#fb923c", dot:"#f97316",  label:"Pending Arrival" },
  applicant:       { text:"#7A4F00", dot:"#f59e0b",  label:"Applicant" },
  discharged:      { text:"#4A5568", dot:"#4A5568",  label:"Discharged" },
  archived:        { text:"#4A5568", dot:"#4A5568",  label:"Archived" },
};
const classStyle = {
  "Non-Violent": { bg:"white", border:"#A3D4B4", color:"#1A6B35", leftBorder:"#1A6B35" },
  "Violent":     { bg:"white", border:"#F5A8A8", color:"#8B1A1A", leftBorder:"#8B1A1A" },
  "Sex Offender":{ bg:"white", border:"#A89FE0", color:"#4A3AAA", leftBorder:"#4A3AAA" },
};

function formatAMPM(h, m) {
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,"0")} ${period}`;
}
function parseTimeInput(hourStr, minStr, period) {
  let h = parseInt(hourStr)||0;
  let m = parseInt(minStr)||0;
  if(h < 1 || h > 12) h = 12;
  if(m < 0 || m > 59) m = 0;
  if(period === "PM" && h !== 12) h += 12;
  if(period === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
}
function timeNow() { return new Date().toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",timeZone:TZ}); }
function initials(name) { return name.split(" ").map(n=>n[0]).slice(0,2).join(""); }
function formatTime12(val) {
  if (!val) return "";
  // Already in 12hr format (e.g. "6:30 PM") — return as-is
  if (/[AP]M/i.test(val)) return val;
  // Convert HH:MM to 12hr
  const parts = val.split(":");
  if (parts.length < 2) return val;
  let h = parseInt(parts[0], 10);
  const m = parts[1].padStart(2,"0");
  if (isNaN(h)) return val;
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}
function seedRandom(seed) { let s=seed; return ()=>{s=(s*16807)%2147483647;return(s-1)/2147483646;}; }
function generateUAList(date,residents) {
  const seed=parseInt(date.replace(/-/g,""))||20260304;
  const rng=seedRandom(seed+residents.length*13);
  return [...residents].sort(()=>rng()-0.5).slice(0,Math.ceil(residents.length/2));
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
const DS = {
  blue:        "#1B5EBE",
  darkBlue:    "#154A99",
  blueTint:    "#E8F0FB",
  blueBorder:  "#B5D4F4",
  yellow:      "#F5C200",
  darkYellow:  "#D4A800",
  textPrimary: "#1A2B45",
  textSec:     "#4A5568",
  border:      "#D8DCE3",
  pageBg:      "#C8D8EC",
  inputBg:     "#F0F4FA",
  elementBg:   "#F0F4FA",
  white:       "#FFFFFF",
  success:     { bg:"#E6F4EC", text:"#1A6B35", border:"#A3D4B4" },
  warning:     { bg:"#FFF3CD", text:"#7A4F00", border:"#F5C200" },
  error:       { bg:"#FDECEA", text:"#8B1A1A", border:"#F5A8A8" },
  purple:      { bg:"#F0EEFB", text:"#4A3AAA", border:"#A89FE0" },
  teal:        { bg:"#E6F6F4", text:"#0D5C54", border:"#7DCEC8" },
  amber:       { bg:"#FEF0E6", text:"#7A3300", border:"#F5B885" },
  neutral:     { bg:"#F0F4FA", text:"#4A5568", border:"#D8DCE3" },
  d1Border:    "#1B5EBE",
  d2Border:    "#D4A800",
};

const S = {
  card:     { background:DS.white, border:`0.5px solid ${DS.border}`, borderRadius:8, padding:16 },
  input:    { background:DS.inputBg, border:"1px solid transparent", borderRadius:6, padding:"8px 10px", fontSize:13, color:DS.textPrimary, width:"100%", outline:"none", boxSizing:"border-box", fontFamily:"'Inter',sans-serif" },
  btn:      { background:DS.blue, color:"white", border:"none", borderRadius:6, padding:"8px 16px", fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"'Inter',sans-serif" },
  btnCTA:   { background:DS.yellow, color:DS.textPrimary, border:"none", borderRadius:6, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" },
  btnGhost: { background:"white", color:DS.textPrimary, border:`1.5px solid ${DS.border}`, borderRadius:6, padding:"6px 14px", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"'Inter',sans-serif" },
  btnSec:   { background:"transparent", color:DS.blue, border:`1.5px solid ${DS.blue}`, borderRadius:6, padding:"6px 14px", fontSize:12, cursor:"pointer", fontFamily:"'Inter',sans-serif" },
};


// Display helper — short facility name for UI only, Firestore values unchanged
function shortFacility(name) {
  if(!name) return name;
  if(name.includes("Dorcus 1")) return "Dorcus 1";
  if(name.includes("Dorcus 2")) return "Dorcus 2";
  return name;
}


// Display helper — Last, First name format for UI
function displayName(name) {
  if(!name) return "";
  const parts = name.trim().split(" ");
  if(parts.length < 2) return name;
  const last = parts[parts.length - 1];
  const first = parts.slice(0, -1).join(" ");
  return `${last}, ${first}`;
}

function Pill({ label, bg=DS.neutral.bg, border=DS.neutral.border, color=DS.neutral.text }) {
  return <span style={{background:bg,border:`1px solid ${border}`,color,borderRadius:10,padding:"2px 8px",fontSize:11,fontWeight:500,fontFamily:"'Inter',sans-serif"}}>{label}</span>;
}

function SectionHdr({ children }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
      <span style={{fontSize:10,fontWeight:600,letterSpacing:"0.1em",color:DS.blue,textTransform:"uppercase",whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>{children}</span>
      <div style={{flex:1,height:1,background:DS.border}}/>
    </div>
  );
}

function InfoRow({ label, value, alert }) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:2}}>
      <span style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:"'Inter',sans-serif"}}>{label}</span>
      <span style={{fontSize:13,color:alert?DS.error.text:DS.textPrimary,fontWeight:alert?600:400,fontFamily:"'Inter',sans-serif"}}>{value||"—"}</span>
    </div>
  );
}

function CheckProgress({ checklist }) {
  const done=(checklist||[]).filter(Boolean).length,total=CHECKLIST_ITEMS.length,pct=total?Math.round(done/total*100):0;
  const c=pct===100?DS.success.border:pct>50?DS.yellow:DS.error.border;
  return (
    <div style={{display:"flex",alignItems:"center",gap:8}}>
      <div style={{flex:1,height:6,background:DS.border,borderRadius:3,overflow:"hidden"}}>
        <div style={{width:`${pct}%`,height:"100%",background:c,borderRadius:3}}/>
      </div>
      <span style={{fontSize:11,color:DS.textSec,whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>{done}/{total}</span>
    </div>
  );
}

// ── Offense Selector ──────────────────────────────────────────────────────────
function OffenseSelector({ label, value, onChange }) {
  // value is an array of strings
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const [open, setOpen] = useState(false);
  const [otherText, setOtherText] = useState(() => {
    const other = selected.find(s => !OFFENSE_OPTIONS.slice(0,-1).includes(s));
    return other && other !== "Other" ? other : "";
  });
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (offense) => {
    if (offense === "Other") {
      const hasOther = selected.some(s => !OFFENSE_OPTIONS.slice(0,-1).includes(s) || s === "Other");
      if (hasOther) {
        onChange(selected.filter(s => OFFENSE_OPTIONS.slice(0,-1).includes(s) && s !== "Other"));
        setOtherText("");
      } else {
        onChange([...selected, "Other"]);
      }
      return;
    }
    if (selected.includes(offense)) {
      onChange(selected.filter(s => s !== offense));
    } else {
      onChange([...selected.filter(s => s !== "Other"), offense]);
    }
  };

  const hasOther = selected.some(s => !OFFENSE_OPTIONS.slice(0,-1).includes(s) || s === "Other");

  const handleOtherText = (text) => {
    setOtherText(text);
    const base = selected.filter(s => OFFENSE_OPTIONS.slice(0,-1).includes(s) && s !== "Other");
    onChange(text.trim() ? [...base, text.trim()] : base);
  };

  const displayText = selected.length === 0 ? "Select offenses..." :
    selected.map(s => s.length > 20 ? s.slice(0,18)+"…" : s).join(", ");

  return (
    <div ref={ref} style={{position:"relative"}}>
      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{label}</div>
      <div onClick={()=>setOpen(o=>!o)} style={{...S.input,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",userSelect:"none"}}>
        <span style={{fontSize:12,color:selected.length?DS.textPrimary:DS.textSec,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{displayText}</span>
        <span style={{color:DS.textSec,flexShrink:0,marginLeft:8}}>{open?"▲":"▼"}</span>
      </div>
      {selected.length > 0 && (
        <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:6}}>
          {selected.map(s=>(
            <span key={s} style={{fontSize:10,padding:"2px 8px",borderRadius:6,background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,color:"#a5b4fc",display:"flex",alignItems:"center",gap:4}}>
              {s.length>24?s.slice(0,22)+"…":s}
              <span onClick={()=>toggle(s==="Other"||!OFFENSE_OPTIONS.slice(0,-1).includes(s)?"Other":s)} style={{cursor:"pointer",fontWeight:700}}>×</span>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:200,background:"white",border:`1px solid ${DS.border}`,borderRadius:12,boxShadow:"0 10px 40px rgba(0,0,0,0.6)",maxHeight:280,overflowY:"auto"}}>
          {OFFENSE_OPTIONS.map(offense => {
            const isSelected = offense === "Other" ? hasOther : selected.includes(offense);
            return (
              <div key={offense} onClick={()=>{toggle(offense);setOpen(false);}}
                style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",cursor:"pointer",background:isSelected?"rgba(27,94,190,0.08)":"white",borderBottom:"1px solid rgba(51,65,85,0.5)"}}
                onMouseEnter={e=>e.currentTarget.style.background=isSelected?"rgba(27,94,190,0.1)":"white,0.04)"}
                onMouseLeave={e=>e.currentTarget.style.background=isSelected?"rgba(27,94,190,0.08)":"white"}>
                <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${isSelected?"#1B5EBE":"#D8DCE3"}`,background:isSelected?"#1B5EBE":"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {isSelected&&<span style={{color:DS.textPrimary,fontSize:10,fontWeight:700}}>✓</span>}
                </div>
                <span style={{fontSize:12,color:isSelected?DS.blue:DS.textPrimary}}>{offense}</span>
              </div>
            );
          })}
        </div>
      )}
      {hasOther && (
        <div style={{marginTop:8}}>
          <input style={{...S.input,fontSize:12}} placeholder="Describe other offense..." value={otherText} onChange={e=>handleOtherText(e.target.value)}/>
        </div>
      )}
    </div>
  );
}

// ── District Dropdown ─────────────────────────────────────────────────────────
function DistrictSelect({ value, onChange, style: extraStyle }) {
  return (
    <select style={{...S.input,...extraStyle}} value={value||""} onChange={e=>onChange(e.target.value)}>
      <option value="">Select district...</option>
      {VA_DISTRICTS.map(d=><option key={d.n} value={String(d.n)}>{d.label}</option>)}
    </select>
  );
}

// ── OTC Entry ─────────────────────────────────────────────────────────────────
function OTCEntry({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ medication:"", dose:"", reason:"", loggedBy:"" });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const [query, setQuery] = useState("");
  const [showSug, setShowSug] = useState(false);
  const ref = useRef(null);

  const OTC_MEDS = MEDICATION_DATABASE.filter(m =>
    ["Allergy","Sleep","Pain","GI","Supplement","Respiratory/Allergy"].includes(m.category)
  ).map(m => m.name);

  const suggestions = useMemo(() => {
    if (!query || query.length < 2) return [];
    return OTC_MEDS.filter(n => n.toLowerCase().includes(query.toLowerCase())).slice(0,6);
  }, [query]);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleAdd = () => {
    if (!form.medication || !form.loggedBy) return;
    onAdd({ ...form, date:TODAY, time:timeNow() });
    setForm({ medication:"", dose:"", reason:"", loggedBy:"" });
    setQuery(""); setOpen(false);
  };

  if (!open) return (
    <button onClick={()=>setOpen(true)} style={{...S.btnGhost,fontSize:12,width:"100%"}}>+ Log OTC Medication</button>
  );

  return (
    <div ref={ref} style={{...S.card,border:"1px solid #334155"}}>
      <div style={{fontSize:11,fontWeight:600,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Log OTC Medication</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        <div style={{position:"relative",gridColumn:"span 2"}}>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Medication *</div>
          <input style={{...S.input}} placeholder="Search or type..." value={query}
            onChange={e=>{setQuery(e.target.value);set("medication",e.target.value);setShowSug(true);}}
            onFocus={()=>query.length>=2&&setShowSug(true)}/>
          {showSug && suggestions.length > 0 && (
            <div style={{position:"absolute",top:"calc(100%+2px)",left:0,right:0,zIndex:200,background:"white",border:`1px solid ${DS.border}`,borderRadius:10,boxShadow:"0 8px 32px rgba(0,0,0,0.5)",maxHeight:180,overflowY:"auto"}}>
              {suggestions.map(name=>(
                <div key={name} onClick={()=>{setQuery(name);set("medication",name);setShowSug(false);}}
                  style={{padding:"8px 14px",cursor:"pointer",fontSize:13,color:DS.textPrimary,borderBottom:"1px solid rgba(51,65,85,0.5)"}}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Dose</div>
          <input style={{...S.input}} placeholder="e.g. 500mg" value={form.dose} onChange={e=>set("dose",e.target.value)}/>
        </div>
        <div>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Reason / Symptom</div>
          <input style={{...S.input}} placeholder="e.g. Headache" value={form.reason} onChange={e=>set("reason",e.target.value)}/>
        </div>
        <div style={{gridColumn:"span 2"}}>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Staff Name *</div>
          <input style={{...S.input}} placeholder="Staff who administered" value={form.loggedBy} onChange={e=>set("loggedBy",e.target.value)}/>
        </div>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button onClick={()=>setOpen(false)} style={{...S.btnGhost,fontSize:12}}>Cancel</button>
        <button onClick={handleAdd} disabled={!form.medication||!form.loggedBy}
          style={{...S.btn,fontSize:12,opacity:(!form.medication||!form.loggedBy)?0.4:1}}>Log OTC</button>
      </div>
    </div>
  );
}

// ── Medication Autocomplete ───────────────────────────────────────────────────
function MedAutocomplete({ onAdd }) {
  const [query, setQuery] = useState("");
  const [selectedMed, setSelectedMed] = useState("");
  const [selectedDose, setSelectedDose] = useState("");
  const [frequency, setFrequency] = useState("Once Daily");
  const [onceSlot, setOnceSlot] = useState("Morning");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef(null);

  const suggestions = useMemo(() => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return MEDICATION_NAMES.filter(n => n.toLowerCase().includes(q)).slice(0, 8);
  }, [query]);

  const doses = useMemo(() => selectedMed ? getDosesForMed(selectedMed) : [], [selectedMed]);

  useEffect(() => {
    const handler = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectMed = (name) => {
    setSelectedMed(name);
    setQuery(name);
    setSelectedDose("");
    setShowSuggestions(false);
  };

  const handleAdd = () => {
    const medName = selectedMed || query.trim();
    if (!medName) return;
    const slots = frequency === "Once Daily" ? [onceSlot] : FREQUENCY_SLOTS[frequency];
    const med = {
      name: medName,
      dose: selectedDose,
      frequency,
      slots: slots || ["Morning"],
      active: true,
      addedDate: TODAY,
    };
    onAdd(med);
    setQuery(""); setSelectedMed(""); setSelectedDose("");
    setFrequency("Once Daily"); setOnceSlot("Morning");
  };

  return (
    <div ref={containerRef} style={{background:DS.border,border:"1px solid #334155",borderRadius:12,padding:14}}>
      <div style={{fontSize:11,fontWeight:600,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Add Medication</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        {/* Medication name */}
        <div style={{position:"relative",gridColumn:"span 2"}}>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Medication Name *</div>
          <input style={{...S.input}} placeholder="Type to search..." value={query}
            onChange={e=>{setQuery(e.target.value);setSelectedMed("");setShowSuggestions(true);}}
            onFocus={()=>query.length>=2&&setShowSuggestions(true)}/>
          {showSuggestions && suggestions.length > 0 && (
            <div style={{position:"absolute",top:"calc(100% + 2px)",left:0,right:0,zIndex:200,background:"white",border:`1px solid ${DS.border}`,borderRadius:10,boxShadow:"0 8px 32px rgba(0,0,0,0.5)",maxHeight:200,overflowY:"auto"}}>
              {suggestions.map(name => {
                const med = MEDICATION_DATABASE.find(m => m.name === name);
                return (
                  <div key={name} onClick={()=>selectMed(name)}
                    style={{padding:"8px 14px",cursor:"pointer",borderBottom:"1px solid rgba(51,65,85,0.5)"}}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div style={{fontSize:13,color:DS.textPrimary}}>{name}</div>
                    {med&&<div style={{fontSize:10,color:DS.textSec}}>{med.category}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* Dosage */}
        <div>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Dosage</div>
          {doses.length > 0
            ? <>
                <select style={{...S.input}} value={doses.includes(selectedDose)?selectedDose:"Other"} onChange={e=>{if(e.target.value!=="Other")setSelectedDose(e.target.value);else setSelectedDose("");}}>
                  <option value="">Select dose...</option>
                  {doses.map(d=><option key={d} value={d}>{d}</option>)}
                  <option value="Other">Other (enter manually)</option>
                </select>
                {!doses.includes(selectedDose)&&<input style={{...S.input,marginTop:6}} placeholder="Enter dosage e.g. 37.5mg" value={selectedDose} onChange={e=>setSelectedDose(e.target.value)}/>}
              </>
            : <input style={{...S.input}} placeholder="e.g. 10mg" value={selectedDose} onChange={e=>setSelectedDose(e.target.value)}/>
          }
        </div>
        {/* Frequency */}
        <div>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Frequency</div>
          <select style={{...S.input}} value={frequency} onChange={e=>setFrequency(e.target.value)}>
            {Object.keys(FREQUENCY_SLOTS).map(f=><option key={f}>{f}</option>)}
          </select>
        </div>
        {/* Once daily slot selector */}
        {frequency === "Once Daily" && (
          <div style={{gridColumn:"span 2"}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Administration Time</div>
            <div style={{display:"flex",gap:6}}>
              {DOSE_SLOTS.map(slot=>(
                <button key={slot} type="button" onClick={()=>setOnceSlot(slot)}
                  style={{flex:1,padding:"7px",fontSize:12,borderRadius:8,cursor:"pointer",fontWeight:onceSlot===slot?600:400,
                    background:onceSlot===slot?"rgba(79,70,229,0.2)":"transparent",
                    border:`1px solid ${onceSlot===slot?DS.blue:DS.border}`,
                    color:onceSlot===slot?DS.blue:DS.textSec}}>
                  {slot}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Show auto slots for other frequencies */}
        {frequency !== "Once Daily" && frequency !== "As Needed (PRN)" && (
          <div style={{gridColumn:"span 2"}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Administration Times</div>
            <div style={{display:"flex",gap:6}}>
              {(FREQUENCY_SLOTS[frequency]||[]).map(slot=>(
                <span key={slot} style={{flex:1,padding:"7px",fontSize:12,borderRadius:8,textAlign:"center",
                  background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,color:DS.blue}}>
                  {slot}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <button onClick={handleAdd} disabled={!query.trim()}
        style={{...S.btn,width:"100%",opacity:!query.trim()?0.4:1}}>
        + Add Medication
      </button>
    </div>
  );
}
function DocumentUploader({ entityType, entityId, checklistIndex, itemLabel, canUpload }) {
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  // Load existing uploads from storage path via URL stored in state
  // In full implementation these would be fetched from Firestore metadata
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!entityId) { setError("Save this record first before uploading documents."); return; }
    setError(""); setUploading(true); setProgress(0);
    try {
      const result = await uploadDocument(entityType, entityId, checklistIndex, file, setProgress);
      setUploads(prev => [...prev, result]);
      auditAction("document_uploaded", { entityId, entityType, fileName:file.name, checklistIndex });
    } catch (err) {
      setError("Upload failed: " + err.message);
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div style={{marginTop:4}}>
      {uploads.length > 0 && (
        <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:6}}>
          {uploads.map((u,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 10px",borderRadius:8,background:"#E6F4EC",border:"1px solid #A3D4B4"}}>
              <span style={{fontSize:11,color:"#1A6B35",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📎 {u.name}</span>
              <a href={u.url} target="_blank" rel="noopener noreferrer" style={{fontSize:10,color:"#1B5EBE",textDecoration:"none",flexShrink:0}}>View</a>
              <a href={u.url} download={u.name} style={{fontSize:10,color:"#1B5EBE",textDecoration:"none",flexShrink:0}}>↓</a>
            </div>
          ))}
        </div>
      )}
      {uploading && (
        <div style={{marginBottom:6}}>
          <div style={{height:4,background:DS.border,borderRadius:2,overflow:"hidden"}}>
            <div style={{width:`${progress}%`,height:"100%",background:DS.blue,transition:"width 0.2s"}}/>
          </div>
          <div style={{fontSize:10,color:DS.textSec,marginTop:2}}>Uploading... {progress}%</div>
        </div>
      )}
      {error && <div style={{fontSize:11,color:"#8B1A1A",marginBottom:4}}>{error}</div>}
      {canUpload && (
        <>
          <input ref={inputRef} type="file" style={{display:"none"}} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileSelect}/>
          <button onClick={()=>inputRef.current?.click()} disabled={uploading}
            style={{fontSize:10,padding:"3px 10px",borderRadius:6,cursor:uploading?"not-allowed":"pointer",background:"transparent",border:"1px solid #334155",color:DS.textSec,opacity:uploading?0.5:1}}>
            {uploads.length>0?"+ Add File":"Upload"}
          </button>
        </>
      )}
    </div>
  );
}

// ── Role Switcher ─────────────────────────────────────────────────────────────
function RoleSwitcher({ role, setRole }) {
  return (
    <div style={{background:"white",borderBottom:"2px solid #1B5EBE",padding:"10px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
      <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:DS.textSec}}>
        <span style={{color:"#1B5EBE",fontWeight:700}}>👁 Preview as:</span>
        <span style={{color:ROLES[role].dot,fontWeight:700}}>{ROLES[role].label}</span>
        {PERMISSIONS[role].assignedFacility && <span style={{color:DS.textSec}}>· {PERMISSIONS[role].assignedFacility}</span>}
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {Object.entries(ROLES).map(([key,val])=>{
          const active=role===key;
          return (
            <button key={key} onClick={()=>setRole(key)} style={{
              padding:"5px 14px",fontSize:12,borderRadius:8,cursor:"pointer",fontWeight:active?700:400,transition:"all 0.15s",
              background:active?val.activeBg:"transparent",
              border:`1px solid ${active?val.activeBorder:DS.border}`,
              color:active?val.activeColor:"#4A5568",
            }}>{val.label}</button>
          );
        })}
      </div>
    </div>
  );
}

// ── Resident Card ─────────────────────────────────────────────────────────────
function ResidentCard({ resident, onClick, perms }) {
  const s = statusColors[resident.status];
  const cs = classStyle[resident.classification];
  const [hover, setHover] = useState(false);
  const isExtension = resident.extension === true;
  const isD2 = resident.facility === "Dorcus 2 - 24th St";
  const houseBorder = isD2 ? DS.d2Border : DS.d1Border;

  // Badge helpers
  const badge = (label, style) => (
    <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,fontWeight:500,fontFamily:"'Inter',sans-serif",...style}}>{label}</span>
  );

  // Status badge
  const statusBadge = () => {
    if (resident.status==="active") return badge("Active", {background:DS.success.bg,color:DS.success.text,border:`1px solid ${DS.success.border}`});
    if (resident.status==="pending_arrival") return badge("Pending Arrival", {background:DS.warning.bg,color:DS.warning.text,border:`1px solid ${DS.warning.border}`});
    if (resident.status==="discharged") return badge("Discharged", {background:DS.neutral.bg,color:DS.neutral.text,border:`1px solid ${DS.neutral.border}`});
    return badge(s?.label||resident.status, {background:DS.neutral.bg,color:DS.neutral.text,border:`1px solid ${DS.neutral.border}`});
  };

  // Classification badge
  const classBadge = () => {
    if (!resident.classification) return null;
    if (resident.classification==="Violent") return badge("Violent", {background:DS.error.bg,color:DS.error.text,border:`1px solid ${DS.error.border}`});
    if (resident.classification==="Sex Offender") return badge("Sex Offender", {background:DS.purple.bg,color:DS.purple.text,border:`1px solid ${DS.purple.border}`});
    return badge("Non-Violent", {background:DS.success.bg,color:DS.success.text,border:`1px solid ${DS.success.border}`});
  };

  // Dietary flag — show for 7 days after intake form completed
  const showDietaryFlag = (() => {
    const dn = resident.dietaryNeeds;
    const dietaryFormDone = resident.intakeForms?.dietaryNeeds?.completedAt;
    if(!dn && !dietaryFormDone) return false;
    // Only show if there are actual restrictions or allergies
    if(dn && !dn.hasRestrictions && !dn.hasAllergies) return false;
    if(!dn?.hasRestrictions && !dn?.hasAllergies) return false;
    if(!resident.entryDate) return true;
    const daysSinceEntry = Math.floor((new Date()-new Date(resident.entryDate))/(1000*60*60*24));
    return daysSinceEntry<=7;
  })();

  return (
    <button onClick={()=>onClick(resident)} onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{width:"100%",textAlign:"left",background:DS.white,borderTop:`0.5px solid ${hover?DS.blue:DS.border}`,borderRight:`0.5px solid ${hover?DS.blue:DS.border}`,borderBottom:`0.5px solid ${hover?DS.blue:DS.border}`,borderLeft:`3px solid ${houseBorder}`,borderRadius:8,padding:"12px 14px",cursor:"pointer",transition:"border-color 0.15s",fontFamily:"'Inter',sans-serif"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
        <div>
          <div style={{fontSize:14,fontWeight:600,color:DS.textPrimary,marginBottom:2,fontFamily:"'DM Sans',sans-serif"}}>{displayName(resident.name)}</div>
          <div style={{fontSize:11,color:DS.textSec}}>{(perms.canEditHighLevel||perms.raOnly)?`DOC# ${resident.doc}`:resident.facility||"Unassigned"}</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
          {statusBadge()}
          {isExtension && badge("Extension", {background:DS.purple.bg,color:DS.purple.text,border:`1px solid ${DS.purple.border}`})}
        </div>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
        {!perms?.raOnly && classBadge()}
        {resident.facility && badge(shortFacility(resident.facility), {background:DS.blueTint,color:DS.blue,border:`1px solid ${DS.blueBorder}`})}
        {!perms?.raOnly && resident.mhLevel && resident.mhLevel !== "MH-0" && badge(resident.mhLevel, {background:"white",color:DS.purple.text,border:`1px solid ${DS.purple.border}`})}
        {!perms?.raOnly && resident.gangAffiliated && badge("Gang", {background:DS.amber.bg,color:DS.amber.text,border:`1px solid ${DS.amber.border}`})}
        {!perms?.raOnly && resident.substanceAbuse && badge("SA", {background:DS.teal.bg,color:DS.teal.text,border:`1px solid ${DS.teal.border}`})}
        {!perms?.raOnly && resident.noContact && badge("No-Contact", {background:DS.error.bg,color:DS.error.text,border:`1px solid ${DS.error.border}`})}
        {showDietaryFlag && <span title={`Dietary: ${resident.dietaryNeeds?.restrictions||""} ${resident.dietaryNeeds?.allergies?"| Allergies: "+resident.dietaryNeeds.allergies:""}`} style={{fontSize:11,padding:"2px 8px",borderRadius:10,fontWeight:500,fontFamily:"'Inter',sans-serif",background:DS.amber.bg,color:DS.amber.text,border:`1px solid ${DS.amber.border}`,cursor:"default"}}>⚠ Dietary</span>}
      </div>
    </button>
  );
}

// ── Movement History ──────────────────────────────────────────────────────────
function MovementHistory({ residentId, residentName }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("");

  useEffect(() => {
    if (!residentId) { setLoading(false); return; }
    const unsub = subscribeToInOutLogs(residentId, data => {
      setLogs(data);
      setLoading(false);
    });
    return unsub;
  }, [residentId]);

  const filtered = dateFilter ? logs.filter(l => l.date === dateFilter) : logs;

  if (!residentId) return (
    <div style={{textAlign:"center",padding:"32px 0",color:DS.textSec}}>
      <div style={{fontSize:13}}>Save this resident profile first to view movement history.</div>
    </div>
  );

  return (
    <div>
      <SectionHdr>Movement History</SectionHdr>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <div style={{fontSize:12,color:DS.textSec}}>{logs.length} total events</div>
        <input type="date" style={{...S.input,width:"auto",fontSize:12}} value={dateFilter} onChange={e=>setDateFilter(e.target.value)} placeholder="Filter by date"/>
        {dateFilter&&<button onClick={()=>setDateFilter("")} style={{...S.btnGhost,fontSize:11,padding:"4px 10px"}}>Clear</button>}
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>
          {dateFilter ? `No movement on ${dateFilter}` : "No movement history yet."}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {filtered.map((log,i) => {
            const isOut = log.eventType === "sign_out";
            return (
              <div key={log.id||i} style={{display:"flex",alignItems:"flex-start",gap:12,padding:"10px 14px",borderRadius:10,background:isOut?"#FFF3CD":"#E6F4EC",border:`1px solid ${isOut?"#F5C200":"#A3D4B4"}`}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:isOut?"#FFF3CD":"#E6F4EC",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0}}>
                  {isOut?"↑":"↓"}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:13,fontWeight:600,color:isOut?"#7A4F00":"#1A6B35"}}>
                      {isOut ? "Signed Out" : "Signed In"}
                    </span>
                    {isOut && log.reason && (
                      <Pill label={log.reason} bg="#FFF3CD" border="#F5C200" color={DS.warning.text}/>
                    )}
                  </div>
                  <div style={{fontSize:11,color:DS.textSec,marginTop:3,display:"flex",gap:12,flexWrap:"wrap"}}>
                    <span>📅 {log.date}</span>
                    <span>🕐 {log.time}</span>
                    {isOut && log.estReturn && <span>Est. return: {formatTime12(log.estReturn)}</span>}
                    {!isOut && log.timeWasOut && <span>Was out since: {log.timeWasOut}</span>}
                  </div>
                  {log.note && <div style={{fontSize:11,color:DS.textSec,fontStyle:"italic",marginTop:3}}>"{log.note}"</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Resident Profile ──────────────────────────────────────────────────────────
// ── Documents Tab ─────────────────────────────────────────────────────────────
const INTAKE_DOC_LABELS = [
  "SS Card",
  "Birth Certificate",
  "State ID",
  "Passport",
  "Social Security Letter",
  "Parole Certificate",
  "Medicaid/Medicare Card",
  "Insurance Card",
  "Other",
];

const RESIDENT_DOC_CATEGORIES = [
  "Participation Summary",
  "CRP Reports",
  "Support & Financial",
  "Intake Documentation",
];

// ── Dossier Generator ─────────────────────────────────────────────────────────
function generateDossierHTML(resident, intakeForms, sections, movementRange, movementLogs=[]) {
  const r = resident;
  const f = intakeForms||{};
  const s = sections||{};

  const section = (title, rows) => `
    <div class="section">
      <div class="section-title">${title}</div>
      ${rows.map(([label,val])=>val?`<div class="row"><span class="label">${label}</span><span class="val">${val}</span></div>`:"").join("")}
    </div>`;

  const yesNo = (v) => v===true?"Yes":v===false?"No":"—";

  let html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <title>Resident Dossier — ${r.name}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,sans-serif;font-size:12px;color:#1A2B45;padding:32px;max-width:800px;margin:0 auto;}
    h1{font-size:20px;font-weight:700;margin-bottom:4px;}
    .meta{font-size:12px;color:#4A5568;margin-bottom:24px;}
    .section{margin-bottom:20px;border:1px solid #D8DCE3;border-radius:6px;overflow:hidden;}
    .section-title{background:#1B5EBE;color:white;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:0.08em;padding:7px 12px;}
    .row{display:flex;padding:6px 12px;border-bottom:1px solid #F0F4FA;}
    .row:last-child{border-bottom:none;}
    .label{width:200px;font-size:11px;color:#4A5568;text-transform:uppercase;letter-spacing:0.06em;flex-shrink:0;}
    .val{flex:1;font-size:12px;color:#1A2B45;}
    .print-btn{background:#1B5EBE;color:white;border:none;padding:8px 20px;border-radius:6px;font-size:13px;cursor:pointer;margin-bottom:20px;}
    @media print{.print-btn{display:none;}}
    @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap');
    .initials-badge{font-family:'Caveat',cursive;font-size:14px;font-weight:600;color:#1B5EBE;background:#E8F0FB;padding:1px 7px;border-radius:4px;border:1px solid #B5D4F4;margin-left:5px;display:inline-block;vertical-align:middle;}
  </style></head><body>
  <button class="print-btn" onclick="window.print()">🖨 Print / Save as PDF</button>
  <h1>${r.name}</h1>
  <div class="meta">DOC# ${r.doc||"—"} · ${shortFacility(r.facility)||"—"} · Generated ${TODAY}</div>`;

  if(s.overview) html += section("Resident Overview", [
    ["Status", r.status],
    ["Date of Birth", r.dob],
    ["Race / Ethnicity", r.race],
    ["Facility", shortFacility(r.facility)],
    ["Room", r.room],
    ["Entry Date", r.entryDate],
    ["Exit Date (Planned)", r.exitDate],
    ["Scheduled Release", r.releaseDate],
    ["Sentencing District", r.sentencingDistrict],
    ["Classification", r.classification],
    ["Gang Affiliated", yesNo(r.gangAffiliated)],
    ["Active Detainer", yesNo(r.detainer)],
    ["No-Contact Order", r.noContact?`Yes — ${r.noContactList}`:"No"],
    ["MH Level", r.mhLevel],
    ["Substance Abuse History", yesNo(r.substanceAbuse)],
    ["Employed", yesNo(r.employed)],
    ["Employer", r.employer],
    ["Referred By", r.referredBy],
    ["Referral Institution", r.referralInstitution],
    ["P&P District", r.ppDistrict],
  ]);

  if(s.intakeForms) {
    DAY1_FORMS.forEach(frm=>{
      const fd = f[frm.key];
      if(!fd?.completedAt) return;
      const rows = [];
      if(frm.key==="noticeOfArrival") {
        rows.push(["Arrival Date",fd.arrivalDate],["Arrival From",fd.arrivalFrom],["P&P Officer",fd.ppOfficer],["Urine Screen Result",fd.uaResult],["Sent to District",fd.noaSentDate?`${fd.noaSentDate} — ${fd.noaSentTo||""}`:null]);
      } else if(frm.key==="intakeForm") {
        rows.push(["Ethnic Origin",fd.ethnicOrigin],["Education",fd.education],["Marital Status",fd.maritalStatus],["EC1",fd.ec1?.name?`${fd.ec1.name} — ${fd.ec1.phone}`:null],["EC2",fd.ec2?.name?`${fd.ec2.name} — ${fd.ec2.phone}`:null]);
      } else if(frm.key==="mhScreen") {
        rows.push(["Referral Decision",fd.referralDecision==="referred"?"Referred":"Not referred"],["Comments",fd.comments]);
      } else if(frm.key==="dietaryNeeds") {
        rows.push(["Restrictions",fd.hasRestrictions?fd.restrictions:"None"],["Allergies",fd.hasAllergies?fd.allergies:"None"]);
      } else if(frm.key==="programFees") {
        rows.push(["Program Fees",fd.serviceFee===true?`Collected ${fd.serviceFeeAmount||"$100"}`:"Not collected"],["Transport Deposit",fd.transportDeposit===true?`Collected ${fd.transportDepositAmount||"$100"}`:"Not collected"],["Locker Fee",fd.lockerFee===true?"$25 collected":"Declined"]);
      } else if(frm.key==="saTreatment") {
        rows.push(["Treatment Requested",yesNo(fd.requestsTreatment)],["Facility",fd.facilityName],["Program Type",fd.programType]);
      } else {
        rows.push(["Status","Completed — "+fd.completedAt]);
      }
      html += section(frm.label, rows);
    });
  }

  if(s.medications && r.medications?.length) {
    html += section("Medications", r.medications.map((m,i)=>{
      const med = typeof m==="string"?{name:m}:m;
      return [`Medication ${i+1}`, med.name+(med.dose?` — ${med.dose}`:"")+(med.frequency?` — ${med.frequency}`:"")];
    }));
  }

  if(s.meetings && r.meetings?.length) {
    const rows = r.meetings.map(m=>[
      m.date||"",
      `${m.type==="day1"?"Day 1 Intake":m.type==="day34"?"Coordinator Assessment":"Follow-up"}${m.dpLevel?" · "+m.dpLevel:""}${m.notes?" · "+m.notes:""}`
    ]);
    html += section("Meetings", rows);
  }

  if(s.caseNotes && r.caseNotes?.length) {
    html += section("Case Notes", r.caseNotes.map(n=>[n.date, `${n.author}: ${n.note}`]));
  }

  if(s.movementLog && movementLogs.length) {
    const filtered = movementRange==="all" ? movementLogs :
      movementLogs.filter(l=>l.date>=movementRange.from&&l.date<=movementRange.to);
    const badge = r.virtualInitials ? `<span style="font-family:Caveat,cursive;font-size:14px;font-weight:600;color:#1B5EBE;background:#E8F0FB;padding:1px 7px;border-radius:4px;border:1px solid #B5D4F4;margin-left:5px;display:inline-block;vertical-align:middle;">${r.virtualInitials.toUpperCase()}</span>` : "";
    const signOuts = filtered.filter(l=>l.eventType==="sign_out").sort((a,b)=>(a.date||"").localeCompare(b.date||"")||(a.time||"").localeCompare(b.time||""));
    const signIns = filtered.filter(l=>l.eventType==="sign_in");
    const parseT = t => { const m=(t||"").match(/(\d+):(\d+)\s*(AM|PM)?/i); if(!m) return 0; let h=parseInt(m[1]),min=parseInt(m[2]); if(m[3]?.toUpperCase()==="PM"&&h!==12) h+=12; if(m[3]?.toUpperCase()==="AM"&&h===12) h=0; return h*60+min; };
    const pairedRows = signOuts.map(out => {
      const outMins = parseT(out.time);
      const ret = signIns.find(sin => sin.date > out.date || (sin.date === out.date && parseT(sin.time) > outMins));
      let dur = "";
      if(ret) { const mins = ((new Date(ret.date+"T00:00:00")).getTime()/60000 + parseT(ret.time)) - ((new Date(out.date+"T00:00:00")).getTime()/60000 + outMins); dur = mins>=60 ? `${Math.floor(mins/60)}h ${mins%60}m` : `${mins}m`; }
      const RC = {Work:"#E6F1FB",Medical:"#EEEDFE",Leisure:"#EAF3DE","P&P":"#FAEEDA",Appointment:"#E1F5EE"};
      const reasonBg = RC[out.reason]||"#F1EFE8";
      return `<tr><td>${out.date}</td><td>${out.time||""}</td><td><span style="background:${reasonBg};padding:1px 6px;border-radius:4px;font-size:11px;">${out.reason||"Out"}</span></td><td>${out.estReturn||"--"}</td><td>${ret?ret.time:"<em>Still out</em>"}</td><td>${dur||"--"}</td><td>${badge}</td></tr>`;
    }).join("");
    html += `<div class="section"><div class="section-title">Sign In / Sign Out Log</div><table style="width:100%;border-collapse:collapse;font-size:12px;"><thead><tr style="background:#E8F0FB;"><th style="text-align:left;padding:6px 8px;">Date</th><th style="text-align:left;padding:6px 8px;">Out</th><th style="text-align:left;padding:6px 8px;">Reason</th><th style="text-align:left;padding:6px 8px;">ETR</th><th style="text-align:left;padding:6px 8px;">Return</th><th style="text-align:left;padding:6px 8px;">Duration</th><th style="padding:6px 8px;"></th></tr></thead><tbody>${pairedRows||"<tr><td colspan=7 style=\"text-align:center;padding:12px;color:#999;\">No movements</td></tr>"}</tbody></table></div>`;
  }

  html += "</body></html>";
  return html;
}

function DocumentsTab({ form, perms, editing, onChecklistChange, onIntakeFormSave }) {
  const canUpload = perms.canEditHighLevel || perms.canLeaveCaseNotes || (perms.canManageReferrals);
  const isRA = perms.raOnly;

  // Dossier download state
  const [showDossier, setShowDossier] = useState(false);
  const [dossierSections, setDossierSections] = useState({
    overview:true, intakeForms:true, medications:true,
    meetings:true, caseNotes:true, movementLog:false,
  });
  const [movementRange, setMovementRange] = useState("all");
  const [movementFrom, setMovementFrom] = useState("");
  const [movementTo, setMovementTo] = useState(TODAY);
  const [movementLogs, setMovementLogs] = useState([]);
  const toggleSection = (k) => setDossierSections(s=>({...s,[k]:!s[k]}));
  const allSelected = Object.values(dossierSections).every(Boolean);
  const selectAll = () => setDossierSections(s=>Object.fromEntries(Object.keys(s).map(k=>[k,true])));
  const deselectAll = () => setDossierSections(s=>Object.fromEntries(Object.keys(s).map(k=>[k,false])));

  useEffect(() => {
    if(!showDossier||!form.id) return;
    const unsub = subscribeToInOutLogs(form.id, setMovementLogs);
    return unsub;
  }, [showDossier, form.id]);

  const openDossier = () => {
    const w = window.open("","_blank");
    const range = movementRange==="all"?"all":{from:movementFrom,to:movementTo};
    const filtered = movementRange==="all"?movementLogs:movementLogs.filter(l=>l.date>=movementFrom&&l.date<=movementTo);
    w.document.write(generateDossierHTML(form, form.intakeForms||{}, dossierSections, range, filtered));
    w.document.close();
  };

  // Section 2 — Resident Documents state
  const [residentDocs, setResidentDocs] = useState([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadCategory, setUploadCategory] = useState(RESIDENT_DOC_CATEGORIES[0]);
  const [uploadName, setUploadName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!form.id) return;
    const unsub = subscribeToResidentDocuments(form.id, setResidentDocs);
    return unsub;
  }, [form.id]);

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) return;
    setUploading(true); setUploadProgress(0);
    try {
      const safeName = uploadFile.name.replace(/[^a-zA-Z0-9._-]/g,"_");
      const path = `residentDocs/${form.id}/${uploadCategory}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, path);
      const url = await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(storageRef, uploadFile);
        task.on("state_changed",
          snap => setUploadProgress(Math.round(snap.bytesTransferred/snap.totalBytes*100)),
          reject,
          async () => resolve(await getDownloadURL(task.snapshot.ref))
        );
      });
      const newDoc = { id: Date.now().toString(), name: uploadName.trim(), category: uploadCategory, fileName: uploadFile.name, url, path, uploadedAt: TODAY, uploadedBy: "Staff" };
      await saveResidentDocument({ ...newDoc, residentId: form.id });
      auditAction("document_uploaded", { residentId:form.id, residentName:form.name, fileName:uploadName.trim(), category:uploadCategory });
      setShowUploadForm(false); setUploadName(""); setUploadFile(null); setUploadCategory(RESIDENT_DOC_CATEGORIES[0]);
    } catch(err) { alert("Upload failed: " + err.message); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const handleDelete = async (docItem) => {
    try {
      const sRef = ref(storage, docItem.path);
      await deleteObject(sRef).catch(()=>{});
      await deleteResidentDocument(form.id, docItem.id);
      auditAction("document_deleted", { residentId:form.id, residentName:form.name, fileName:docItem.name });
    } catch(err) { alert("Delete failed: " + err.message); }
    setConfirmDelete(null);
  };

  const [showIntakeUpload, setShowIntakeUpload] = useState(false);
  const [intakeLabel, setIntakeLabel] = useState(INTAKE_DOC_LABELS[0]);
  const [intakeLabelCustom, setIntakeLabelCustom] = useState("");
  const [intakeFile, setIntakeFile] = useState(null);
  const [intakeUploading, setIntakeUploading] = useState(false);
  const intakeFileRef = useRef(null);

  const handleIntakeUpload = async () => {
    if (!intakeFile) return;
    setIntakeUploading(true);
    try {
      const label = intakeLabel === "Other" ? (intakeLabelCustom.trim()||"Other") : intakeLabel;
      const safeName = intakeFile.name.replace(/[^a-zA-Z0-9._-]/g,"_");
      const path = `residentDocs/${form.id}/Intake Documentation/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, path);
      const url = await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(storageRef, intakeFile);
        task.on("state_changed", null, reject, async () => resolve(await getDownloadURL(task.snapshot.ref)));
      });
      await saveResidentDocument({ id:Date.now().toString(), name:label, category:"Intake Documentation", fileName:intakeFile.name, url, path, residentId:form.id, uploadedAt:TODAY });
      setShowIntakeUpload(false); setIntakeFile(null); setIntakeLabel(INTAKE_DOC_LABELS[0]); setIntakeLabelCustom("");
    } catch(e) { alert("Upload failed: "+e.message); }
    setIntakeUploading(false);
  };

  const docsByCategory = RESIDENT_DOC_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = residentDocs.filter(d => d.category === cat);
    return acc;
  }, {});

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>

      {/* Dossier download panel — admin only */}
      {perms.canEditHighLevel&&(
        <div style={{...S.card}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <i className="bi bi-printer-fill" style={{fontSize:14,color:DS.blue}}></i>
              <span style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Download Dossier</span>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowDossier(s=>!s)} style={{...S.btnGhost,fontSize:11,padding:"4px 12px"}}>
                {showDossier?"Hide options":"Select sections"}
              </button>
              {showDossier&&<button onClick={openDossier} style={{...S.btn,fontSize:11,padding:"4px 14px"}}>
                <i className="bi bi-printer" style={{fontSize:11,marginRight:4}}></i>Open for printing
              </button>}
            </div>
          </div>
          {showDossier&&(
            <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:10}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Select sections to include</span>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={selectAll} style={{fontSize:10,color:DS.blue,background:"transparent",border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Select all</button>
                  <button onClick={deselectAll} style={{fontSize:10,color:DS.textSec,background:"transparent",border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Deselect all</button>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[
                  {key:"overview",      label:"Resident Overview"},
                  {key:"intakeForms",   label:"Day 1 Intake Forms"},
                  {key:"medications",   label:"Medications"},
                  {key:"meetings",      label:"Meetings"},
                  {key:"caseNotes",     label:"Case Notes"},
                  {key:"movementLog",   label:"Sign In/Sign Out Log"},
                ].map(({key,label})=>(
                  <div key={key} onClick={()=>toggleSection(key)}
                    style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:6,cursor:"pointer",
                      background:dossierSections[key]?DS.blueTint:"transparent",
                      border:`1px solid ${dossierSections[key]?DS.blueBorder:DS.border}`}}>
                    <div style={{width:14,height:14,borderRadius:4,border:`2px solid ${dossierSections[key]?DS.blue:DS.border}`,background:dossierSections[key]?DS.blue:"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {dossierSections[key]&&<i className="bi bi-check" style={{fontSize:9,color:DS.textPrimary}}></i>}
                    </div>
                    <span style={{fontSize:12,color:dossierSections[key]?DS.blue:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{label}</span>
                  </div>
                ))}
              </div>
              {dossierSections.movementLog&&(
                <div style={{background:DS.pageBg,borderRadius:6,padding:"10px 12px",display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{fontSize:11,fontWeight:500,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>Movement Log range</div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>setMovementRange("all")} style={{fontSize:11,padding:"4px 12px",borderRadius:5,border:`1.5px solid ${movementRange==="all"?DS.blue:DS.border}`,background:movementRange==="all"?DS.blueTint:"white",color:movementRange==="all"?DS.blue:DS.textSec,cursor:"pointer"}}>All movement</button>
                    <button onClick={()=>setMovementRange("range")} style={{fontSize:11,padding:"4px 12px",borderRadius:5,border:`1.5px solid ${movementRange==="range"?DS.blue:DS.border}`,background:movementRange==="range"?DS.blueTint:"white",color:movementRange==="range"?DS.blue:DS.textSec,cursor:"pointer"}}>Date range</button>
                  </div>
                  {movementRange==="range"&&(
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <input type="date" style={{...S.input,border:`1px solid ${DS.border}`,width:"auto",fontSize:11}} value={movementFrom} onChange={e=>setMovementFrom(e.target.value)}/>
                      <span style={{fontSize:11,color:DS.textSec}}>to</span>
                      <input type="date" style={{...S.input,border:`1px solid ${DS.border}`,width:"auto",fontSize:11}} value={movementTo} onChange={e=>setMovementTo(e.target.value)}/>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Section 0 — Day 1 Intake Forms */}
      {!isRA && (
        <div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <SectionHdr>Day 1 Intake Forms</SectionHdr>
            {perms.canEditHighLevel&&(
              <button onClick={()=>{
                const w = window.open("","_blank");
                const forms = form.intakeForms||{};
                w.document.write(generateDossierHTML(form, forms, {
                  noticeOfArrival:true,intakeForm:true,mhScreen:true,dietaryNeeds:true,
                  propertyAgreement:true,leaseAgreement:true,programFees:true,
                  releaseOfInfo:true,saTreatment:true
                }, "all"));
                w.document.close();
              }} style={{...S.btnSec,fontSize:11,padding:"4px 12px",display:"flex",alignItems:"center",gap:5}}>
                <i className="bi bi-printer" style={{fontSize:11}}></i> Print Dossier
              </button>
            )}
          </div>
          {(()=>{
            const forms = form.intakeForms||{};
            const completedCount = DAY1_FORMS.filter(f=>forms[f.key]?.completedAt).length;
            const allDone = completedCount===DAY1_FORMS.length;
            const canEditForms = perms.canEditHighLevel || perms.role==="pm";
            return (
              <div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{completedCount} of {DAY1_FORMS.length} forms complete</div>
                  {allDone&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:DS.success.bg,color:DS.success.text,border:`1px solid ${DS.success.border}`,fontFamily:"'Inter',sans-serif"}}>All complete</span>}
                </div>
                <div style={{height:4,background:DS.border,borderRadius:2,overflow:"hidden",marginBottom:12}}>
                  <div style={{width:`${(completedCount/DAY1_FORMS.length)*100}%`,height:"100%",background:allDone?DS.success.border:DS.blue,borderRadius:2}}/>
                </div>
                {/* Completed forms — compact rows with view/edit modals */}
                <div style={{border:`0.5px solid ${DS.border}`,borderRadius:8,overflow:"hidden",marginBottom:8}}>
                  {DAY1_FORMS.filter(f=>forms[f.key]?.completedAt).map(f=>(
                    <IntakeFormViewer
                      key={f.key}
                      formKey={f.key}
                      formData={forms[f.key]}
                      residentName={form.name}
                      doc={form.doc}
                      entryDate={form.entryDate}
                      facility={form.facility}
                      canEdit={canEditForms}
                      currentUser={perms.displayName||"Staff"}
                      onSave={(updated)=>{
                        const updatedForms = {...forms,[f.key]:updated};
                        onIntakeFormSave&&onIntakeFormSave(updatedForms);
                      }}
                    />
                  ))}
                </div>
                {/* Pending forms */}
                {DAY1_FORMS.filter(f=>!forms[f.key]?.completedAt).map((f,i)=>(
                  <div key={f.key} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:6,background:DS.pageBg,border:`0.5px solid ${DS.border}`,marginBottom:6}}>
                    <div style={{width:20,height:20,borderRadius:"50%",background:DS.border,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <span style={{fontSize:9,color:DS.textPrimary,fontWeight:600}}>{DAY1_FORMS.indexOf(f)+1}</span>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{f.label}</div>
                    </div>
                    <span style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:DS.pageBg,color:DS.textSec,border:`1px solid ${DS.border}`,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>Pending</span>
                  </div>
                ))}
                {completedCount===0&&(
                  <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif",padding:"4px 0"}}>
                    No intake forms completed yet · Use PC Workflow → Intake to begin
                  </div>
                )}
                {/* Discharge Meeting — show if completed */}
                {forms.dischargeMeeting?.completedAt&&(
                  <div style={{marginTop:10,border:`0.5px solid ${DS.success.border}`,borderRadius:8,overflow:"hidden"}}>
                    <div style={{padding:"8px 12px",background:DS.success.bg,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <i className="bi bi-box-arrow-right" style={{fontSize:13,color:DS.success.text}}></i>
                        <span style={{fontSize:12,fontWeight:600,color:DS.success.text,fontFamily:"'Inter',sans-serif"}}>Discharge Meeting</span>
                      </div>
                      <span style={{fontSize:10,color:DS.success.text,fontFamily:"'Inter',sans-serif"}}>Completed {forms.dischargeMeeting.completedAt} · {forms.dischargeMeeting.completedBy||"PC"}</span>
                    </div>
                    <div style={{padding:"10px 12px",display:"flex",flexDirection:"column",gap:4}}>
                      {[
                        ["Discharge Type", forms.dischargeMeeting.dischargeType==="d15"?"Staying in District 15":"Returning to Reporting District"],
                        ["Meeting Date", forms.dischargeMeeting.meetingDate],
                        ["Exit Date", forms.dischargeMeeting.exitDate],
                        ["Transportation", forms.dischargeMeeting.transport],
                        ["Compensation current", forms.dischargeMeeting.compensationYN],
                      ].filter(([,v])=>v).map(([label,val])=>(
                        <div key={label} style={{display:"flex",gap:8,fontSize:11,fontFamily:"'Inter',sans-serif"}}>
                          <span style={{color:DS.textSec,minWidth:140}}>{label}</span>
                          <span style={{color:DS.textPrimary,fontWeight:500}}>{val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Section 1 — Referral Packet (hidden from RAs) */}
      {!isRA && (
        <div>
          <SectionHdr>Referral Packet</SectionHdr>
          {(()=>{const checklist=form.checklist||Array(CHECKLIST_ITEMS.length).fill(false);
          const isPP = form.referralSource === "pp";
          const needsMH = form.mhLevel === "MH-1" || form.mhLevel === "MH-2";
          const isItemDisabled = (i) => {
            if (i === 9 && !isPP) return { disabled:true, reason:"Only required for P&P referrals" };
            if (i === 8 && !needsMH) return { disabled:true, reason:"Only required for MH-1 or MH-2" };
            return { disabled:false };
          };
          return(<>
          <div style={{marginBottom:12}}>
            <CheckProgress checklist={checklist}/>
            <div style={{fontSize:11,color:DS.textSec,marginTop:4}}>{checklist.filter(Boolean).length} of {CHECKLIST_ITEMS.length} documents received</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {CHECKLIST_ITEMS.map((item,i)=>{
              const {disabled, reason} = isItemDisabled(i);
              return (
              <div key={i} style={{borderRadius:10,border:`1px solid ${disabled?DS.border:checklist[i]?DS.success.border:DS.border}`,background:disabled?DS.pageBg:checklist[i]?DS.success.bg:"white",overflow:"hidden",opacity:disabled?0.5:1}}>
                <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px"}}>
                  <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${disabled?DS.border:checklist[i]?DS.success.text:DS.border}`,background:checklist[i]&&!disabled?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {checklist[i]&&!disabled&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}
                  </div>
                  <span style={{fontSize:13,color:disabled?DS.textSec:checklist[i]?DS.success.text:DS.textPrimary,flex:1}}>{typeof item==="object"?item.label:item}{typeof item==="object"&&item.required&&<span style={{color:DS.error.text,marginLeft:2,fontSize:10}}>*</span>}</span>
                  {disabled
                    ? <span style={{fontSize:10,color:DS.textSec,fontStyle:"italic"}}>{reason}</span>
                    : !checklist[i]&&<span style={{fontSize:11,color:DS.error.text,fontWeight:600}}>Missing</span>
                  }
                </div>
                {!disabled&&(
                <div style={{padding:"0 14px 10px 14px"}}>
                  <DocumentUploader
                    entityType="residents"
                    entityId={typeof form.id==="string"?form.id:null}
                    checklistIndex={i}
                    itemLabel={typeof item==="object"?item.label:item}
                    canUpload={canUpload}
                    onFileAdded={()=>onChecklistChange(i,true)}
                    onFileRemoved={()=>onChecklistChange(i,false)}
                  />
                </div>
                )}
              </div>
            );})}
          </div>
          </>);})()}
        </div>
      )}

      {/* Section 2 — Resident Documents */}
      <div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <SectionHdr>Resident Documents</SectionHdr>
          {canUpload && (
            <button onClick={()=>setShowUploadForm(s=>!s)}
              style={{...S.btn,fontSize:11,padding:"5px 14px"}}>
              {showUploadForm?"Cancel":"+ Upload Document"}
            </button>
          )}
        </div>

        {/* Upload form */}
        {showUploadForm && canUpload && (
          <div style={{...S.card,marginBottom:16,border:"1px solid #B5D4F4"}}>
            <div style={{fontSize:12,fontWeight:600,color:"#1B5EBE",marginBottom:12}}>Upload New Document</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Category</div>
                <select style={{...S.input}} value={uploadCategory} onChange={e=>setUploadCategory(e.target.value)}>
                  {RESIDENT_DOC_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Document Name *</div>
                <input style={{...S.input}} placeholder="e.g. Goals Progress Note — March 2026" value={uploadName} onChange={e=>setUploadName(e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>File *</div>
                <input ref={fileInputRef} type="file" style={{display:"none"}} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e=>setUploadFile(e.target.files[0]||null)}/>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <button type="button" onClick={()=>fileInputRef.current?.click()}
                    style={{...S.btnGhost,fontSize:12}}>
                    {uploadFile?"Change File":"Choose File"}
                  </button>
                  {uploadFile&&<span style={{fontSize:12,color:"#1A6B35"}}>📎 {uploadFile.name}</span>}
                </div>
              </div>
              {uploading && (
                <div>
                  <div style={{height:4,background:DS.border,borderRadius:2,overflow:"hidden"}}>
                    <div style={{width:`${uploadProgress}%`,height:"100%",background:"#4f46e5",transition:"width 0.2s"}}/>
                  </div>
                  <div style={{fontSize:11,color:DS.textSec,marginTop:2}}>Uploading {uploadProgress}%...</div>
                </div>
              )}
              <button onClick={handleUpload} disabled={!uploadFile||!uploadName.trim()||uploading}
                style={{...S.btn,opacity:(!uploadFile||!uploadName.trim()||uploading)?0.4:1}}>
                {uploading?"Uploading...":"Upload"}
              </button>
            </div>
          </div>
        )}

        {/* Documents by category */}
        {RESIDENT_DOC_CATEGORIES.map(cat=>(
          <div key={cat} style={{marginBottom:10,background:"white",border:`0.5px solid ${DS.border}`,borderRadius:8,overflow:"hidden"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 14px",background:DS.pageBg,borderBottom:`0.5px solid ${DS.border}`}}>
              <span style={{fontSize:11,fontWeight:600,color:DS.textPrimary,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>{cat}</span>
              <span style={{fontSize:11,color:DS.textSec}}>({docsByCategory[cat].length})</span>
            </div>
            <div style={{padding:"8px 14px"}}>
            {docsByCategory[cat].length === 0 ? (
              <div style={{fontSize:12,color:DS.textSec,fontStyle:"italic",padding:"4px 0"}}>No documents uploaded yet</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {docsByCategory[cat].map(docItem=>(
                  <div key={docItem.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:DS.pageBg,border:`0.5px solid ${DS.border}`,borderRadius:6}}>
                    <span style={{fontSize:12,color:DS.textPrimary,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{docItem.name}</span>
                    <span style={{fontSize:10,color:DS.textSec,flexShrink:0}}>{docItem.uploadedAt}</span>
                    <a href={docItem.url} target="_blank" rel="noopener noreferrer"
                      style={{fontSize:11,color:DS.blue,textDecoration:"none",flexShrink:0,padding:"3px 8px",borderRadius:4,border:`1px solid ${DS.blue}`,fontFamily:"'Inter',sans-serif"}}>
                      View
                    </a>
                    {canUpload && (
                      <button onClick={()=>setConfirmDelete(docItem)}
                        style={{fontSize:11,color:DS.error.text,background:"transparent",border:`1px solid ${DS.error.border}`,borderRadius:4,padding:"3px 8px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        ))}

        {/* Intake Documentation Upload */}
        {canUpload && (
          <div style={{...S.card,padding:"14px 16px",marginTop:4}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:showIntakeUpload?12:0}}>
              <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Upload Intake Document</div>
              <button onClick={()=>setShowIntakeUpload(o=>!o)}
                style={{padding:"4px 10px",fontSize:11,borderRadius:6,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer"}}>
                {showIntakeUpload?"Cancel":"+ Upload"}
              </button>
            </div>
            {showIntakeUpload && (
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Document Type</div>
                  <select value={intakeLabel} onChange={e=>setIntakeLabel(e.target.value)}
                    style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none"}}>
                    {INTAKE_DOC_LABELS.map(l=><option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                {intakeLabel==="Other" && (
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Custom Label</div>
                    <input value={intakeLabelCustom} onChange={e=>setIntakeLabelCustom(e.target.value)}
                      placeholder="Describe the document..."
                      style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none",boxSizing:"border-box"}}/>
                  </div>
                )}
                <div>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>File</div>
                  <input type="file" ref={intakeFileRef} style={{display:"none"}} onChange={e=>setIntakeFile(e.target.files[0])}/>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <button onClick={()=>intakeFileRef.current?.click()}
                      style={{padding:"6px 12px",fontSize:12,borderRadius:6,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer"}}>
                      Choose File
                    </button>
                    <span style={{fontSize:11,color:DS.textSec}}>{intakeFile?intakeFile.name:"No file chosen"}</span>
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"flex-end"}}>
                  <button onClick={handleIntakeUpload} disabled={!intakeFile||intakeUploading}
                    style={{padding:"7px 16px",fontSize:12,borderRadius:6,border:"none",background:DS.blue,color:"white",cursor:"pointer",opacity:(!intakeFile||intakeUploading)?0.5:1}}>
                    {intakeUploading?"Uploading...":"Upload Document"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div style={{position:"fixed",inset:0,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(26,43,69,0.5)",padding:24}}>
          <div style={{background:"white",border:"1px solid #334155",borderRadius:16,padding:28,maxWidth:400,width:"100%"}}>
            <div style={{fontSize:15,fontWeight:600,color:DS.textPrimary,marginBottom:8}}>Delete Document?</div>
            <p style={{fontSize:13,color:DS.textSec,marginBottom:20}}>
              Delete <strong style={{color:DS.textPrimary}}>{confirmDelete.name}</strong>? This cannot be undone.
            </p>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setConfirmDelete(null)} style={{...S.btnGhost}}>Cancel</button>
              <button onClick={()=>handleDelete(confirmDelete)}
                style={{fontSize:12,padding:"7px 16px",borderRadius:8,cursor:"pointer",background:DS.error.text,border:"none",color:"white",fontWeight:600}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Discharge Modal ───────────────────────────────────────────────────────────
const DISCHARGE_TYPES = [
  { id:"planned",    label:"Planned Exit",         color:"#1A6B35", border:"#1A6B35", bg:"#E6F4EC",    textColor:"#1A6B35",  sub:"Scheduled program completion" },
  { id:"voluntary",  label:"Voluntary Discharge",  color:DS.blue, border:DS.blue, bg:DS.blueTint, textColor:DS.blue, sub:"Resident chose to leave program" },
  { id:"violation",  label:"P&P Violation",        color:"#7A4F00", border:"#7A4F00", bg:"#FFF3CD",    textColor:"#7A4F00",  sub:"Probation or parole violation" },
  { id:"abscond",    label:"Abscond",              color:DS.error.text, border:DS.error.border, bg:DS.error.bg, textColor:DS.error.text, sub:"Resident left without notice" },
  { id:"extension",  label:"Grant Extension",      color:"#4A3AAA", border:"#4A3AAA", bg:"#F0EEFB",   textColor:"#4A3AAA",  sub:"Admin only · Extends exit by 30 days", adminOnly:true, span:2 },
];

function DischargeModal({ resident, perms, onClose, onComplete }) {
  const [type, setType] = useState(null);
  const [step, setStep] = useState("select"); // select | checklist
  const now = new Date();
  const dateTime = now.toLocaleString("en-US", {month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"});

  // Planned / Voluntary state
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [medChecks, setMedChecks] = useState(() => {
    const meds = (resident.medications||[]).filter(m => (m.active!==false));
    return Object.fromEntries(meds.map(m => [typeof m==="string"?m:m.name, false]));
  });
  const [finances, setFinances] = useState("");
  const [financesChecked, setFinancesChecked] = useState(false);
  const [linens, setLinens] = useState(false);
  const [signoff, setSignoff] = useState(false);

  // P&P Violation state
  const [notifiedChecked, setNotifiedChecked] = useState(false);
  const [notifiedDetail, setNotifiedDetail] = useState("");

  // Abscond state
  const [notifyAdmin, setNotifyAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const activeMeds = (resident.medications||[]).filter(m => (typeof m==="string"||m.active!==false));
  const medNames = activeMeds.map(m => typeof m==="string" ? m : m.name);

  const isPlannedLike = type==="planned"||type==="voluntary";

  const plannedComplete = isPlannedLike &&
    phone.trim() && address.trim() &&
    medNames.every(n => medChecks[n]) &&
    financesChecked && finances.trim() &&
    linens && signoff;

  const violationComplete = type==="violation" && notifiedChecked &&
    medNames.every(n => medChecks[n]) && signoff;

  const abscondComplete = type==="abscond" && notifyAdmin && signoff;

  const canComplete = plannedComplete || violationComplete || abscondComplete;

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      const dischargeData = {
        residentId: resident.id,
        residentName: resident.name,
        facility: resident.facility,
        type,
        dateTime,
        completedBy: "Staff",
        ...(isPlannedLike && { phone, address, financesReturned: finances }),
        ...(type==="violation" && { notifiedDetail, financesReturned: finances }),
        medChecks,
      };
      await saveDischargeRecord(dischargeData);
      auditAction("discharge_completed", { residentId:resident.id, residentName:resident.name, dischargeType:type });

      // Fire NOD ready notification for admin
      await saveNotification({
        type: "nod_ready",
        title: `Notice of Discharge ready — ${resident.name}`,
        body: `${resident.name} (DOC# ${resident.doc}) was discharged on ${TODAY}. Generate and send the Notice of Discharge to district personnel.`,
        residentId: resident.id,
        residentName: resident.name,
        facility: resident.facility,
        read: false,
        actioned: false,
        createdAt: TODAY,
      });

      if (type==="abscond") {
        await saveAbscondNotification({
          residentId: resident.id,
          residentName: resident.name,
          facility: resident.facility,
          reportedBy: "Staff",
          reportedAt: dateTime,
          resolved: false,
          ppNotified: false,
        });
      }

      const updated = {
        ...resident,
        status: "discharged",
        actualDischargeDate: now.toISOString().split("T")[0],
        dischargeType: type,
      };
      onComplete(updated);
    } finally { setSubmitting(false); }
  };

  const handleExtension = async () => {
    if (!perms.canEditHighLevel) return;
    setSubmitting(true);
    try {
      const originalExit = resident.exitDate || "";
      const base = originalExit ? new Date(originalExit) : new Date();
      base.setDate(base.getDate() + 30);
      const newExit = base.toISOString().split("T")[0];
      const updated = {
        ...resident,
        exitDate: newExit,
        extension: true,
        extensionHistory: [...(resident.extensionHistory||[]), {
          grantedAt: now.toISOString().split("T")[0],
          grantedBy: "Admin",
          originalExit,
          newExit,
        }],
      };
      onComplete(updated);
    } finally { setSubmitting(false); }
  };

  const CS = { // checklist item styles
    row: { display:"flex", alignItems:"flex-start", gap:10, padding:"9px 12px", background:"white", border:"1px solid #1e293b", borderRadius:8, marginBottom:6 },
    box: (checked) => ({ width:18, height:18, borderRadius:5, border:`2px solid ${checked?DS.success.text:DS.border}`, background:checked?DS.success.text:"white", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, cursor:"pointer", marginTop:1 }),
    label: (checked) => ({ fontSize:13, color:checked?"#1A6B35":"#1A2B45", flex:1 }),
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.8)",padding:16}}>
      <div style={{width:"100%",maxWidth:560,maxHeight:"90vh",background:"white",border:"1px solid #334155",borderRadius:16,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 25px 60px rgba(0,0,0,0.6)"}}>

        {/* Header */}
        <div style={{padding:"16px 20px",borderBottom:"1px solid #1e293b",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontSize:15,fontWeight:600,color:DS.textPrimary}}>{step==="select"?"Select Discharge Type":`${DISCHARGE_TYPES.find(t=>t.id===type)?.label} — ${resident.name}`}</div>
            {step==="checklist"&&<div style={{fontSize:11,color:DS.textSec,marginTop:2}}>{dateTime}</div>}
          </div>
          <button onClick={onClose} style={{background:"transparent",border:"none",color:DS.textSec,cursor:"pointer",fontSize:20}}>✕</button>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:20}}>
          {/* Type selector */}
          {step==="select"&&(
            <div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {DISCHARGE_TYPES.map(t=>{
                  if (t.adminOnly && !perms.canEditHighLevel) return (
                    <div key={t.id} style={{gridColumn:t.span===2?"span 2":"auto",background:"white",border:"1px solid #1e293b",borderRadius:10,padding:14,opacity:0.4}}>
                      <div style={{fontSize:13,fontWeight:500,color:DS.textSec,marginBottom:3}}>{t.label}</div>
                      <div style={{fontSize:11,color:DS.textSec}}>Admin only</div>
                    </div>
                  );
                  return (
                    <div key={t.id} onClick={()=>{
                      if (t.id==="extension") { handleExtension(); return; }
                      setType(t.id); setStep("checklist");
                    }}
                      style={{gridColumn:t.span===2?"span 2":"auto",background:t.bg,border:`1px solid ${t.border}`,borderRadius:10,padding:14,cursor:"pointer",transition:"opacity 0.15s"}}
                      onMouseEnter={e=>e.currentTarget.style.opacity="0.85"}
                      onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                      <div style={{fontSize:13,fontWeight:500,color:t.textColor,marginBottom:3}}>{t.label}</div>
                      <div style={{fontSize:11,color:DS.textSec}}>{t.sub}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Planned / Voluntary checklist */}
          {step==="checklist"&&isPlannedLike&&(
            <div>
              <div style={{...CS.row}}>
                <div style={{...CS.box(true)}}><span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span></div>
                <div style={{...CS.label(true)}}>Date and time captured<span style={{fontSize:11,color:DS.textSec,marginLeft:8}}>{dateTime}</span></div>
              </div>
              <div style={{...CS.row,flexDirection:"column"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,width:"100%",marginBottom:phone?0:0}}>
                  <div style={{...CS.box(!!phone.trim())}} onClick={()=>{}}>
                    {phone.trim()&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}
                  </div>
                  <div style={{...CS.label(!!phone.trim())}}>Phone number</div>
                </div>
                <input style={{...S.input,marginTop:8}} placeholder="Enter phone number..." value={phone} onChange={e=>setPhone(e.target.value)}/>
              </div>
              <div style={{...CS.row,flexDirection:"column"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,width:"100%"}}>
                  <div style={{...CS.box(!!address.trim())}} onClick={()=>{}}>
                    {address.trim()&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}
                  </div>
                  <div style={{...CS.label(!!address.trim())}}>Forwarding address</div>
                </div>
                <input style={{...S.input,marginTop:8}} placeholder="Enter forwarding address..." value={address} onChange={e=>setAddress(e.target.value)}/>
              </div>
              {medNames.length > 0 && (
                <div style={{background:"white",border:"1px solid #1e293b",borderRadius:8,padding:12,marginBottom:6}}>
                  <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Medications issued back to resident</div>
                  {medNames.map(name=>(
                    <div key={name} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                      <div style={{...CS.box(medChecks[name])}} onClick={()=>setMedChecks(p=>({...p,[name]:!p[name]}))}>
                        {medChecks[name]&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}
                      </div>
                      <span style={{fontSize:12,color:medChecks[name]?"#1A6B35":"#1A2B45"}}>{name}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{...CS.row,flexDirection:"column"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,width:"100%"}}>
                  <div style={{...CS.box(financesChecked&&!!finances.trim())}} onClick={()=>financesChecked&&setFinancesChecked(false)}>
                    {financesChecked&&finances.trim()&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}
                  </div>
                  <div style={{...CS.label(financesChecked&&!!finances.trim())}}>Finances returned</div>
                </div>
                <div style={{display:"flex",gap:8,marginTop:8}}>
                  <input style={{...S.input,flex:1}} placeholder="Amount returned ($)..." value={finances} onChange={e=>setFinances(e.target.value)}/>
                  <button onClick={()=>setFinancesChecked(c=>!c)} style={{padding:"8px 14px",fontSize:11,borderRadius:8,cursor:"pointer",background:financesChecked?"rgba(5,150,105,0.2)":"transparent",border:`1px solid ${financesChecked?"#1A6B35":DS.border}`,color:financesChecked?"#1A6B35":"#D8DCE3",whiteSpace:"nowrap"}}>
                    {financesChecked?"✓ Confirmed":"Confirm"}
                  </button>
                </div>
              </div>
              <div style={{...CS.row,cursor:"pointer"}} onClick={()=>setLinens(l=>!l)}>
                <div style={{...CS.box(linens)}}>{linens&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}</div>
                <div style={{...CS.label(linens)}}>Linens returned</div>
              </div>
            </div>
          )}

          {/* P&P Violation checklist */}
          {step==="checklist"&&type==="violation"&&(
            <div>
              <div style={{...CS.row}}>
                <div style={{...CS.box(true)}}><span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span></div>
                <div style={{...CS.label(true)}}>Date and time captured<span style={{fontSize:11,color:DS.textSec,marginLeft:8}}>{dateTime}</span></div>
              </div>
              <div style={{...CS.row,flexDirection:"column"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>setNotifiedChecked(c=>!c)}>
                  <div style={{...CS.box(notifiedChecked)}}>{notifiedChecked&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}</div>
                  <div style={{...CS.label(notifiedChecked)}}>Resident notified on reporting instructions</div>
                </div>
                {perms.canEditHighLevel&&(
                  <input style={{...S.input,marginTop:8}} placeholder="Admin: add reporting details (optional)..." value={notifiedDetail} onChange={e=>setNotifiedDetail(e.target.value)}/>
                )}
              </div>
              <div style={{...CS.row,flexDirection:"column",opacity:0.4,pointerEvents:"none"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,width:"100%"}}>
                  <div style={{...CS.box(false)}}/>
                  <div style={{...CS.label(false)}}>Finances — N/A for P&P Violation</div>
                </div>
              </div>
              {medNames.length > 0 && (
                <div style={{background:"white",border:"1px solid #1e293b",borderRadius:8,padding:12,marginBottom:6}}>
                  <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Medications returned</div>
                  {medNames.map(name=>(
                    <div key={name} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                      <div style={{...CS.box(medChecks[name])}} onClick={()=>setMedChecks(p=>({...p,[name]:!p[name]}))}>
                        {medChecks[name]&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}
                      </div>
                      <span style={{fontSize:12,color:medChecks[name]?"#1A6B35":"#1A2B45"}}>{name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Abscond checklist */}
          {step==="checklist"&&type==="abscond"&&(
            <div>
              <div style={{...CS.row}}>
                <div style={{...CS.box(true)}}><span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span></div>
                <div style={{...CS.label(true)}}>Date and time captured<span style={{fontSize:11,color:DS.textSec,marginLeft:8}}>{dateTime}</span></div>
              </div>
              <div style={{...CS.row,cursor:"pointer"}} onClick={()=>setNotifyAdmin(c=>!c)}>
                <div style={{...CS.box(notifyAdmin)}}>{notifyAdmin&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}</div>
                <div style={{flex:1}}>
                  <div style={{...CS.label(notifyAdmin)}}>Notify admin</div>
                  <div style={{fontSize:11,color:DS.textSec,marginTop:2}}>Admin will be prompted to notify Probation & Parole</div>
                </div>
              </div>
            </div>
          )}

          {/* Sign off — shown for all checklist types */}
          {step==="checklist"&&(
            <div style={{background:DS.blueTint,border:"1px solid #4f46e5",borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:10,marginTop:8,cursor:"pointer"}} onClick={()=>setSignoff(c=>!c)}>
              <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${signoff?"#1B5EBE":"#D8DCE3"}`,background:signoff?"#1B5EBE":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {signoff&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}
              </div>
              <div style={{fontSize:12,color:"#1A2B45",flex:1}}>I confirm this discharge checklist is complete</div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step==="checklist"&&(
          <div style={{padding:"14px 20px",borderTop:"1px solid #1e293b",display:"flex",gap:8,justifyContent:"flex-end",flexShrink:0}}>
            <button onClick={()=>setStep("select")} style={{...S.btnGhost,fontSize:12}}>Back</button>
            <button onClick={handleComplete} disabled={!canComplete||submitting}
              style={{...S.btn,background:DS.error.text,opacity:(!canComplete||submitting)?0.4:1}}>
              {submitting?"Completing...":"Complete Discharge"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Meetings Tab ──────────────────────────────────────────────────────────────
// ── Notice of Arrival ────────────────────────────────────────────────────────
const NOTICE_OF_ARRIVAL_ITEMS = [
  {key:"noticeAcceptance",    label:"Notice of Acceptance & signed program rules"},
  {key:"certIncarceration",   label:"Certificate of Incarceration"},
  {key:"clothing",            label:"At least 2 sets of clothing"},
  {key:"referralMedRecords",  label:"Referral and medical records"},
  {key:"money",               label:"Arrived with money"},
  {key:"ssCard",              label:"Social Security Card"},
  {key:"birthCert",           label:"Birth Certificate"},
  {key:"unexpiredId",         label:"Unexpired ID"},
  {key:"requiresMed",         label:"Requires medication"},
  {key:"broughtMed",          label:"Brought medication"},
];

// ── Property Agreement items ──────────────────────────────────────────────────
const PROPERTY_ITEMS = [
  "Blanket","Comforter","Top Sheet","Bottom Sheet (Fitted)",
  "Pillow","Pillow Case","Towel","Wash Cloth","Hygiene Kit",
];

// ── Mental Health Screen questions ───────────────────────────────────────────
const MH_QUESTIONS = [
  "Do you currently believe that someone can control your mind by putting thoughts into your head or taking thoughts out of your head?",
  "Do you currently feel that other people know your thoughts?",
  "Have you currently lost or gained as much as two pounds a week for several weeks without even trying?",
  "Have you or your family or friends noticed that you are currently more active than you usually are?",
  "Do you currently feel like you have to talk or move more slowly than you usually do?",
  "Have there currently been a few weeks when you felt like you were useless or sinful?",
  "Are you currently taking any medication prescribed for you by a physician for any emotional or mental health concerns?",
  "Have you ever been in a hospital for emotional or mental health concerns?",
];
const MH_IMPRESSIONS = [
  "Language Barrier",
  "Under the influence of drug/alcohol",
  "Difficulty understanding questions",
  "Other",
];

// ── Intake form concern items ────────────────────────────────────────────────
const INTAKE_CONCERNS = [
  "Transitioning back to society",
  "Finding a decent job",
  "Reconnecting with family",
  "Seeking housing",
  "Staying alcohol and/or drug free",
];

// ── Day 1 form list ───────────────────────────────────────────────────────────
const DAY1_FORMS = [
  {key:"noticeOfArrival",    label:"Notice of Arrival",              sub:"Arrival checklist · sends to district", downloadable:true},
  {key:"intakeForm",         label:"Intake Form",                    sub:"Personal info · health · goals"},
  {key:"mhScreen",           label:"Mental Health Screen",           sub:"8-question screen · referral decision"},
  {key:"dietaryNeeds",       label:"Dietary Needs",                  sub:"Restrictions · allergies · 7-day flag"},
  {key:"propertyAgreement",  label:"Property Agreement",             sub:"Bedding & linen issued"},
  {key:"leaseAgreement",     label:"Proof of Temporary Address",    sub:"Temporary address acknowledgement"},
  {key:"programFees",        label:"Program Fees",                   sub:"Service fee · transport deposit · locker"},
  {key:"releaseOfInfo",      label:"Release of Information",         sub:"Consent for record sharing"},
  {key:"saTreatment",        label:"Substance Abuse Treatment",      sub:"Treatment request or declination"},
  {key:"virtualInitials",    label:"Virtual Initials",               sub:"Select & acknowledge personal initials style"},
];

// ── Legacy checklist constants (kept for backward compat) ────────────────────
const DAY1_PART1 = NOTICE_OF_ARRIVAL_ITEMS.map(i=>i.label);
const DAY1_PART2 = [
  "Program Rules/Handbook reviewed","Grievance Policy reviewed",
  "Service Agreement signed","Code of Conduct signed",
  "Money/deposit recorded","Misc fees collected",
];

const DAY34_ITEMS = [
  {key:"idCheck",         label:"ID Check"},
  {key:"medicaid",        label:"Medicaid Switchover"},
  {key:"mcoSelection",    label:"MCO Selection Received"},
  {key:"vetServices",     label:"Vet Services Eligibility"},
  {key:"bradleyClinic",   label:"Bradley Clinic Application"},
  {key:"mhAssessment",    label:"Mental Health Assessment"},
  {key:"goalSetting",     label:"Goal Setting"},
];
const SERVICES_LIST = [
  {key:"medicaid",       label:"Medicaid Switch",          hasTarget:true},
  {key:"mcoSelection",   label:"MCO Selection",            hasTarget:true},
  {key:"vetServices",    label:"Vet Services",             hasTarget:false},
  {key:"transport",      label:"Medicaid Transport",       hasTarget:false},
  {key:"medAppt",        label:"Medical Appointments",     hasTarget:false},
  {key:"mindset",        label:"Mindset / Disability App", hasTarget:false},
  {key:"employment",     label:"Employment",               hasTarget:false},
  {key:"housing",        label:"Housing",                  hasTarget:false},
  {key:"mentalHealth",   label:"Mental Health Services",   hasTarget:false},
  {key:"saTreatment",    label:"SA Treatment / IOP",       hasTarget:false},
];


// ── Day 1 Intake Forms ────────────────────────────────────────────────────────

// Shared form shell
function IntakeFormShell({title, formNum, totalForms, residentName, doc, facility, entryDate, onBack, onSave, saveLabel="Save & Continue", children, currentData, extraActions}) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:0,height:"100%"}}>
      <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:8,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        <i className="bi bi-person-fill" style={{fontSize:16,color:DS.blue,flexShrink:0}}></i>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{residentName} · DOC# {doc}</div>
          <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{facility} · Entry: {entryDate||"TBD"}</div>
        </div>
        <span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Form {formNum} of {totalForms}</span>
      </div>
      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:12}}>
        {children}
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:16,borderTop:`0.5px solid ${DS.border}`,marginTop:16,flexShrink:0}}>
        <button onClick={onBack} style={{...S.btnGhost}}>← Back</button>
        {extraActions}
        <button onClick={()=>onSave(currentData)} style={{...S.btn}}>{saveLabel}</button>
      </div>
    </div>
  );
}

// 1. Notice of Arrival
function FormNoticeOfArrival({resident, data, onChange, onBack, onSave}) {
  const [local, setLocal] = useState(data||{});
  const d = local;
  const set = (k,v) => { const u={...local,[k]:v}; setLocal(u); onChange(u); };
  const setItem = (k,v) => { const u={...local,items:{...(local.items||{}),[k]:v}}; setLocal(u); onChange(u); };

  React.useEffect(() => {
    const updates = {};
    if (!d.ppOfficer) {
      try {
        const contacts = JSON.parse(localStorage.getItem("dorcus_district_contacts")||"[]");
        const match = contacts.find(c =>
          (c.title||"").toLowerCase().includes("p&p") &&
          (!c.facility || c.facility === (resident&&resident.facility) || c.facility === "")
        );
        if (match) updates.ppOfficer = match.name;
      } catch(e) {}
    }
    if (!d.scheduledRelease && resident?.exitDate) updates.scheduledRelease = resident.exitDate;
    if (!d.arrivalDate && resident?.entryDate) updates.arrivalDate = resident.entryDate;
    if (Object.keys(updates).length > 0) { const u = {...local,...updates}; setLocal(u); onChange(u); }
  }, []);


  const handleGenerateNOA = async () => {
    const logoBase64 = await fetchLogoBase64();
    const html = generateNOADocument(resident, d, logoBase64);
    const win = window.open("","_blank");
    if (win) {
      win.document.write(html);
      win.document.close();
      // Wait for logo to load before printing
      const img = win.document.querySelector("img");
      if (img) {
        img.onload = () => win.print();
        img.onerror = () => win.print(); // print anyway if logo fails
        // Fallback if onload already fired
        if (img.complete) win.print();
      } else {
        win.print();
      }
    }
  };

  return (
    <IntakeFormShell title="Notice of Arrival" formNum={1} totalForms={10}
      residentName={resident.name} doc={resident.doc} facility={resident.facility} entryDate={resident.entryDate}
      onBack={onBack} onSave={onSave} currentData={d}
      extraActions={<button onClick={handleGenerateNOA} style={{fontSize:11,padding:"5px 12px",borderRadius:6,border:`1px solid ${DS.blue}`,color:DS.blue,background:DS.blueTint,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:5}}><i className="bi bi-printer-fill" style={{fontSize:11}}></i> Generate &amp; Print</button>}>
      <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:6,padding:"8px 12px",fontSize:11,color:DS.blue,display:"flex",alignItems:"center",gap:8,fontFamily:"'Inter',sans-serif"}}>
        <i className="bi bi-info-circle-fill" style={{fontSize:13,flexShrink:0}}></i>
        This form will be saved to the resident record and can be downloaded to send to district personnel.
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Arrival Information</SectionHdr>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {[
            {label:"Date of Arrival",key:"arrivalDate",type:"date"},
            {label:"Arrival From",key:"arrivalFrom"},
            {label:"P&P Officer",key:"ppOfficer"},
            {label:"Offender Classification",key:"classification"},
            {label:"Scheduled Release Date",key:"scheduledRelease",type:"date"},
          ].map(({label,key,type})=>(
            <div key={key}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>{label}</div>
              <input type={type||"text"} style={{...S.input,border:`1px solid ${DS.border}`}} value={d[key]||""} onChange={e=>set(key,e.target.value)}/>
            </div>
          ))}
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Resident Arrived With</SectionHdr>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {NOTICE_OF_ARRIVAL_ITEMS.map(({key,label})=>{
            const val = (d.items||{})[key];
            return (
              <React.Fragment key={key}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 10px",borderRadius:6,background:val===true?DS.success.bg:val===false?DS.pageBg:DS.pageBg,border:`1px solid ${val===true?DS.success.border:DS.border}`}}>
                <span style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{label}</span>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>setItem(key,true)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${val===true?DS.success.text:DS.border}`,background:val===true?DS.success.bg:"white",color:val===true?DS.success.text:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Yes</button>
                  <button onClick={()=>setItem(key,false)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${val===false?"#8B1A1A":DS.border}`,background:val===false?"#FDECEA":"white",color:val===false?"#8B1A1A":DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>No</button>
                </div>
              </div>
              {key==="money"&&val===true&&(
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:6,background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,marginTop:-4}}>
                  <div style={{fontSize:12,fontWeight:600,color:DS.blue,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}>Amount ($)</div>
                  <input type="number" step="0.01" min="0" placeholder="0.00"
                    style={{...S.input,width:120,border:`1px solid ${DS.blueBorder}`,textAlign:"right",fontSize:13}}
                    value={d.moneyAmount||""}
                    onChange={e=>set("moneyAmount",e.target.value)}/>
                </div>
              )}
              {key==="broughtMed"&&val===true&&(
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:6,background:"#FFF3CD",border:"1px solid #F5C200",marginTop:-4}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:"#7A4F00",fontFamily:"'DM Sans',sans-serif"}}>Medication needs to be logged</div>
                    <div style={{fontSize:11,color:"#7A4F00",fontFamily:"'Inter',sans-serif"}}>Add to the resident's medication profile now or later from their profile</div>
                  </div>
                  <button onClick={()=>onSave({...local, _jumpToMeds:true})}
                    style={{fontSize:11,padding:"5px 12px",borderRadius:6,background:"#7A4F00",color:"white",border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap",marginLeft:10}}>
                    Add to Med Log →
                  </button>
                </div>
              )}
              {["ssCard","birthCert","unexpiredId"].includes(key) && val===true && (
                <div style={{padding:"10px 12px",background:"#E6F1FB",borderRadius:6,border:"1px solid #B5D4F4",marginTop:2,marginBottom:2}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#0C447C",marginBottom:6}}>Upload {label}</div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <input type="file" id={`noa_upload_${key}`} style={{display:"none"}}
                      onChange={async e=>{
                        const file = e.target.files[0]; if(!file) return;
                        if(!resident?.id){alert("Save resident first.");return;}
                        try {
                          const labelMap = {ssCard:"SS Card",birthCert:"Birth Certificate",unexpiredId:"State ID"};
                          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g,"_");
                          const fpath = `residentDocs/${resident.id}/Intake Documentation/${Date.now()}_${safeName}`;
                          const storageRef = ref(storage, fpath);
                          const url = await new Promise((resolve,reject)=>{
                            const task = uploadBytesResumable(storageRef,file);
                            task.on("state_changed",null,reject,async()=>resolve(await getDownloadURL(task.snapshot.ref)));
                          });
                          await saveResidentDocument({id:Date.now().toString(),name:labelMap[key]||label,category:"Intake Documentation",fileName:file.name,url,path:fpath,residentId:resident.id,uploadedAt:TODAY});
                          alert((labelMap[key]||label)+" uploaded!");
                        } catch(err){alert("Upload failed: "+err.message);}
                        e.target.value="";
                      }}/>
                    <button onClick={()=>document.getElementById(`noa_upload_${key}`).click()}
                      style={{padding:"5px 12px",fontSize:11,borderRadius:6,border:"none",background:"#0C447C",color:"white",cursor:"pointer"}}>
                      Choose & Upload
                    </button>
                  </div>
                </div>
              )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Initial Urine Screen</SectionHdr>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:d.uaResult==="Positive"?10:0}}>
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Screen Date</div>
            <input type="date" style={{...S.input,border:`1px solid ${DS.border}`}} value={d.uaDate||TODAY} onChange={e=>set("uaDate",e.target.value)}/>
          </div>
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Result</div>
            <select style={{...S.input,border:`1px solid ${DS.border}`}} value={d.uaResult||""} onChange={e=>{
              const val = e.target.value;
              const u = {...local, uaResult:val, uaSubstance: val==="Positive" ? (local.uaSubstance||"") : ""};
              setLocal(u); onChange(u);
            }}>              <option value="">Select...</option>
              <option>Negative</option>
              <option>Positive</option>
              <option>Refused</option>
            </select>
          </div>
        </div>
        {d.uaResult==="Positive"&&(
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Substance Detected *</div>
            <select style={{...S.input,border:`1px solid ${DS.border}`}} value={d.uaSubstance||""} onChange={e=>set("uaSubstance",e.target.value)}>
              <option value="">Select substance...</option>
              <option>Marijuana (THC)</option>
              <option>Cocaine</option>
              <option>Amphetamines</option>
              <option>Opiates</option>
              <option>PCP</option>
            </select>
          </div>
        )}
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Comments</SectionHdr>
        <textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} placeholder="Additional comments..." value={d.comments||""} onChange={e=>set("comments",e.target.value)}/>
      </div>
    </IntakeFormShell>
  );
}

// 2. Intake Form
function FormIntake({resident, data, onChange, onBack, onSave}) {
  const [local, setLocal] = useState(data||{});
  const d = local;
  const set = (k,v) => { const u={...local,[k]:v}; setLocal(u); onChange(u); };
  const setEC1 = (k,v) => { const u={...local,ec1:{...(local.ec1||{}),[k]:v}}; setLocal(u); onChange(u); };
  const setEC2 = (k,v) => { const u={...local,ec2:{...(local.ec2||{}),[k]:v}}; setLocal(u); onChange(u); };
  const setConcern = (k,v) => { const u={...local,concerns:{...(local.concerns||{}),[k]:v}}; setLocal(u); onChange(u); };
  return (
    <IntakeFormShell title="Intake Form" formNum={2} totalForms={10}
      residentName={resident.name} doc={resident.doc} facility={resident.facility} entryDate={resident.entryDate}
      onBack={onBack} onSave={onSave} currentData={d}>
      <div style={{...S.card}}>
        <SectionHdr>Personal Information</SectionHdr>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {[
            {label:"Hometown / State",key:"hometown"},
            {label:"Ethnic Origin",key:"ethnicOrigin"},
            {label:"Height",key:"height"},
            {label:"Weight",key:"weight"},
            {label:"Eye Color",key:"eyeColor"},
            {label:"Hair Color",key:"hairColor"},
          ].map(({label,key})=>(
            <div key={key}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>{label}</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} value={d[key]||""} onChange={e=>set(key,e.target.value)}/>
            </div>
          ))}
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Education Completed</div>
            <select style={{...S.input,border:`1px solid ${DS.border}`}} value={d.education||""} onChange={e=>set("education",e.target.value)}>
              <option value="">Select...</option>
              {["1","2","3","4","5","6","7","8","9","10","11","12","GED","College 1yr","College 2yr","College 3yr","College 4yr+"].map(v=><option key={v}>{v}</option>)}
            </select>
          </div>
          <div style={{display:"flex",gap:12,alignItems:"center",paddingTop:20}}>
            {[{label:"Can read?",key:"canRead"},{label:"Can write?",key:"canWrite"}].map(({label,key})=>(
              <div key={key} style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{label}</span>
                <div style={{display:"flex",gap:4}}>
                  <button onClick={()=>set(key,true)} style={{fontSize:11,padding:"2px 8px",borderRadius:4,border:`1.5px solid ${d[key]===true?DS.success.text:DS.border}`,background:d[key]===true?DS.success.bg:"white",color:d[key]===true?DS.success.text:DS.textSec,cursor:"pointer"}}>Yes</button>
                  <button onClick={()=>set(key,false)} style={{fontSize:11,padding:"2px 8px",borderRadius:4,border:`1.5px solid ${d[key]===false?"#8B1A1A":DS.border}`,background:d[key]===false?"#FDECEA":"white",color:d[key]===false?"#8B1A1A":DS.textSec,cursor:"pointer"}}>No</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Emergency Contacts</SectionHdr>
        {[{label:"Contact 1",ec:d.ec1||{},setFn:setEC1},{label:"Contact 2",ec:d.ec2||{},setFn:setEC2}].map(({label,ec,setFn})=>(
          <div key={label} style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:500,color:DS.textSec,marginBottom:6,fontFamily:"'Inter',sans-serif"}}>{label}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {[{l:"Name",k:"name"},{l:"Phone",k:"phone"},{l:"Address",k:"address"},{l:"Relationship",k:"relationship"}].map(({l,k})=>(
                <div key={k}>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:2,fontFamily:"'Inter',sans-serif"}}>{l}</div>
                  <input style={{...S.input,border:`1px solid ${DS.border}`,fontSize:12}} value={ec[k]||""} onChange={e=>setFn(k,e.target.value)}/>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Health Information</SectionHdr>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Mental Health / Other Disabilities</div>
            <textarea rows={2} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} value={d.mhNotes||""} onChange={e=>set("mhNotes",e.target.value)} placeholder="List any mental health conditions or disabilities..."/>
          </div>
          {[{label:"Ever been in mental health counseling?",key:"mhCounseling"},{label:"History of family illness?",key:"familyIllness"},{label:"Currently taking medications?",key:"takingMeds"}].map(({label,key})=>(
            <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 10px",background:DS.pageBg,borderRadius:6,border:`0.5px solid ${DS.border}`}}>
              <span style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{label}</span>
              <div style={{display:"flex",gap:4}}>
                <button onClick={()=>set(key,true)} style={{fontSize:11,padding:"2px 8px",borderRadius:4,border:`1.5px solid ${d[key]===true?DS.success.text:DS.border}`,background:d[key]===true?DS.success.bg:"white",color:d[key]===true?DS.success.text:DS.textSec,cursor:"pointer"}}>Yes</button>
                <button onClick={()=>set(key,false)} style={{fontSize:11,padding:"2px 8px",borderRadius:4,border:`1.5px solid ${d[key]===false?"#8B1A1A":DS.border}`,background:d[key]===false?"#FDECEA":"white",color:d[key]===false?"#8B1A1A":DS.textSec,cursor:"pointer"}}>No</button>
              </div>
            </div>
          ))}
          {d.takingMeds&&(
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>List Medications</div>
              <textarea rows={2} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} value={d.medList||""} onChange={e=>set("medList",e.target.value)} placeholder="List all current medications..."/>
            </div>
          )}
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Substance abuse problem or addiction?</div>
            <div style={{display:"flex",gap:4,marginBottom:6}}>
              <button onClick={()=>set("substanceIssue",true)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${d.substanceIssue===true?DS.success.text:DS.border}`,background:d.substanceIssue===true?DS.success.bg:"white",color:d.substanceIssue===true?DS.success.text:DS.textSec,cursor:"pointer"}}>Yes</button>
              <button onClick={()=>set("substanceIssue",false)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${d.substanceIssue===false?"#8B1A1A":DS.border}`,background:d.substanceIssue===false?"#FDECEA":"white",color:d.substanceIssue===false?"#8B1A1A":DS.textSec,cursor:"pointer"}}>No</button>
            </div>
            {d.substanceIssue&&<textarea rows={2} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} value={d.substanceNotes||""} onChange={e=>set("substanceNotes",e.target.value)} placeholder="Explain..."/>}
          </div>
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Substance Abuse Background</SectionHdr>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {[{label:"Ever attended AA/NA meetings?",key:"aaNa"},{label:"Probation or Parole?",key:"probParole"},{label:"Sanctioned to complete program?",key:"sanctioned"}].map(({label,key})=>(
            <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 10px",background:DS.pageBg,borderRadius:6,border:`0.5px solid ${DS.border}`}}>
              <span style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{label}</span>
              <div style={{display:"flex",gap:4}}>
                <button onClick={()=>set(key,true)} style={{fontSize:11,padding:"2px 8px",borderRadius:4,border:`1.5px solid ${d[key]===true?DS.success.text:DS.border}`,background:d[key]===true?DS.success.bg:"white",color:d[key]===true?DS.success.text:DS.textSec,cursor:"pointer"}}>Yes</button>
                <button onClick={()=>set(key,false)} style={{fontSize:11,padding:"2px 8px",borderRadius:4,border:`1.5px solid ${d[key]===false?"#8B1A1A":DS.border}`,background:d[key]===false?"#FDECEA":"white",color:d[key]===false?"#8B1A1A":DS.textSec,cursor:"pointer"}}>No</button>
              </div>
            </div>
          ))}
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Reason for Referral</div>
            <textarea rows={2} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} value={d.referralReason||""} onChange={e=>set("referralReason",e.target.value)} placeholder="Reason for referral to Dorcus Home..."/>
          </div>
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Marital Status</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {["Married","Single","Separated","Divorced","Widower"].map(s=>(
                <button key={s} onClick={()=>set("maritalStatus",s)} style={{fontSize:11,padding:"4px 10px",borderRadius:10,border:`1.5px solid ${d.maritalStatus===s?DS.blue:DS.border}`,background:d.maritalStatus===s?DS.blueTint:"white",color:d.maritalStatus===s?DS.blue:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{s}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Goals & Concerns</SectionHdr>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
          {INTAKE_CONCERNS.map(c=>(
            <div key={c} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 10px",background:DS.pageBg,borderRadius:6,border:`0.5px solid ${DS.border}`}}>
              <span style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{c}</span>
              <div style={{display:"flex",gap:4}}>
                <button onClick={()=>setConcern(c,true)} style={{fontSize:11,padding:"2px 8px",borderRadius:4,border:`1.5px solid ${(d.concerns||{})[c]===true?DS.success.text:DS.border}`,background:(d.concerns||{})[c]===true?DS.success.bg:"white",color:(d.concerns||{})[c]===true?DS.success.text:DS.textSec,cursor:"pointer"}}>Yes</button>
                <button onClick={()=>setConcern(c,false)} style={{fontSize:11,padding:"2px 8px",borderRadius:4,border:`1.5px solid ${(d.concerns||{})[c]===false?"#8B1A1A":DS.border}`,background:(d.concerns||{})[c]===false?"#FDECEA":"white",color:(d.concerns||{})[c]===false?"#8B1A1A":DS.textSec,cursor:"pointer"}}>No</button>
              </div>
            </div>
          ))}
        </div>
        {[{label:"How do you feel about being here?",key:"feelingsAbout"},{label:"What kind of work are you looking for?",key:"workInterest"}].map(({label,key})=>(
          <div key={key} style={{marginBottom:10}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>{label}</div>
            <textarea rows={2} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} value={d[key]||""} onChange={e=>set(key,e.target.value)}/>
          </div>
        ))}
        <div>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Work history rating (1–10)</div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {[1,2,3,4,5,6,7,8,9,10].map(n=>(
              <button key={n} onClick={()=>set("workRating",n)} style={{width:32,height:32,borderRadius:6,border:`1.5px solid ${d.workRating===n?DS.blue:DS.border}`,background:d.workRating===n?DS.blue:"white",color:d.workRating===n?"white":DS.textSec,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{n}</button>
            ))}
          </div>
        </div>
      </div>
    </IntakeFormShell>
  );
}

// 3. Mental Health Screen
function FormMHScreen({resident, data, onChange, onBack, onSave}) {
  const [local, setLocal] = useState(data||{});
  const d = local;
  const set = (k,v) => { const u={...local,[k]:v}; setLocal(u); onChange(u); };
  const setQ = (i,v) => { const a=[...(local.answers||Array(8).fill(null))];a[i]=v; const u={...local,answers:a}; setLocal(u); onChange(u); };
  const setImp = (imp) => {
    const cur = local.impressions||[];
    const updated = cur.includes(imp)?cur.filter(x=>x!==imp):[...cur,imp];
    const u={...local,impressions:updated}; setLocal(u); onChange(u);
  };
  const answers = d.answers||Array(8).fill(null);
  // Referral logic: yes to Q7(index 6), Q8(index 7), or 2+ of Q1-6
  const yesCount16 = answers.slice(0,6).filter(a=>a===true).length;
  const referralIndicated = answers[6]===true || answers[7]===true || yesCount16>=2;

  return (
    <IntakeFormShell title="Mental Health Screen" formNum={3} totalForms={10}
      residentName={resident.name} doc={resident.doc} facility={resident.facility} entryDate={resident.entryDate}
      onBack={onBack} onSave={onSave} currentData={d}>
      <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:6,padding:"8px 12px",fontSize:11,color:DS.blue,display:"flex",alignItems:"center",gap:8,fontFamily:"'Inter',sans-serif"}}>
        <i className="bi bi-info-circle-fill" style={{fontSize:13,flexShrink:0}}></i>
        Read each question aloud to the resident. Record their response.
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Screen Questions</SectionHdr>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {MH_QUESTIONS.map((q,i)=>{
            const ans = answers[i];
            return (
              <div key={i} style={{background:ans===true?DS.warning.bg:ans===false?DS.success.bg:DS.pageBg,border:`1px solid ${ans===true?DS.warning.border:ans===false?DS.success.border:DS.border}`,borderRadius:6,padding:"10px 12px"}}>
                <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                  <div style={{width:20,height:20,borderRadius:"50%",background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:600,color:DS.blue,flexShrink:0}}>{i+1}</div>
                  <div style={{flex:1,fontSize:12,color:DS.textPrimary,lineHeight:1.5,fontFamily:"'Inter',sans-serif"}}>{q}</div>
                  <div style={{display:"flex",gap:5,flexShrink:0}}>
                    <button onClick={()=>setQ(i,true)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${ans===true?DS.warning.text:DS.border}`,background:ans===true?DS.warning.bg:"white",color:ans===true?DS.warning.text:DS.textSec,cursor:"pointer",fontWeight:ans===true?500:400}}>Yes</button>
                    <button onClick={()=>setQ(i,false)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${ans===false?DS.success.text:DS.border}`,background:ans===false?DS.success.bg:"white",color:ans===false?DS.success.text:DS.textSec,cursor:"pointer",fontWeight:ans===false?500:400}}>No</button>
                  </div>
                </div>
                {ans===true&&i===6&&<div style={{marginTop:8}}><input style={{...S.input,border:`1px solid ${DS.border}`,fontSize:12}} placeholder="List medications..." value={d.prescribedMeds||""} onChange={e=>set("prescribedMeds",e.target.value)}/></div>}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Program Coordinator Impressions</SectionHdr>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
          {MH_IMPRESSIONS.map(imp=>{
            const sel = (d.impressions||[]).includes(imp);
            return <button key={imp} onClick={()=>setImp(imp)} style={{fontSize:11,padding:"5px 12px",borderRadius:10,border:`1.5px solid ${sel?DS.blue:DS.border}`,background:sel?DS.blueTint:"white",color:sel?DS.blue:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{imp}</button>;
          })}
        </div>
        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Additional Comments</div>
        <textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} value={d.comments||""} onChange={e=>set("comments",e.target.value)} placeholder="Notes, observations, context..."/>
      </div>
      {referralIndicated?(
        <div style={{...S.card,border:`1px solid ${DS.purple.border}`,background:DS.purple.bg}}>
          <div style={{fontSize:11,fontWeight:600,color:DS.purple.text,marginBottom:8,fontFamily:"'DM Sans',sans-serif"}}>Referral Indicated</div>
          <div style={{fontSize:11,color:DS.purple.text,marginBottom:10,fontFamily:"'Inter',sans-serif"}}>
            {answers[6]===true&&"Yes to Q7 (current medication). "}
            {answers[7]===true&&"Yes to Q8 (prior hospitalization). "}
            {yesCount16>=2&&`${yesCount16} yes responses in Q1–6. `}
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>set("referralDecision","referred")} style={{flex:1,padding:"8px",borderRadius:6,cursor:"pointer",border:`2px solid ${d.referralDecision==="referred"?DS.purple.border:DS.border}`,background:d.referralDecision==="referred"?DS.purple.bg:"white",color:d.referralDecision==="referred"?DS.purple.text:DS.textSec,fontSize:12,fontFamily:"'Inter',sans-serif"}}>Refer for further evaluation</button>
            <button onClick={()=>set("referralDecision","not_referred")} style={{flex:1,padding:"8px",borderRadius:6,cursor:"pointer",border:`2px solid ${d.referralDecision==="not_referred"?DS.border:DS.border}`,background:d.referralDecision==="not_referred"?DS.pageBg:"white",color:DS.textSec,fontSize:12,fontFamily:"'Inter',sans-serif"}}>Not referred at this time</button>
          </div>
          {d.referralDecision==="referred"&&(
            <div style={{marginTop:8}}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Date of Referral</div>
              <input type="date" style={{...S.input,border:`1px solid ${DS.border}`,width:"auto"}} value={d.referralDate||TODAY} onChange={e=>set("referralDate",e.target.value)}/>
            </div>
          )}
        </div>
      ):(
        <div style={{...S.card}}>
          <SectionHdr>Referral Decision</SectionHdr>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>set("referralDecision","referred")} style={{flex:1,padding:"8px",borderRadius:6,cursor:"pointer",border:`2px solid ${d.referralDecision==="referred"?DS.blue:DS.border}`,background:d.referralDecision==="referred"?DS.blueTint:"white",color:d.referralDecision==="referred"?DS.blue:DS.textSec,fontSize:12,fontFamily:"'Inter',sans-serif"}}>Refer for evaluation</button>
            <button onClick={()=>set("referralDecision","not_referred")} style={{flex:1,padding:"8px",borderRadius:6,cursor:"pointer",border:`2px solid ${d.referralDecision==="not_referred"?DS.success.text:DS.border}`,background:d.referralDecision==="not_referred"?DS.success.bg:"white",color:d.referralDecision==="not_referred"?DS.success.text:DS.textSec,fontSize:12,fontFamily:"'Inter',sans-serif"}}>Not referred</button>
          </div>
        </div>
      )}
    </IntakeFormShell>
  );
}

// 4. Dietary Needs
function FormDietaryNeeds({resident, data, onChange, onBack, onSave}) {
  const [local, setLocal] = useState(data||{});
  const d = local;
  const set = (k,v) => { const u={...local,[k]:v}; setLocal(u); onChange(u); };
  return (
    <IntakeFormShell title="Dietary Needs" formNum={4} totalForms={10}
      residentName={resident.name} doc={resident.doc} facility={resident.facility} entryDate={resident.entryDate}
      onBack={onBack} onSave={onSave} currentData={d}>
      <div style={{background:DS.warning.bg,border:`1px solid ${DS.warning.border}`,borderRadius:6,padding:"8px 12px",fontSize:11,color:DS.warning.text,display:"flex",alignItems:"center",gap:8,fontFamily:"'Inter',sans-serif"}}>
        <i className="bi bi-exclamation-triangle-fill" style={{fontSize:13,flexShrink:0}}></i>
        If restrictions or allergies are noted, a flag will appear on the resident card and In/Out board for 7 days.
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Dietary Restrictions</SectionHdr>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,color:DS.textPrimary,marginBottom:8,fontFamily:"'Inter',sans-serif"}}>Does the resident require any dietary restrictions?</div>
          <div style={{display:"flex",gap:6}}>
            <button onClick={()=>set("hasRestrictions",true)} style={{padding:"6px 16px",borderRadius:6,border:`2px solid ${d.hasRestrictions===true?DS.blue:DS.border}`,background:d.hasRestrictions===true?DS.blueTint:"white",color:d.hasRestrictions===true?DS.blue:DS.textSec,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Yes</button>
            <button onClick={()=>set("hasRestrictions",false)} style={{padding:"6px 16px",borderRadius:6,border:`2px solid ${d.hasRestrictions===false?DS.success.text:DS.border}`,background:d.hasRestrictions===false?DS.success.bg:"white",color:d.hasRestrictions===false?DS.success.text:DS.textSec,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>No</button>
          </div>
        </div>
        {d.hasRestrictions&&(
          <>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Restrictions</div>
              <textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} value={d.restrictions||""} onChange={e=>set("restrictions",e.target.value)} placeholder="Describe dietary restrictions..."/>
            </div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Due To</div>
              <div style={{display:"flex",gap:6}}>
                {["Religious Beliefs","Medical Conditions","Other"].map(r=>(
                  <button key={r} onClick={()=>set("reason",r)} style={{fontSize:11,padding:"4px 12px",borderRadius:10,border:`1.5px solid ${d.reason===r?DS.blue:DS.border}`,background:d.reason===r?DS.blueTint:"white",color:d.reason===r?DS.blue:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{r}</button>
                ))}
              </div>
              {d.reason==="Other"&&<input style={{...S.input,border:`1px solid ${DS.border}`,marginTop:8}} placeholder="Specify..." value={d.reasonOther||""} onChange={e=>set("reasonOther",e.target.value)}/>}
            </div>
          </>
        )}
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Food Allergies</SectionHdr>
        <div style={{marginBottom:6}}>
          <div style={{fontSize:11,color:DS.textPrimary,marginBottom:8,fontFamily:"'Inter',sans-serif"}}>Does the resident have any food allergies?</div>
          <div style={{display:"flex",gap:6,marginBottom:8}}>
            <button onClick={()=>set("hasAllergies",true)} style={{padding:"6px 16px",borderRadius:6,border:`2px solid ${d.hasAllergies===true?DS.blue:DS.border}`,background:d.hasAllergies===true?DS.blueTint:"white",color:d.hasAllergies===true?DS.blue:DS.textSec,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Yes</button>
            <button onClick={()=>set("hasAllergies",false)} style={{padding:"6px 16px",borderRadius:6,border:`2px solid ${d.hasAllergies===false?DS.success.text:DS.border}`,background:d.hasAllergies===false?DS.success.bg:"white",color:d.hasAllergies===false?DS.success.text:DS.textSec,fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>No</button>
          </div>
          {d.hasAllergies&&<textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} value={d.allergies||""} onChange={e=>set("allergies",e.target.value)} placeholder="List food allergies..."/>}
        </div>
      </div>
    </IntakeFormShell>
  );
}

// 5. Property Agreement
function FormPropertyAgreement({resident, data, onChange, onBack, onSave}) {
  const [local, setLocal] = useState(data||{});
  const d = local;
  const setItem = (item,v) => { const u={...local,items:{...(local.items||{}),[item]:v}}; setLocal(u); onChange(u); };
  return (
    <IntakeFormShell title="Property Agreement" formNum={5} totalForms={10}
      residentName={resident.name} doc={resident.doc} facility={resident.facility} entryDate={resident.entryDate}
      onBack={onBack} onSave={onSave} currentData={d}>
      <div style={{...S.card}}>
        <SectionHdr>Items Issued at Arrival</SectionHdr>
        <div style={{fontSize:11,color:DS.textSec,marginBottom:10,fontFamily:"'Inter',sans-serif"}}>Resident agrees to return all items upon departure. Items not returned will be charged to resident.</div>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {PROPERTY_ITEMS.map(item=>{
            const val = (d.items||{})[item];
            return (
              <div key={item} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:6,background:val===true?DS.success.bg:DS.pageBg,border:`1px solid ${val===true?DS.success.border:DS.border}`}}>
                <span style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{item}</span>
                <div style={{display:"flex",gap:5}}>
                  <button onClick={()=>setItem(item,true)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${val===true?DS.success.text:DS.border}`,background:val===true?DS.success.bg:"white",color:val===true?DS.success.text:DS.textSec,cursor:"pointer"}}>Issued</button>
                  <button onClick={()=>setItem(item,false)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${val===false?"#8B1A1A":DS.border}`,background:val===false?"#FDECEA":"white",color:val===false?"#8B1A1A":DS.textSec,cursor:"pointer"}}>N/A</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Acknowledgement</SectionHdr>
        <div onClick={()=>{ const u={...local,acknowledged:!local.acknowledged}; setLocal(u); onChange(u); }} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",background:d.acknowledged?DS.success.bg:DS.pageBg,borderRadius:6,border:`1px solid ${d.acknowledged?DS.success.border:DS.border}`,cursor:"pointer"}}>
          <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${d.acknowledged?DS.success.text:DS.border}`,background:d.acknowledged?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
            {d.acknowledged&&<i className="bi bi-check" style={{fontSize:10,color:DS.textPrimary}}></i>}
          </div>
          <span style={{fontSize:12,color:d.acknowledged?DS.success.text:DS.textPrimary,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>
            Resident agrees to return all listed items upon departure and understands that items not returned will be charged to them, as outlined in the Resident Handbook.
          </span>
        </div>
      </div>
    </IntakeFormShell>
  );
}

// 6. Lease Agreement
function FormLeaseAgreement({resident, data, onChange, onBack, onSave}) {
  const [local, setLocal] = useState(data||{});
  const d = local;
  return (
    <IntakeFormShell title="Proof of Temporary Address" formNum={6} totalForms={10}
      residentName={resident.name} doc={resident.doc} facility={resident.facility} entryDate={resident.entryDate}
      onBack={onBack} onSave={onSave} currentData={d}>
      <div style={{...S.card}}>
        <SectionHdr>Proof of Temporary Address</SectionHdr>
        <div style={{background:DS.pageBg,borderRadius:6,padding:"12px 14px",marginBottom:12}}>
          <div style={{fontSize:12,color:DS.textPrimary,lineHeight:1.7,fontFamily:"'Inter',sans-serif"}}>
            <p style={{marginBottom:8}}>This document serves as proof of temporary address for <strong>{resident.name}</strong>, who is a participant in the Dorcus Home LLC 90-Day Re-entry Program, beginning <strong>{resident.entryDate||"___"}</strong> and ending <strong>{resident.exitDate||"___"}</strong>.</p>
            <p style={{marginBottom:8}}>Dorcus Home LLC provides temporary housing at 801 Shenandoah Ave NW, Roanoke, VA 24016. This address serves as the participant's temporary address for the duration of program participation. Housing includes 3 meals per day, all utilities, re-entry supportive services, and supervised housing.</p>
            <p style={{marginBottom:8}}>By acknowledging this document, the participant agrees to adhere to all guidelines of the Dorcus Home LLC 90-Day Re-entry Program, including curfews, continuous employment, signing in and out of the facility, drug screening, and all other policies in place or created in the future.</p>
            <p>Failure to comply with program guidelines will result in immediate removal from the program and loss of temporary address status. The participant must vacate the premises immediately upon request and has 24 hours to collect belongings. Items remaining after 24 hours will be discarded or donated at the direction of Dorcus Home LLC Management.</p>
          </div>
        </div>
        <div onClick={()=>{ const u={...local,acknowledged:!local.acknowledged}; setLocal(u); onChange(u); }} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",background:d.acknowledged?DS.success.bg:DS.pageBg,borderRadius:6,border:`1px solid ${d.acknowledged?DS.success.border:DS.border}`,cursor:"pointer"}}>
          <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${d.acknowledged?DS.success.text:DS.border}`,background:d.acknowledged?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
            {d.acknowledged&&<i className="bi bi-check" style={{fontSize:10,color:DS.textPrimary}}></i>}
          </div>
          <span style={{fontSize:12,color:d.acknowledged?DS.success.text:DS.textPrimary,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>
            Participant confirms they have read and understood this Proof of Temporary Address document and agree to all program terms and conditions. Acknowledged digitally on {TODAY}.
          </span>
        </div>
      </div>
    </IntakeFormShell>
  );
}

// 7. Program Fees
function FormProgramFees({resident, data, onChange, onBack, onSave}) {
  const [local, setLocal] = useState(data||{});
  const d = local;
  const set = (k,v) => { const u={...local,[k]:v}; setLocal(u); onChange(u); };
  return (
    <IntakeFormShell title="Program Fees" formNum={7} totalForms={10}
      residentName={resident.name} doc={resident.doc} facility={resident.facility} entryDate={resident.entryDate}
      onBack={onBack} onSave={onSave} currentData={d}>
      <div style={{...S.card}}>
        <SectionHdr>Mandatory Fees</SectionHdr>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {[
            {key:"serviceFee",label:"Program Fees",amount:"$100.00",refund:"Non-Refundable",desc:"Covers all miscellaneous administrative items, personal and miscellaneous items issued, including initial hygiene kit, handbook, linen, bedding, and all upfront services or expenses during onboarding."},
            {key:"transportDeposit",label:"Transportation Deposit",amount:"$100.00",refund:"Refundable minus outstanding balances",desc:"Primarily covers transportation to resident's sentencing district upon discharge. Outstanding balances owed will be deducted from the deposit."},
          ].map(({key,label,amount,refund,desc})=>(
            <div key={key} style={{background:DS.pageBg,border:`0.5px solid ${DS.border}`,borderRadius:6,padding:"12px 14px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{label}</div>
                  <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{amount} · {refund}</div>
                </div>
                <div style={{display:"flex",gap:5}}>
                  <button onClick={()=>set(key,true)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${d[key]===true?DS.success.text:DS.border}`,background:d[key]===true?DS.success.bg:"white",color:d[key]===true?DS.success.text:DS.textSec,cursor:"pointer"}}>Collected</button>
                  <button onClick={()=>set(key,false)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${d[key]===false?"#8B1A1A":DS.border}`,background:d[key]===false?"#FDECEA":"white",color:d[key]===false?"#8B1A1A":DS.textSec,cursor:"pointer"}}>Not collected</button>
                </div>
              </div>
              <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>{desc}</div>
              {d[key]===true&&(
                <div style={{marginTop:8,display:"flex",alignItems:"center",gap:8}}>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>Amount collected</div>
                  <input style={{...S.input,border:`1px solid ${DS.border}`,width:100,fontSize:12}} placeholder={amount} value={d[`${key}Amount`]||""} onChange={e=>set(`${key}Amount`,e.target.value)}/>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Optional Fee</SectionHdr>
        <div style={{background:DS.pageBg,border:`0.5px solid ${DS.border}`,borderRadius:6,padding:"12px 14px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Locker Fee</div>
              <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>$25.00 · Non-Refundable · Highly recommended</div>
            </div>
            <div style={{display:"flex",gap:5}}>
              <button onClick={()=>set("lockerFee",true)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${d.lockerFee===true?DS.success.text:DS.border}`,background:d.lockerFee===true?DS.success.bg:"white",color:d.lockerFee===true?DS.success.text:DS.textSec,cursor:"pointer"}}>Collected</button>
              <button onClick={()=>set("lockerFee",false)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,border:`1.5px solid ${d.lockerFee===false?DS.neutral.text:DS.border}`,background:d.lockerFee===false?DS.neutral.bg:"white",color:d.lockerFee===false?DS.neutral.text:DS.textSec,cursor:"pointer"}}>Declined</button>
            </div>
          </div>
          <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Covers locker usage and provided lock for duration of program participation.</div>
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Fee Summary</SectionHdr>
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {[
            {label:"Program Fees",val:d.serviceFee,amount:d.serviceFeeAmount||"$100.00"},
            {label:"Transport Deposit",val:d.transportDeposit,amount:d.transportDepositAmount||"$100.00"},
            {label:"Locker Fee (optional)",val:d.lockerFee,amount:"$25.00"},
          ].map(({label,val,amount})=>(
            <div key={label} style={{display:"flex",justifyContent:"space-between",padding:"5px 8px",borderRadius:4,background:DS.pageBg}}>
              <span style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{label}</span>
              <span style={{fontSize:12,fontWeight:500,color:val===true?DS.success.text:val===false?"#8B1A1A":DS.textSec,fontFamily:"'Inter',sans-serif"}}>{val===true?amount:val===false?"Not collected":"—"}</span>
            </div>
          ))}
        </div>
      </div>
    </IntakeFormShell>
  );
}

// 8. Release of Information
function FormReleaseOfInfo({resident, data, onChange, onBack, onSave}) {
  const [local, setLocal] = useState(data||{});
  const d = local;
  return (
    <IntakeFormShell title="Release of Information" formNum={8} totalForms={10}
      residentName={resident.name} doc={resident.doc} facility={resident.facility} entryDate={resident.entryDate}
      onBack={onBack} onSave={onSave} currentData={d}>
      <div style={{...S.card}}>
        <SectionHdr>Consent for Release of Information</SectionHdr>
        <div style={{background:DS.pageBg,borderRadius:6,padding:"12px 14px",marginBottom:12,fontSize:12,color:DS.textPrimary,lineHeight:1.7,fontFamily:"'Inter',sans-serif"}}>
          <p style={{marginBottom:8}}>I, <strong>{resident.name}</strong>, hereby authorize any public or private agency or institution, or person to release any information they may have concerning me or my background to a Dorcus Home LLC authorized agent.</p>
          <p style={{marginBottom:8}}>This consent includes information relating to my emotional, mental and physical health, medical records, school records, test scores, academic or behavioral memoranda, all court records, past and present employment records, any possible military history, and information regarding alcohol and other drug use including history of use, diagnosis, testing results, treatment recommendations, and compliance with screening assessments.</p>
          <p style={{marginBottom:8}}>This consent also authorizes the release of information associated with my Social Security Number (SSN) and Medicaid identification number, to the extent necessary for program administration, benefits coordination, and supervision compliance.</p>
          <p>I understand that: (1) information is used only in the investigation and/or supervision of my case; (2) while under supervision I cannot revoke this consent; and (3) this consent terminates upon my release from Dorcus Home LLC custody or upon substantial change in my correctional status.</p>
        </div>
        <div onClick={()=>{ const u={...local,consentGeneral:!local.consentGeneral}; setLocal(u); onChange(u); }} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",background:d.consentGeneral?DS.success.bg:DS.pageBg,borderRadius:6,border:`1px solid ${d.consentGeneral?DS.success.border:DS.border}`,cursor:"pointer",marginBottom:8}}>
          <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${d.consentGeneral?DS.success.text:DS.border}`,background:d.consentGeneral?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
            {d.consentGeneral&&<i className="bi bi-check" style={{fontSize:10,color:DS.textPrimary}}></i>}
          </div>
          <span style={{fontSize:12,color:d.consentGeneral?DS.success.text:DS.textPrimary,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>Resident consents to the Consent for Release of Information. Acknowledged digitally on {TODAY}.</span>
        </div>
      </div>
      <div style={{...S.card}}>
        <SectionHdr>Substance Abuse Disclosure Consent</SectionHdr>
        <div style={{background:DS.pageBg,borderRadius:6,padding:"12px 14px",marginBottom:12,fontSize:12,color:DS.textPrimary,lineHeight:1.7,fontFamily:"'Inter',sans-serif"}}>
          <p>I, <strong>{resident.name}</strong>, hereby authorize Dorcus Home LLC and their agents to release confidential information on my criminal history, instant offense and disposition or on my history of alcohol and other drug use, diagnosis, treatment recommendations and compliance with treatment for the purpose of identification, diagnosis, prognosis, and/or treatment supervision of my case, to include any application for supervision transfer to any Virginia Department of Corrections agency or facility or any member of the Interstate Compact.</p>
        </div>
        <div onClick={()=>{ const u={...local,consentSubstance:!local.consentSubstance}; setLocal(u); onChange(u); }} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",background:d.consentSubstance?DS.success.bg:DS.pageBg,borderRadius:6,border:`1px solid ${d.consentSubstance?DS.success.border:DS.border}`,cursor:"pointer"}}>
          <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${d.consentSubstance?DS.success.text:DS.border}`,background:d.consentSubstance?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
            {d.consentSubstance&&<i className="bi bi-check" style={{fontSize:10,color:DS.textPrimary}}></i>}
          </div>
          <span style={{fontSize:12,color:d.consentSubstance?DS.success.text:DS.textPrimary,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>Resident consents to the Substance Abuse Disclosure Consent Authorization. Acknowledged digitally on {TODAY}.</span>
        </div>
      </div>
    </IntakeFormShell>
  );
}

// 9. Substance Abuse Treatment Request
function FormSATreatment({resident, data, onChange, onBack, onSave}) {
  const [local, setLocal] = useState(data||{});
  const d = local;
  const set = (k,v) => { const u={...local,[k]:v}; setLocal(u); onChange(u); };
  return (
    <IntakeFormShell title="Substance Abuse Treatment" formNum={9} totalForms={10}
      residentName={resident.name} doc={resident.doc} facility={resident.facility} entryDate={resident.entryDate}
      onBack={onBack} onSave={onSave} currentData={d} saveLabel="Save & Continue">
      <div style={{...S.card}}>
        <SectionHdr>Treatment Request</SectionHdr>
        <div style={{fontSize:12,color:DS.textSec,marginBottom:12,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>
          The resident must provide District 15 P&P and the Dorcus Home Director with the treatment facility name and contact information. After every appointment, the resident must obtain a note on official letterhead to be given to the RA on duty upon return.
        </div>
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          <button onClick={()=>set("requestsTreatment",true)} style={{flex:1,padding:"10px",borderRadius:6,border:`2px solid ${d.requestsTreatment===true?DS.blue:DS.border}`,background:d.requestsTreatment===true?DS.blueTint:"white",color:d.requestsTreatment===true?DS.blue:DS.textSec,fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            Resident requests substance abuse treatment
          </button>
          <button onClick={()=>set("requestsTreatment",false)} style={{flex:1,padding:"10px",borderRadius:6,border:`2px solid ${d.requestsTreatment===false?DS.neutral.text:DS.border}`,background:d.requestsTreatment===false?DS.neutral.bg:"white",color:d.requestsTreatment===false?DS.neutral.text:DS.textSec,fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            Resident does not wish to have services at this time
          </button>
        </div>
        {d.requestsTreatment===true&&(
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Treatment Facility Name</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} value={d.facilityName||""} onChange={e=>set("facilityName",e.target.value)} placeholder="Name of treatment facility..."/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Facility Contact Information</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} value={d.facilityContact||""} onChange={e=>set("facilityContact",e.target.value)} placeholder="Phone, address, or contact name..."/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Type of Program (optional)</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["IOP","Outpatient","Inpatient","AA/NA","Other"].map(t=>(
                  <button key={t} onClick={()=>set("programType",t)} style={{fontSize:11,padding:"4px 12px",borderRadius:10,border:`1.5px solid ${d.programType===t?DS.blue:DS.border}`,background:d.programType===t?DS.blueTint:"white",color:d.programType===t?DS.blue:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{t}</button>
                ))}
              </div>
            </div>
          </div>
        )}
        {d.requestsTreatment===false&&(
          <div style={{background:DS.neutral.bg,border:`1px solid ${DS.neutral.border}`,borderRadius:6,padding:"10px 12px",fontSize:11,color:DS.neutral.text,fontFamily:"'Inter',sans-serif"}}>
            Resident has declined substance abuse treatment services at this time. This decision is recorded and can be revisited at any follow-up meeting.
          </div>
        )}
      </div>
    </IntakeFormShell>
  );
}

// ── Form 10: Virtual Initials ─────────────────────────────────────────────────
function getResidentInitials(name) {
  const parts = (name||"").trim().split(/\s+/);
  const first = parts[0]?.[0]?.toUpperCase()||"?";
  const last = parts[parts.length-1]?.[0]?.toUpperCase()||"?";
  return first + last;
}

function FormVirtualInitials({resident, onBack, onSave}) {
  const initStr = getResidentInitials(resident.name);
  const [acknowledged, setAcknowledged] = useState(false);
  const isLocked = !!resident.virtualInitialsLockedAt;
  const caveatStyle = {fontFamily:"'Caveat',cursive",fontWeight:600,fontSize:34,letterSpacing:"0.05em"};

  const handleSave = () => {
    if(!acknowledged) return;
    onSave({ virtualInitials:initStr, virtualInitialsLockedAt:TODAY });
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:0,height:"100%"}}>
      <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:8,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{resident.name} · DOC# {resident.doc}</div>
          <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Form 10 of 10 — Virtual Initials</div>
        </div>
        {isLocked&&<span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:DS.success.bg,border:`1px solid ${DS.success.border}`,color:DS.success.text,fontFamily:"'Inter',sans-serif"}}>Locked</span>}
      </div>

      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:12}}>
        <div style={{...S.card}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:4,fontFamily:"'DM Sans',sans-serif"}}>Your Initials</div>
          <div style={{fontSize:12,color:DS.textSec,marginBottom:16,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>
            These initials will represent you on all sign-in/out records and case note documents throughout your time in the program. This cannot be changed after intake.
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8,padding:"24px 16px",background:DS.blueTint,borderRadius:10,border:`2px solid ${DS.blueBorder}`}}>
            <div style={{...caveatStyle,color:DS.blue}}>{initStr}</div>
            <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Generated from your name on file</div>
          </div>
        </div>

        {!isLocked&&(
          <div style={{...S.card,border:`1px solid ${DS.blueBorder}`,background:DS.blueTint}}>
            <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:8,fontFamily:"'DM Sans',sans-serif"}}>How it will appear in reports</div>
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"white",borderRadius:8,border:`1px solid ${DS.border}`}}>
              <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif",flex:1}}>Sign Out — Mon 5/12 · 5:30 PM</div>
              <div style={{...caveatStyle,fontSize:22,color:DS.blue,background:DS.blueTint,padding:"2px 10px",borderRadius:6,border:`1px solid ${DS.blueBorder}`}}>{initStr}</div>
            </div>
          </div>
        )}

        {isLocked ? (
          <div style={{...S.card,border:`1px solid ${DS.success.border}`,background:DS.success.bg}}>
            <div style={{fontSize:12,color:DS.success.text,fontFamily:"'Inter',sans-serif"}}>
              ✓ Virtual initials locked on {resident.virtualInitialsLockedAt}.
            </div>
          </div>
        ) : (
          <div style={{...S.card}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}} onClick={()=>setAcknowledged(a=>!a)}>
              <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${acknowledged?DS.blue:DS.border}`,background:acknowledged?DS.blue:"white",flexShrink:0,marginTop:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {acknowledged&&<span style={{color:"white",fontSize:12,fontWeight:700}}>✓</span>}
              </div>
              <div style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>
                I acknowledge that these initials will serve as my personal initials throughout the duration of my participation in the Dorcus Home program and will be applied to all relevant records and documentation.
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:16,borderTop:`0.5px solid ${DS.border}`,marginTop:16,flexShrink:0}}>
        <button onClick={onBack} style={{...S.btnGhost}}>← Back</button>
        {isLocked ? (
          <button onClick={()=>onSave({})} style={{...S.btn}}>Complete Day 1 Intake ✓</button>
        ) : (
          <button onClick={handleSave} disabled={!acknowledged}
            style={{...S.btn,opacity:!acknowledged?0.4:1}}>
            Confirm Initials & Complete Intake
          </button>
        )}
      </div>
    </div>
  );
}

function MeetingsTab({ form }) {
  const meetings = [...(form.meetings||[])].reverse();
  const actionItems = form.actionItems||[];
  const services = form.services||{};

  const dpColors = {
    "DP-0":{color:DS.success.text,bg:DS.success.bg,border:DS.success.border},
    "DP-1":{color:DS.teal.text,bg:DS.teal.bg,border:DS.teal.border},
    "DP-2":{color:DS.purple.text,bg:DS.purple.bg,border:DS.purple.border},
    "DP-3":{color:DS.amber.text,bg:DS.amber.bg,border:DS.amber.border},
  };

  const typeLabel = {day1:"Day 1 Intake",day34:"Coordinator Assessment Primary Meeting",followup:"Follow-up Meeting"};

  const openActions = actionItems.filter(a=>a.status==="open");
  const doneActions = actionItems.filter(a=>a.status==="complete");
  const activeServices = Object.entries(services).filter(([,v])=>v.status&&v.status!=="");

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* Meeting history */}
      <div>
        <SectionHdr>Meeting History</SectionHdr>
        {meetings.length===0
          ? <div style={{textAlign:"center",padding:"24px 0",color:DS.textSec,fontSize:13,fontFamily:"'Inter',sans-serif"}}>No meetings logged yet. Use PC Workflow → Intake to log meetings.</div>
          : <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {meetings.map((m,i)=>(
                <div key={m.id||i} style={{...S.card}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:m.notes||m.items?10:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:13,fontWeight:600,color:DS.blue,fontFamily:"'DM Sans',sans-serif"}}>{typeLabel[m.type]||m.type}</span>
                      <span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{m.date}</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      {m.dpLevel&&(()=>{const dc=dpColors[m.dpLevel]||{};return <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,fontWeight:500,background:dc.bg,border:`1px solid ${dc.border}`,color:dc.color,fontFamily:"'Inter',sans-serif"}}>{m.dpLevel}</span>})()}
                      {m.nextMeeting&&<span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Next: {m.nextMeeting}</span>}
                    </div>
                  </div>
                  {m.type==="day1"&&m.part1&&(
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>
                        Part 1: {(m.part1||[]).filter(Boolean).length}/{DAY1_PART1.length} items · Part 2: {(m.part2||[]).filter(Boolean).length}/{DAY1_PART2.length} items
                      </div>
                      <div style={{height:4,background:DS.border,borderRadius:2,overflow:"hidden"}}>
                        <div style={{width:`${((m.part1||[]).filter(Boolean).length+(m.part2||[]).filter(Boolean).length)/(DAY1_PART1.length+DAY1_PART2.length)*100}%`,height:"100%",background:DS.success.border,borderRadius:2}}/>
                      </div>
                    </div>
                  )}
                  {m.type==="day34"&&m.items&&(
                    <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:m.notes?8:0}}>
                      {DAY34_ITEMS.map(({key,label})=>{
                        const it=m.items[key]||{};
                        return it.done?(
                          <span key={key} style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:DS.success.bg,color:DS.success.text,border:`1px solid ${DS.success.border}`,fontFamily:"'Inter',sans-serif"}}>✓ {label}</span>
                        ):(
                          <span key={key} style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:DS.pageBg,color:DS.textSec,border:`1px solid ${DS.border}`,fontFamily:"'Inter',sans-serif"}}>{label}</span>
                        );
                      })}
                    </div>
                  )}
                  {m.notes&&<p style={{fontSize:12,color:DS.textSec,margin:0,lineHeight:1.5,fontFamily:"'Inter',sans-serif"}}>{m.notes}</p>}
                </div>
              ))}
            </div>
        }
      </div>

      {/* Action items */}
      {(openActions.length>0||doneActions.length>0)&&(
        <div>
          <SectionHdr>Action Items</SectionHdr>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {openActions.map((a,i)=>(
              <div key={a.id||i} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:DS.warning.bg,borderRadius:6,border:`1px solid ${DS.warning.border}`}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:DS.warning.text,flexShrink:0}}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{a.description}</div>
                  {a.dueDate&&<div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Due: {a.dueDate}</div>}
                </div>
                <span style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:DS.warning.bg,color:DS.warning.text,border:`1px solid ${DS.warning.border}`,fontFamily:"'Inter',sans-serif"}}>Open</span>
              </div>
            ))}
            {doneActions.map((a,i)=>(
              <div key={a.id||i} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:DS.success.bg,borderRadius:6,border:`1px solid ${DS.success.border}`}}>
                <i className="bi bi-check-circle-fill" style={{fontSize:14,color:DS.success.text,flexShrink:0}}></i>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,color:DS.success.text,fontFamily:"'Inter',sans-serif",textDecoration:"line-through",opacity:0.8}}>{a.description}</div>
                  {a.completedAt&&<div style={{fontSize:10,color:DS.success.text,opacity:0.7,fontFamily:"'Inter',sans-serif"}}>Completed: {a.completedAt}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Services summary */}
      {activeServices.length>0&&(
        <div>
          <SectionHdr>Services Coordination</SectionHdr>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {activeServices.map(([key,svc])=>{
              const label = SERVICES_LIST.find(s=>s.key===key)?.label||key;
              const statusColors = {complete:DS.success,pending:DS.warning,"in progress":DS.blue,"N/A":DS.neutral,referred:DS.teal};
              const sc = statusColors[svc.status]||DS.neutral;
              return (
                <div key={key} style={{padding:"8px 10px",background:DS.pageBg,borderRadius:6,border:`1px solid ${DS.border}`}}>
                  <div style={{fontSize:11,fontWeight:500,color:DS.textPrimary,marginBottom:3,fontFamily:"'DM Sans',sans-serif"}}>{label}</div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:10,padding:"1px 6px",borderRadius:8,background:typeof sc==="object"?sc.bg:DS.neutral.bg,color:typeof sc==="object"?sc.text:DS.neutral.text,border:`1px solid ${typeof sc==="object"?sc.border:DS.neutral.border}`,fontFamily:"'Inter',sans-serif"}}>{svc.status}</span>
                    {svc.targetDate&&<span style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{svc.targetDate}</span>}
                  </div>
                  {svc.notes&&<div style={{fontSize:10,color:DS.textSec,marginTop:3,fontFamily:"'Inter',sans-serif"}}>{svc.notes}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Intake Form Viewer / Editor ───────────────────────────────────────────────
function IntakeFormViewer({ formKey, formData, residentName, doc, entryDate, facility, onSave, canEdit, currentUser }) {
  const [mode, setMode] = useState(null); // null | "view" | "edit"
  const [localData, setLocalData] = useState(formData||{});
  const fd = formData||{};
  const lastEdited = fd.lastEditedAt;
  const lastEditor = fd.lastEditedBy;

  const handleSave = (overrideData) => {
    const updated = {
      ...(overrideData||localData),
      lastEditedAt: new Date().toISOString(),
      lastEditedBy: currentUser||"Staff",
    };
    onSave(updated);
    setMode(null);
  };

  const timeAgo = (isoStr) => {
    if(!isoStr) return "";
    const diff = Math.floor((new Date()-new Date(isoStr))/1000);
    if(diff<60) return "just now";
    if(diff<3600) return `${Math.floor(diff/60)}m ago`;
    if(diff<86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  };

  const userInitials = (name) => {
    if(!name) return "?";
    return name.split(" ").map(n=>n[0]).join("").slice(0,3).toUpperCase();
  };

  const ReadRow = ({label, value}) => value ? (
    <div style={{display:"flex",flexDirection:"column",gap:2,padding:"7px 0",borderBottom:`0.5px solid ${DS.border}`}}>
      <span style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>{label}</span>
      <span style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{value}</span>
    </div>
  ) : null;

  const renderReadOnly = () => {
    switch(formKey) {
      case "noticeOfArrival": return (<>
        <ReadRow label="Arrival Date" value={fd.arrivalDate}/>
        <ReadRow label="Arrival From" value={fd.arrivalFrom}/>
        <ReadRow label="P&P Officer" value={fd.ppOfficer}/>
        <ReadRow label="Classification" value={fd.classification}/>
        <ReadRow label="Scheduled Release" value={fd.scheduledRelease}/>
        <ReadRow label="Urine Screen" value={fd.uaResult?`${fd.uaResult}${fd.uaSubstance?" — "+fd.uaSubstance:""}`:null}/>
        <ReadRow label="Items Arrived With" value={fd.items?Object.entries(fd.items).filter(([,v])=>v===true).map(([k])=>NOTICE_OF_ARRIVAL_ITEMS.find(i=>i.key===k)?.label||k).join(", "):null}/>
        <ReadRow label="Comments" value={fd.comments}/>
        {fd.noaSentDate&&<ReadRow label="Sent to District" value={`${fd.noaSentDate} — ${fd.noaSentTo||"District Personnel"}`}/>}
      </>);
      case "intakeForm": return (<>
        <ReadRow label="Ethnic Origin" value={fd.ethnicOrigin}/>
        <ReadRow label="Height / Weight" value={[fd.height,fd.weight].filter(Boolean).join(" / ")}/>
        <ReadRow label="Education" value={fd.education}/>
        <ReadRow label="Marital Status" value={fd.maritalStatus}/>
        <ReadRow label="Emergency Contact 1" value={fd.ec1?.name?`${fd.ec1.name} — ${fd.ec1.phone} — ${fd.ec1.relationship}`:null}/>
        <ReadRow label="Emergency Contact 2" value={fd.ec2?.name?`${fd.ec2.name} — ${fd.ec2.phone} — ${fd.ec2.relationship}`:null}/>
        <ReadRow label="Substance Issues" value={fd.substanceIssue===true?"Yes":fd.substanceIssue===false?"No":null}/>
        <ReadRow label="Currently Taking Meds" value={fd.takingMeds===true?`Yes — ${fd.medList||""}`:fd.takingMeds===false?"No":null}/>
        <ReadRow label="Work History Rating" value={fd.workRating?`${fd.workRating}/10`:null}/>
      </>);
      case "mhScreen": return (<>
        <ReadRow label="Referral Decision" value={fd.referralDecision==="referred"?"Referred for evaluation":fd.referralDecision==="not_referred"?"Not referred":null}/>
        <ReadRow label="Referral Date" value={fd.referralDate}/>
        <ReadRow label="Prescribed Meds" value={fd.prescribedMeds}/>
        <ReadRow label="PC Impressions" value={fd.impressions?.join(", ")}/>
        <ReadRow label="Comments" value={fd.comments}/>
      </>);
      case "dietaryNeeds": return (<>
        <ReadRow label="Dietary Restrictions" value={fd.hasRestrictions?"Yes — "+fd.restrictions:fd.hasRestrictions===false?"None":"—"}/>
        <ReadRow label="Food Allergies" value={fd.hasAllergies?"Yes — "+fd.allergies:fd.hasAllergies===false?"None":"—"}/>
      </>);
      case "propertyAgreement": return (<>
        <ReadRow label="Items Issued" value={fd.items?Object.entries(fd.items).filter(([,v])=>v===true).map(([k])=>k).join(", "):"None recorded"}/>
        <ReadRow label="Acknowledged" value={fd.acknowledged?"Yes":"No"}/>
      </>);
      case "leaseAgreement": return (<>
        <ReadRow label="Acknowledged" value={fd.acknowledged?"Yes — digitally acknowledged":"No"}/>
      </>);
      case "programFees": return (<>
        <ReadRow label="Program Fees" value={fd.serviceFee===true?`Collected — ${fd.serviceFeeAmount||"$100"}`:fd.serviceFee===false?"Not collected":"—"}/>
        <ReadRow label="Transport Deposit" value={fd.transportDeposit===true?`Collected — ${fd.transportDepositAmount||"$100"}`:fd.transportDeposit===false?"Not collected":"—"}/>
        <ReadRow label="Locker Fee" value={fd.lockerFee===true?"Collected — $25":fd.lockerFee===false?"Declined":"—"}/>
      </>);
      case "releaseOfInfo": return (<>
        <ReadRow label="General Consent" value={fd.consentGeneral?"Acknowledged":"Not acknowledged"}/>
        <ReadRow label="Substance Abuse Consent" value={fd.consentSubstance?"Acknowledged":"Not acknowledged"}/>
      </>);
      case "saTreatment": return (<>
        <ReadRow label="Treatment Requested" value={fd.requestsTreatment===true?"Yes":fd.requestsTreatment===false?"No — declined at this time":"—"}/>
        {fd.requestsTreatment&&<ReadRow label="Facility" value={fd.facilityName}/>}
        {fd.requestsTreatment&&<ReadRow label="Program Type" value={fd.programType}/>}
      </>);
      default: return <div style={{fontSize:12,color:DS.textSec}}>No preview available.</div>;
    }
  };

  const formInfo = DAY1_FORMS.find(f=>f.key===formKey);

  // Summary line for the compact row
  const summaryLine = (() => {
    const parts = [fd.completedAt];
    if(formKey==="noticeOfArrival") { if(fd.noaSentDate) parts.push(`Sent to district ${fd.noaSentDate}`); }
    if(formKey==="mhScreen") { parts.push(fd.referralDecision==="referred"?"MH-1 · Referred":"Not referred"); }
    if(formKey==="dietaryNeeds") { parts.push(fd.hasRestrictions||fd.hasAllergies?"Restrictions noted":"No restrictions"); }
    if(formKey==="propertyAgreement") { const n=Object.values(fd.items||{}).filter(v=>v===true).length; parts.push(`${n} items issued`); }
    if(formKey==="leaseAgreement"||formKey==="releaseOfInfo") { parts.push("Digitally acknowledged"); }
    if(formKey==="programFees") { const c=[fd.serviceFee&&"Program fees",fd.transportDeposit&&"Transport deposit",fd.lockerFee&&"Locker fee"].filter(Boolean); if(c.length) parts.push(c.join(", ")+" collected"); }
    if(formKey==="saTreatment") { parts.push(fd.requestsTreatment?"Treatment requested":fd.requestsTreatment===false?"Services declined":""); }
    return parts.filter(Boolean).join(" · ");
  })();

  return (
    <>
      {/* Compact row */}
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderBottom:`0.5px solid ${DS.border}`,background:"white"}}>
        <div style={{width:20,height:20,borderRadius:"50%",background:DS.success.bg,border:`1px solid ${DS.success.border}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <i className="bi bi-check" style={{fontSize:10,color:DS.success.text}}></i>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{formInfo?.label||formKey}</div>
          <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif",marginTop:1}}>
            {summaryLine}
            {lastEdited&&<span style={{color:DS.textSec,marginLeft:6}}><i className="bi bi-pencil" style={{fontSize:8}}></i> edited {timeAgo(lastEdited)} by {userInitials(lastEditor)}</span>}
          </div>
        </div>
        <div style={{display:"flex",gap:5,flexShrink:0}}>
          <button onClick={()=>setMode("view")} style={{...S.btnGhost,fontSize:10,padding:"2px 9px"}}>View</button>
          {canEdit&&<button onClick={()=>{setLocalData(fd);setMode("edit");}} style={{...S.btnSec,fontSize:10,padding:"2px 9px"}}>Edit</button>}
        </div>
      </div>

      {/* View modal */}
      {mode==="view"&&(
        <div style={{position:"fixed",inset:0,zIndex:200,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"white",borderRadius:10,width:"100%",maxWidth:500,maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden",border:`0.5px solid ${DS.border}`}}>
            <div style={{padding:"12px 16px",background:DS.blue,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
              <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{formInfo?.label||formKey} — {residentName}</div>
              <button onClick={()=>setMode(null)} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:5,color:DS.textPrimary,fontSize:11,padding:"3px 10px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>✕ Close</button>
            </div>
            {lastEdited&&(
              <div style={{padding:"4px 14px",background:"#F8FAFF",borderBottom:`0.5px solid ${DS.border}`,display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                <i className="bi bi-pencil" style={{fontSize:9,color:DS.textSec}}></i>
                <span style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Last edited {timeAgo(lastEdited)} by {userInitials(lastEditor)}</span>
              </div>
            )}
            <div style={{padding:"14px 16px",overflowY:"auto",flex:1}}>
              {renderReadOnly()}
            </div>
            <div style={{padding:"10px 14px",background:"white",borderTop:`0.5px solid ${DS.border}`,display:"flex",justifyContent:"flex-end",gap:8,flexShrink:0}}>
              <button onClick={()=>setMode(null)} style={{...S.btnGhost,fontSize:11}}>Close</button>
              {canEdit&&<button onClick={()=>{setLocalData(fd);setMode("edit");}} style={{...S.btnSec,fontSize:11}}>Switch to Edit</button>}
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {mode==="edit"&&(
        <div style={{position:"fixed",inset:0,zIndex:200,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"white",borderRadius:10,width:"100%",maxWidth:600,maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden",border:`0.5px solid ${DS.border}`}}>
            <div style={{padding:"12px 16px",background:DS.blue,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
              <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Edit — {formInfo?.label||formKey}</div>
              <button onClick={()=>setMode(null)} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:5,color:DS.textPrimary,fontSize:11,padding:"3px 10px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>✕ Cancel</button>
            </div>
            <div style={{flex:1,overflowY:"auto"}}>
              <div style={{padding:"10px 16px",background:DS.blueTint,borderBottom:`0.5px solid ${DS.blueBorder}`,display:"flex",alignItems:"center",gap:8,fontSize:11,color:DS.blue,fontFamily:"'Inter',sans-serif"}}>
                <i className="bi bi-info-circle-fill" style={{fontSize:13,flexShrink:0}}></i>
                Editing a completed form. Changes are saved with your name and timestamp.
              </div>
              <div style={{padding:"14px 16px"}}>
                {formKey==="noticeOfArrival"&&<FormNoticeOfArrival resident={{name:residentName,doc,entryDate,facility}} data={localData} onChange={setLocalData} onBack={()=>setMode(null)} onSave={handleSave}/>}
                {formKey==="intakeForm"&&<FormIntake resident={{name:residentName,doc,entryDate,facility}} data={localData} onChange={setLocalData} onBack={()=>setMode(null)} onSave={handleSave}/>}
                {formKey==="mhScreen"&&<FormMHScreen resident={{name:residentName,doc,entryDate,facility}} data={localData} onChange={setLocalData} onBack={()=>setMode(null)} onSave={handleSave}/>}
                {formKey==="dietaryNeeds"&&<FormDietaryNeeds resident={{name:residentName,doc,entryDate,facility}} data={localData} onChange={setLocalData} onBack={()=>setMode(null)} onSave={handleSave}/>}
                {formKey==="propertyAgreement"&&<FormPropertyAgreement resident={{name:residentName,doc,entryDate,facility}} data={localData} onChange={setLocalData} onBack={()=>setMode(null)} onSave={handleSave}/>}
                {formKey==="leaseAgreement"&&<FormLeaseAgreement resident={{name:residentName,doc,entryDate,facility,exitDate:""}} data={localData} onChange={setLocalData} onBack={()=>setMode(null)} onSave={handleSave}/>}
                {formKey==="programFees"&&<FormProgramFees resident={{name:residentName,doc,entryDate,facility}} data={localData} onChange={setLocalData} onBack={()=>setMode(null)} onSave={handleSave}/>}
                {formKey==="releaseOfInfo"&&<FormReleaseOfInfo resident={{name:residentName,doc,entryDate,facility}} data={localData} onChange={setLocalData} onBack={()=>setMode(null)} onSave={handleSave}/>}
                {formKey==="saTreatment"&&<FormSATreatment resident={{name:residentName,doc,entryDate,facility}} data={localData} onChange={setLocalData} onBack={()=>setMode(null)} onSave={handleSave}/>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function DietaryEditSection({ dn, canEdit, onSave }) {
  const [editDiet, setEditDiet] = useState(false);
  const [dietLocal, setDietLocal] = useState(dn);
  const hasFlag = dn.hasRestrictions || dn.hasAllergies;

  const saveDiet = () => { onSave(dietLocal); setEditDiet(false); };

  if (!hasFlag && !editDiet) return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:DS.pageBg,borderRadius:8,border:`0.5px solid ${DS.border}`}}>
      <span style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>No dietary restrictions or allergies on record</span>
      {canEdit&&<button onClick={()=>{setDietLocal(dn);setEditDiet(true);}} style={{...S.btnSec,fontSize:11,padding:"3px 10px"}}>Add dietary info</button>}
    </div>
  );

  return (
    <div style={{background:hasFlag?DS.amber.bg:DS.pageBg,border:`1px solid ${hasFlag?DS.amber.border:DS.border}`,borderRadius:8,padding:"12px 14px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <span style={{fontSize:11,fontWeight:600,color:hasFlag?DS.amber.text:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>
          {hasFlag?"⚠ Dietary Flag":"Dietary Needs"}
        </span>
        {canEdit&&!editDiet&&<button onClick={()=>{setDietLocal(dn);setEditDiet(true);}} style={{...S.btnSec,fontSize:11,padding:"3px 10px"}}>Edit</button>}
      </div>
      {editDiet?(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Dietary Restrictions</div>
            <div style={{display:"flex",gap:6,marginBottom:6}}>
              <button onClick={()=>setDietLocal(d=>({...d,hasRestrictions:true}))} style={{...S.btnSec,fontSize:11,padding:"4px 12px",background:dietLocal.hasRestrictions?DS.blueTint:"transparent"}}>Yes</button>
              <button onClick={()=>setDietLocal(d=>({...d,hasRestrictions:false,restrictions:"",reason:""}))} style={{...S.btnGhost,fontSize:11,padding:"4px 12px"}}>No</button>
            </div>
            {dietLocal.hasRestrictions&&<textarea rows={2} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical",fontSize:12}} placeholder="Describe restrictions..." value={dietLocal.restrictions||""} onChange={e=>setDietLocal(d=>({...d,restrictions:e.target.value}))}/>}
          </div>
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Food Allergies</div>
            <div style={{display:"flex",gap:6,marginBottom:6}}>
              <button onClick={()=>setDietLocal(d=>({...d,hasAllergies:true}))} style={{...S.btnSec,fontSize:11,padding:"4px 12px",background:dietLocal.hasAllergies?DS.blueTint:"transparent"}}>Yes</button>
              <button onClick={()=>setDietLocal(d=>({...d,hasAllergies:false,allergies:""}))} style={{...S.btnGhost,fontSize:11,padding:"4px 12px"}}>No</button>
            </div>
            {dietLocal.hasAllergies&&<textarea rows={2} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical",fontSize:12}} placeholder="List food allergies..." value={dietLocal.allergies||""} onChange={e=>setDietLocal(d=>({...d,allergies:e.target.value}))}/>}
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>setEditDiet(false)} style={{...S.btnGhost,fontSize:11}}>Cancel</button>
            <button onClick={saveDiet} style={{...S.btn,fontSize:11}}>Save</button>
          </div>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {dn.hasRestrictions&&<div style={{fontSize:12,color:DS.amber.text,fontFamily:"'Inter',sans-serif"}}><strong>Restrictions:</strong> {dn.restrictions||"—"}{dn.reason&&` (${dn.reason})`}</div>}
          {dn.hasAllergies&&<div style={{fontSize:12,color:DS.amber.text,fontFamily:"'Inter',sans-serif"}}><strong>Allergies:</strong> {dn.allergies||"—"}</div>}
        </div>
      )}
    </div>
  );
}

// ── Resident Logs Tab ─────────────────────────────────────────────────────────

// ── Reusable AM/PM Time Input ─────────────────────────────────────────────────
function TimeInput({ value, onChange, style={} }) {
  const parse24 = (v) => {
    const m = (v||"").match(/^(\d{1,2}):(\d{2})$/);
    if(!m) return {h:"",min:"",period:"PM"};
    let h = parseInt(m[1]), mn = parseInt(m[2]);
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return {h:String(h12), min:String(mn).padStart(2,"0"), period};
  };

  const {h:ph, min:pm, period:pp} = parse24(value);
  const [hour, setHour] = React.useState(ph);
  const [min, setMin] = React.useState(pm);
  const [period, setPeriod] = React.useState(pp||"PM");

  // Sync when parent clears value
  React.useEffect(()=>{
    const {h,min:mn,period:p} = parse24(value);
    setHour(h); setMin(mn); if(p) setPeriod(p);
  },[value]);

  const emit = (h, m, p) => {
    const hNum = parseInt(h);
    const mNum = parseInt(m)||0;
    if(!h || isNaN(hNum) || hNum < 1 || hNum > 12) return;
    let h24 = hNum;
    if(p === "PM" && hNum !== 12) h24 = hNum + 12;
    if(p === "AM" && hNum === 12) h24 = 0;
    onChange(`${String(h24).padStart(2,"0")}:${String(mNum).padStart(2,"0")}`);
  };

  const inputBase = {background:"transparent",border:"none",fontSize:15,color:"#1A2B45",fontFamily:"'Inter',sans-serif",outline:"none",textAlign:"center",width:44,padding:"8px 4px"};

  return (
    <div style={{display:"inline-flex",alignItems:"center",border:"1px solid #D8DCE3",borderRadius:6,overflow:"hidden",background:"#F0F4FA",...style}}>
      <input style={inputBase} type="text" inputMode="numeric" maxLength={2} placeholder="8"
        value={hour}
        onChange={e=>{
          const v = e.target.value.replace(/\D/g,"").slice(0,2);
          setHour(v);
          emit(v, min, period);
        }}
      />
      <span style={{color:"#4A5568",fontSize:15,userSelect:"none",padding:"0 2px"}}>:</span>
      <input style={inputBase} type="text" inputMode="numeric" maxLength={2} placeholder="00"
        value={min}
        onChange={e=>{
          const v = e.target.value.replace(/\D/g,"").slice(0,2);
          setMin(v);
          if(v.length === 2) emit(hour, v, period);
        }}
        onBlur={e=>{
          if(e.target.value.length === 1) {
            const padded = e.target.value.padStart(2,"0");
            setMin(padded);
            emit(hour, padded, period);
          }
        }}
      />
      <div style={{display:"flex",borderLeft:"1px solid #D8DCE3"}}>
        {["AM","PM"].map(p=>(
          <button key={p} type="button"
            onClick={()=>{ setPeriod(p); emit(hour, min, p); }}
            style={{padding:"8px 10px",fontSize:12,fontWeight:600,cursor:"pointer",border:"none",fontFamily:"'Inter',sans-serif",
              background:period===p?"#1B5EBE":"transparent",
              color:period===p?"white":"#4A5568",
              transition:"all 0.1s"}}>
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Resident Avatar — shows photo or initials fallback ───────────────────────
function ResidentAvatar({ resident, size=44, borderRadius=8, canEdit=false, onUpload=null }) {
  const [uploading, setUploading] = useState(false);
  const photoURL = resident?.photoURL;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if(!file) return;
    setUploading(true);
    try {
      const { storage } = await import("./firebase");
      const { ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
      const storageRef = ref(storage, `residentPhotos/${resident.id}_${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      if(onUpload) await onUpload(url);
    } catch(err) { alert("Upload failed: "+err.message); }
    setUploading(false);
  };

  return (
    <div style={{position:"relative",flexShrink:0}}>
      {photoURL ? (
        <img src={photoURL} alt={resident.name}
          style={{width:size,height:size,borderRadius,objectFit:"cover",border:`1px solid ${DS.border}`}}/>
      ) : (
        <div style={{width:size,height:size,borderRadius,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:600,fontSize:Math.round(size*0.33),fontFamily:"'DM Sans',sans-serif",border:"1px solid rgba(255,255,255,0.2)"}}>
          {initials(resident?.name||"?")}
        </div>
      )}
      {canEdit&&(
        <label style={{position:"absolute",bottom:-4,right:-4,width:20,height:20,borderRadius:"50%",background:DS.blue,border:"2px solid white",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:9,color:"white"}}>
          {uploading?"…":"📷"}
          <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={handleUpload}/>
        </label>
      )}
    </div>
  );
}

// ── Photo Capture Step — shown before intake forms ────────────────────────────
function PhotoCaptureStep({ resident, onSave, onSkip }) {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleCapture = (e) => {
    const f = e.target.files?.[0];
    if(!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setError("");
  };

  const handleSave = async () => {
    if(!file) return;
    setUploading(true);
    try {
      const { storage } = await import("./firebase");
      const { ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
      const storageRef = ref(storage, `residentPhotos/${resident.id}_${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await onSave(url);
    } catch(err) {
      setError("Upload failed — " + err.message);
      setUploading(false);
    }
  };

  return (
    <div style={{maxWidth:480,margin:"0 auto",display:"flex",flexDirection:"column",gap:16}}>
      <div style={{...S.card,padding:0,overflow:"hidden"}}>
        <div style={{padding:"12px 16px",background:DS.blue}}>
          <div style={{fontSize:14,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>📷 Resident Photo — Required</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>Capture before starting intake for {displayName(resident.name)}</div>
        </div>
        <div style={{padding:20,display:"flex",flexDirection:"column",alignItems:"center",gap:16}}>
          {preview ? (
            <img src={preview} alt="Preview" style={{width:160,height:160,objectFit:"cover",borderRadius:10,border:`1px solid ${DS.border}`}}/>
          ) : (
            <div style={{width:160,height:160,borderRadius:10,background:DS.pageBg,border:`2px dashed ${DS.border}`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,color:DS.textSec}}>
              <span style={{fontSize:36}}>📷</span>
              <span style={{fontSize:12}}>No photo yet</span>
            </div>
          )}
          <label style={{...S.btnCTA,cursor:"pointer",textAlign:"center",display:"flex",alignItems:"center",gap:8}}>
            {preview?"Retake Photo":"Take Photo / Upload"}
            <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={handleCapture}/>
          </label>
          {error&&<div style={{fontSize:12,color:DS.error.text,background:DS.error.bg,border:`1px solid ${DS.error.border}`,borderRadius:6,padding:"8px 12px",width:"100%",textAlign:"center"}}>{error}</div>}
        </div>
      </div>
      <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
        <button onClick={onSkip} style={{background:DS.warning.bg,fontSize:12,fontWeight:600,color:DS.warning.text,border:`1.5px solid ${DS.warning.border}`,borderRadius:6,padding:"8px 14px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
          Skip — Flag for Follow-up
        </button>
        <button onClick={handleSave} disabled={!file||uploading}
          style={{...S.btnCTA,opacity:(!file||uploading)?0.4:1}}>
          {uploading?"Uploading...":"Save Photo & Continue →"}
        </button>
      </div>
    </div>
  );
}

function ResidentLogsTab({ resident, residentId }) {
  const [logTab, setLogTab] = useState("movement");
  const [inOutLogs, setInOutLogs] = useState([]);
  const [uaLogs, setUALogs] = useState([]);
  const [medLogs, setMedLogs] = useState([]);
  const [compLogs, setCompLogs] = useState([]);

  useEffect(()=>{
    const u1 = subscribeToInOutLogs(residentId, setInOutLogs);
    const u2 = subscribeToResidentUAHistory(residentId, setUALogs);
    const u3 = subscribeToMedicationLogs(null, null, all=>setMedLogs(all.filter(l=>l.residentId===residentId)));
    // Subscribe to compensation logs for this resident
    let u4 = ()=>{};
    (async()=>{
      const { db } = await import("./firebase");
      const { collection, query, where, onSnapshot } = await import("firebase/firestore");
      const q = query(collection(db,"compensationLogs"), where("residentId","==",residentId));
      u4 = onSnapshot(q, snap=>{
        const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
        docs.sort((a,b)=>(b.date||"").localeCompare(a.date||""));
        setCompLogs(docs);
      });
    })();
    return ()=>{u1();u2();u3();u4();};
  },[residentId]);

  // Work log entries from inOutLogs
  const workEntries = inOutLogs.filter(l=>l.eventType==="work_log_end"&&l.hoursWorked>0)
    .sort((a,b)=>(b.date||"").localeCompare(a.date||""));

  // Group work entries by week (Sun-Sat)
  const getWeekStart = (dateStr) => {
    const d = new Date(dateStr+"T12:00");
    const day = d.getDay();
    d.setDate(d.getDate()-day);
    return d.toISOString().split("T")[0];
  };
  const workByWeek = workEntries.reduce((acc,e)=>{
    const ws = getWeekStart(e.date||TODAY);
    if(!acc[ws]) acc[ws]={weekStart:ws,entries:[],totalHours:0};
    acc[ws].entries.push(e);
    acc[ws].totalHours += parseFloat(e.hoursWorked)||0;
    return acc;
  },{});
  const workWeeks = Object.values(workByWeek).sort((a,b)=>b.weekStart.localeCompare(a.weekStart));

  const calcOwed = hrs => Math.min(Math.floor(hrs/8)*10, 50);

  const movementLogs = inOutLogs.filter(l=>l.eventType==="sign_out"||l.eventType==="sign_in")
    .sort((a,b)=>(b.date||"").localeCompare(a.date||"")||(b.time||"").localeCompare(a.time||""));

  const LOG_TABS = [
    {id:"movement",label:"Sign In/Sign Out",count:movementLogs.length},
    {id:"worklog",label:"Work Log",count:workEntries.length},
    {id:"compensation",label:"Compensation",count:compLogs.length},
    {id:"ua",label:"Urine & BAC",count:uaLogs.length},
    {id:"medication",label:"Medication",count:medLogs.length},
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {/* Sub-tabs */}
      <div style={{display:"flex",gap:3,background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:8,padding:3,width:"fit-content"}}>
        {LOG_TABS.map(t=>(
          <button key={t.id} onClick={()=>setLogTab(t.id)}
            style={{padding:"6px 14px",fontSize:12,fontWeight:logTab===t.id?600:400,borderRadius:6,cursor:"pointer",
              background:logTab===t.id?DS.blue:"transparent",border:"none",
              color:logTab===t.id?"white":DS.textSec,fontFamily:"'Inter',sans-serif"}}>
            {t.label}{t.count>0?` (${t.count})`:""}
          </button>
        ))}
      </div>

      {/* Movement log */}
      {logTab==="movement"&&(()=>{
        const signOuts = movementLogs.filter(l=>l.eventType==="sign_out");
        const signIns = movementLogs.filter(l=>l.eventType==="sign_in");
        const parseTime = (t) => {
          const m = (t||"").match(/(\d+):(\d+)\s*(AM|PM)?/i);
          if(!m) return 0;
          let h=parseInt(m[1]),min=parseInt(m[2]);
          if(m[3]?.toUpperCase()==="PM"&&h!==12) h+=12;
          if(m[3]?.toUpperCase()==="AM"&&h===12) h=0;
          return h*60+min;
        };
        const paired = signOuts.map(out => {
          const outMins = parseTime(out.time);
          const matchIn = signIns.find(sin => {
            if(sin.date > out.date) return true;
            if(sin.date === out.date && parseTime(sin.time) > outMins) return true;
            return false;
          });
          let duration = null;
          if(matchIn) {
            const outTotal = (new Date(out.date+"T00:00:00")).getTime()/60000 + outMins;
            const inTotal = (new Date(matchIn.date+"T00:00:00")).getTime()/60000 + parseTime(matchIn.time);
            const mins = Math.round(inTotal - outTotal);
            duration = mins >= 60 ? `${Math.floor(mins/60)}h ${mins%60}m` : `${mins}m`;
          }
          return { out, matchIn, duration };
        });
        const RC = {Work:{bg:"#E6F1FB",color:"#0C447C"},Medical:{bg:"#EEEDFE",color:"#3C3489"},Leisure:{bg:"#EAF3DE",color:"#3B6D11"},"P&P":{bg:"#FAEEDA",color:"#854F0B"},Appointment:{bg:"#E1F5EE",color:"#085041"},Personal:{bg:"#F1EFE8",color:"#5F5E5A"}};
        const rc = r => RC[r]||{bg:"#F1EFE8",color:"#5F5E5A"};
        return (
        <div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <div style={{fontSize:13,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Movement log</div>
            <div style={{fontSize:11,color:DS.textSec}}>{paired.length} movements</div>
          </div>
          {paired.length===0?(
            <div style={{...S.card,textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>No movement logs yet</div>
          ):(
            <div style={{border:`0.5px solid ${DS.border}`,borderRadius:8,overflow:"hidden"}}>
              <div style={{display:"grid",gridTemplateColumns:"68px 62px 56px 62px 62px 52px",gap:6,padding:"7px 14px",background:DS.pageBg,borderBottom:`0.5px solid ${DS.border}`}}>
                <span style={{fontSize:10,color:DS.textSec}}>Date</span>
                <span style={{fontSize:10,color:DS.textSec}}>Out</span>
                <span style={{fontSize:10,color:DS.textSec}}>Reason</span>
                <span style={{fontSize:10,color:DS.textSec}}>ETR</span>
                <span style={{fontSize:10,color:DS.textSec}}>Return</span>
                <span style={{fontSize:10,color:DS.textSec}}>Duration</span>
              </div>
              {paired.map((p,i)=>(
                <div key={i} style={{display:"grid",gridTemplateColumns:"68px 62px 56px 62px 62px 52px",gap:6,padding:"8px 14px",borderBottom:`0.5px solid ${DS.border}`,background:!p.matchIn?"#FFFBEB":"white",alignItems:"center"}}>
                  <span style={{fontSize:12,fontWeight:500,color:DS.textPrimary}}>{new Date(p.out.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"numeric",day:"numeric"})}</span>
                  <span style={{fontSize:12,color:DS.textPrimary}}>{p.out.time}</span>
                  <span style={{fontSize:10,fontWeight:500,padding:"2px 7px",borderRadius:99,background:rc(p.out.reason).bg,color:rc(p.out.reason).color,display:"inline-block",whiteSpace:"nowrap"}}>{p.out.reason||"Out"}</span>
                  <span style={{fontSize:11,color:DS.textSec}}>{p.out.estReturn||"--"}</span>
                  {p.matchIn?(
                    <span style={{fontSize:12,color:DS.textPrimary}}>{p.matchIn.time}</span>
                  ):(
                    <span style={{fontSize:10,fontWeight:500,padding:"2px 7px",borderRadius:99,background:"#FAEEDA",color:"#854F0B"}}>Still out</span>
                  )}
                  <span style={{fontSize:11,color:p.matchIn?DS.textSec:"#854F0B"}}>{p.duration||"--"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        );
      })()}

      {/* Work log */}
      {logTab==="worklog"&&(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {workWeeks.length===0?<div style={{...S.card,textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>No work hours logged yet</div>:
          workWeeks.map(w=>{
            const owed = calcOwed(w.totalHours);
            return (
              <div key={w.weekStart} style={{...S.card,padding:0,overflow:"hidden"}}>
                <div style={{padding:"10px 14px",background:DS.pageBg,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Week of {w.weekStart}</div>
                    <div style={{fontSize:11,color:DS.textSec}}>{w.totalHours.toFixed(1)} hrs total</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:13,fontWeight:600,color:DS.blue}}>${owed.toFixed(2)} owed</div>
                    <div style={{fontSize:10,color:DS.textSec}}>$10/8hrs · cap $50</div>
                  </div>
                </div>
                {w.entries.map((e,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",borderTop:`0.5px solid ${DS.border}`}}>
                    <div style={{fontSize:11,color:DS.textSec,width:70,flexShrink:0}}>{e.date}</div>
                    <div style={{fontSize:12,color:DS.textPrimary,flex:1}}>{resident.employer||"Work"}</div>
                    <div style={{fontSize:12,fontWeight:600,color:DS.blue}}>{parseFloat(e.hoursWorked).toFixed(1)} hrs</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* UA/BAC log */}
      {logTab==="ua"&&(
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {uaLogs.length===0?<div style={{...S.card,textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>No UA results yet</div>:
          uaLogs.map((l,i)=>(
            <div key={i} style={{...S.card,display:"flex",alignItems:"center",gap:10,background:l.result==="pass"?DS.success.bg:DS.error.bg,border:`0.5px solid ${l.result==="pass"?DS.success.border:DS.error.border}`}}>
              <div style={{width:32,height:32,borderRadius:8,background:l.result==="pass"?DS.success.text:DS.error.text,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:700,fontSize:12,flexShrink:0}}>
                {l.result==="pass"?"P":"F"}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:500,color:l.result==="pass"?DS.success.text:DS.error.text}}>{l.result==="pass"?"Pass":"Fail"}{l.substance?` — ${l.substance}`:""}</div>
                <div style={{fontSize:11,color:DS.textSec}}>{l.date} · {l.time||"—"}{l.adHoc?" · Unscheduled Urine Test":""}</div>
              </div>
              <Pill label={l.adHoc?"Unscheduled Urine Test":"Scheduled"} bg={DS.pageBg} border={DS.border} color={DS.textSec}/>
            </div>
          ))}
        </div>
      )}

      {/* Medication log */}
      {logTab==="medication"&&(
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {medLogs.length===0?<div style={{...S.card,textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>No medication logs yet</div>:
          [...medLogs].sort((a,b)=>(b.date||"").localeCompare(a.date||"")).map((l,i)=>{
            const st = MAR_STYLES[l.status]||MAR_STYLES.missed;
            return (
              <div key={i} style={{...S.card,display:"flex",alignItems:"center",gap:10,background:st.bg,border:`0.5px solid ${st.border}`}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:500,color:st.color}}>{l.medication} — {l.slot}</div>
                  <div style={{fontSize:11,color:DS.textSec}}>{l.date} · {l.time||"—"}</div>
                </div>
                <Pill label={st.label} bg={st.bg} border={st.border} color={st.color}/>
              </div>
            );
          })}
        </div>
      )}

      {logTab==="compensation"&&(()=>{
        const totalCollected = compLogs.reduce((s,l)=>s+(parseFloat(l.amount)||0),0);
        const totalOwed = workEntries.reduce((s,e)=>{
          const ws = getWeekStart(e.date||TODAY);
          return s;
        },0);
        return (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {/* Summary */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div style={{...S.card,textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:700,color:DS.success.text,fontFamily:"'DM Sans',sans-serif"}}>${totalCollected.toFixed(2)}</div>
                <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginTop:2}}>Total Collected</div>
              </div>
              <div style={{...S.card,textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:700,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{compLogs.length}</div>
                <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginTop:2}}>Collections</div>
              </div>
            </div>
            {/* Ledger */}
            {compLogs.length===0?(
              <div style={{...S.card,textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>No compensation collected yet</div>
            ):compLogs.map((l,i)=>(
              <div key={i} style={{...S.card,display:"flex",alignItems:"center",gap:10,background:DS.success.bg,border:`0.5px solid ${DS.success.border}`}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:DS.success.text}}>${parseFloat(l.amount||0).toFixed(2)} collected</div>
                  <div style={{fontSize:11,color:DS.textSec}}>{l.date} · By {l.collectedBy||"—"} · Conf# {l.confirmNo||"—"}</div>
                  {l.weeks?.length>0&&<div style={{fontSize:11,color:DS.textSec}}>Weeks: {l.weeks.join(", ")}</div>}
                </div>
                <Pill label="Collected" bg={DS.success.bg} border={DS.success.border} color={DS.success.text}/>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

function ResidentProfile({ resident, onClose, onSave, onArchive, onDischarge, perms }) {
  const [tab,setTab]=useState("overview");
  const [editingName,setEditingName]=useState(false);
  const [nameInput,setNameInput]=useState("");
  const [editing,setEditing]=useState(false);
  const [confirmArchive,setConfirmArchive]=useState(false);
  const [showDischarge,setShowDischarge]=useState(false);
  const [confirmWithdraw,setConfirmWithdraw]=useState(false);
  const [dischargeType,setDischargeType]=useState(null);
  const [form,setForm]=useState({
    exitChecklist: Array(EXIT_CHECKLIST_ITEMS.length).fill(false),
    entryDate:"", exitDate:"", actualDischargeDate:"", dischargeNotes:"",
    gangAffiliated: false, mhLevel: "MH-0", room:"", race:"", employer:"",
    trainingDecisionPoints:false, trainingAngerManagement:false, trainingParenting:false,
    trainingNotes:"",
    ...resident
  });
  const [newNote,setNewNote]=useState("");
  const [newMed,setNewMed]=useState("");
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  const handleEntryDate=(v)=>{
    set("entryDate",v);
    if(v) set("exitDate", add90Days(v));
  };

  const canEdit=perms.canEditHighLevel||perms.canEditHealth||perms.canEditDocs||perms.canEditMeds;
  const tabs = perms.raOnly
    ? ["medications","documents","logs"]
    : ["overview","health","employment","medications","documents","logs",...((perms.canLeaveCaseNotes||perms.canEditHighLevel||perms.role==="pm")?["meetings_notes"]:[])];

  const TAB_LABELS = {
    overview:"Overview", health:"Health", employment:"Employment",
    medications:"Medications", documents:"Documents",
    logs:"Logs", meetings_notes:"Meetings & Notes",
  };  const cs=classStyle[form.classification]||classStyle["Non-Violent"];
  const ss=statusColors[form.status]||statusColors.active;

  const daysLeft = daysUntil(form.exitDate);
  const exitSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;
  const exitOverdue = daysLeft !== null && daysLeft < 0 && form.status === "active";

  return (
    <div style={{position:"fixed",inset:0,zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:16,background:"rgba(26,43,69,0.5)"}}>
      <div style={{position:"relative",width:"100%",maxWidth:760,maxHeight:"90vh",background:DS.white,border:`0.5px solid ${DS.border}`,borderRadius:8,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Header */}
        <div style={{padding:"16px 20px",background:DS.blue,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            {form.photoURL ? (
              <img src={form.photoURL} alt={form.name} style={{width:44,height:44,borderRadius:8,objectFit:"cover",border:"1px solid rgba(255,255,255,0.3)",flexShrink:0}}/>
            ) : (
              <div style={{width:44,height:44,borderRadius:8,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:600,fontSize:15,flexShrink:0,fontFamily:"'DM Sans',sans-serif"}}>{initials(form.name)}</div>
            )}
            {(perms.canEditHighLevel||perms.role==="admin")&&(
              <label title="Update photo" style={{position:"absolute",bottom:4,left:52,width:18,height:18,borderRadius:"50%",background:DS.blue,border:"2px solid white",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:8,color:"white",zIndex:2,display:tab==="overview"?"flex":"none"}}>
                📷<input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={async e=>{
                  const file=e.target.files?.[0]; if(!file) return;
                  try {
                    const { storage }=await import("./firebase");
                    const { ref,uploadBytes,getDownloadURL }=await import("firebase/storage");
                    const sr=ref(storage,`residentPhotos/${resident.id}_${Date.now()}.jpg`);
                    await uploadBytes(sr,file);
                    const url=await getDownloadURL(sr);
                    await saveResident({...resident,photoURL:url});
                    setForm(f=>({...f,photoURL:url}));
                  } catch(err){alert("Upload failed: "+err.message);}
                }}/>
              </label>
            )}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {editingName && perms?.canEditHighLevel ? (
                  <>
                    <input value={nameInput} onChange={e=>setNameInput(e.target.value)} autoFocus
                      style={{fontSize:15,fontWeight:600,padding:"4px 8px",borderRadius:6,border:"1px solid rgba(255,255,255,0.3)",background:"rgba(255,255,255,0.1)",color:"white",fontFamily:"'DM Sans',sans-serif",outline:"none"}}/>
                    <button onClick={async()=>{const t=nameInput.trim();if(t&&t!==form.name){const u={...form,name:t};setForm(u);await onSave(u);}setEditingName(false);}}
                      style={{padding:"3px 10px",fontSize:10,fontWeight:600,borderRadius:5,border:"none",background:"white",color:"#1B5EBE",cursor:"pointer"}}>Save</button>
                    <button onClick={()=>setEditingName(false)}
                      style={{padding:"3px 10px",fontSize:10,borderRadius:5,border:"1px solid rgba(255,255,255,0.3)",background:"transparent",color:"rgba(255,255,255,0.8)",cursor:"pointer"}}>Cancel</button>
                  </>
                ) : (
                  <>
                    <div style={{fontSize:17,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>{displayName(form.name)}</div>
                    {perms?.canEditHighLevel && (
                      <button onClick={()=>{setNameInput(form.name||"");setEditingName(true);}}
                        title="Edit name (Admin only)"
                        style={{padding:"2px 6px",fontSize:11,borderRadius:4,border:"1px solid rgba(255,255,255,0.25)",background:"transparent",color:"rgba(255,255,255,0.6)",cursor:"pointer"}}>
                        <i className="bi bi-pencil"></i>
                      </button>
                    )}
                  </>
                )}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:3,flexWrap:"wrap"}}>
                {perms.canEditHighLevel&&<span style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontFamily:"'Inter',sans-serif"}}>DOC# {form.doc}</span>}
                {form.room&&<span style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontFamily:"'Inter',sans-serif"}}>· Rm {form.room}</span>}
                {form.status==="active"&&<span style={{fontSize:11,padding:"1px 7px",borderRadius:10,background:DS.success.bg,color:DS.success.text,fontWeight:500,fontFamily:"'Inter',sans-serif"}}>Active</span>}
                {form.status==="pending_arrival"&&<span style={{fontSize:11,padding:"1px 7px",borderRadius:10,background:DS.warning.bg,color:DS.warning.text,fontWeight:500,fontFamily:"'Inter',sans-serif"}}>Pending Arrival</span>}
                {form.status==="discharged"&&<span style={{fontSize:11,padding:"1px 7px",borderRadius:10,background:DS.neutral.bg,color:DS.neutral.text,fontWeight:500,fontFamily:"'Inter',sans-serif"}}>Discharged</span>}
                {form.status==="pending_arrival"&&form.entryDate&&<span style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontFamily:"'Inter',sans-serif"}}>· Arrives {form.entryDate}</span>}
                {form.facility&&<span style={{fontSize:11,color:"rgba(255,255,255,0.6)",fontFamily:"'Inter',sans-serif"}}>· {form.facility}</span>}
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {canEdit&&!editing&&<button onClick={()=>setEditing(true)} style={{fontSize:11,padding:"5px 14px",borderRadius:6,cursor:"pointer",background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>Edit</button>}
            {editing&&<>
              <button onClick={()=>{onSave(form);setEditing(false);}} style={{...S.btnCTA,fontSize:11,padding:"5px 14px"}}>Save</button>
              <button onClick={()=>{setForm({...resident});setEditing(false);}} style={{fontSize:11,padding:"5px 14px",borderRadius:6,cursor:"pointer",background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.2)",color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>Cancel</button>
            </>}
            {perms.canEditHighLevel&&!editing&&onArchive&&(
              <button onClick={()=>setConfirmArchive(true)}
                style={{fontSize:11,padding:"5px 14px",borderRadius:6,cursor:"pointer",background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.2)",color:"rgba(255,255,255,0.8)",fontFamily:"'Inter',sans-serif"}}>Archive</button>
            )}
            {(perms.canEditHighLevel||perms.canLeaveCaseNotes||perms.canUseRATools)&&!editing&&form.status!=="discharged"&&form.status!=="archived"&&form.status!=="withdrawn"&&(
              form.status==="pending_arrival" ? (
                <button onClick={()=>setConfirmWithdraw(true)}
                  style={{fontSize:11,padding:"5px 14px",borderRadius:6,cursor:"pointer",background:"#8B1A1A",border:"1px solid #8B1A1A",color:"white",fontWeight:500,fontFamily:"'Inter',sans-serif"}}>
                  Withdraw
                </button>
              ) : (
                <button onClick={()=>{setShowDischarge(true);setDischargeType(null);}}
                  style={{fontSize:11,padding:"5px 14px",borderRadius:6,cursor:"pointer",background:"#8B1A1A",border:"1px solid #8B1A1A",color:"white",fontWeight:500,fontFamily:"'Inter',sans-serif"}}>
                  Discharge
                </button>
              )
            )}
            <button onClick={onClose} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.7)",cursor:"pointer",fontSize:20,lineHeight:1,padding:"0 4px"}}>✕</button>
          </div>
        </div>

        {/* Archive confirmation */}
        {confirmArchive&&(
          <div style={{borderTop:`3px solid ${DS.error.text}`,background:DS.error.bg,padding:"12px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexShrink:0}}>
            <div style={{fontSize:13,color:DS.error.text,fontFamily:"'Inter',sans-serif"}}>Archive <strong>{form.name}</strong>? They will be hidden from all views but data is preserved.</div>
            <div style={{display:"flex",gap:8,flexShrink:0}}>
              <button onClick={()=>setConfirmArchive(false)} style={{...S.btnGhost,fontSize:11}}>Cancel</button>
              <button onClick={()=>onArchive(form)} style={{fontSize:11,padding:"5px 14px",borderRadius:6,cursor:"pointer",background:DS.error.text,border:"none",color:DS.textPrimary,fontWeight:500,fontFamily:"'Inter',sans-serif"}}>Confirm Archive</button>
            </div>
          </div>
        )}

        {/* Withdraw confirmation — pending arrival only */}
        {confirmWithdraw&&(
          <div style={{borderTop:`3px solid #7A4F00`,background:"#FFF3CD",padding:"12px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexShrink:0}}>
            <div style={{fontSize:13,color:"#7A4F00",fontFamily:"'Inter',sans-serif"}}>Withdraw <strong>{form.name}</strong>? They will be moved to Archived with Withdrawn status.</div>
            <div style={{display:"flex",gap:8,flexShrink:0}}>
              <button onClick={()=>setConfirmWithdraw(false)} style={{...S.btnGhost,fontSize:11}}>Cancel</button>
              <button onClick={async()=>{
                const updated={...form,status:"withdrawn",withdrawnDate:TODAY};
                await onSave(updated);
                setConfirmWithdraw(false);
                onClose();
              }} style={{fontSize:11,padding:"5px 14px",borderRadius:6,cursor:"pointer",background:"#7A4F00",border:"none",color:"white",fontWeight:500,fontFamily:"'Inter',sans-serif"}}>Confirm Withdraw</button>
            </div>
          </div>
        )}

        {/* Discharge modal */}
        {showDischarge&&(
          <DischargeModal
            resident={form}
            perms={perms}
            onClose={()=>{setShowDischarge(false);setDischargeType(null);}}
            onComplete={(updatedResident)=>{onSave(updatedResident);setShowDischarge(false);onClose();}}
          />
        )}

        {/* Access notice for non-admins */}
        {!perms.canEditHighLevel&&(
          <div style={{padding:"8px 20px",background:DS.blueTint,borderBottom:`1px solid ${DS.blueBorder}`,display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
            <i className="bi bi-info-circle-fill" style={{fontSize:13,color:DS.blue,flexShrink:0}}></i>
            <span style={{fontSize:11,color:DS.blue,fontFamily:"'Inter',sans-serif"}}>{perms.raOnly ? "RA view — medications tab only. Contact a Program Coordinator for other changes." : perms.canLeaveCaseNotes?"Program Coordinator view — legal details are read-only.":"Limited view — contact a Program Coordinator to make changes."}</span>
          </div>
        )}

        {/* Tabs */}
        <div style={{display:"flex",borderBottom:`0.5px solid ${DS.border}`,padding:"0 20px",gap:2,background:DS.white,flexShrink:0}}>
          {tabs.map(t=>(
            <button key={t} onClick={()=>setTab(t)} style={{padding:"10px 14px",fontSize:12,fontWeight:tab===t?500:400,background:"transparent",border:"none",cursor:"pointer",borderBottom:`2px solid ${tab===t?DS.blue:"transparent"}`,color:tab===t?DS.blue:DS.textSec,marginBottom:-1,fontFamily:"'Inter',sans-serif",transition:"color 0.15s",whiteSpace:"nowrap"}}>
              {TAB_LABELS[t]||t}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{flex:1,overflowY:"auto",padding:20,background:DS.pageBg}}>

          {tab==="overview"&&(
            <div style={{display:"flex",flexDirection:"column",gap:24}}>

              {/* Exit soon / overdue banner */}
              {(exitSoon||exitOverdue)&&(
                <div style={{background:exitOverdue?DS.error.bg:DS.warning.bg,border:`1px solid ${exitOverdue?DS.error.border:DS.warning.border}`,borderRadius:8,padding:"12px 16px",display:"flex",alignItems:"center",gap:12}}>
                  <i className={`bi ${exitOverdue?"bi-exclamation-triangle-fill":"bi-clock-fill"}`} style={{fontSize:18,color:exitOverdue?DS.error.text:DS.warning.text,flexShrink:0}}></i>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:exitOverdue?DS.error.text:DS.warning.text,fontFamily:"'DM Sans',sans-serif"}}>
                      {exitOverdue?`Exit date passed ${Math.abs(daysLeft)} day${Math.abs(daysLeft)!==1?"s":""} ago`:`Exiting in ${daysLeft} day${daysLeft!==1?"s":""}`}
                    </div>
                    <div style={{fontSize:11,color:exitOverdue?DS.error.text:DS.warning.text,opacity:0.8,marginTop:2,fontFamily:"'Inter',sans-serif"}}>
                      Planned exit: {form.exitDate} — {exitOverdue?"Update status or exit date.":"Ensure exit checklist is being completed."}
                    </div>
                  </div>
                </div>
              )}

              {/* Entry / Exit dates */}
              <div>
                <SectionHdr>Program Dates</SectionHdr>
                {perms.canEditHighLevel&&editing?(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    <div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Entry Date (program start)</div>
                      <input type="date" style={{...S.input}} value={form.entryDate||""} onChange={e=>handleEntryDate(e.target.value)}/>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Exit Date (planned, auto 90 days)</div>
                      <input type="date" style={{...S.input}} value={form.exitDate||""} onChange={e=>set("exitDate",e.target.value)}/>
                    </div>
                  </div>
                ):(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                    <InfoRow label="Entry Date (program start)" value={form.entryDate||"—"}/>
                    <div style={{display:"flex",flexDirection:"column",gap:2}}>
                      <span style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em"}}>Exit Date (planned)</span>
                      <span style={{fontSize:13,color:exitOverdue?DS.error.text:exitSoon?DS.warning.text:DS.textPrimary,fontWeight:exitSoon||exitOverdue?600:400,fontFamily:"'Inter',sans-serif"}}>
                        {form.exitDate||"—"}
                        {daysLeft!==null&&form.exitDate&&<span style={{fontSize:11,marginLeft:8,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>
                          {daysLeft>0?`(${daysLeft} days away)`:daysLeft===0?"(today)":"(overdue)"}
                        </span>}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* DP Level — Admin and PC only */}
              {(perms.canEditHighLevel||perms.role==="pm")&&(
                <div>
                  <SectionHdr>Disposition Level</SectionHdr>
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {editing?(
                      <div>
                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8,fontFamily:"'Inter',sans-serif"}}>Set DP Level</div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:10}}>
                          {[
                            {v:"DP-0",label:"DP-0",sub:"No issues",color:DS.success.text,bg:DS.success.bg,border:DS.success.border},
                            {v:"DP-1",label:"DP-1",sub:"SA only",color:DS.teal.text,bg:DS.teal.bg,border:DS.teal.border},
                            {v:"DP-2",label:"DP-2",sub:"MH only",color:DS.purple.text,bg:DS.purple.bg,border:DS.purple.border},
                            {v:"DP-3",label:"DP-3",sub:"SA + MH",color:DS.amber.text,bg:DS.amber.bg,border:DS.amber.border},
                          ].map(({v,label,sub,color,bg,border})=>(
                            <button key={v} onClick={()=>{
                              const prev = form.dpLevel;
                              if(prev===v) return;
                              set("dpLevel",v);
                              const history = [...(form.dpHistory||[]),{from:prev||"none",to:v,date:TODAY,by:"staff"}];
                              set("dpHistory",history);
                            }} style={{padding:"10px 8px",borderRadius:6,cursor:"pointer",textAlign:"center",
                              background:(form.dpLevel||"")=== v?bg:"white",
                              border:`2px solid ${(form.dpLevel||"")=== v?border:DS.border}`,
                              transition:"all 0.15s"}}>
                              <div style={{fontSize:14,fontWeight:600,color:(form.dpLevel||"")===v?color:DS.textSec,fontFamily:"'DM Sans',sans-serif"}}>{label}</div>
                              <div style={{fontSize:10,color:(form.dpLevel||"")===v?color:DS.textSec,marginTop:2,fontFamily:"'Inter',sans-serif"}}>{sub}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ):(
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        {form.dpLevel?(()=>{
                          const dpColors = {"DP-0":{color:DS.success.text,bg:DS.success.bg,border:DS.success.border,sub:"No issues"},"DP-1":{color:DS.teal.text,bg:DS.teal.bg,border:DS.teal.border,sub:"SA only"},"DP-2":{color:DS.purple.text,bg:DS.purple.bg,border:DS.purple.border,sub:"MH only"},"DP-3":{color:DS.amber.text,bg:DS.amber.bg,border:DS.amber.border,sub:"SA + MH"}};
                          const dc = dpColors[form.dpLevel]||{color:DS.textSec,bg:DS.pageBg,border:DS.border,sub:""};
                          return (
                            <div style={{display:"flex",alignItems:"center",gap:10}}>
                              <span style={{fontSize:16,fontWeight:600,padding:"4px 14px",borderRadius:8,background:dc.bg,border:`1px solid ${dc.border}`,color:dc.color,fontFamily:"'DM Sans',sans-serif"}}>{form.dpLevel}</span>
                              <span style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{dc.sub}</span>
                            </div>
                          );
                        })():<span style={{fontSize:13,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Not set</span>}
                      </div>
                    )}
                    {/* DP history */}
                    {(form.dpHistory||[]).length>0&&(
                      <div style={{background:DS.pageBg,borderRadius:6,padding:"10px 12px"}}>
                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Change History</div>
                        <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          {[...(form.dpHistory||[])].reverse().map((h,i)=>(
                            <div key={i} style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>
                              {h.date} — Changed from <strong>{h.from}</strong> to <strong>{h.to}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Employment summary */}
              {(form.employed||form.trainingDecisionPoints||form.trainingAngerManagement||form.trainingParenting)&&(
                <div>
                  <SectionHdr>Employment & Training</SectionHdr>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    <div style={{...S.card,padding:"10px 14px"}}>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Employment</div>
                      <div style={{fontSize:13,color:form.employed?DS.success.text:DS.textSec,fontWeight:form.employed?600:400}}>
                        {form.employed?(form.employer||"Employed"):"Not Employed"}
                      </div>
                    </div>
                    <div style={{...S.card,padding:"10px 14px"}}>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Training</div>
                      <div style={{fontSize:12,color:DS.textPrimary}}>
                        {[form.trainingDecisionPoints&&"Decision Points",form.trainingAngerManagement&&"Anger Management",form.trainingParenting&&"Parenting"].filter(Boolean).join(", ")||"None"}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <SectionHdr>Referral Information</SectionHdr>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                  <InfoRow label="Referred By" value={form.referredBy}/>
                  <InfoRow label="Source" value={form.referralSource==="pp"?"Probation & Parole":"Institution"}/>
                  {form.referralSource==="pp"
                    ? <InfoRow label="P&P District" value={VA_DISTRICTS.find(d=>String(d.n)===String(form.ppDistrict))?.label||form.ppDistrict}/>
                    : <InfoRow label="Institution" value={form.referralInstitution}/>
                  }
                  {perms.canEditHighLevel&&<InfoRow label="DOC#" value={form.doc}/>}
                  {perms.canEditHighLevel&&<InfoRow label="Race" value={form.race}/>}
                </div>
              </div>

              <div>
                <SectionHdr>Personal Details</SectionHdr>
                {perms.canEditHighLevel&&editing?(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    {[
                      {label:"Date of Birth",key:"dob",type:"date"},
                      {label:"Release Date",key:"releaseDate",type:"date"},
                      {label:"Room Number",key:"room"},
                    ].map(({label,key,type})=>(
                      <div key={key}>
                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{label}</div>
                        <input type={type||"text"} style={{...S.input}} value={form[key]||""} onChange={e=>set(key,e.target.value)}/>
                      </div>
                    ))}
                    <div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Race</div>
                      <select style={{...S.input}} value={form.race||""} onChange={e=>set("race",e.target.value)}>
                        <option value="">Select...</option>
                        {RACE_OPTIONS.map(r=><option key={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Sentencing District</div>
                      <DistrictSelect value={form.sentencingDistrict} onChange={v=>set("sentencingDistrict",v)}/>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Classification</div>
                      <select style={{...S.input}} value={form.classification} onChange={e=>set("classification",e.target.value)}>
                        {["Non-Violent","Violent","Sex Offender"].map(c=><option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Referral Source</div>
                      <select style={{...S.input}} value={form.referralSource||"institution"} onChange={e=>set("referralSource",e.target.value)}>
                        <option value="institution">Institution</option>
                        <option value="pp">Probation & Parole</option>
                      </select>
                    </div>
                    {(form.referralSource||"institution")==="institution"?(
                      <div>
                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Institution</div>
                        <select style={{...S.input}} value={form.referralInstitution||""} onChange={e=>set("referralInstitution",e.target.value)}>
                          <option value="">Select institution...</option>
                          {VA_INSTITUTIONS.map(i=><option key={i}>{i}</option>)}
                        </select>
                      </div>
                    ):(
                      <div>
                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>P&P District</div>
                        <select style={{...S.input}} value={form.ppDistrict||""} onChange={e=>set("ppDistrict",e.target.value)}>
                          <option value="">Select district...</option>
                          {VA_DISTRICTS.map(d=><option key={d.n} value={d.n}>{d.label}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Gang Affiliated</div>
                      <div style={{display:"flex",gap:8}}>
                        {[true,false].map(v=>(
                          <button key={String(v)} onClick={()=>set("gangAffiliated",v)} style={{flex:1,padding:"7px",fontSize:12,borderRadius:8,cursor:"pointer",background:form.gangAffiliated===v?(v?"#2a1a0c":DS.border):"transparent",border:`1px solid ${form.gangAffiliated===v?(v?"#f97316":"#D8DCE3"):DS.border}`,color:form.gangAffiliated===v?(v?"#fdba74":"#e2e8f0"):"#4A5568",fontWeight:form.gangAffiliated===v?600:400}}>
                            {v?"Yes":"No"}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Status</div>
                      <select style={{...S.input}} value={form.status} onChange={e=>set("status",e.target.value)}>
                        <option value="applicant">Applicant</option>
                        <option value="pending_arrival">Pending Arrival</option>
                        <option value="active">Active</option>
                        {form.status==="pending_arrival"
                          ? <option value="withdrawn">Withdraw</option>
                          : <option value="discharged">Discharged</option>
                        }
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Facility</div>
                      <select style={{...S.input}} value={form.facility||""} onChange={e=>set("facility",e.target.value)}>
                        <option value="">Unassigned</option>{FACILITIES.map(f=><option key={f}>{f}</option>)}
                      </select>
                    </div>
                    {(form.status==="active"||form.status==="discharged")&&(
                      <div>
                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Actual Discharge Date</div>
                        <input type="date" style={{...S.input}} value={form.actualDischargeDate||""} onChange={e=>{
                          set("actualDischargeDate",e.target.value);
                          if(e.target.value) set("status","discharged");
                        }}/>
                        {!form.actualDischargeDate&&<div style={{fontSize:10,color:DS.textSec,marginTop:3}}>Setting this date will mark resident as discharged</div>}
                      </div>
                    )}
                  </div>
                ):(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                    <InfoRow label="Date of Birth" value={form.dob}/>
                    <InfoRow label="Release Date" value={form.releaseDate}/>
                    <InfoRow label="Sentencing District" value={VA_DISTRICTS.find(d=>String(d.n)===String(form.sentencingDistrict))?.label || form.sentencingDistrict}/>
                    {form.room&&<InfoRow label="Room Number" value={form.room}/>}
                    <div style={{display:"flex",flexDirection:"column",gap:2}}>
                      <span style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em"}}>Classification</span>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:2}}>
                        <Pill label={form.classification} bg={cs.bg} border={cs.border} color={cs.color}/>
                        {form.gangAffiliated&&<Pill label="Gang Affiliated" bg="#2a1a0c" border="#f97316" color="#fdba74"/>}
                      </div>
                    </div>
                    {form.actualDischargeDate&&<InfoRow label="Discharge Date" value={form.actualDischargeDate}/>}
                  </div>
                )}
              </div>

              {perms.canEditHighLevel&&(
                <div>
                  <SectionHdr>Offenses</SectionHdr>
                  {editing?(
                    <div style={{display:"flex",flexDirection:"column",gap:16}}>
                      <OffenseSelector label="Current Offenses" value={Array.isArray(form.currentOffenses)?form.currentOffenses:(form.currentOffenses?[form.currentOffenses]:[])} onChange={v=>set("currentOffenses",v)}/>
                      <OffenseSelector label="Prior Offenses" value={Array.isArray(form.priorOffenses)?form.priorOffenses:(form.priorOffenses?[form.priorOffenses]:[])} onChange={v=>set("priorOffenses",v)}/>
                    </div>
                  ):(
                    <div style={{display:"flex",flexDirection:"column",gap:12}}>
                      <div>
                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Current Offenses</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {(Array.isArray(form.currentOffenses)?form.currentOffenses:(form.currentOffenses?[form.currentOffenses]:[])).map((o,i)=><Pill key={i} label={o} bg="white" border={DS.border} color={DS.textPrimary}/>)}
                          {(!form.currentOffenses||form.currentOffenses.length===0)&&<span style={{fontSize:13,color:DS.textSec}}>—</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Prior Offenses</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {(Array.isArray(form.priorOffenses)?form.priorOffenses:(form.priorOffenses?[form.priorOffenses]:[])).map((o,i)=><Pill key={i} label={o} bg="white" border={DS.border} color={DS.textPrimary}/>)}
                          {(!form.priorOffenses||form.priorOffenses.length===0)&&<span style={{fontSize:13,color:DS.textSec}}>—</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {form.noContact&&(
                <div style={{background:DS.error.bg,border:`1px solid ${DS.error.border}`,borderRadius:8,padding:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#8B1A1A",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>⚠ No-Contact Order</div>
                  <div style={{fontSize:13,color:DS.error.text}}>{form.noContactList}</div>
                </div>
              )}
              <DietaryEditSection dn={form.dietaryNeeds||{}} canEdit={!!(perms.canEditHealth&&editing)} onSave={v=>set("dietaryNeeds",v)}/>
            </div>
          )}

          {tab==="health"&&(
            <div style={{display:"flex",flexDirection:"column",gap:20}}>

              {/* Mental Health — with MH level */}
              <div>
                <SectionHdr>Mental / Behavioral Health</SectionHdr>
                <div style={{background:form.mentalHealth?DS.purple.bg:DS.pageBg,border:`1px solid ${form.mentalHealth?DS.purple.border:DS.border}`,borderRadius:12,padding:16}}>
                  {perms.canEditHealth&&editing?(
                    <>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>MH Status Level</div>
                      <div style={{display:"flex",gap:8,marginBottom:12}}>
                        {[{v:"MH-0",label:"MH-0",sub:"No concerns",bg:DS.pageBg,border:DS.border,color:DS.textSec},
                          {v:"MH-1",label:"MH-1",sub:"Some concerns",bg:DS.purple.bg,border:DS.purple.border,color:DS.purple.text},
                          {v:"MH-2",label:"MH-2",sub:"Active treatment",bg:DS.purple.bg,border:DS.purple.border,color:DS.purple.text}
                        ].map(({v,label,sub,bg,border,color})=>(
                          <button key={v} onClick={()=>{set("mhLevel",v);set("mentalHealth",v!=="MH-0");}} style={{flex:1,padding:"8px 6px",borderRadius:10,cursor:"pointer",background:(form.mhLevel||"MH-0")===v?bg:"transparent",border:`2px solid ${(form.mhLevel||"MH-0")===v?border:DS.border}`,color:(form.mhLevel||"MH-0")===v?color:DS.textSec,textAlign:"center"}}>
                            <div style={{fontSize:13,fontWeight:700}}>{label}</div>
                            <div style={{fontSize:10,marginTop:2}}>{sub}</div>
                          </button>
                        ))}
                      </div>
                      {form.mentalHealth&&<textarea rows={3} style={{...S.input,resize:"none"}} placeholder="Notes..." value={form.mentalHealthNotes} onChange={e=>set("mentalHealthNotes",e.target.value)}/>}
                    </>
                  ):(
                    <>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:form.mentalHealth?8:0}}>
                        <Pill label={form.mhLevel||"MH-0"}
                          bg={form.mentalHealth?DS.purple.bg:DS.border}
                          border={form.mentalHealth?"#4A3AAA":"#D8DCE3"}
                          color={form.mentalHealth?DS.purple.text:DS.textSec}/>
                        <span style={{fontSize:12,color:DS.textSec}}>
                          {(form.mhLevel||"MH-0")==="MH-0"?"No concerns":(form.mhLevel||"MH-0")==="MH-1"?"Some concerns present":"Active mental health treatment"}
                        </span>
                      </div>
                      {form.mentalHealth&&form.mentalHealthNotes&&<p style={{fontSize:13,color:DS.textPrimary,margin:0}}>{form.mentalHealthNotes}</p>}
                    </>
                  )}
                </div>
              </div>

              {/* Physical Health & Substance Abuse */}
              {[{key:"physicalHealth",nk:"physicalHealthNotes",label:"Physical Health",ac:"#0284c7"},{key:"substanceAbuse",nk:"substanceAbuseNotes",label:"Substance Abuse History",ac:"#0d9488"}].map(({key,nk,label,ac})=>(
                <div key={key}>
                  <SectionHdr>{label}</SectionHdr>
                  <div style={{background:form[key]?DS.warning.bg:DS.pageBg,border:`1px solid ${form[key]?"#F5C200":DS.border}`,borderRadius:12,padding:16}}>
                    {perms.canEditHealth&&editing?(
                      <>
                        <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:8}}>
                          <input type="checkbox" checked={form[key]} onChange={e=>set(key,e.target.checked)}/>
                          <span style={{fontSize:13,color:DS.textPrimary}}>Concerns present</span>
                        </label>
                        {form[key]&&<textarea rows={3} style={{...S.input,resize:"none"}} value={form[nk]} onChange={e=>set(nk,e.target.value)}/>}
                      </>
                    ):(
                      <>
                        <Pill label={form[key]?"Concerns Present":"No Concerns"} bg={form[key]?DS.warning.bg:"white"} border={form[key]?ac:DS.border} color={form[key]?DS.warning.text:DS.textPrimary}/>
                        {form[key]&&<p style={{fontSize:13,color:DS.textPrimary,marginTop:8}}>{form[nk]||"No details provided."}</p>}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab==="employment"&&(
            <div style={{display:"flex",flexDirection:"column",gap:20}}>
              <div>
                <SectionHdr>Employment Status</SectionHdr>
                <div style={{...S.card}}>
                  {perms.canEditHighLevel&&editing?(
                    <div style={{display:"flex",flexDirection:"column",gap:12}}>
                      <div>
                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Currently Employed?</div>
                        <div style={{display:"flex",gap:8,marginBottom:form.employed?12:0}}>
                          {[true,false].map(v=>(
                            <button key={String(v)} onClick={()=>set("employed",v)} style={{flex:1,padding:"8px",fontSize:12,borderRadius:8,cursor:"pointer",background:form.employed===v?(v?"#E6F4EC":"#FDECEA"):"white",border:`1px solid ${form.employed===v?(v?"#1A6B35":"#D8DCE3"):DS.border}`,color:form.employed===v?(v?"#1A6B35":"#8B1A1A"):"#4A5568",fontWeight:form.employed===v?600:400}}>
                              {v?"Yes":"No"}
                            </button>
                          ))}
                        </div>
                        {form.employed&&(
                          <div>
                            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Employer</div>
                            <input style={{...S.input}} placeholder="Employer name..." value={form.employer||""} onChange={e=>set("employer",e.target.value)}/>
                          </div>
                        )}
                      </div>
                    </div>
                  ):(
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                      <div style={{display:"flex",flexDirection:"column",gap:2}}>
                        <span style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em"}}>Employment Status</span>
                        <Pill label={form.employed?"Employed":"Not Employed"} bg={form.employed?DS.success.bg:DS.border} border={form.employed?DS.success.border:DS.border} color={form.employed?DS.success.text:DS.textSec}/>
                      </div>
                      {form.employed&&<InfoRow label="Employer" value={form.employer}/>}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <SectionHdr>Self Improvement Programs</SectionHdr>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[
                    {key:"trainingDecisionPoints",  label:"Decision Points"},
                    {key:"trainingAngerManagement", label:"Anger Management"},
                    {key:"trainingParenting",       label:"Parenting"},
                  ].map(({key,label})=>(
                    <div key={key} onClick={()=>perms.canEditHighLevel&&editing&&set(key,!form[key])}
                      style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:10,border:`1px solid ${form[key]?DS.success.border:DS.border}`,background:form[key]?DS.success.bg:"white",border:`1px solid ${DS.border}`,cursor:perms.canEditHighLevel&&editing?"pointer":"default"}}>
                      <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${form[key]?DS.success.text:DS.border}`,background:form[key]?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {form[key]&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}
                      </div>
                      <span style={{fontSize:13,color:form[key]?DS.success.text:DS.textPrimary}}>{label}</span>
                    </div>
                  ))}
                </div>
                {(perms.canEditHighLevel&&editing)&&(
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Training Notes</div>
                    <textarea rows={3} style={{...S.input,resize:"none"}} placeholder="Additional training notes..." value={form.trainingNotes||""} onChange={e=>set("trainingNotes",e.target.value)}/>
                  </div>
                )}
                {!editing&&form.trainingNotes&&(
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Training Notes</div>
                    <p style={{fontSize:13,color:DS.textPrimary,margin:0}}>{form.trainingNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab==="medications"&&(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* Scheduled Medications */}
              <div>
                <SectionHdr>Scheduled Medications</SectionHdr>
                {form.medications.length===0&&<p style={{fontSize:13,color:DS.textSec}}>No medications on file.</p>}
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {form.medications.map((rawMed,i)=>{
                    const med = normalizeMed(rawMed);
                    const slots = getMedSlots(med);
                    const isActive = med.active !== false;
                    return (
                      <div key={i} style={{...S.card,opacity:isActive?1:0.55,border:`1px solid ${isActive?DS.border:DS.border}`}}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                              <span style={{fontSize:13,fontWeight:600,color:isActive?DS.textPrimary:DS.textSec}}>{med.name}</span>
                              {med.dose&&<span style={{fontSize:11,color:DS.textSec}}>{med.dose}</span>}
                              <MedTypeBadge medName={med.name}/>
                              {!isActive&&<span style={{fontSize:10,padding:"1px 6px",borderRadius:4,background:DS.error.bg,border:`1px solid ${DS.error.border}`,color:DS.error.text}}>DISCONTINUED</span>}
                            </div>
                            <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>
                              <Pill label={med.frequency||"Once Daily"} bg={DS.blueTint} border={DS.blueBorder} color={DS.blue}/>
                              {slots.map(s=><Pill key={s} label={s} bg={DS.pageBg} border={DS.border} color={DS.textSec}/>)}
                            </div>
                            {!isActive&&med.discontinuedDate&&(
                              <div style={{fontSize:10,color:DS.textSec,marginTop:4}}>Discontinued {med.discontinuedDate}{med.discontinuedBy?` by ${med.discontinuedBy}`:""}</div>
                            )}
                          </div>
                          {perms.canEditMeds&&editing&&(
                            <div style={{display:"flex",gap:6,flexShrink:0}}>
                              <button onClick={()=>{
                                const updated=[...form.medications];
                                updated[i]=normalizeMed(rawMed);
                                updated[i].active=!isActive;
                                if(!isActive){delete updated[i].discontinuedDate;delete updated[i].discontinuedBy;}
                                else{updated[i].discontinuedDate=TODAY;updated[i].discontinuedBy="Staff";}
                                set("medications",updated);
                              }} style={{fontSize:10,padding:"3px 8px",borderRadius:6,cursor:"pointer",background:"transparent",border:`1px solid ${isActive?DS.error.border:DS.success.border}`,color:isActive?DS.error.text:DS.success.text}}>
                                {isActive?"Discontinue":"Reactivate"}
                              </button>
                              <button onClick={()=>set("medications",form.medications.filter((_,j)=>j!==i))} style={{background:"transparent",border:"none",color:DS.textSec,cursor:"pointer",fontSize:12}}>✕</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {perms.canEditMeds&&editing&&(
                  <div style={{marginTop:12}}>
                    <MedAutocomplete onAdd={(med)=>set("medications",[...form.medications,med])}/>
                  </div>
                )}
              </div>

              {/* OTC Medications */}
              <div>
                <SectionHdr>OTC Medication Log</SectionHdr>
                <div style={{fontSize:11,color:DS.textSec,marginBottom:10}}>Document over-the-counter medications given to this resident.</div>
                {(form.otcLog||[]).length===0&&<p style={{fontSize:13,color:DS.textSec}}>No OTC medications logged.</p>}
                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
                  {(form.otcLog||[]).map((entry,i)=>(
                    <div key={i} style={{...S.card,display:"flex",alignItems:"center",gap:12}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,color:DS.textPrimary,fontWeight:500}}>{entry.medication}{entry.dose?` — ${entry.dose}`:""}</div>
                        <div style={{fontSize:12,color:DS.textSec,marginTop:2}}>{entry.date} at {entry.time} · {entry.reason}</div>
                        <div style={{fontSize:10,color:DS.textSec}}>Logged by {entry.loggedBy}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {perms.canEditMeds&&(
                  <OTCEntry onAdd={(entry)=>set("otcLog",[...(form.otcLog||[]),entry])}/>
                )}
              </div>

              <div style={{...S.card,background:"white"}}>
                <span style={{fontSize:12,color:DS.textSec}}><span style={{color:"#1B5EBE",fontWeight:600}}>Note:</span> All medications are self-administered. Daily observation logs are tracked in the Medication Log module.</span>
              </div>
            </div>
          )}

          {tab==="documents"&&(
            <DocumentsTab
              form={form}
              perms={perms}
              editing={editing}
              onChecklistChange={(i, checked) => {
                const c=[...form.checklist]; c[i]=checked; set("checklist",c);
              }}
              onIntakeFormSave={(updatedForms) => {
                set("intakeForms", updatedForms);
              }}
            />
          )}



          {tab==="logs"&&(
            <ResidentLogsTab resident={form} residentId={resident.id}/>
          )}

          {tab==="meetings_notes"&&(
            <div style={{display:"flex",flexDirection:"column",gap:20}}>
              {(perms.canEditHighLevel||perms.role==="pm")&&(
                <div>
                  <MeetingsTab form={form}/>
                </div>
              )}
              {perms.canLeaveCaseNotes&&(
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <SectionHdr>Case Notes</SectionHdr>
                  {form.caseNotes.length===0&&<p style={{fontSize:13,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>No case notes yet.</p>}
                  {form.caseNotes.map((note,i)=>(
                    <div key={i} style={{...S.card}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                        <span style={{fontSize:12,fontWeight:600,color:DS.blue,fontFamily:"'Inter',sans-serif"}}>{note.author}</span>
                        <span style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{note.date}</span>
                      </div>
                      <p style={{fontSize:13,color:DS.textPrimary,margin:0,fontFamily:"'Inter',sans-serif"}}>{note.note}</p>
                    </div>
                  ))}
                  <textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"none",marginTop:4}} placeholder="Add a case note..." value={newNote} onChange={e=>setNewNote(e.target.value)}/>
                  <button onClick={()=>{if(newNote.trim()){set("caseNotes",[...form.caseNotes,{date:TODAY,author:"Staff",note:newNote}]);auditAction("case_note_added",{residentId:form.id,residentName:form.name});setNewNote("");}}} style={{...S.btn,alignSelf:"flex-end"}}>Add Note</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Intake Modal ──────────────────────────────────────────────────────────────
function IntakeModal({ onClose, onSave }) {
  const [step,setStep]=useState(0);
  const [form,setForm]=useState({...emptyForm});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const steps=["Referral Info","Personal & Legal","Health","Medications","Documents"];
  return (
    <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16,background:"rgba(0,0,0,0.8)"}}>
      <div style={{width:"100%",maxWidth:640,maxHeight:"90vh",background:"white",border:"1px solid #334155",borderRadius:20,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{padding:"20px 24px",borderBottom:"1px solid #1e293b"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <span style={{fontSize:16,fontWeight:700,color:DS.textPrimary}}>New Referral Intake</span>
            <button onClick={onClose} style={{background:"transparent",border:"none",color:DS.textSec,cursor:"pointer",fontSize:20}}>✕</button>
          </div>
          <div style={{display:"flex",gap:8}}>
            {steps.map((s,i)=>(
              <button key={i} onClick={()=>setStep(i)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,background:"transparent",border:"none",cursor:"pointer"}}>
                <div style={{width:"100%",height:4,borderRadius:2,background:i<=step?"#4f46e5":DS.border}}/>
                <span style={{fontSize:10,color:i===step?"#a5b4fc":"#64748b",fontWeight:i===step?600:400}}>{s}</span>
              </button>
            ))}
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:24}}>
          {step===0&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Full Name *</div>
                <input style={{...S.input}} placeholder="First Middle Last" value={form.name} onChange={e=>set("name",e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Referred By *</div>
                <input style={{...S.input}} placeholder="Case counselor name" value={form.referredBy} onChange={e=>set("referredBy",e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Referral Date</div>
                <input type="date" style={{...S.input}} value={form.referralDate} onChange={e=>set("referralDate",e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Referring From</div>
                <div style={{display:"flex",gap:6}}>
                  {[{v:"institution",l:"Institution"},{v:"pp",l:"Probation & Parole"}].map(({v,l})=>(
                    <button key={v} type="button" onClick={()=>set("referralSource",v)}
                      style={{flex:1,padding:"8px",fontSize:12,fontWeight:600,borderRadius:8,cursor:"pointer",
                        background:(form.referralSource||"institution")===v?"rgba(79,70,229,0.2)":"transparent",
                        border:`1px solid ${(form.referralSource||"institution")===v?"#4f46e5":DS.border}`,
                        color:(form.referralSource||"institution")===v?"#a5b4fc":"#64748b"}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              {(form.referralSource||"institution")==="institution" ? (
                <div>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Institution *</div>
                  <select style={{...S.input}} value={form.referralInstitution||""} onChange={e=>set("referralInstitution",e.target.value)}>
                    <option value="">Select institution...</option>
                    {VA_INSTITUTIONS.map(i=><option key={i}>{i}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>P&P District *</div>
                  <select style={{...S.input}} value={form.ppDistrict||""} onChange={e=>set("ppDistrict",e.target.value)}>
                    <option value="">Select district...</option>
                    {VA_DISTRICTS.map(d=><option key={d.n} value={d.n}>{d.label}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
          {step===1&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              {[{label:"DOC# *",key:"doc"},{label:"Date of Birth",key:"dob",type:"date"},{label:"Date of Release",key:"releaseDate",type:"date"}].map(({label,key,type})=>(
                <div key={key}>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{label}</div>
                  <input type={type||"text"} style={{...S.input}} value={form[key]} onChange={e=>set(key,e.target.value)}/>
                </div>
              ))}
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Sentencing District</div>
                <DistrictSelect value={form.sentencingDistrict} onChange={v=>set("sentencingDistrict",v)}/>
              </div>
              <div style={{gridColumn:"span 2"}}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Classification</div>
                <div style={{display:"flex",gap:6}}>
                  {["Non-Violent","Violent","Sex Offender"].map(c=>{const cs=classStyle[c];return(<button key={c} onClick={()=>set("classification",c)} style={{flex:1,padding:"8px 4px",fontSize:11,fontWeight:600,borderRadius:8,cursor:"pointer",background:form.classification===c?cs.bg:"transparent",border:`1px solid ${form.classification===c?cs.border:DS.border}`,color:form.classification===c?cs.color:DS.textSec}}>{c}</button>);})}
                </div>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Gang Affiliated?</div>
                <div style={{display:"flex",gap:6}}>
                  {[true,false].map(v=><button key={String(v)} onClick={()=>set("gangAffiliated",v)} style={{flex:1,padding:"7px",fontSize:12,borderRadius:8,cursor:"pointer",background:form.gangAffiliated===v?(v?"#2a1a0c":DS.border):"transparent",border:`1px solid ${form.gangAffiliated===v?(v?"#f97316":"#475569"):DS.border}`,color:form.gangAffiliated===v?(v?"#fdba74":"#e2e8f0"):"#64748b",fontWeight:form.gangAffiliated===v?600:400}}>{v?"Yes":"No"}</button>)}
                </div>
              </div>
              <div style={{gridColumn:"span 2"}}>
                <OffenseSelector label="Current Offenses" value={Array.isArray(form.currentOffenses)?form.currentOffenses:[]} onChange={v=>set("currentOffenses",v)}/>
              </div>
              <div style={{gridColumn:"span 2"}}>
                <OffenseSelector label="Prior Offenses" value={Array.isArray(form.priorOffenses)?form.priorOffenses:[]} onChange={v=>set("priorOffenses",v)}/>
              </div>
              {[{label:"Detainer?",key:"detainer"},{label:"ICOT in Progress?",key:"icot"},{label:"No-Contact Restrictions?",key:"noContact"}].map(({label,key})=>(
                <div key={key}>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{label}</div>
                  <div style={{display:"flex",gap:6}}>
                    {[true,false].map(v=><button key={String(v)} onClick={()=>set(key,v)} style={{flex:1,padding:"7px",fontSize:12,borderRadius:8,cursor:"pointer",background:form[key]===v?(v?DS.error.bg:DS.success.bg):"transparent",border:`1px solid ${form[key]===v?(v?DS.error.border:DS.success.border):DS.border}`,color:form[key]===v?(v?DS.error.text:DS.success.text):DS.textSec,fontWeight:form[key]===v?600:400}}>{v?"Yes":"No"}</button>)}
                  </div>
                </div>
              ))}
              {form.noContact&&<div style={{gridColumn:"span 2"}}><div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>No-Contact List</div><textarea rows={2} style={{...S.input,resize:"none"}} value={form.noContactList} onChange={e=>set("noContactList",e.target.value)}/></div>}
            </div>
          )}
          {step===2&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {/* MH Level selector */}
              <div style={{...S.card}}>
                <div style={{fontSize:13,color:DS.textPrimary,fontWeight:500,marginBottom:12}}>Mental / Behavioral Health Status</div>
                <div style={{display:"flex",gap:8}}>
                  {[{v:"MH-0",label:"MH-0",sub:"No concerns",bg:DS.border,border:"#475569",color:DS.textSec},
                    {v:"MH-1",label:"MH-1",sub:"Some concerns",bg:DS.purple.bg,border:DS.purple.border,color:DS.purple.text},
                    {v:"MH-2",label:"MH-2",sub:"Active treatment",bg:DS.purple.bg,border:DS.purple.border,color:DS.purple.text}
                  ].map(({v,label,sub,bg,border,color})=>(
                    <button key={v} onClick={()=>{set("mhLevel",v);set("mentalHealth",v!=="MH-0");}} style={{flex:1,padding:"10px 6px",borderRadius:10,cursor:"pointer",background:(form.mhLevel||"MH-0")===v?bg:"transparent",border:`2px solid ${(form.mhLevel||"MH-0")===v?border:DS.border}`,color:(form.mhLevel||"MH-0")===v?color:DS.textSec,textAlign:"center"}}>
                      <div style={{fontSize:13,fontWeight:700}}>{label}</div>
                      <div style={{fontSize:10,marginTop:2}}>{sub}</div>
                    </button>
                  ))}
                </div>
                {form.mentalHealth&&<textarea rows={2} style={{...S.input,resize:"none",marginTop:10}} placeholder="Notes (optional)..." value={form.mentalHealthNotes} onChange={e=>set("mentalHealthNotes",e.target.value)}/>}
              </div>
              {[{key:"physicalHealth",nk:"physicalHealthNotes",label:"Physical Health Concerns"},{key:"substanceAbuse",nk:"substanceAbuseNotes",label:"Substance Abuse Concerns"}].map(({key,nk,label})=>(
                <div key={key} style={{...S.card}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <span style={{fontSize:13,color:DS.textPrimary,fontWeight:500}}>{label}</span>
                    <div style={{display:"flex",gap:6}}>
                      {[true,false].map(v=><button key={String(v)} onClick={()=>set(key,v)} style={{padding:"4px 12px",fontSize:11,borderRadius:8,cursor:"pointer",background:form[key]===v?(v?DS.error.bg:DS.success.bg):"transparent",border:`1px solid ${form[key]===v?(v?DS.error.border:DS.success.border):DS.border}`,color:form[key]===v?(v?DS.error.text:DS.success.text):DS.textSec,fontWeight:form[key]===v?600:400}}>{v?"Yes":"No"}</button>)}
                    </div>
                  </div>
                  {form[key]&&<textarea rows={3} style={{...S.input,resize:"none"}} placeholder="Details or 'see attached'..." value={form[nk]} onChange={e=>set(nk,e.target.value)}/>}
                </div>
              ))}
            </div>
          )}
          {step===3&&(
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <p style={{fontSize:12,color:DS.textSec,marginBottom:4}}>Add any current medications. You can also add these later from the resident profile.</p>
              {form.medications.map((med,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",borderRadius:10,background:DS.border,border:"1px solid #334155"}}>
                  <span style={{fontSize:13,color:DS.textPrimary,flex:1}}>{med}</span>
                  <button onClick={()=>set("medications",form.medications.filter((_,j)=>j!==i))} style={{background:"transparent",border:"none",color:"#8B1A1A",cursor:"pointer",fontSize:12}}>Remove</button>
                </div>
              ))}
              <MedAutocomplete onAdd={(med)=>set("medications",[...form.medications,med])}/>
              {form.medications.length===0&&(
                <div style={{fontSize:12,color:DS.textSec,textAlign:"center",padding:"12px 0"}}>No medications added yet</div>
              )}
            </div>
          )}
          {step===4&&(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <p style={{fontSize:12,color:DS.textSec,marginBottom:4}}>Mark documents received with this referral packet.</p>
              {CHECKLIST_ITEMS.map((item,i)=>(
                <button key={i} onClick={()=>{const c=[...form.checklist];c[i]=!c[i];set("checklist",c);}} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:10,border:`1px solid ${form.checklist[i]?"#059669":DS.border}`,background:form.checklist[i]?"rgba(5,150,105,0.1)":DS.border,cursor:"pointer",textAlign:"left"}}>
                  <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${form.checklist[i]?DS.success.text:DS.border}`,background:form.checklist[i]?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {form.checklist[i]&&<span style={{color:DS.textPrimary,fontSize:11,fontWeight:700}}>✓</span>}
                  </div>
                  <span style={{fontSize:13,color:form.checklist[i]?DS.success.text:DS.textSec}}>{typeof item==="object"?item.label:item}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{padding:"16px 24px",borderTop:"1px solid #1e293b",display:"flex",justifyContent:"space-between"}}>
          <button onClick={()=>setStep(s=>Math.max(0,s-1))} disabled={step===0} style={{...S.btnGhost,opacity:step===0?0.3:1}}>Back</button>
          {step<steps.length-1
            ?<button onClick={()=>setStep(s=>s+1)} style={{...S.btn}}>Continue</button>
            :<button onClick={()=>{onSave({...form,id:Date.now()});onClose();}} style={{...S.btn,background:"#059669"}}>Submit Referral</button>
          }
        </div>
      </div>
    </div>
  );
}

// ── Daily Operations Log ────────────────────
function DailyOpsLog({ residents, role, facility }) {
  const [opsDate, setOpsDate] = useState(TODAY);
  const [facFilter, setFacFilter] = useState(role==="admin"?"all":facility||"all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    setLoading(true);
    const unsubs = [];

    const allEvents = [];
    const flush = () => {
      allEvents.sort((a,b)=>(a.sortTime||"").localeCompare(b.sortTime||""));
      setEvents([...allEvents]);
    };

    // 1. Sign in/out movements
    const u1 = subscribeToInOutLogs(null, logs => {
      const dayLogs = logs.filter(l=>l.date===opsDate&&(l.eventType==="sign_out"||l.eventType==="sign_in"));
      // Remove old movement events, add new
      for(let i=allEvents.length-1;i>=0;i--) if(allEvents[i].type==="movement") allEvents.splice(i,1);
      dayLogs.forEach(l=>{
        const isOut = l.eventType==="sign_out";
        allEvents.push({
          type:"movement",
          time: l.time||"",
          sortTime: l.time||"",
          facility: l.facility||"",
          title: `${l.residentName||"Unknown"} — ${isOut?"out":"in"}`,
          detail: isOut ? `${l.reason||"Out"} · ETR ${l.estReturn||"--"}` : `${l.note||""}`,
          dot: isOut?"move-out":"move-in",
        });
      });
      flush();
    });
    unsubs.push(u1);

    // 2. Medication logs
    const u2 = subscribeToMedicationLogs(opsDate, null, logs => {
      for(let i=allEvents.length-1;i>=0;i--) if(allEvents[i].type==="med") allEvents.splice(i,1);
      // Group by facility+slot
      const groups = {};
      logs.forEach(l=>{
        const key = `${l.facility||""}__${l.slot||"General"}`;
        if(!groups[key]) groups[key]={facility:l.facility,slot:l.slot||"General",taken:0,missed:0,refused:0,absent:0,time:l.time||""};
        groups[key][l.status]=(groups[key][l.status]||0)+1;
        if(l.time && l.time > groups[key].time) groups[key].time = l.time;
      });
      Object.values(groups).forEach(g=>{
        allEvents.push({
          type:"med",
          time: g.time||"",
          sortTime: g.time||"",
          facility: g.facility||"",
          title: `${g.slot} meds`,
          detail: `${g.taken} taken${g.missed?`, ${g.missed} missed`:""}${g.refused?`, ${g.refused} refused`:""}`,
          dot: "med",
        });
      });
      flush();
    });
    unsubs.push(u2);

    // 3. UA Results
    const u3 = subscribeToUAResults(null, null, null, results => {
      const dayResults = results.filter(r=>r.date===opsDate);
      for(let i=allEvents.length-1;i>=0;i--) if(allEvents[i].type==="ua") allEvents.splice(i,1);
      dayResults.forEach(r=>{
        allEvents.push({
          type:"ua",
          time: r.time||"",
          sortTime: r.time||"",
          facility: r.facility||"",
          title: `${r.testType||"UA"} — ${r.residentName||""}`,
          detail: `${r.panelType||""} · ${r.result||"Pending"}`,
          dot: "ua",
        });
      });
      flush();
    });
    unsubs.push(u3);

    // 4. Headcounts
    const u4 = subscribeToHeadcounts(opsDate, counts => {
      for(let i=allEvents.length-1;i>=0;i--) if(allEvents[i].type==="headcount") allEvents.splice(i,1);
      counts.forEach(h=>{
        allEvents.push({
          type:"headcount",
          time: h.time||"",
          sortTime: h.time||"",
          facility: h.facility||"",
          title: "Headcount",
          detail: `${h.presentCount||0} present, ${h.absentCount||0} absent — ${h.conductedBy||""}`,
          dot: "head",
        });
      });
      flush();
    });
    unsubs.push(u4);

    // 5. Shift notes
    const u5 = subscribeToShiftNotes(opsDate, notes => {
      for(let i=allEvents.length-1;i>=0;i--) if(allEvents[i].type==="note") allEvents.splice(i,1);
      notes.forEach(n=>{
        allEvents.push({
          type:"note",
          time: n.time||"",
          sortTime: n.time||"",
          facility: n.facility||"",
          title: "Shift note",
          detail: `"${n.note||""}" — ${n.author||""}`,
          dot: "shift",
        });
      });
      flush();
    });
    unsubs.push(u5);

    // 6. Incident reports
    (async()=>{
      try {
        const { db } = await import("./firebase");
        const { collection, query, where, onSnapshot } = await import("firebase/firestore");
        const q = query(collection(db,"incidentReports"), where("date","==",opsDate));
        const u6 = onSnapshot(q, snap=>{
          for(let i=allEvents.length-1;i>=0;i--) if(allEvents[i].type==="incident") allEvents.splice(i,1);
          snap.docs.forEach(d=>{
            const inc = d.data();
            allEvents.push({
              type:"incident",
              time: inc.time||"",
              sortTime: inc.time||"",
              facility: inc.facility||"",
              title: "Incident",
              detail: `${inc.category||"Report"} · ${inc.residentName||""} — ${inc.reportedBy||""}`,
              dot: "incident",
            });
          });
          flush();
        });
        unsubs.push(u6);
      } catch(e) { console.error("Incident sub failed",e); }
      // Fire drills
      try {
        const { collection, getDocs, query: fsQuery, where } = await import("firebase/firestore");
        const snap = await getDocs(fsQuery(collection(db,"fireDrills"), where("date","==",opsDate)));
        for(let i=allEvents.length-1;i>=0;i--) if(allEvents[i].type==="fire") allEvents.splice(i,1);
        snap.docs.forEach(d => {
          const dr = {id:d.id,...d.data()};
          allEvents.push({
            type:"fire", time:dr.time||"", sortTime:dr.time||"",
            facility:dr.facility||"",
            title:"Fire Drill",
            detail:`${dr.shift||""} · ${dr.residentCount||"?"} residents · ${dr.conductedBy||""}`,
            dot:"fire",
          });
        });
        flush();
      } catch(e) { console.error("Fire drill sub failed",e); }

      setLoading(false);
    })();

    return ()=>unsubs.forEach(u=>u&&u());
  },[opsDate]);

  const filtered = events.filter(e =>
    (facFilter==="all" || e.facility.includes(facFilter==="d1"?"801":"24th")) &&
    (typeFilter==="all" || e.type===typeFilter)
  );

  // Group by hour
  const parseHour = t => {
    const m = (t||"").match(/(\d+):(\d+)\s*(AM|PM)?/i);
    if(!m) return -1;
    let h = parseInt(m[1]);
    if(m[3]?.toUpperCase()==="PM"&&h!==12) h+=12;
    if(m[3]?.toUpperCase()==="AM"&&h===12) h=0;
    return h;
  };
  const hours = {};
  filtered.forEach(e=>{
    const h = parseHour(e.time);
    const label = h<0?"Unknown":h===0?"12:00 AM":h<12?`${h}:00 AM`:h===12?"12:00 PM":`${h-12}:00 PM`;
    if(!hours[label]) hours[label]=[];
    hours[label].push(e);
  });

  const DOT_COLORS = {"move-out":"#EF9F27","move-in":"#639922",med:"#7F77DD",ua:"#1D9E75",head:"#378ADD",shift:"#888780",fire:"#E85D20",incident:"#E24B4A"};
  const facTag = f => f.includes("801")?"D1":f.includes("24th")?"D2":"";
  const facClass = f => f.includes("801")?"f1":"f2";

  const movementCount = filtered.filter(e=>e.type==="movement").length;
  const medCount = filtered.filter(e=>e.type==="med").length;
  const uaCount = filtered.filter(e=>e.type==="ua").length;
  const outNow = filtered.filter(e=>e.type==="movement"&&e.dot==="move-out").length - filtered.filter(e=>e.type==="movement"&&e.dot==="move-in").length;

  const handlePrint = () => {
    const rows = filtered.map(e=>`<tr><td style="padding:3px 6px;border-bottom:1px solid #ddd;font-size:11px;">${e.time}</td><td style="padding:3px 6px;border-bottom:1px solid #ddd;font-size:11px;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${DOT_COLORS[e.dot]||"#888"};margin-right:4px;"></span>${e.title}</td><td style="padding:3px 6px;border-bottom:1px solid #ddd;font-size:11px;">${e.detail}</td><td style="padding:3px 6px;border-bottom:1px solid #ddd;font-size:10px;">${facTag(e.facility)}</td></tr>`).join("");
    const html = `<html><head><title>Daily Operations Log — ${opsDate}</title><style>body{font-family:Inter,sans-serif;padding:20px;}@media print{.no-print{display:none;}}</style></head><body><h2 style="margin:0 0 4px;">Daily Operations Log</h2><p style="color:#666;margin:0 0 16px;">${opsDate} · ${facFilter==="all"?"All Facilities":facFilter==="d1"?"Dorcus 1 - 801":"Dorcus 2 - 24th St"}</p><table style="width:100%;border-collapse:collapse;"><thead><tr style="background:#E8F0FB;"><th style="text-align:left;padding:6px;">Time</th><th style="text-align:left;padding:6px;">Event</th><th style="text-align:left;padding:6px;">Details</th><th style="padding:6px;">Fac</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open("","_blank");
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const TYPE_FILTERS = [
    {id:"all",label:"All",dot:null},
    {id:"movement",label:"Movement",dot:"move-out"},
    {id:"med",label:"Meds",dot:"med"},
    {id:"ua",label:"Urine Screen/BAC",dot:"ua"},
    {id:"headcount",label:"Headcount",dot:"head"},
    {id:"note",label:"Notes",dot:"shift"},
    {id:"fire",label:"Fire Drills",dot:"fire"},
  ];

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div>
          <div style={{fontSize:15,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Daily Operations Log</div>
          <div style={{fontSize:12,color:DS.textSec}}>{new Date(opsDate+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input type="date" value={opsDate} onChange={e=>setOpsDate(e.target.value)} style={{...S.input,border:`1px solid ${DS.border}`,padding:"4px 8px",fontSize:12}}/>
          <button onClick={handlePrint} style={{...S.btnGhost,fontSize:11,padding:"4px 10px"}}>Print</button>
        </div>
      </div>

      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10,flexWrap:"wrap"}}>
        {role==="admin"&&(
          <div style={{display:"flex",border:`0.5px solid ${DS.border}`,borderRadius:6,overflow:"hidden"}}>
            {[{id:"all",label:"All"},{id:"d1",label:"D1"},{id:"d2",label:"D2"}].map(f=>(
              <button key={f.id} onClick={()=>setFacFilter(f.id)}
                style={{padding:"5px 12px",fontSize:11,cursor:"pointer",border:"none",
                  background:facFilter===f.id?DS.blue:"white",
                  color:facFilter===f.id?"white":DS.textSec,fontFamily:"'Inter',sans-serif",fontWeight:facFilter===f.id?600:400}}>
                {f.label}
              </button>
            ))}
          </div>
        )}
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {TYPE_FILTERS.map(t=>(
            <button key={t.id} onClick={()=>setTypeFilter(t.id)}
              style={{padding:"4px 10px",fontSize:11,borderRadius:99,
                border:`0.5px solid ${typeFilter===t.id?DS.blue:DS.border}`,
                background:typeFilter===t.id?DS.blue:"white",
                color:typeFilter===t.id?"white":DS.textSec,
                cursor:"pointer",fontFamily:"'Inter',sans-serif",fontWeight:typeFilter===t.id?600:400,
                display:"flex",alignItems:"center",gap:4}}>
              {t.dot&&<span style={{width:8,height:8,borderRadius:"50%",background:DOT_COLORS[t.dot]||"#888",display:"inline-block"}}/>}
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <div style={{background:"#fff",border:`0.5px solid ${DS.border}`,borderRadius:6,padding:"6px 12px",flex:1}}>
          <div style={{fontSize:10,color:DS.textSec}}>Movements</div>
          <div style={{fontSize:16,fontWeight:600,color:DS.textPrimary}}>{movementCount}</div>
        </div>
        <div style={{background:"#fff",border:`0.5px solid ${DS.border}`,borderRadius:6,padding:"6px 12px",flex:1}}>
          <div style={{fontSize:10,color:DS.textSec}}>Med calls</div>
          <div style={{fontSize:16,fontWeight:600,color:DS.textPrimary}}>{medCount}</div>
        </div>
        <div style={{background:"#fff",border:`0.5px solid ${DS.border}`,borderRadius:6,padding:"6px 12px",flex:1}}>
          <div style={{fontSize:10,color:DS.textSec}}>Urine Screen/BAC</div>
          <div style={{fontSize:16,fontWeight:600,color:DS.textPrimary}}>{uaCount}</div>
        </div>
        <div style={{background:"#fff",border:`0.5px solid ${DS.border}`,borderRadius:6,padding:"6px 12px",flex:1}}>
          <div style={{fontSize:10,color:DS.textSec}}>Out now</div>
          <div style={{fontSize:16,fontWeight:600,color:DS.textPrimary}}>{Math.max(0,outNow)}</div>
        </div>
      </div>

      <div style={{border:`0.5px solid ${DS.border}`,borderRadius:8,overflow:"hidden",maxHeight:500,overflowY:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{background:DS.pageBg,position:"sticky",top:0,zIndex:1}}>
              <th style={{textAlign:"left",padding:"6px 8px",fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.04em",width:50}}>Time</th>
              <th style={{width:12,padding:"6px 2px"}}></th>
              <th style={{textAlign:"left",padding:"6px 8px",fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.04em"}}>Event</th>
              <th style={{textAlign:"right",padding:"6px 8px",fontSize:10,color:DS.textSec,width:36}}>Fac</th>
            </tr>
          </thead>
          <tbody>
            {loading&&<tr><td colSpan={4} style={{textAlign:"center",padding:24,color:DS.textSec}}>Loading...</td></tr>}
            {!loading&&filtered.length===0&&<tr><td colSpan={4} style={{textAlign:"center",padding:24,color:DS.textSec}}>No events for this date</td></tr>}
            {Object.entries(hours).map(([hour,evts])=>(
              <React.Fragment key={hour}>
                <tr><td colSpan={4} style={{background:DS.pageBg,fontSize:11,fontWeight:500,color:DS.textSec,padding:"4px 8px"}}>{hour}</td></tr>
                {evts.map((e,i)=>(
                  <tr key={i} style={{background:"#fff"}}>
                    <td style={{padding:"5px 8px",borderBottom:`0.5px solid ${DS.border}`,color:DS.textSec,fontSize:12,whiteSpace:"nowrap"}}>{e.time}</td>
                    <td style={{padding:"5px 2px",borderBottom:`0.5px solid ${DS.border}`}}><span style={{width:8,height:8,borderRadius:"50%",background:DOT_COLORS[e.dot]||"#888",display:"inline-block"}}/></td>
                    <td style={{padding:"5px 8px",borderBottom:`0.5px solid ${DS.border}`,color:DS.textPrimary}}>{e.title} <span style={{color:DS.textSec}}>{e.detail}</span></td>
                    <td style={{padding:"5px 8px",borderBottom:`0.5px solid ${DS.border}`,textAlign:"right"}}><span style={{fontSize:9,fontWeight:500,padding:"1px 5px",borderRadius:99,background:facClass(e.facility)==="f1"?"#E6F1FB":"#FFF3CD",color:facClass(e.facility)==="f1"?"#0C447C":"#854F0B"}}>{facTag(e.facility)}</span></td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Overdue Banner ────────────────────────────────────────────────────────────
function OverdueBanner({ residents }) {
  const [now, setNow] = useState(NOW_NY());
  useEffect(() => {
    const t = setInterval(() => setNow(NOW_NY()), 60000);
    return () => clearInterval(t);
  }, []);

  // Load today's in/out statuses from Firestore
  const [statuses, setStatuses] = useState({});
  useEffect(() => {
    const unsub = subscribeToTodayInOutLogs(null, logs => {
      const latest = {};
      logs.forEach(log => {
        if (!latest[log.residentId] || (log.createdAt?.seconds||0) > (latest[log.residentId].createdAt?.seconds||0)) {
          latest[log.residentId] = log;
        }
      });
      setStatuses(latest);
    });
    return unsub;
  }, []);

  const overdue = residents.filter(r => {
    const log = statuses[r.id];
    if (!log || log.eventType !== "sign_out") return false;
    if (!log.estReturn) {
      // No est return — flag if out more than 30 min
      const outTime = log.createdAt?.seconds ? new Date(log.createdAt.seconds * 1000) : null;
      if (!outTime) return false;
      return (now - outTime) > 30 * 60 * 1000;
    }
    // Has est return — check estReturnDate for cross-midnight
    const etrDate = log.estReturnDate || TODAY;
    if (etrDate > TODAY) return false; // ETR is tomorrow, not overdue yet
    if (etrDate < TODAY) return true; // ETR was yesterday, fully overdue
    const match = log.estReturn.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!match) return false;
    let h = parseInt(match[1]), m = parseInt(match[2]);
    const ampm = match[3]?.toUpperCase();
    if (ampm === "PM" && h !== 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    const est = new Date(); est.setHours(h, m, 0, 0);
    return (now - est) > 30 * 60 * 1000;
  });

  if (overdue.length === 0) return null;

  return (
    <div style={{background:"#FDECEA",border:`1px solid ${DS.error.border}`,borderRadius:12,padding:"12px 16px",marginBottom:20}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <div style={{width:8,height:8,borderRadius:"50%",background:DS.error.text}}/>
        <span style={{fontSize:13,fontWeight:700,color:DS.error.text}}>
          {overdue.length} Resident{overdue.length!==1?"s":""} Overdue
        </span>
        <span style={{fontSize:11,color:DS.error.text}}>— 30+ min past expected return</span>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
        {overdue.map(r => {
          const log = statuses[r.id];
          return (
            <div key={r.id} style={{background:"#FDECEA",border:`1px solid ${DS.error.border}`,borderRadius:8,padding:"5px 12px"}}>
              <span style={{fontSize:12,fontWeight:600,color:DS.error.text}}>{r.name}</span>
              {log?.estReturn&&<span style={{fontSize:11,color:DS.error.text,marginLeft:6}}>est. {formatTime12(log.estReturn)}</span>}
              {log?.reason&&<span style={{fontSize:11,color:DS.error.text,marginLeft:6}}>· {log.reason}</span>}
            </div>
          );
        })}
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
}

// ── RA: In/Out Board ──────────────────────────────────────────────────────────
// Out reasons
const OUT_REASONS = ["Leisure","Medical","Appointment","Work","P&P"];
// Reasons allowed even when suspended
const SUSPENDED_ALLOWED = ["Work","P&P"];

const IN_STATUS_STYLES = {
  in:  { label:"In",  bg:"#E6F4EC", border:"#A3D4B4", color:"#1A6B35", dot:"#1A6B35" },
  out: { label:"Out", bg:"#FFF3CD", border:"#F5C200", color:"#7A4F00", dot:"#D4A800" },
};

function InOutBoard({ residents, perms, raName }) {
  const [hoursPrompt, setHoursPrompt] = useState(null);
  const [hoursWorked, setHoursWorked] = useState("");
  const [statuses, setStatuses] = useState(() =>
    Object.fromEntries(residents.map(r => [r.id, {
      status: "in", reason: null, timeOut: null,
      estReturn: "", timeIn: null, note: "", suspended: false,
    }]))
  );
  const [popup, setPopup] = useState(null);
  const [facFilter, setFacFilter] = useState(() => perms?.canSeeAllFacilities ? "all" : (perms?.assignedFacility || "all"));
  const [search, setSearch] = useState("");
  const [now, setNow] = useState(NOW_NY());

  // Update clock every minute for overdue detection
  useEffect(() => {
    const t = setInterval(() => setNow(NOW_NY()), 60000);
    return () => clearInterval(t);
  }, []);

  // Load today's latest status for each resident from Firestore
  useEffect(() => {
    const unsub = subscribeToTodayInOutLogs(null, logs => {
      // Build current status map from latest log entry per resident
      const latestByResident = {};
      logs.forEach(log => {
        const existing = latestByResident[log.residentId];
        if (!existing) {
          latestByResident[log.residentId] = log;
        } else {
          const existingTs = existing.createdAt?.seconds || 0;
          const logTs = log.createdAt?.seconds || 0;
          if (logTs > existingTs) {
            latestByResident[log.residentId] = log;
          } else if (logTs === existingTs) {
            const toMins = (t) => {
              if (!t) return 0;
              const m = t.match(/(\d+):(\d+)\s*(AM|PM)?/i);
              if (!m) return 0;
              let h = parseInt(m[1]), min = parseInt(m[2]);
              if (m[3]?.toUpperCase() === "PM" && h !== 12) h += 12;
              if (m[3]?.toUpperCase() === "AM" && h === 12) h = 0;
              return h * 60 + min;
            };
            if (toMins(log.time) > toMins(existing.time)) {
              latestByResident[log.residentId] = log;
            }
          }
        }
      });
      setStatuses(prev => {
        const updated = {...prev};
        Object.entries(latestByResident).forEach(([residentId, log]) => {
          updated[residentId] = {
            status: log.eventType === "sign_out" ? "out" : "in",
            reason: log.reason || null,
            timeOut: log.eventType === "sign_out" ? log.time : null,
            estReturn: log.estReturn || "",
            estReturnDate: log.estReturnDate || null,
            timeIn: log.eventType === "sign_in" ? log.time : null,
            note: log.note || "",
            suspended: prev[residentId]?.suspended || log.suspended || false,
            suspendReason: prev[residentId]?.suspendReason || log.suspendReason || "",
          };
        });
        return updated;
      });
    });
    return unsub;
  }, []);

  const filtered = residents.filter(r => (facFilter === "all" || r.facility === facFilter) && (!search || r.name.toLowerCase().includes(search.toLowerCase())))
    .sort((a,b) => {
      const ra = a.room ? parseInt(a.room)||a.room : null;
      const rb = b.room ? parseInt(b.room)||b.room : null;
      if (ra === null && rb === null) return (a.name||"").localeCompare(b.name||"");
      if (ra === null) return 1;
      if (rb === null) return -1;
      return typeof ra === "number" && typeof rb === "number" ? ra - rb : String(ra).localeCompare(String(rb));
    });
  const inCount  = Object.values(statuses).filter(s => s.status === "in").length;
  const outCount = Object.values(statuses).filter(s => s.status === "out").length;

  function isOverdue(s) {
    if (!s || s.status !== "out" || !s.estReturn) return false;
    const etrDate = s.estReturnDate || TODAY;
    if (etrDate > TODAY) return false;
    if (etrDate < TODAY) return true;
    const match = s.estReturn.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return false;
    const [h, m] = [parseInt(match[1]), parseInt(match[2])];
    const est = new Date(); est.setHours(h, m, 0, 0);
    return (now - est) > 30 * 60 * 1000;
  }

  const handleHoursSubmit = async () => {
    if(!hoursPrompt) return;
    const hrs = parseFloat(hoursWorked)||0;
    if(hrs > 0) {
      await saveInOutEvent({ residentId:hoursPrompt.resident.id, residentName:hoursPrompt.resident.name, facility:hoursPrompt.resident.facility, date:TODAY, eventType:"work_log_end", hoursWorked:hrs, time:timeNow() });
      try {
        const { db } = await import("./firebase");
        const { collection, addDoc } = await import("firebase/firestore");
        await addDoc(collection(db,"compensationLogs"),{ residentId:hoursPrompt.resident.id, residentName:hoursPrompt.resident.name, facility:hoursPrompt.resident.facility, date:TODAY, hoursWorked:hrs, amount:hrs*(5/8), source:"inout_board", createdAt:TODAY });
      } catch(e){ console.error("comp log failed",e); }
    }
    setHoursPrompt(null); setHoursWorked("");
  };

  async function signOut(id, reason, estReturn, note) {
    // TimeInput returns "HH:MM AM/PM" — validate it's a real time not just empty
    const etrValid = estReturn && typeof estReturn === "string" && estReturn.includes(":");
    if (!etrValid) { alert("Estimated return time is required before signing out."); return; }
    const resident = residents.find(r => r.id === id);
    const _now = NOW_NY(); const _nowMins = _now.getHours()*60+_now.getMinutes();
    const _ep = estReturn.match(/(\d+):(\d+)/);
    const _eMins = _ep ? parseInt(_ep[1])*60+parseInt(_ep[2]) : 999;
    const estReturnDate = _eMins < _nowMins ? daysFromToday(1) : TODAY;
    setStatuses(p => ({ ...p, [id]: {
      ...(p[id]||{}), status:"out", reason, timeOut:timeNow(), estReturn, estReturnDate, timeIn:null, note, _updated:Date.now(),
    }}));
    await saveInOutEvent({
      residentId: id,
      residentName: resident?.name || "",
      facility: resident?.facility || "",
      date: TODAY,
      eventType: "sign_out",
      reason, estReturn, estReturnDate, note,
      time: timeNow(),
      suspended: statuses[id]?.suspended || false,
    });
    setPopup(null);
  }

  async function signIn(id) {
    const resident = residents.find(r => r.id === id);
    const prev = statuses[id] || {};
    setStatuses(p => ({ ...p, [id]: {
      ...(p[id]||{}), status:"in", reason:null, timeOut:null, estReturn:"", timeIn:timeNow(), _updated:Date.now(),
    }}));
    await saveInOutEvent({
      residentId: id,
      residentName: resident?.name || "",
      facility: resident?.facility || "",
      date: TODAY,
      eventType: "sign_in",
      time: timeNow(),
      previousReason: prev.reason || null,
      timeWasOut: prev.timeOut || null,
    });
    // Work return - show hours prompt
    if (prev.reason === "Work") {
      setHoursPrompt({ resident, timeOut: prev.timeOut||null });
      setPopup(null);
      return;
    }
    // Auto-log work hours if they were signed out for Work (legacy fallback)
    if (false && prev.reason === "Work" && prev.timeOut) {
      try {
        const [outH, outM, outP] = prev.timeOut.match(/(\d+):(\d+)\s*(AM|PM)/i)?.slice(1) || [];
        const [inH, inM, inP] = [timeNow().match(/(\d+):(\d+)\s*(AM|PM)/i)?.slice(1)].flat() || [];
        if (outH && inH) {
          let oh = parseInt(outH), om = parseInt(outM);
          let ih = parseInt(inH), im = parseInt(inM);
          if (outP?.toUpperCase() === "PM" && oh !== 12) oh += 12;
          if (outP?.toUpperCase() === "AM" && oh === 12) oh = 0;
          if (inP?.toUpperCase() === "PM" && ih !== 12) ih += 12;
          if (inP?.toUpperCase() === "AM" && ih === 12) ih = 0;
          const hoursWorked = Math.round(((ih * 60 + im) - (oh * 60 + om)) / 60 * 10) / 10;
          if (hoursWorked > 0 && hoursWorked < 16) {
            await saveInOutEvent({
              residentId: id, residentName: resident?.name || "",
              facility: resident?.facility || "", date: TODAY,
              eventType: "work_log_end", hoursWorked, time: timeNow(),
            });
            const { db } = await import("./firebase");
            const { collection, addDoc } = await import("firebase/firestore");
            await addDoc(collection(db, "compensationLogs"), {
              residentId: id, residentName: resident?.name || "",
              facility: resident?.facility || "", date: TODAY,
              hoursWorked, amount: hoursWorked * (5/8),
              source: "auto_sign_in", createdAt: TODAY,
            });
          }
        }
      } catch(e) { console.error("Work hours auto-log failed:", e); }
    }
    setPopup(null);
  }

  const [suspendPrompt, setSuspendPrompt] = useState(null); // {id, name, mode}
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendDuration, setSuspendDuration] = useState(1);

  function toggleSuspended(id, name) {
    const current = statuses[id]?.suspended;
    if (current) {
      setSuspendPrompt({id, name, mode:"lift"});
      setSuspendReason("");
      return;
      // legacy: lift
      setStatuses(p => ({ ...p, [id]: { ...p[id], suspended:false, suspendReason:"" }}));
      saveInOutEvent({ residentId:id, residentName:name, facility:residents.find(r=>r.id===id)?.facility||"", date:TODAY, eventType:"restriction_lifted", time:timeNow() });
    } else {
      // Suspending — require reason
      setSuspendPrompt({id, name});
      setSuspendReason("");
    }
  }

  async function confirmSuspend() {
    if (!suspendReason.trim()) return;
    const {id, name, mode} = suspendPrompt;
    const facility = residents.find(r=>r.id===id)?.facility||"";
    if (mode === "lift") {
      setStatuses(p => ({ ...p, [id]: { ...p[id], suspended:false, suspendReason:"", suspendDuration:null, suspendPending:false }}));
      await saveInOutEvent({ residentId:id, residentName:name, facility, date:TODAY, time:timeNow(), eventType:"suspend_lift", suspended:false, suspendReason:"", liftReason:suspendReason.trim(), raName });
      saveNotification({ type:"suspension_lifted", title:`Suspension lifted — ${name}`, body:`Reason: ${suspendReason.trim()} · By: ${raName}`, residentId:id, residentName:name, facility, read:false, createdAt:TODAY });
      setSuspendPrompt(null); setSuspendReason(""); setSuspendDuration(1);
      return;
    }
    const pendingApproval = suspendDuration > 1;
    if (pendingApproval) {
      await saveInOutEvent({ residentId:id, residentName:name, facility, date:TODAY, time:timeNow(), eventType:"suspend_request", suspended:false, suspendReason:suspendReason.trim(), suspendDuration, suspendPending:true, raName });
      saveNotification({ type:"suspension_request", title:`Suspension request — ${name} (${suspendDuration} days)`, body:`Requested by: ${raName} · Reason: ${suspendReason.trim()}`, residentId:id, residentName:name, facility, suspendDuration, read:false, actioned:false, createdAt:TODAY });
      alert(`Suspension request submitted for admin approval (${suspendDuration} days).`);
    } else {
      setStatuses(p => ({ ...p, [id]: { ...p[id], suspended:true, suspendReason:suspendReason.trim(), suspendDuration }}));
      await saveInOutEvent({ residentId:id, residentName:name, facility, date:TODAY, eventType:"restriction_applied", reason:suspendReason.trim(), time:timeNow() });
    }
    setSuspendPrompt(null);
    setSuspendReason("");
    setSuspendDuration(1);
  }

  // Popup state
  const [popupReason, setPopupReason] = useState(OUT_REASONS[0]);
  const [popupEst, setPopupEst] = useState("");
  const [popupNote, setPopupNote] = useState("");

  return (
    <div>
      {/* Summary */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
        <div style={{background:DS.success.bg,border:`1px solid ${DS.success.border}`,borderRadius:12,padding:16,display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:DS.success.text}}/>
          <div><div style={{fontSize:24,fontWeight:700,color:DS.success.text}}>{inCount}</div><div style={{fontSize:11,color:DS.success.text,textTransform:"uppercase"}}>In Building</div></div>
        </div>
        <div style={{background:DS.warning.bg,border:`1px solid ${DS.warning.border}`,borderRadius:12,padding:"12px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <div style={{width:10,height:10,borderRadius:"50%",background:DS.warning.text}}/>
            <div style={{fontSize:24,fontWeight:700,color:DS.warning.text,lineHeight:1}}>{outCount}</div>
            <div style={{fontSize:11,color:DS.warning.text,textTransform:"uppercase"}}>Out</div>
          </div>
          {outCount > 0 && (()=>{
            const tagCounts = {};
            Object.values(statuses).forEach(s=>{
              if(s?.status==="out"&&s.reason){
                tagCounts[s.reason]=(tagCounts[s.reason]||0)+1;
              }
            });
            return Object.entries(tagCounts).length > 0 ? (
              <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                {Object.entries(tagCounts).map(([tag,count])=>(
                  <span key={tag} style={{fontSize:9,fontWeight:600,padding:"1px 6px",borderRadius:6,background:"rgba(122,79,0,0.12)",color:DS.warning.text}}>
                    {tag} ×{count}
                  </span>
                ))}
              </div>
            ) : null;
          })()}
        </div>
      </div>

      {/* Overdue alert */}
      {filtered.some(r => isOverdue(statuses[r.id])) && (
        <div style={{background:"#FDECEA",border:`1px solid ${DS.error.border}`,borderRadius:12,padding:"10px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:18}}>🚨</span>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:DS.error.text}}>Overdue Return</div>
            <div style={{fontSize:11,color:DS.error.text}}>
              {filtered.filter(r=>isOverdue(statuses[r.id])).map(r=>r.name).join(", ")} — 10+ min past expected return
            </div>
          </div>
        </div>
      )}

      {/* RA3 — Search box */}
      <div style={{marginBottom:10}}>
        <input style={{...S.input,border:`1px solid ${DS.border}`,background:"white"}} placeholder="🔍  Search resident..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>

      {/* Facility filter removed — residents already scoped by facility via raResidents prop */}

      {/* Resident rows */}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {filtered.map(r => {
          const s = statuses[r.id] || { status:"in", reason:null, timeOut:null, estReturn:"", timeIn:null, note:"", suspended:false };
          const overdue = isOverdue(s);
          const isOpen = popup === r.id;
          const isOut = s.status === "out";

          // Light theme colors
          const bgColor = overdue ? "#FBBFBF" : s.suspended ? DS.amber.bg : isOut ? "#FDE68A" : "white";
          const borderColor = overdue ? DS.error.border : s.suspended ? DS.amber.border : isOut ? DS.warning.border : DS.border;
          const textColor = overdue ? DS.error.text : s.suspended ? DS.amber.text : isOut ? DS.warning.text : DS.success.text;

          return (
            <div key={r.id} style={{position:"relative"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:10,border:`1px solid ${borderColor}`,background:bgColor,transition:"all 0.15s"}}>

                {/* Avatar */}
                {r.photoURL
                  ? <img src={r.photoURL} alt={r.name} style={{width:36,height:36,borderRadius:8,objectFit:"cover",border:`1px solid ${DS.blueBorder}`,flexShrink:0}}/>
                  : <div style={{width:36,height:36,borderRadius:8,background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,display:"flex",alignItems:"center",justifyContent:"center",color:DS.blue,fontWeight:700,fontSize:12,flexShrink:0,fontFamily:"'DM Sans',sans-serif"}}>{initials(r.name)}</div>
                }

                {/* Info — RA4: removed Violence/MH/location tags, RA5: added DOC#, Employment, DP Level */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(r.name)}</span>
                    {r.room&&<span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Rm {r.room}</span>}
                    {s.suspended&&<span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:4,background:DS.amber.bg,border:`1px solid ${DS.amber.border}`,color:DS.amber.text}}>SUSPENDED</span>}
                    {overdue&&<span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:4,background:DS.error.bg,border:`1px solid ${DS.error.border}`,color:DS.error.text}}>OVERDUE</span>}
                    {(()=>{const dn=r.dietaryNeeds;if(!dn||(!dn.hasRestrictions&&!dn.hasAllergies))return null;const days=r.entryDate?Math.floor((new Date()-new Date(r.entryDate))/(1000*60*60*24)):0;if(days>7)return null;return <span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:4,background:DS.amber.bg,border:`1px solid ${DS.amber.border}`,color:DS.amber.text}}>DIETARY</span>;})()}
                  </div>
                  {/* RA5 — DOC#, Employment, DP Level */}
                  <div style={{display:"flex",gap:6,marginTop:4,flexWrap:"wrap",alignItems:"center"}}>
                    {r.doc&&<span style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>DOC# {r.doc}</span>}
                    {r.dpLevel&&<span style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>· {r.dpLevel}</span>}
                    {r.employed&&<span style={{fontSize:9,fontWeight:600,padding:"1px 7px",borderRadius:10,background:DS.success.bg,border:`1px solid ${DS.success.border}`,color:DS.success.text,fontFamily:"'Inter',sans-serif"}}>EMPLOYED</span>}
                  </div>
                  <div style={{fontSize:11,color:DS.textSec,marginTop:2,fontFamily:"'Inter',sans-serif"}}>
                    {isOut?(
                      <>{s.estReturnDate&&s.estReturnDate<TODAY?`Out since yesterday · ${s.timeOut||""}`:`Out: ${s.timeOut||""}`} · {s.reason}{s.estReturn&&<> · Est. return: {formatTime12(s.estReturn)}</>}{s.note&&<> · "{s.note}"</>}</>
                    ):(
                      <>{s.timeIn?`Returned: ${s.timeIn}`:`In since: ${s.timeOut||"start of shift"}`}</>
                    )}
                  </div>
                </div>

                {/* Status + actions */}
                <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                  <span style={{fontSize:11,fontWeight:500,padding:"2px 8px",borderRadius:10,border:`1px solid ${borderColor}`,color:textColor,background:"white",fontFamily:"'Inter',sans-serif"}}>
                    {isOut?`Out — ${s.reason||""}`:s.status==="in"?"In":"—"}
                  </span>
                  <button onClick={()=>toggleSuspended(r.id, r.name)} title={s.suspended?"Remove suspension":"Suspend leisure/movement"}
                    style={{width:28,height:28,borderRadius:6,border:`1px solid ${s.suspended?DS.amber.border:DS.border}`,background:s.suspended?DS.amber.bg:"white",cursor:"pointer",fontSize:13,color:s.suspended?DS.amber.text:DS.textSec}}>
                    🚫
                  </button>
                  <button onClick={()=>{setPopup(isOpen?null:r.id);if(!isOpen){setPopupReason(OUT_REASONS[0]);setPopupEst("");setPopupNote("");}}}
                    style={{background:"white",border:`1px solid ${DS.border}`,borderRadius:6,width:28,height:28,cursor:"pointer",color:DS.textSec,fontSize:13}}>
                    ▾
                  </button>
                </div>
              </div>

              {/* Suspension reason banner */}
              {s.suspended && s.suspendReason && (
                <div style={{margin:"4px 0 2px",padding:"4px 10px",background:DS.amber.bg,border:`1px solid ${DS.amber.border}`,borderRadius:6,fontSize:11,color:DS.amber.text}}>
                  🚫 Movement suspended: {s.suspendReason}
                </div>
              )}

              {/* Popup */}
              {isOpen && (
                <>
                  <div onClick={()=>setPopup(null)} style={{position:"fixed",inset:0,zIndex:9000}}/>
                  <div style={{position:"fixed",zIndex:9001,top:"50%",left:"50%",transform:"translate(-50%,-50%)",background:"white",border:`1px solid ${DS.border}`,borderRadius:12,padding:16,width:300,boxShadow:"0 10px 40px rgba(0,0,0,0.15)"}}>
                    {s.status === "out" ? (
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,marginBottom:12,fontFamily:"'DM Sans',sans-serif"}}>Sign {displayName(r.name)} back in?</div>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={()=>signIn(r.id)} style={{...S.btn,flex:1,background:DS.success.text}}>✓ Sign In</button>
                          <button onClick={()=>setPopup(null)} style={{...S.btnGhost,flex:1}}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,marginBottom:12,fontFamily:"'DM Sans',sans-serif"}}>Sign {displayName(r.name)} out</div>
                        {s.suspended && (
                          <div style={{background:DS.warning.bg,border:`1px solid ${DS.warning.border}`,borderRadius:8,padding:"8px 10px",marginBottom:12,fontSize:11,color:DS.warning.text,fontFamily:"'Inter',sans-serif"}}>
                            ⚠ Suspended — only Work and P&P allowed
                          </div>
                        )}
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Reason</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
                        {OUT_REASONS.map(reason => {
                          const blocked = s.suspended && !SUSPENDED_ALLOWED.includes(reason);
                          return (
                            <button key={reason} onClick={()=>!blocked&&setPopupReason(reason)}
                              style={{padding:"5px 10px",fontSize:11,fontWeight:600,borderRadius:8,cursor:blocked?"not-allowed":"pointer",opacity:blocked?0.35:1,background:popupReason===reason?DS.blue:"transparent",border:`1px solid ${popupReason===reason?DS.blue:DS.border}`,color:popupReason===reason?"white":DS.textSec,fontFamily:"'Inter',sans-serif"}}>
                              {reason}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Est. Return Time *</div>
                      <div style={{marginBottom:10}}><TimeInput value={popupEst} onChange={setPopupEst}/></div>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Note (optional)</div>
                      <input style={{...S.input,border:`1px solid ${DS.border}`,marginBottom:12}} placeholder="e.g. Dr. Johnson appt" value={popupNote} onChange={e=>setPopupNote(e.target.value)}/>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>signOut(r.id,popupReason,popupEst,popupNote)}
                          disabled={!popupEst||!popupEst.includes(":")}
                          style={{...S.btn,flex:1,background:DS.warning.text,opacity:(!popupEst||!popupEst.includes(":"))?0.4:1}}>Sign Out</button>
                        <button onClick={()=>setPopup(null)} style={{...S.btnGhost,flex:1}}>Cancel</button>
                      </div>
                    </div>
                  )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Suspend reason modal */}
      {suspendPrompt && (
        <div style={{position:"fixed",inset:0,zIndex:9900,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(26,43,69,0.5)",padding:24}}>
          <div style={{background:"white",borderRadius:12,width:"100%",maxWidth:360,padding:20,border:`1px solid ${DS.border}`}}>
            <div style={{fontSize:14,fontWeight:600,color:DS.textPrimary,marginBottom:4,fontFamily:"'DM Sans',sans-serif"}}>{suspendPrompt.mode==="lift"?"Lift Suspension":"Suspend Movement"}</div>
            <div style={{fontSize:12,color:DS.textSec,marginBottom:14}}>{suspendPrompt.name} — reason required.{suspendPrompt.mode!=="lift" && suspendDuration>1 ? " Suspensions over 1 day require admin approval." : ""} Displays on In/Out board until lifted.</div>
            {suspendPrompt.mode !== "lift" && (
              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Duration</div>
                <select value={suspendDuration} onChange={e=>setSuspendDuration(parseInt(e.target.value))}
                  style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none"}}>
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>
            )}
            <textarea rows={3} style={{...S.input,border:`1px solid ${DS.amber.border}`,resize:"none",marginBottom:12,width:"100%",boxSizing:"border-box"}}
              placeholder="e.g. Curfew violation — pending admin review"
              value={suspendReason} onChange={e=>setSuspendReason(e.target.value)}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>{setSuspendPrompt(null);setSuspendReason("");}} style={{...S.btnGhost,fontSize:12}}>Cancel</button>
              <button onClick={confirmSuspend} disabled={!suspendReason.trim()}
                style={{...S.btn,fontSize:12,background:DS.amber.text,border:"none",opacity:!suspendReason.trim()?0.4:1}}>
                {suspendPrompt?.mode==="lift" ? "Lift Suspension" : (suspendDuration>1 ? "Submit for Approval" : "Confirm Suspension")}
              </button>
            </div>
          </div>
        </div>
      )}
      {hoursPrompt&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"white",borderRadius:10,padding:24,width:300,boxShadow:"0 4px 24px rgba(0,0,0,0.15)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:14,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif",marginBottom:6}}>Hours worked</div>
            <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif",marginBottom:14}}>{displayName(hoursPrompt.resident.name)} is signing in from Work{hoursPrompt.timeOut?` (out since ${hoursPrompt.timeOut})`:""}.</div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Hours worked</div>
            <input type="number" step="0.5" min="0" max="24" style={{...S.input,border:`1px solid ${DS.border}`,marginBottom:14}} placeholder="e.g. 8" value={hoursWorked} onChange={e=>setHoursWorked(e.target.value)}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>{setHoursPrompt(null);setHoursWorked("");}} style={{...S.btnGhost,fontSize:12}}>Skip</button>
              <button onClick={handleHoursSubmit} style={{...S.btn,fontSize:12}}>Save hours</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// ── RA: MAR ───────────────────────────────────────────────────────────────────
const DOSE_SLOTS = ["Morning", "Lunch", "Evening"];

const FREQUENCY_SLOTS = {
  "Once Daily":      null, // chosen by staff
  "Twice Daily":     ["Morning","Evening"],
  "Three Times Daily":["Morning","Lunch","Evening"],
  "As Needed (PRN)": ["PRN"],
};

const MAR_STYLES = {
  taken:   { label:"Taken",   bg:"#E6F4EC", border:"#A3D4B4", color:"#1A6B35" },
  missed:  { label:"Missed",  bg:"#FFF3CD", border:"#F5C200", color:"#7A4F00" },
  refused: { label:"Refused", bg:"#FEF0E6", border:"#F5B885", color:"#7A3300" },
  absent:  { label:"Absent",  bg:"#FDECEA", border:"#F5A8A8", color:"#8B1A1A" },
};

// Normalize medication — handle both legacy string format and new object format
function normalizeMed(med) {
  if (typeof med === "string") {
    return { name:med, dose:"", frequency:"Once Daily", slots:["Morning"], active:true, addedDate:TODAY };
  }
  return med;
}

// Get active slots for a medication
function getMedSlots(med) {
  const m = normalizeMed(med);
  if (m.frequency === "As Needed (PRN)") return ["PRN"];
  if (m.frequency === "Twice Daily") return ["Morning","Evening"];
  if (m.frequency === "Three Times Daily") return ["Morning","Lunch","Evening"];
  // Once Daily — use chosen slot
  return m.slots?.length ? m.slots : ["Morning"];
}

// Psychotropic and narcotic medication detection
const PSYCHOTROPIC_KEYWORDS = ["sertraline","fluoxetine","escitalopram","citalopram","paroxetine","venlafaxine","duloxetine","bupropion","mirtazapine","trazodone","quetiapine","olanzapine","risperidone","aripiprazole","haloperidol","lithium","valproate","lamotrigine","clonazepam","lorazepam","diazepam","alprazolam","buspirone","prazosin","hydroxyzine"];
const NARCOTIC_KEYWORDS = ["buprenorphine","methadone","naltrexone","suboxone","subutex","oxycodone","hydrocodone","morphine","fentanyl","tramadol","codeine","hydromorphone","oxymorphone","gabapentin","pregabalin","naloxone"];

function getMedType(medName) {
  const lower = (medName||"").toLowerCase();
  if (NARCOTIC_KEYWORDS.some(k => lower.includes(k))) return "narcotic";
  if (PSYCHOTROPIC_KEYWORDS.some(k => lower.includes(k))) return "psychotropic";
  return "standard";
}

function MedTypeBadge({ medName }) {
  const type = getMedType(medName);
  if (type === "narcotic")     return <span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:4,background:DS.error.bg,border:`1px solid ${DS.error.border}`,color:DS.error.text,marginLeft:6}}>NARCOTIC</span>;
  if (type === "psychotropic") return <span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:4,background:DS.purple.bg,border:`1px solid ${DS.purple.border}`,color:DS.purple.text,marginLeft:6}}>PSYCHOTROPIC</span>;
  return null;
}

function MAR({ residents, raName, perms }) {
  const resWithMeds = residents.filter(r => r.medications && r.medications.length > 0 &&
    r.medications.some(m => normalizeMed(m).active !== false));
  const [logs, setLogs] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [facFilter, setFacFilter] = useState("all"); // residents already scoped via raResidents prop
  const [search, setSearch] = useState("");

  // Smart call list — get current call time
  const nowNY = NOW_NY();
  const currentHour = nowNY.getHours();
  const getCurrentCall = () => {
    const calls = getNextMedCall ? null : null;
    // Find which call window we're in (within 2hrs of a scheduled call)
    const CALL_HOURS = [8, 12, 18, 21]; // 8am, 12pm, 6pm, 9pm
    for(const h of CALL_HOURS) {
      if(Math.abs(currentHour - h) <= 1) return h;
    }
    return null;
  };
  const activeCallHour = getCurrentCall();

  const currentSlot = getCurrentCallSlot();

  const filtered = resWithMeds.filter(r => {
    if(facFilter !== "all" && r.facility !== facFilter) return false;
    if(search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
    // Smart call list — only show residents with meds due at current call slot
    // and hide once logged
    if(currentSlot) {
      const meds = (r.medications||[]).filter(m => normalizeMed(m).active !== false);
      const hasMedAtThisCall = meds.some(m => getMedSlots(m).includes(currentSlot));
      if(!hasMedAtThisCall) return false;
      // Check if already logged for this call today
      const alreadyLogged = meds.every(m => {
        const nm = normalizeMed(m);
        if(!getMedSlots(m).includes(currentSlot)) return true; // not for this call, skip
        const key = `${r.id}-${nm.name}-${currentSlot}`;
        return logs[key];
      });
      if(alreadyLogged) return false;
    }
    return true;
  });

  useEffect(() => {
    const unsub = subscribeToMedicationLogs(TODAY, null, firestoreLogs => {
      const mapped = {};
      firestoreLogs.forEach(l => { mapped[l.logKey || `${l.residentId}-${l.medication}-${l.slot}`] = { status:l.status, time:l.time }; });
      setLogs(mapped);
    });
    return unsub;
  }, []);

  const allKeys = filtered.flatMap(r =>
    r.medications.filter(m=>normalizeMed(m).active!==false).flatMap(rawMed => {
      const med = normalizeMed(rawMed);
      return getMedSlots(med).map(slot => `${r.id}-${med.name}-${slot}`);
    })
  );
  const takenCount   = allKeys.filter(k => logs[k]?.status === "taken").length;
  const missedCount  = allKeys.filter(k => logs[k]?.status === "missed").length;
  const refusedCount = allKeys.filter(k => logs[k]?.status === "refused").length;
  const absentCount  = allKeys.filter(k => logs[k]?.status === "absent").length;
  const loggedCount  = allKeys.filter(k => logs[k]?.status).length;
  const totalSlots   = allKeys.length;
  const pct = totalSlots > 0 ? Math.round(loggedCount / totalSlots * 100) : 0;

  const logStatus = async (residentId, medName, slot, status) => {
    const logKey = `${residentId}-${medName}-${slot}`;
    setLogs(p => ({ ...p, [logKey]: { status, time:timeNow(), recorded:true }}));
    setActiveKey(null);
    const resident = residents.find(r => r.id === residentId);
    if (status === "missed" && resident) {
      const medObj = (resident.medications||[]).find(m => (typeof m === "string" ? m : m?.name) === medName);
      const medData = typeof medObj === "string" ? {} : (medObj||{});
      const isPsych = medData.isPsychotropic || medData.psychotropic || /psych|antidep|antipsych|mood|stabiliz|ssri|snri|benzo|lithium|risperdal|seroquel|abilify|prozac|zoloft|lexapro|wellbutrin/i.test((medData.category||"") + " " + medName);
      if (isPsych) {
        saveNotification({
          type:"psychotropic_missed",
          title:`Psychotropic missed — ${resident.name}`,
          body:`${medName} · ${slot} · ${TODAY} · RA: ${raName||"Unknown"}`,
          residentId, residentName:resident.name,
          facility:resident.facility||"",
          medicationName:medName, slot,
          read:false, createdAt:TODAY
        });
      }
    }
    await saveMedicationLog({
      date: TODAY, residentId,
      residentName: resident?.name || "",
      facility: resident?.facility || "",
      medication: medName, slot, status,
      time: timeNow(), logKey,
    });
    auditAction("medication_logged", { residentId, residentName:resident?.name, medication:medName, slot, status });
  };

  return (
    <div>
      <div style={{...S.card,marginBottom:20,background:"white"}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
          <span style={{fontSize:14,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Today's Medication Log — {TODAY}</span>
          <span style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{loggedCount}/{totalSlots} doses logged</span>
        </div>
        <div style={{height:8,background:DS.border,borderRadius:4,overflow:"hidden"}}>
          <div style={{width:`${pct}%`,height:"100%",background:pct===100?"#1A6B35":"#1B5EBE",borderRadius:4}}/>
        </div>
        <div style={{display:"flex",gap:16,marginTop:10,flexWrap:"wrap"}}>
          <span style={{fontSize:11,color:"#1A6B35"}}>Taken: <strong>{takenCount}</strong></span>
          <span style={{fontSize:11,color:"#7A4F00"}}>Missed: <strong>{missedCount}</strong></span>
          <span style={{fontSize:11,color:"#fdba74"}}>Refused: <strong>{refusedCount}</strong></span>
          <span style={{fontSize:11,color:DS.error.text}}>Absent: <strong>{absentCount}</strong></span>
          <span style={{fontSize:11,color:DS.textSec}}>Pending: <strong>{totalSlots-loggedCount}</strong></span>
        </div>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:DS.textSec}}>Highlighting:</span>
        <span style={{fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:4,background:DS.error.bg,border:`1px solid ${DS.error.border}`,color:DS.error.text}}>NARCOTIC</span>
        <span style={{fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:4,background:"#F0EEFB",border:"1px solid #A89FE0",color:"#4A3AAA"}}>PSYCHOTROPIC</span>
      </div>
      {currentSlot ? (
        <div style={{background:DS.success.bg,border:`1px solid ${DS.success.border}`,borderRadius:6,padding:"8px 12px",marginBottom:10,fontSize:12,color:DS.success.text,fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:8}}>
          <i className="bi bi-capsule-pill"></i>
          <span><strong>{currentSlot} Med Call active</strong> — showing residents with {currentSlot.toLowerCase()} medications only. Residents are removed once recorded.</span>
        </div>
      ) : (
        <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:6,padding:"8px 12px",marginBottom:10,fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:8}}>
          <i className="bi bi-clock"></i>
          <span>No active med call window — showing all residents with medications.</span>
        </div>
      )}
      <input style={{...S.input,border:`1px solid ${DS.border}`,marginBottom:10}} placeholder="🔍  Search resident..." value={search} onChange={e=>setSearch(e.target.value)}/>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {[...(perms?.canSeeAllFacilities?["all"]:[]),(perms?.assignedFacility||FACILITIES[0]),...(perms?.canSeeAllFacilities?FACILITIES.filter(f=>f!==perms?.assignedFacility):[])].filter((v,i,a)=>a.indexOf(v)===i).map(f=><button key={f} onClick={()=>setFacFilter(f)} style={{padding:"6px 14px",fontSize:12,borderRadius:8,cursor:"pointer",background:facFilter===f?DS.blue:"white",border:`1px solid ${facFilter===f?DS.blue:DS.border}`,color:facFilter===f?"white":DS.textSec,fontFamily:"'Inter',sans-serif"}}>{f==="all"?"All Houses":shortFacility(f)}</button>)}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {filtered.length===0&&<div style={{textAlign:"center",padding:"40px 0",color:DS.textSec}}>No residents with active medications.</div>}
        {filtered.map(r=>{
          const activeMeds=r.medications.filter(m=>normalizeMed(m).active!==false);
          return (
            <div key={r.id} style={{border:"1px solid #334155",borderRadius:12,overflow:"hidden"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:DS.border,borderBottom:"1px solid #334155"}}>
                {r.photoURL
                  ? <img src={r.photoURL} alt={r.name} style={{width:32,height:32,borderRadius:8,objectFit:"cover",border:"1px solid #B5D4F4",flexShrink:0}}/>
                  : <div style={{width:32,height:32,borderRadius:8,background:"#E8F0FB",border:"1px solid #B5D4F4",display:"flex",alignItems:"center",justifyContent:"center",color:"#1B5EBE",fontWeight:700,fontSize:11}}>{initials(r.name)}</div>
                }
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{r.name}</div>
                  <div style={{fontSize:11,color:DS.textSec}}>{r.facility} · {activeMeds.length} active med{activeMeds.length!==1?"s":""}</div>
                </div>
              </div>
              {activeMeds.map(rawMed=>{
                const med=normalizeMed(rawMed);
                const slots=getMedSlots(med);
                const medType=getMedType(med.name);
                const medBg=medType==="narcotic"?DS.error.bg:medType==="psychotropic"?DS.purple.bg:"white";
                return (
                  <div key={med.name} style={{borderBottom:"1px solid rgba(51,65,85,0.4)",background:medBg}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px 4px",flexWrap:"wrap"}}>
                      <span style={{fontSize:13,color:DS.textPrimary,fontWeight:500}}>{med.name}</span>
                      {med.dose&&<span style={{fontSize:11,color:DS.textSec}}>{med.dose}</span>}
                      <MedTypeBadge medName={med.name}/>
                      <Pill label={med.frequency||"Once Daily"} bg={DS.blueTint} border={DS.blueBorder} color={DS.blue}/>
                    </div>
                    <div style={{display:"flex",gap:10,padding:"4px 14px 12px",flexWrap:"wrap"}}>
                      {slots.map(slot=>{
                        const logKey=`${r.id}-${med.name}-${slot}`;
                        const log=logs[logKey]||{status:null,time:null};
                        const st=log.status?MAR_STYLES[log.status]:null;
                        const isOpen=activeKey===logKey;
                        return (
                          <div key={slot} style={{position:"relative"}}>
                            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                              <span style={{fontSize:9,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.06em"}}>{slot}</span>
                              <button onClick={(e)=>{e.stopPropagation();setActiveKey(isOpen?null:logKey);}}
                                style={{padding:"5px 16px",fontSize:11,fontWeight:600,borderRadius:8,cursor:"pointer",minWidth:84,
                                  background:st?st.bg:DS.border,border:`1px solid ${st?st.border:"#475569"}`,color:st?st.color:DS.textSec}}>
                                {st?st.label:"Log"}
                              </button>
                              {log.time&&<span style={{fontSize:9,color:DS.textSec}}>{log.time}</span>}
                            </div>
                            {isOpen&&(
                              <>
                                <div onClick={()=>setActiveKey(null)} style={{position:"fixed",inset:0,zIndex:9000}}/>
                                <div style={{position:"fixed",zIndex:9001,background:"white",border:`1px solid ${DS.border}`,borderRadius:12,padding:12,width:220,boxShadow:"0 10px 40px rgba(0,0,0,0.7)",top:"50%",left:"50%",transform:"translate(-50%,-50%)"}}>
                                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{slot} Dose — {med.name}</div>
                                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                                    {Object.entries(MAR_STYLES).map(([k,s])=>(
                                      <button key={k} onClick={()=>logStatus(r.id,med.name,slot,k)}
                                        style={{padding:"7px 12px",fontSize:12,fontWeight:600,borderRadius:8,cursor:"pointer",textAlign:"left",
                                          background:log.status===k?s.bg:"transparent",border:`1px solid ${log.status===k?s.border:DS.border}`,color:log.status===k?s.color:DS.textSec}}>
                                        {s.label}
                                        <span style={{fontSize:10,color:DS.textSec,fontWeight:400,marginLeft:6}}>
                                          {k==="taken"?"observed & taken":k==="missed"?"present, skipped":k==="refused"?"refused dose":"not in building"}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                  <button onClick={()=>setActiveKey(null)} style={{marginTop:10,fontSize:11,color:DS.textSec,background:"transparent",border:"none",cursor:"pointer",width:"100%",textAlign:"center"}}>Cancel</button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── RA: UA Tracker ────────────────────────────────────────────────────────────
// UA Scheduling Rules:
// - Week = Sunday through Saturday
// - Each resident tested exactly twice per Sun-Sat week
// - Tests must be 3-5 days apart
// - Max 7 residents per day per house

const UA_MAX_PER_DAY = 7;
const UA_MIN_GAP = 3; // days
const UA_MAX_GAP = 5; // days

function getWeekBounds(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  const day = d.getDay(); // 0=Sun
  const toYMD = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth()+1).padStart(2,"0");
    const dd = String(dt.getDate()).padStart(2,"0");
    return `${y}-${m}-${dd}`;
  };
  const sun = new Date(d); sun.setDate(d.getDate() - day);
  const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
  return {
    sunday: toYMD(sun),
    saturday: toYMD(sat),
    days: Array.from({length:7}, (_,i) => {
      const dd = new Date(sun); dd.setDate(sun.getDate()+i);
      return toYMD(dd);
    })
  };
}

function daysBetween(a, b) {
  return Math.abs((new Date(b) - new Date(a)) / 86400000);
}

function generateWeeklyUASchedule(weekDays, residents, existingResults) {
  // existingResults: { "YYYY-MM-DD-residentId": {result, ...} }
  const MAX = UA_MAX_PER_DAY;
  const schedule = {}; // date -> [residentIds]
  weekDays.forEach(d => { schedule[d] = []; });

  // For each resident, figure out how many tests they already have this week
  // and plan remaining tests
  const residentTests = {}; // residentId -> [dates already tested]
  residents.forEach(r => {
    residentTests[r.id] = weekDays.filter(d =>
      existingResults[`${d}-${r.id}`] !== undefined
    );
  });

  // Shuffle residents deterministically by week start
  const weekSeed = parseInt(weekDays[0].replace(/-/g,"")) || 20260101;
  const rng = seedRandom(weekSeed);
  const shuffled = [...residents].sort(() => rng() - 0.5);

  // Try to schedule 2 tests per resident across the week
  shuffled.forEach(r => {
    const done = residentTests[r.id];
    const needed = 2 - done.length;
    if (needed <= 0) return;

    let scheduled = [...done];
    for (let attempt = 0; attempt < needed; attempt++) {
      for (const day of weekDays) {
        if (scheduled.includes(day)) continue;
        if (schedule[day].length >= MAX) continue;
        // Check spacing constraints
        const tooClose = scheduled.some(prev => daysBetween(prev, day) < UA_MIN_GAP);
        const tooFar = scheduled.length > 0 &&
          scheduled.every(prev => daysBetween(prev, day) > UA_MAX_GAP);
        if (tooClose) continue;
        if (tooFar && scheduled.length > 0) continue;
        schedule[day].push(r.id);
        scheduled.push(day);
        break;
      }
    }
  });

  return schedule;
}

// ── BAC Tracker ───────────────────────────────────────────────────────────────
function BACTracker({ residents }) {
  const [logs, setLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedResident, setSelectedResident] = useState("");
  const [result, setResult] = useState("pass");
  const [bac, setBac] = useState("0.00");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    const unsub = subscribeToUAResults(null, null, null, allResults => {
      const bacLogs = allResults
        .filter(r => r.testType === "bac")
        .sort((a,b) => b.date.localeCompare(a.date));
      setLogs(bacLogs);
    });
    return unsub;
  }, []);

  const handleSubmit = async () => {
    if (!selectedResident) return;
    setSaving(true);
    const resident = residents.find(r => r.id === selectedResident);
    let photoUrl = null;
    if (photoFile) {
      setPhotoUploading(true);
      try { photoUrl = await uploadDocument(photoFile, `ua-results/${selectedResident}/${Date.now()}`); } catch(e) {}
      setPhotoUploading(false);
    }
    await saveUAResult({
      date: TODAY,
      completedDate: TODAY,
      weekStart: TODAY,
      weekEnd: TODAY,
      residentId: selectedResident,
      residentName: resident?.name || "",
      facility: resident?.facility || "",
      result,
      bac: result === "fail" ? bac : "0.00",
      notes,
      testType: "bac",
      time: timeNow(),
      ...(photoUrl ? { photoUrl } : {}),
    });
    auditAction("ua_bac_result_entered", { residentId:selectedResident, residentName:resident?.name, result });
    setShowForm(false);
    setSelectedResident("");
    setResult("pass");
    setBac("0.00");
    setNotes("");
    setPhotoFile(null);
    setPhotoPreview(null);
    setSaving(false);
  };

  const recentLogs = logs.slice(0, 20);

  return (
    <div style={{marginTop:28}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <div>
          <div style={{fontSize:15,fontWeight:700,color:DS.textPrimary}}>BAC / Breathalyzer Testing</div>
          <div style={{fontSize:11,color:DS.textSec,marginTop:2}}>Ad-hoc alcohol testing — log by resident</div>
        </div>
        <button onClick={()=>setShowForm(s=>!s)} style={{...S.btn,display:"flex",alignItems:"center",gap:6}}>
          {showForm?"Cancel":"+ Log BAC Test"}
        </button>
      </div>

      {/* Log form */}
      {showForm && (
        <div style={{...S.card,border:"1px solid rgba(79,70,229,0.4)",marginBottom:16}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div style={{gridColumn:"span 2"}}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Resident *</div>
              <select style={{...S.input}} value={selectedResident} onChange={e=>setSelectedResident(e.target.value)}>
                <option value="">Select resident...</option>
                {residents.sort((a,b)=>a.name.localeCompare(b.name)).map(r=>(
                  <option key={r.id} value={r.id}>{r.name} — {r.facility}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Result</div>
              <div style={{display:"flex",gap:8}}>
                {["pass","fail"].map(v=>(
                  <button key={v} onClick={()=>setResult(v)}
                    style={{flex:1,padding:"8px",fontSize:12,fontWeight:600,borderRadius:8,cursor:"pointer",textTransform:"capitalize",
                      background:result===v?(v==="pass"?DS.success.bg:DS.error.bg):"white",
                      border:`1px solid ${result===v?(v==="pass"?DS.success.border:DS.error.border):DS.border}`,
                      color:result===v?(v==="pass"?DS.success.text:DS.error.text):DS.textSec}}>
                    {v==="pass"?"✓ Pass":"✗ Fail"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>BAC Reading</div>
              <input style={{...S.input}} placeholder="e.g. 0.00" value={bac} onChange={e=>setBac(e.target.value)}/>
              <div style={{fontSize:10,color:DS.textSec,marginTop:3}}>Legal limit: 0.08</div>
            </div>
            <div style={{gridColumn:"span 2"}}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Notes (optional)</div>
              <input style={{...S.input}} placeholder="Circumstances, location, etc." value={notes} onChange={e=>setNotes(e.target.value)}/>
            </div>
            <div style={{gridColumn:"span 2"}}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Photo of Test Result (optional)</div>
              {photoPreview ? (
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <img src={photoPreview} alt="Test result" style={{width:72,height:72,objectFit:"cover",borderRadius:8,border:`1px solid ${DS.border}`}}/>
                  <button onClick={()=>{setPhotoFile(null);setPhotoPreview(null);}} style={{...S.btnGhost,fontSize:11}}>Remove</button>
                </div>
              ) : (
                <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:8,border:`1px dashed ${DS.border}`,cursor:"pointer",fontSize:12,color:DS.textSec,background:DS.pageBg}}>
                  📷 Take / Upload Photo
                  <input ref={photoInputRef} type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;setPhotoFile(f);const r=new FileReader();r.onload=ev=>setPhotoPreview(ev.target.result);r.readAsDataURL(f);}}/>
                </label>
              )}
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
            <button onClick={()=>setShowForm(false)} style={{...S.btnGhost}}>Cancel</button>
            <button onClick={handleSubmit} disabled={!selectedResident||saving||photoUploading}
              style={{...S.btn,opacity:(!selectedResident||saving||photoUploading)?0.4:1,background:result==="fail"?DS.error.text:DS.success.text}}>
              {(saving||photoUploading)?"Saving...":"Save BAC Test"}
            </button>
          </div>
        </div>
      )}

      {/* Recent logs */}
      {recentLogs.length === 0 ? (
        <div style={{...S.card,textAlign:"center",padding:"24px 0",color:DS.textSec,fontSize:13}}>No BAC tests logged yet.</div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {recentLogs.map((log,i)=>(
            <div key={i} style={{...S.card,display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:32,height:32,borderRadius:8,background:log.result==="pass"?DS.success.bg:DS.error.bg,border:`1px solid ${log.result==="pass"?DS.success.border:DS.error.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:log.result==="pass"?DS.success.text:DS.error.text,flexShrink:0}}>
                {log.result==="pass"?"✓":"✗"}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{log.residentName}</div>
                <div style={{fontSize:11,color:DS.textSec}}>{log.date} at {log.time} · BAC: <strong style={{color:parseFloat(log.bac)>0?DS.error.text:DS.success.text}}>{log.bac||"0.00"}</strong></div>
                {log.notes&&<div style={{fontSize:11,color:DS.textSec,fontStyle:"italic"}}>{log.notes}</div>}
              </div>
              {log.photoUrl&&<img src={log.photoUrl} alt="Test" onClick={()=>window.open(log.photoUrl,"_blank")} style={{width:40,height:40,objectFit:"cover",borderRadius:6,border:`1px solid ${DS.border}`,cursor:"pointer",flexShrink:0}} title="Tap to view full size"/>}
              <Pill label={log.result==="pass"?"Pass":"Fail"} bg={log.result==="pass"?DS.success.bg:DS.error.bg} border={log.result==="pass"?DS.success.border:DS.error.border} color={log.result==="pass"?DS.success.text:DS.error.text}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── UA Tracker ─────────────────────────────────────────────────────────────────
function UATracker({ residents, perms }) {
  const [date, setDate] = useState(TODAY);
  const [facFilter, setFacFilter] = useState("all"); // residents already scoped via raResidents prop
  const [results, setResults] = useState({});
  const [popup, setPopup] = useState(null);
  const [failSubstance, setFailSubstance] = useState("");
  const [failNotes, setFailNotes] = useState("");
  const [failPhotoFile, setFailPhotoFile] = useState(null);
  const [failPhotoPreview, setFailPhotoPreview] = useState(null);
  const [pendingResult, setPendingResult] = useState(null); // "pass" | "fail" | null

  const facResidents = useMemo(() =>
    residents.filter(r => r.status === "active" && (facFilter === "all" || r.facility === facFilter)),
    [residents, facFilter]
  );

  const week = useMemo(() => getWeekBounds(date), [date]);

  // Load UA results for this week from Firestore
  useEffect(() => {
    const effectiveFacility = facFilter === "all" ? (perms?.canSeeAllFacilities ? null : (perms?.assignedFacility||null)) : facFilter;
    const unsub = subscribeToUAResults(week.sunday, week.saturday, effectiveFacility, firestoreResults => {
      const mapped = {};
      firestoreResults.forEach(r => {
        mapped[`${r.date}-${r.residentId}`] = {
          result: r.result,
          time: r.time,
          substance: r.substance || "",
          notes: r.notes || "",
        };
      });
      setResults(mapped);
    });
    return unsub;
  }, [week.sunday, week.saturday, facFilter]);

  const schedule = useMemo(() =>
    generateWeeklyUASchedule(week.days, facResidents, results),
    [week.days, facResidents, results]
  );

  const todayList = useMemo(() => {
    const ids = schedule[date] || [];
    return facResidents.filter(r => ids.includes(r.id));
  }, [schedule, date, facResidents]);

  const passCount = todayList.filter(r => results[`${date}-${r.id}`]?.result === "pass").length;
  const failCount = todayList.filter(r => results[`${date}-${r.id}`]?.result === "fail").length;

  const weekProgress = facResidents.map(r => ({
    ...r,
    tested: week.days.filter(d => results[`${d}-${r.id}`] !== undefined).length
  }));

  const logResult = async (residentId, result) => {
    const key = `${date}-${residentId}`;
    const resident = residents.find(r => r.id === residentId);
    let photoUrl = null;
    if (result === "fail" && failPhotoFile) {
      try { photoUrl = await uploadDocument(failPhotoFile, `ua-results/${residentId}/${Date.now()}`); } catch(e) {}
    }
    const entry = {
      result,
      time: timeNow(),
      substance: result === "fail" ? failSubstance : "",
      notes: result === "fail" ? failNotes : "",
    };
    // Optimistic update so UI responds immediately
    setResults(p => ({ ...p, [key]: entry }));
    setPopup(null);
    setFailSubstance("");
    setFailNotes("");
    setFailPhotoFile(null);
    setFailPhotoPreview(null);
    await saveUAResult({
      date,
      completedDate: TODAY,
      weekStart: week.sunday,
      weekEnd: week.saturday,
      residentId,
      residentName: resident?.name || "",
      facility: resident?.facility || "",
      result,
      time: entry.time,
      substance: entry.substance,
      notes: entry.notes,
      ...(photoUrl ? { photoUrl } : {}),
    });
    // Firestore subscription will sync actual state after save
  };

  return (
    <div>
      {/* Controls */}
      <div style={{display:"flex",flexWrap:"wrap",gap:12,marginBottom:20}}>
        <div>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Date</div>
          <input type="date" style={{...S.input,width:"auto"}} value={date} onChange={e=>setDate(e.target.value)}/>
        </div>
        {perms?.canSeeAllFacilities && (
        <div>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Facility</div>
          <div style={{display:"flex",gap:6}}>
            {["all",...FACILITIES].map(f=>(
              <button key={f} onClick={()=>setFacFilter(f)} style={{padding:"8px 14px",fontSize:12,borderRadius:8,cursor:"pointer",background:facFilter===f?DS.blue:"transparent",border:`1px solid ${facFilter===f?"#4f46e5":DS.border}`,color:facFilter===f?"white":"#64748b"}}>
                {f==="all"?"All":f.replace("Dorcus ","")}
              </button>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* Week banner */}
      <div style={{background:DS.blueTint,border:"1px solid rgba(79,70,229,0.3)",borderRadius:10,padding:"10px 14px",marginBottom:16}}>
        <div style={{fontSize:12,color:"#a5b4fc",marginBottom:4}}>
          📅 Week of <strong>{week.sunday}</strong> – <strong>{week.saturday}</strong>
        </div>
        <div style={{fontSize:11,color:DS.textSec}}>
          Max {UA_MAX_PER_DAY} per day · Each resident tested twice/week · Tests spaced {UA_MIN_GAP}–{UA_MAX_GAP} days apart
        </div>
      </div>

      {/* Today's stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,marginBottom:20}}>
        <div style={{...S.card,textAlign:"center"}}>
          <div style={{fontSize:28,fontWeight:700,color:"#1B5EBE"}}>{todayList.length}</div>
          <div style={{fontSize:11,color:"#1B5EBE",textTransform:"uppercase",letterSpacing:"0.08em",marginTop:4}}>Today's List</div>
        </div>
        <div style={{...S.card,textAlign:"center"}}>
          <div style={{fontSize:28,fontWeight:700,color:DS.success.text}}>{Object.values(results).filter(r=>r?.result).length}</div>
          <div style={{fontSize:11,color:DS.success.text,textTransform:"uppercase",letterSpacing:"0.08em",marginTop:4}}>Completed Today</div>
        </div>
      </div>

      {/* Today's list */}
      <div style={{marginBottom:24}}>
        <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:10}}>Today's Test List</div>
        {todayList.length === 0 ? (
          <div style={{...S.card,textAlign:"center",color:DS.textSec,fontSize:13}}>
            No tests scheduled for this date.
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {todayList.map((r,i)=>{
              const key=`${date}-${r.id}`,res=results[key],isOpen=popup===r.id;
              return (
                <div key={r.id} style={{position:"relative"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:12,border:`1px solid ${res?.result==="pass"?DS.success.border:res?.result==="fail"?DS.error.border:DS.border}`,background:res?.result==="pass"?DS.success.bg:res?.result==="fail"?DS.error.bg:"white"}}>
                    <div style={{width:26,height:26,borderRadius:8,background:DS.border,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:DS.textSec,flexShrink:0}}>{i+1}</div>
                    {r.photoURL?<img src={r.photoURL} alt={r.name} style={{width:32,height:32,borderRadius:8,objectFit:"cover",border:"1px solid #B5D4F4",flexShrink:0}}/>:<div style={{width:32,height:32,borderRadius:8,background:"#E8F0FB",border:"1px solid #B5D4F4",display:"flex",alignItems:"center",justifyContent:"center",color:"#1B5EBE",fontWeight:700,fontSize:11,flexShrink:0}}>{initials(r.name)}</div>}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{r.name}</div>
                      <div style={{fontSize:11,color:DS.textSec}}>
                        {r.facility}
                        {res && ` · ${res.time}`}
                        {res?.substance && <span style={{color:"#8B1A1A"}}> · {res.substance}</span>}
                      </div>
                      {res?.notes && <div style={{fontSize:11,color:DS.textSec,fontStyle:"italic"}}>{res.notes}</div>}
                    </div>
                    {res ? (
                      <Pill label={res.result==="pass"?"Pass":"Fail"} bg={res.result==="pass"?DS.success.bg:DS.error.bg} border={res.result==="pass"?"#059669":"#F5A8A8"} color={res.result==="pass"?"#1A6B35":"#8B1A1A"}/>
                    ) : (
                      <button onClick={()=>{setPopup(isOpen?null:r.id);setFailSubstance("");setFailNotes("");setFailPhotoFile(null);setFailPhotoPreview(null);setPendingResult(null);}} style={{...S.btn,fontSize:11,padding:"6px 14px"}}>Log Result</button>
                    )}
                  </div>

                  {isOpen && (
                    <>
                      <div onClick={()=>{setPopup(null);setFailSubstance("");setFailNotes("");setFailPhotoFile(null);setFailPhotoPreview(null);setPendingResult(null);}} style={{position:"fixed",inset:0,zIndex:9000}}/>
                      <div onClick={e=>e.stopPropagation()} style={{position:"fixed",zIndex:9001,top:"50%",left:"50%",transform:"translate(-50%,-50%)",background:"white",border:`1px solid ${DS.border}`,borderRadius:12,padding:16,width:300,boxShadow:"0 10px 40px rgba(0,0,0,0.7)"}}>
                        <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,marginBottom:12}}>{r.name} — Log Result</div>

                        {/* Step 1: Select Pass or Fail */}
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                          <button onClick={()=>setPendingResult("pass")} style={{padding:"10px",fontSize:13,fontWeight:700,borderRadius:8,cursor:"pointer",background:pendingResult==="pass"?DS.success.text:DS.success.bg,border:`2px solid ${pendingResult==="pass"?DS.success.text:DS.success.border}`,color:pendingResult==="pass"?"white":DS.success.text}}>✓ Pass</button>
                          <button onClick={()=>setPendingResult("fail")} style={{padding:"10px",fontSize:13,fontWeight:700,borderRadius:8,cursor:"pointer",background:pendingResult==="fail"?DS.error.text:DS.error.bg,border:`2px solid ${pendingResult==="fail"?DS.error.text:DS.error.border}`,color:pendingResult==="fail"?"white":DS.error.text}}>✗ Fail</button>
                        </div>

                        {/* Step 2: Fail details */}
                        {pendingResult==="fail"&&(<>
                          <div style={{fontSize:10,color:DS.error.text,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Substance (required) *</div>
                          <select style={{...S.input,marginBottom:8,border:`1px solid ${DS.error.border}`}} value={failSubstance} onChange={e=>setFailSubstance(e.target.value)}>
                            <option value="">Select substance...</option>
                            {FIVE_PANEL_SUBSTANCES.map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </>)}

                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Comments (optional)</div>
                        <textarea rows={2} style={{...S.input,resize:"none",marginBottom:8}} placeholder="Additional notes..." value={failNotes} onChange={e=>setFailNotes(e.target.value)}/>

                        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Photo (optional)</div>
                        {failPhotoPreview ? (
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                            <img src={failPhotoPreview} alt="result" style={{width:48,height:48,objectFit:"cover",borderRadius:6,border:`1px solid ${DS.border}`}}/>
                            <button onClick={()=>{setFailPhotoFile(null);setFailPhotoPreview(null);}} style={{fontSize:11,color:DS.error.text,background:"transparent",border:"none",cursor:"pointer"}}>Remove</button>
                          </div>
                        ) : (
                          <label style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",borderRadius:6,border:`1px dashed ${DS.border}`,cursor:"pointer",fontSize:11,color:DS.textSec,background:DS.pageBg,marginBottom:8}}>
                            📷 Photo of test result
                            <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;setFailPhotoFile(f);const rd=new FileReader();rd.onload=ev=>setFailPhotoPreview(ev.target.result);rd.readAsDataURL(f);}}/>
                          </label>
                        )}

                        {/* Step 3: Save or Cancel */}
                        <div style={{display:"flex",gap:8,marginTop:4}}>
                          <button onClick={()=>{setPopup(null);setFailSubstance("");setFailNotes("");setFailPhotoFile(null);setFailPhotoPreview(null);setPendingResult(null);}} style={{...S.btnGhost,flex:1,fontSize:12}}>Cancel</button>
                          <button
                            disabled={!pendingResult||(pendingResult==="fail"&&!failSubstance)}
                            onClick={()=>{logResult(r.id,pendingResult);setPendingResult(null);}}
                            style={{...S.btn,flex:1,fontSize:12,opacity:(!pendingResult||(pendingResult==="fail"&&!failSubstance))?0.4:1,background:pendingResult==="fail"?DS.error.text:pendingResult==="pass"?DS.success.text:DS.blue}}>
                            Save Result
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Weekly overview */}
      <div>
        <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:10}}>Weekly Testing Overview</div>
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          {/* Header row */}
          <div style={{display:"grid",gridTemplateColumns:"1fr repeat(7,40px)",background:DS.pageBg,padding:"8px 14px",gap:4}}>
            <div style={{fontSize:10,color:DS.textSec,fontWeight:600}}>Resident</div>
            {week.days.map(d=>(
              <div key={d} style={{fontSize:10,color:d===date?DS.blue:DS.textSec,fontWeight:d===date?700:400,textAlign:"center"}}>
                {new Date(d+"T12:00").toLocaleDateString("en-US",{weekday:"short"}).slice(0,1)}<br/>
                <span style={{fontSize:9}}>{new Date(d+"T12:00").getDate()}</span>
              </div>
            ))}
          </div>
          {weekProgress.map((r,i)=>(
            <div key={r.id} style={{display:"grid",gridTemplateColumns:"1fr repeat(7,40px)",padding:"8px 14px",gap:4,background:i%2===0?DS.pageBg:"white",borderTop:`1px solid ${DS.border}`}}>
              <div style={{fontSize:12,color:DS.textPrimary,display:"flex",alignItems:"center",gap:6}}>
                {r.name.split(" ")[0]}
                <span style={{fontSize:10,color:r.tested>=2?"#1A6B35":"#4A5568",fontWeight:600}}>({r.tested}/2)</span>
              </div>
              {week.days.map(d=>{
                const res=results[`${d}-${r.id}`];
                const isScheduled=(schedule[d]||[]).includes(r.id);
                return (
                  <div key={d} style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {res ? (
                      <div onClick={()=>res.photoUrl&&window.open(res.photoUrl,"_blank")}
                        style={{width:20,height:20,borderRadius:"50%",background:res.result==="pass"?"#1A6B35":"#8B1A1A",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"white",fontWeight:700,cursor:res.photoUrl?"pointer":"default",position:"relative"}}
                        title={res.photoUrl?"Tap to view test photo":undefined}>
                        {res.result==="pass"?"P":"F"}
                        {res.photoUrl&&<span style={{position:"absolute",top:-4,right:-4,width:8,height:8,borderRadius:"50%",background:"#1B5EBE",border:"1px solid white"}}/>}
                      </div>
                    ) : isScheduled ? (
                      <div style={{width:20,height:20,borderRadius:"50%",background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:DS.blue}}>●</div>
                    ) : (
                      <div style={{width:8,height:1,background:DS.border}}/>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:12,marginTop:10,fontSize:11,color:DS.textSec}}>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:12,height:12,borderRadius:"50%",background:"#059669"}}/> Pass</div>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:12,height:12,borderRadius:"50%",background:"#8B1A1A"}}/> Fail</div>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:12,height:12,borderRadius:"50%",background:"rgba(79,70,229,0.3)",border:"1px solid #4f46e5"}}/> Scheduled</div>
        </div>
      </div>

      {/* ── BAC / Breathalyzer Section ── */}
      <BACTracker residents={residents}/>
    </div>
  );
}

// ── RA: Shift Notes ───────────────────────────────────────────────────────────
function ShiftNotes({ raName }) {
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState({date:TODAY, shift:SHIFT_TYPES[0], facility:FACILITIES[0], author:raName||"", note:""});
  const [showForm, setShowForm] = useState(false);
  const [facFilter, setFacFilter] = useState("all");
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(() => {
    const unsub = subscribeToShiftNotes(allNotes => {
      const cutoff = new Date(NOW_NY());
      cutoff.setDate(cutoff.getDate() - 7);
      // Filter out old notes in UI only — don't attempt to delete
      setNotes(allNotes.filter(n => {
        if(!n.submittedAt) return true;
        return new Date(n.submittedAt) >= cutoff;
      }));
    });
    return unsub;
  }, []);

  const submit = async () => {
    if (!form.author.trim() || !form.note.trim()) return;
    await saveShiftNote({...form, timestamp:timeNow(), submittedAt:new Date().toISOString()});
    auditAction("shift_note_submitted", { facility:form.facility, shift:form.shift });
    setForm(f=>({...f, author:raName||"", note:""}));
    setShowForm(false);
  };

  const filtered = notes.filter(n => facFilter==="all" || n.facility===facFilter);
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",gap:8}}>
          {["all",...FACILITIES].map(f=><button key={f} onClick={()=>setFacFilter(f)} style={{padding:"6px 14px",fontSize:12,borderRadius:8,cursor:"pointer",background:facFilter===f?DS.blue:"white",border:`1px solid ${facFilter===f?DS.blue:DS.border}`,color:facFilter===f?"white":DS.textSec,fontFamily:"'Inter',sans-serif"}}>{f==="all"?"All Houses":shortFacility(f)}</button>)}
        </div>
        <button onClick={()=>setShowForm(s=>!s)} style={{...S.btn,display:"flex",alignItems:"center",gap:6}}>+ New Shift Report</button>
      </div>
      {showForm&&<div style={{...S.card,border:`1px solid ${DS.blueBorder}`,marginBottom:16}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          {[{label:"Date",key:"date",type:"date"},{label:"Shift",key:"shift",options:SHIFT_TYPES},{label:"Facility",key:"facility",options:FACILITIES},{label:"Your Name *",key:"author",placeholder:"RA name..."}].map(({label,key,type,options,placeholder})=>(
            <div key={key}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{label}</div>
              {options?<select style={{...S.input,border:`1px solid ${DS.border}`}} value={form[key]} onChange={e=>set(key,e.target.value)}>{options.map(o=><option key={o}>{o}</option>)}</select>
              :<input type={type||"text"} style={{...S.input,border:`1px solid ${DS.border}`}} placeholder={placeholder||""} value={form[key]} onChange={e=>set(key,e.target.value)}/>}
            </div>
          ))}
        </div>
        <div style={{marginBottom:12}}>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Shift Report *</div>
          <textarea rows={5} style={{...S.input,border:`1px solid ${DS.border}`,resize:"none"}} placeholder="Summarize the shift — resident activity, incidents, medication observations, notable events..." value={form.note} onChange={e=>set("note",e.target.value)}/>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>setShowForm(false)} style={{...S.btnGhost}}>Cancel</button>
          <button onClick={submit} disabled={!form.author.trim()||!form.note.trim()} style={{...S.btn,opacity:(!form.author.trim()||!form.note.trim())?0.4:1}}>Submit Report</button>
        </div>
      </div>}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {filtered.length===0&&<p style={{fontSize:13,color:DS.textSec,textAlign:"center",padding:"32px 0"}}>No shift reports yet.</p>}
        {filtered.map(n=>(
          <div key={n.id} style={{...S.card}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:36,height:36,borderRadius:10,background:DS.pageBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:DS.textSec}}>{(n.author||"?").split(" ").filter(Boolean).map(x=>x[0]).join("").toUpperCase().slice(0,2)}</div>
                <div><div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{n.author||"Unknown"}</div><div style={{fontSize:11,color:DS.textSec}}>{shortFacility(n.facility||"")}</div></div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:11,color:DS.textSec}}>{n.date}</div>
                <Pill label={n.shift||"Shift"} bg={DS.purple.bg} border={DS.purple.border} color={DS.purple.text}/>
              </div>
            </div>
            <p style={{fontSize:13,color:DS.textPrimary,lineHeight:1.6,margin:0,fontFamily:"'Inter',sans-serif"}}>{n.note}</p>
            <div style={{fontSize:11,color:DS.textSec,marginTop:8,fontFamily:"'Inter',sans-serif"}}>Submitted at {n.timestamp}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
// ── Archived Residents ────────────────────────────────────────────────────────
// ── PC Intake Page ────────────────────────────────────────────────────────────
function PCIntake({ residents, onSaveResident, currentUser }) {
  const [view, setView] = useState("list"); // list | form | day34
  const [selected, setSelected] = useState(null);
  const [currentFormIdx, setCurrentFormIdx] = useState(0);
  const [formData, setFormData] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [meetingForm, setMeetingForm] = useState({});
  const mset = (k,v) => setMeetingForm(f=>({...f,[k]:v}));

  const active = residents.filter(r=>r.status==="active"||r.status==="pending_arrival");

  const getFormStatus = (r) => {
    const forms = r.intakeForms||{};
    return DAY1_FORMS.map(f=>({...f, completed:!!forms[f.key], data:forms[f.key]||null}));
  };

  const needsDay1 = active.filter(r=>{
    const m = r.meetings||[];
    return !m.some(x=>x.type==="day1");
  });
  const needsDay34 = active.filter(r=>{
    const m = r.meetings||[];
    return m.some(x=>x.type==="day1") && !m.some(x=>x.type==="day34");
  });
  const complete = active.filter(r=>{
    const m = r.meetings||[];
    return m.some(x=>x.type==="day1") && m.some(x=>x.type==="day34");
  });

  const startIntake = (r) => {
    setSelected(r);
    const existingForms = r.intakeForms||{};
    const prefilled = {...existingForms};
    if(!prefilled.noticeOfArrival) {
      const exitDate = r.exitDate || (() => {
        if(!r.entryDate) return "";
        const d = new Date(r.entryDate);
        d.setDate(d.getDate()+90);
        return d.toISOString().split("T")[0];
      })();
      prefilled.noticeOfArrival = {
        arrivalDate: r.entryDate||"",
        arrivalFrom: r.referralInstitution||"",
        ppOfficer: r.ppOfficerName||r.ppOfficer||r.referredBy||"",
        classification: r.classification||"",
        scheduledRelease: r.releaseDate||"",
        exitDate,
        uaDate: r.entryDate||"",
        uaResult: "",
        items: {},
        comments: "",
      };
    }
    if(!prefilled.intakeForm) {
      prefilled.intakeForm = {
        ethnicOrigin: r.race||"",
      };
    }
    setFormData(prefilled);
    const firstIncomplete = DAY1_FORMS.findIndex(f=>!existingForms[f.key]);
    setCurrentFormIdx(firstIncomplete>=0?firstIncomplete:0);
    // If no photo yet, go to photo capture first
    if(!r.photoURL) {
      setView("photo_capture");
    } else {
      setView("form");
    }
  };

  const saveForm = (overrideData) => {
    const formKey = DAY1_FORMS[currentFormIdx].key;
    const currentData = overrideData || formData[formKey] || {};
    const updatedForms = {...formData,[formKey]:{...currentData,completedAt:TODAY}};
    setFormData(updatedForms);

    // Handle special side effects
    const fd = currentData;

    // MH Screen — update MH level if referred
    let updatedResident = {...selected, intakeForms:updatedForms};
    if(formKey==="mhScreen") {
      if(fd.referralDecision==="referred") {
        updatedResident.mhLevel = "MH-1";
        updatedResident.mentalHealth = true;
      }
    }
    // Dietary needs — save to resident record
    if(formKey==="dietaryNeeds") {
      updatedResident.dietaryNeeds = {
        hasRestrictions:fd.hasRestrictions||false,
        restrictions:fd.restrictions||"",
        reason:fd.reason||"",
        hasAllergies:fd.hasAllergies||false,
        allergies:fd.allergies||"",
        completedDate:TODAY,
      };
    }
    // SA treatment — feed into services tracker
    if(formKey==="saTreatment"&&fd.requestsTreatment===true) {
      const services = updatedResident.services||{};
      updatedResident.services = {...services, saTreatment:{status:"referred",notes:fd.facilityName||"",targetDate:""}};
    }

    onSaveResident(updatedResident);

    // If last form — mark Day 1 as complete
    if(currentFormIdx===DAY1_FORMS.length-1) {
      const meetings = [...(selected.meetings||[]), {type:"day1",date:TODAY,id:Date.now(),createdAt:TODAY,intakeFormsComplete:true}];
      onSaveResident({...updatedResident, meetings});
      // Fire Notice of Arrival notification to admin
      const noaData = updatedForms.noticeOfArrival||{};
      const mhData = updatedForms.mhScreen||{};
      saveNotification({
        type:"noa_ready",
        title:`Notice of Arrival ready — ${selected.name}`,
        body:`Day 1 intake completed · Send to district`,
        residentId:selected.id,
        residentName:selected.name,
        completedBy:"PC",
        completedDate:TODAY,
        districtContact:`P&P District ${selected.ppDistrict||selected.sentencingDistrict||""}`,
        arrivalFrom:noaData.arrivalFrom||selected.referralInstitution||"",
        read:false,
        actioned:false,
        createdAt:TODAY,
      });
      // Fire MH referral notification if referred
      // Notify admin if intake UA was positive
      const noaUa = updatedForms?.noticeOfArrival;
      if(noaUa?.uaResult==="Positive") {
        saveNotification({
          type:"intake_ua_positive",
          title:`Intake UA Positive — ${selected.name}`,
          body:`${displayName(selected.name)} tested positive for ${noaUa.uaSubstance||"controlled substance"} on arrival. Notify P&P officer and document accordingly.`,
          residentId:selected.id, residentName:selected.name,
          read:false, actioned:false, createdAt:TODAY,
        });
      }
      if(mhData.referralDecision==="referred") {
        saveNotification({
          type:"mh_referral",
          title:`MH Referral flagged — ${selected.name}`,
          body:`Mental health screen indicates referral needed · MH-1 assigned`,
          residentId:selected.id,
          residentName:selected.name,
          read:false,
          actioned:false,
          createdAt:TODAY,
        });
      }
      setSaveSuccess({name:selected.name, complete:true});
      setView("list");
      setSelected(null);
      setFormData({});
    } else {
      setCurrentFormIdx(i=>i+1);
    }
  };

  const completeIntake = () => {
    // Called when virtualInitials form completes — fire completion notifications
    const updatedForms = {...formData};
    const noaData = updatedForms.noticeOfArrival||{};
    const mhData = updatedForms.mhScreen||{};
    const meetings = [...(selected.meetings||[]), {type:"day1",date:TODAY,id:Date.now(),createdAt:TODAY,intakeFormsComplete:true}];
    onSaveResident({...selected, meetings});
    saveNotification({
      type:"noa_ready",
      title:`Notice of Arrival ready — ${selected.name}`,
      body:`Day 1 intake completed · Send to district`,
      residentId:selected.id, residentName:selected.name,
      completedBy:"PC", completedDate:TODAY,
      districtContact:`P&P District ${selected.ppDistrict||selected.sentencingDistrict||""}`,
      arrivalFrom:noaData.arrivalFrom||selected.referralInstitution||"",
      read:false, actioned:false, createdAt:TODAY,
    });
    const noaUa = noaData;
    if(noaUa?.uaResult==="Positive") {
      saveNotification({
        type:"intake_ua_positive",
        title:`Intake UA Positive — ${selected.name}`,
        body:`${displayName(selected.name)} tested positive for ${noaUa.uaSubstance||"controlled substance"} on arrival.`,
        residentId:selected.id, residentName:selected.name,
        read:false, actioned:false, createdAt:TODAY,
      });
    }
    if(mhData.referralDecision==="referred") {
      saveNotification({
        type:"mh_referral",
        title:`MH Referral — ${selected.name}`,
        body:`Mental health referral indicated during Day 1 intake.`,
        residentId:selected.id, residentName:selected.name,
        read:false, actioned:false, createdAt:TODAY,
      });
    }
    setSaveSuccess({name:selected.name, complete:true});
    setView("list");
    setSelected(null);
    setFormData({});
    setCurrentFormIdx(0);
  };

  const goBack = () => {
    if(currentFormIdx===0) {
      setView("list");
      setSelected(null);
      setFormData({});
    } else {
      setCurrentFormIdx(i=>i-1);
    }
  };

  const updateFormData = (key, data) => {
    setFormData(prev=>({...prev,[key]:data}));
  };

  const saveDay34Meeting = () => {
    const updated = [...(selected.meetings||[]),{...meetingForm,id:Date.now(),createdAt:TODAY}];
    const updatedResident = {...selected,meetings:updated};
    if(meetingForm.dpLevel&&meetingForm.dpLevel!==selected.dpLevel) {
      const history=[...(selected.dpHistory||[]),{from:selected.dpLevel||"none",to:meetingForm.dpLevel,date:TODAY,by:"PC"}];
      updatedResident.dpLevel=meetingForm.dpLevel;
      updatedResident.dpHistory=history;
    }
    onSaveResident(updatedResident);
    setSaveSuccess({name:selected.name,complete:false,type:"day34"});
    setView("list");setSelected(null);setMeetingForm({});
  };

  const dpColors = {
    "DP-0":{color:DS.success.text,bg:DS.success.bg,border:DS.success.border},
    "DP-1":{color:DS.teal.text,bg:DS.teal.bg,border:DS.teal.border},
    "DP-2":{color:DS.purple.text,bg:DS.purple.bg,border:DS.purple.border},
    "DP-3":{color:DS.amber.text,bg:DS.amber.bg,border:DS.amber.border},
  };

  if(view==="day34"&&selected) return (
    <div style={{display:"flex",flexDirection:"column",gap:0}}>
      <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:8,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
        <i className="bi bi-person-fill" style={{fontSize:16,color:DS.blue,flexShrink:0}}></i>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{selected.name} · DOC# {selected.doc}</div>
          <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{selected.facility} · Entry: {selected.entryDate||"TBD"}</div>
        </div>
        <div>
          <div style={{fontSize:10,color:DS.textSec,marginBottom:2,fontFamily:"'Inter',sans-serif"}}>Meeting date</div>
          <input type="date" style={{...S.input,border:`1px solid ${DS.border}`,width:"auto",fontSize:12}} value={meetingForm.date||TODAY} onChange={e=>mset("date",e.target.value)}/>
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div style={{...S.card}}>
          <SectionHdr>Items Covered</SectionHdr>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {DAY34_ITEMS.map(({key,label})=>{
              const item=(meetingForm.items||{})[key]||{done:false,followUp:false,notes:""};
              return (
                <div key={key} style={{background:item.done?DS.success.bg:DS.pageBg,border:`1px solid ${item.done?DS.success.border:DS.border}`,borderRadius:6,padding:"10px 12px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div onClick={()=>mset("items",{...(meetingForm.items||{}),[key]:{...item,done:!item.done}})}
                      style={{width:18,height:18,borderRadius:4,border:`2px solid ${item.done?DS.success.text:DS.border}`,background:item.done?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,cursor:"pointer"}}>
                      {item.done&&<i className="bi bi-check" style={{fontSize:10,color:DS.textPrimary}}></i>}
                    </div>
                    <span style={{fontSize:13,color:item.done?DS.success.text:DS.textPrimary,fontWeight:500,flex:1,fontFamily:"'Inter',sans-serif"}}>{label}</span>
                    <button onClick={()=>mset("items",{...(meetingForm.items||{}),[key]:{...item,followUp:!item.followUp}})}
                      style={{fontSize:10,padding:"3px 10px",borderRadius:10,border:`1px solid ${item.followUp?DS.warning.border:DS.border}`,background:item.followUp?DS.warning.bg:"transparent",color:item.followUp?DS.warning.text:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      {item.followUp?"Follow-up set ✓":"Set follow-up"}
                    </button>
                  </div>
                  {item.followUp&&<input style={{...S.input,border:`1px solid ${DS.border}`,fontSize:12,marginTop:8}} placeholder="Follow-up notes..." value={item.notes||""} onChange={e=>mset("items",{...(meetingForm.items||{}),[key]:{...item,notes:e.target.value}})}/>}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{...S.card}}>
          <SectionHdr>Disposition Level</SectionHdr>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:8}}>
            {[{v:"DP-0",sub:"No issues"},{v:"DP-1",sub:"SA only"},{v:"DP-2",sub:"MH only"},{v:"DP-3",sub:"SA + MH"}].map(({v,sub})=>{
              const dc=dpColors[v]||{};
              return <button key={v} onClick={()=>mset("dpLevel",v)} style={{padding:"10px 8px",borderRadius:6,cursor:"pointer",textAlign:"center",background:meetingForm.dpLevel===v?(dc.bg||DS.pageBg):"white",border:`2px solid ${meetingForm.dpLevel===v?(dc.border||DS.border):DS.border}`,transition:"all 0.15s"}}>
                <div style={{fontSize:14,fontWeight:600,color:meetingForm.dpLevel===v?(dc.color||DS.textPrimary):DS.textSec,fontFamily:"'DM Sans',sans-serif"}}>{v}</div>
                <div style={{fontSize:10,color:meetingForm.dpLevel===v?(dc.color||DS.textPrimary):DS.textSec,marginTop:2,fontFamily:"'Inter',sans-serif"}}>{sub}</div>
              </button>;
            })}
          </div>
          {meetingForm.dpLevel&&<div style={{fontSize:11,color:["DP-0","DP-1"].includes(meetingForm.dpLevel)?DS.teal.text:DS.purple.text,background:["DP-0","DP-1"].includes(meetingForm.dpLevel)?DS.teal.bg:DS.purple.bg,borderRadius:6,padding:"6px 10px",fontFamily:"'Inter',sans-serif"}}>{["DP-0","DP-1"].includes(meetingForm.dpLevel)?"Bi-weekly meetings":"Weekly meetings — reassess after first follow-up"}</div>}
        </div>
        <div style={{...S.card}}>
          <SectionHdr>Case Notes</SectionHdr>
          <textarea rows={4} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} placeholder="Document outcomes, action items assigned, next steps..." value={meetingForm.notes||""} onChange={e=>mset("notes",e.target.value)}/>
        </div>
        <div style={{...S.card}}>
          <SectionHdr>Next Meeting Date</SectionHdr>
          <input type="date" style={{...S.input,border:`1px solid ${DS.border}`,width:"auto"}} value={meetingForm.nextMeeting||""} onChange={e=>mset("nextMeeting",e.target.value)}/>
        </div>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:16,borderTop:`0.5px solid ${DS.border}`,marginTop:16}}>
        <button onClick={()=>{setView("list");setSelected(null);setMeetingForm({});}} style={{...S.btnGhost}}>Cancel</button>
        <button onClick={saveDay34Meeting} disabled={!meetingForm.nextMeeting} style={{...S.btn,opacity:!meetingForm.nextMeeting?0.4:1}}>Save Meeting Record</button>
      </div>
    </div>
  );

  // ── Form view ──
  if(view==="photo_capture"&&selected) {
    return (
      <PhotoCaptureStep
        resident={selected}
        onSave={async(photoURL)=>{
          // Save photo to resident record
          await saveResident({...selected, photoURL});
          setSelected(s=>({...s, photoURL}));
          setView("form");
        }}
        onSkip={()=>{
          // Flag for follow-up and continue
          saveNotification({
            type:"photo_missing",
            title:`Resident Photo Missing — ${displayName(selected.name)}`,
            body:`Photo was not captured during intake for ${displayName(selected.name)}. Please upload when available.`,
            residentId:selected.id, residentName:selected.name,
            read:false, actioned:false, createdAt:TODAY,
          });
          setView("form");
        }}
      />
    );
  }

  if(view==="form"&&selected) {
    const formKey = DAY1_FORMS[currentFormIdx].key;
    const formStatuses = getFormStatus(selected);
    const completedCount = Object.keys(formData).filter(k=>formData[k]?.completedAt).length;
    const props = {
      resident:selected,
      data:formData[formKey]||{},
      onChange:(d)=>updateFormData(formKey,d),
      onBack:goBack,
      onSave:saveForm,
    };
    return (
      <div style={{display:"flex",flexDirection:"column",gap:0}}>
        {/* Progress queue */}
        <div style={{...S.card,marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:600,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>Day 1 Intake — {selected.name}</div>
            <span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{completedCount} of {DAY1_FORMS.length} complete</span>
          </div>
          <div style={{height:4,background:DS.border,borderRadius:2,overflow:"hidden",marginBottom:10}}>
            <div style={{width:`${(completedCount/DAY1_FORMS.length)*100}%`,height:"100%",background:DS.blue,borderRadius:2,transition:"width 0.3s"}}/>
          </div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {DAY1_FORMS.map((f,i)=>{
              const done = formData[f.key]?.completedAt;
              const isCur = i===currentFormIdx;
              return (
                <div key={f.key} style={{fontSize:10,padding:"2px 7px",borderRadius:10,fontFamily:"'Inter',sans-serif",
                  background:done?DS.success.bg:isCur?DS.blue:DS.pageBg,
                  color:done?DS.success.text:isCur?"white":DS.textSec,
                  border:`1px solid ${done?DS.success.border:isCur?DS.blue:DS.border}`,
                  fontWeight:isCur?500:400}}>
                  {done?<><i className="bi bi-check" style={{fontSize:9}}></i> </>:""}{f.label}
                </div>
              );
            })}
          </div>
        </div>
        {formKey==="noticeOfArrival"&&<FormNoticeOfArrival {...props}/>}
        {formKey==="intakeForm"&&<FormIntake {...props}/>}
        {formKey==="mhScreen"&&<FormMHScreen {...props}/>}
        {formKey==="dietaryNeeds"&&<FormDietaryNeeds {...props}/>}
        {formKey==="propertyAgreement"&&<FormPropertyAgreement {...props}/>}
        {formKey==="leaseAgreement"&&<FormLeaseAgreement {...props}/>}
        {formKey==="programFees"&&<FormProgramFees {...props}/>}
        {formKey==="releaseOfInfo"&&<FormReleaseOfInfo {...props}/>}
        {formKey==="saTreatment"&&<FormSATreatment {...props}/>}
        {formKey==="virtualInitials"&&(
          <FormVirtualInitials
            resident={selected}
            onBack={goBack}
            onSave={(initialsData)=>{
              if(Object.keys(initialsData).length===0) {
                // Already locked — just complete
                completeIntake();
              } else {
                const updatedResident = {...selected, ...initialsData};
                saveResident(updatedResident).then(()=>{
                  setSelected(updatedResident);
                  completeIntake();
                });
              }
            }}
          />
        )}
      </div>
    );
  }

  // ── List view ──
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {saveSuccess&&(
        <div style={{background:DS.success.bg,border:`1px solid ${DS.success.border}`,borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
          <i className="bi bi-check-circle-fill" style={{fontSize:16,color:DS.success.text,flexShrink:0}}></i>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:DS.success.text,fontFamily:"'DM Sans',sans-serif"}}>
              {saveSuccess.complete?"Day 1 Intake complete":"Form saved"} — {saveSuccess.name}
            </div>
            {saveSuccess.complete&&<div style={{fontSize:11,color:DS.success.text,opacity:0.8,fontFamily:"'Inter',sans-serif"}}>All 10 forms completed · Documents saved to resident profile</div>}
          </div>
          <button onClick={()=>setSaveSuccess(null)} style={{background:"transparent",border:"none",cursor:"pointer",color:DS.success.text,fontSize:16}}>✕</button>
        </div>
      )}

      {needsDay1.length>0&&(
        <div style={{...S.card}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <SectionHdr>Intake and Orientation Forms</SectionHdr>
            <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:DS.error.bg,color:DS.error.text,border:`1px solid ${DS.error.border}`,fontFamily:"'Inter',sans-serif"}}>{needsDay1.length} pending</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {needsDay1.map(r=>{
              const completedForms = Object.keys(r.intakeForms||{}).filter(k=>(r.intakeForms||{})[k]?.completedAt).length;
              const inProgress = completedForms>0;
              return (
                <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:DS.pageBg,borderRadius:6,border:`1px solid ${DS.border}`}}>
                  <div style={{width:32,height:32,borderRadius:6,background:DS.blueTint,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600,color:DS.blue,flexShrink:0,fontFamily:"'DM Sans',sans-serif"}}>{(r.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(r.name)}</div>
                    <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>DOC# {r.doc} · {r.facility}{inProgress?` · ${completedForms} of ${DAY1_FORMS.length} forms complete`:""}</div>
                  </div>
                  {inProgress?(
                    <div style={{height:4,width:60,background:DS.border,borderRadius:2,overflow:"hidden"}}>
                      <div style={{width:`${(completedForms/DAY1_FORMS.length)*100}%`,height:"100%",background:DS.blue,borderRadius:2}}/>
                    </div>
                  ):(
                    <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:DS.warning.bg,color:DS.warning.text,border:`1px solid ${DS.warning.border}`,fontFamily:"'Inter',sans-serif"}}>Day 1 pending</span>
                  )}
                  <button onClick={()=>startIntake(r)} style={{...S.btnSec,fontSize:11,padding:"5px 12px"}}>{inProgress?"Resume →":"Start →"}</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {needsDay34.length>0&&(
        <div style={{...S.card}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <SectionHdr>Pending Coordinator Assessment</SectionHdr>
            <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:DS.purple.bg,color:DS.purple.text,border:`1px solid ${DS.purple.border}`,fontFamily:"'Inter',sans-serif"}}>{needsDay34.length} pending</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {needsDay34.map(r=>(
              <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:DS.pageBg,borderRadius:6,border:`1px solid ${DS.border}`}}>
                <div style={{width:32,height:32,borderRadius:6,background:DS.blueTint,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600,color:DS.blue,flexShrink:0}}>{(r.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(r.name)}</div>
                  <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>DOC# {r.doc} · {r.facility} · Day 1 complete</div>
                </div>
                <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:DS.purple.bg,color:DS.purple.text,border:`1px solid ${DS.purple.border}`,fontFamily:"'Inter',sans-serif"}}>Coordinator Assessment due</span>
                <button onClick={()=>{setSelected(r);setMeetingForm({type:"day34",date:TODAY,items:{},dpLevel:r.dpLevel||"",notes:"",nextMeeting:""});setView("day34");}} style={{...S.btnSec,fontSize:11,padding:"5px 12px"}}>Start →</button>
              </div>
            ))}
          </div>
        </div>
      )}


      {needsDay1.length===0&&needsDay34.length===0&&complete.length===0&&(
        <div style={{...S.card,textAlign:"center",padding:"48px 0"}}>
          <div style={{width:48,height:48,borderRadius:12,background:DS.blueTint,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
            <i className="bi bi-clipboard2-check-fill" style={{fontSize:20,color:DS.blue}}></i>
          </div>
          <div style={{fontSize:14,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif",marginBottom:4}}>No active residents</div>
          <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Residents will appear here once admitted</div>
        </div>
      )}
    </div>
  );
}


function PCFollowups({ residents, onSaveResident, currentUser }) {
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [meetingForm, setMeetingForm] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(null);
  const mset = (k,v) => setMeetingForm(f=>({...f,[k]:v}));

  const active = residents.filter(r=>r.status==="active");

  const today = new Date(); today.setHours(0,0,0,0);
  const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate()+7);

  const withStatus = active.map(r=>{
    const meetings = r.meetings||[];
    const hasDone34 = meetings.some(m=>m.type==="day34");
    if(!hasDone34) return null;
    const allMeetings = [...meetings].sort((a,b)=>String(b.createdAt).localeCompare(String(a.createdAt)));
    const latest = allMeetings[0];
    const nextDate = latest?.nextMeeting ? new Date(latest.nextMeeting) : null;
    const daysToNext = nextDate ? Math.round((nextDate-today)/(1000*60*60*24)) : null;
    const isOverdue = daysToNext !== null && daysToNext < 0;
    const isDueSoon = daysToNext !== null && daysToNext >= 0 && daysToNext <= 7;
    const openActions = (r.actionItems||[]).filter(a=>a.status==="open");
    return {...r, daysToNext, isOverdue, isDueSoon, openActions, latest};
  }).filter(Boolean);

  const overdue = withStatus.filter(r=>r.isOverdue).sort((a,b)=>a.daysToNext-b.daysToNext);
  const dueSoon = withStatus.filter(r=>r.isDueSoon&&!r.isOverdue).sort((a,b)=>a.daysToNext-b.daysToNext);
  const onTrack = withStatus.filter(r=>!r.isOverdue&&!r.isDueSoon);
  const allOpenActions = active.flatMap(r=>(r.actionItems||[]).filter(a=>a.status==="open").map(a=>({...a,residentName:r.name,residentDoc:r.doc,residentId:r.id})));

  const dpColors = {
    "DP-0":{color:DS.success.text,bg:DS.success.bg,border:DS.success.border},
    "DP-1":{color:DS.teal.text,bg:DS.teal.bg,border:DS.teal.border},
    "DP-2":{color:DS.purple.text,bg:DS.purple.bg,border:DS.purple.border},
    "DP-3":{color:DS.amber.text,bg:DS.amber.bg,border:DS.amber.border},
  };

  const toggleAction = (residentId, actionId) => {
    const r = residents.find(r=>r.id===residentId);
    if(!r) return;
    const updated = (r.actionItems||[]).map(a=>a.id===actionId?{...a,status:a.status==="open"?"complete":"open",completedAt:a.status==="open"?TODAY:null}:a);
    onSaveResident({...r, actionItems:updated});
  };

  const [acknowledged, setAcknowledged] = useState(false);

  const saveFollowup = () => {
    const meetingRecord = {
      ...meetingForm,
      id: Date.now(),
      createdAt: TODAY,
      ...(acknowledged ? {
        acknowledgedAt: TODAY,
        acknowledgedBy: currentUser||"PC",
        acknowledgedInitials: selected.virtualInitials||"",
      } : {}),
    };
    const updated = [...(selected.meetings||[]), meetingRecord];
    const updatedResident = {...selected, meetings:updated};
    if(meetingForm.dpLevel && meetingForm.dpLevel !== selected.dpLevel) {
      const history = [...(selected.dpHistory||[]),{from:selected.dpLevel||"none",to:meetingForm.dpLevel,date:TODAY,by:"PC"}];
      updatedResident.dpLevel = meetingForm.dpLevel;
      updatedResident.dpHistory = history;
    }
    onSaveResident(updatedResident);
    setSaveSuccess({name:selected.name});
    setView("list");
    setSelected(null);
    setMeetingForm({});
    setAcknowledged(false);
  };

  const ResRow = ({r, urgency}) => {
    const dc = dpColors[r.dpLevel]||{};
    const urgencyBadge = urgency==="overdue"
      ? {bg:DS.error.bg,text:DS.error.text,border:DS.error.border,label:`${Math.abs(r.daysToNext)}d overdue`}
      : urgency==="soon"
      ? {bg:DS.warning.bg,text:DS.warning.text,border:DS.warning.border,label:r.daysToNext===0?"Due today":`Due in ${r.daysToNext}d`}
      : {bg:DS.success.bg,text:DS.success.text,border:DS.success.border,label:"On track"};
    return (
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:DS.pageBg,borderRadius:6,border:`1px solid ${DS.border}`}}>
        <div style={{width:32,height:32,borderRadius:6,background:DS.blueTint,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600,color:DS.blue,flexShrink:0}}>{(r.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(r.name)}</div>
          <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>DOC# {r.doc} · {r.facility}{r.openActions?.length>0?` · ${r.openActions.length} open action${r.openActions.length>1?"s":""}`:""}</div>
        </div>
        {r.dpLevel&&<span style={{fontSize:11,padding:"2px 7px",borderRadius:10,fontWeight:500,background:dc.bg,border:`1px solid ${dc.border}`,color:dc.color,fontFamily:"'Inter',sans-serif"}}>{r.dpLevel}</span>}
        <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:urgencyBadge.bg,color:urgencyBadge.text,border:`1px solid ${urgencyBadge.border}`,fontFamily:"'Inter',sans-serif"}}>{urgencyBadge.label}</span>
        <button onClick={()=>{setSelected(r);setMeetingForm({type:"followup",date:TODAY,notes:"",dpLevel:r.dpLevel||"",nextMeeting:""});setView("followup");}}
          style={{...S.btnSec,fontSize:11,padding:"5px 12px"}}>Log →</button>
      </div>
    );
  };

  if(view==="list") return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {saveSuccess&&(
        <div style={{background:DS.success.bg,border:`1px solid ${DS.success.border}`,borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
          <i className="bi bi-check-circle-fill" style={{fontSize:16,color:DS.success.text}}></i>
          <span style={{fontSize:13,fontWeight:600,color:DS.success.text,fontFamily:"'DM Sans',sans-serif"}}>Follow-up saved — {saveSuccess.name}</span>
          <button onClick={()=>setSaveSuccess(null)} style={{marginLeft:"auto",background:"transparent",border:"none",cursor:"pointer",color:DS.success.text,fontSize:16}}>✕</button>
        </div>
      )}

      {(() => {
        const milestones = active.filter(r => {
          if (!r.entryDate) return false;
          const days = Math.floor((new Date(TODAY+"T00:00:00") - new Date(r.entryDate+"T00:00:00")) / 86400000);
          if (days < 30) return false;
          const period = days >= 90 ? 90 : days >= 60 ? 60 : 30;
          return !(r.meetings||[]).some(m => m.type === `milestone_${period}`);
        }).map(r => {
          const days = Math.floor((new Date(TODAY+"T00:00:00") - new Date(r.entryDate+"T00:00:00")) / 86400000);
          const period = days >= 90 ? 90 : days >= 60 ? 60 : 30;
          return {...r, milestonePeriod: period, daysSinceEntry: days};
        }).sort((a,b) => a.name.localeCompare(b.name));
        if (!milestones.length) return null;
        return (
          <div style={{...S.card, border:"1px solid #A8D5B5"}}>
            <SectionHdr>30/60/90 Day Reports Due</SectionHdr>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {milestones.map(r=>(
                <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"#E6F4EC",borderRadius:6,border:"1px solid #A8D5B5"}}>
                  <div style={{width:32,height:32,borderRadius:6,background:"#1A6B35",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600,color:"white",flexShrink:0}}>{r.milestonePeriod}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(r.name)}</div>
                    <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>DOC# {r.doc} · Day {r.daysSinceEntry} · {r.milestonePeriod}-day report due</div>
                  </div>
                  <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:99,background:"#1A6B35",color:"white"}}>{r.milestonePeriod}-day</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {overdue.length>0&&(
        <div style={{...S.card,border:`1px solid ${DS.error.border}`}}>
          <SectionHdr>Overdue</SectionHdr>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {overdue.map(r=><ResRow key={r.id} r={r} urgency="overdue"/>)}
          </div>
        </div>
      )}

      {dueSoon.length>0&&(
        <div style={{...S.card,border:`1px solid ${DS.warning.border}`}}>
          <SectionHdr>Due This Week</SectionHdr>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {dueSoon.map(r=><ResRow key={r.id} r={r} urgency="soon"/>)}
          </div>
        </div>
      )}

      {allOpenActions.length>0&&(
        <div style={{...S.card}}>
          <SectionHdr>Open Action Items</SectionHdr>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {allOpenActions.map(a=>(
              <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:DS.pageBg,borderRadius:6,border:`1px solid ${DS.border}`}}>
                <div onClick={()=>toggleAction(a.residentId,a.id)}
                  style={{width:18,height:18,borderRadius:4,border:`2px solid ${DS.border}`,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",background:"white"}}></div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{a.description}</div>
                  <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{a.residentName} · DOC# {a.residentDoc}{a.dueDate?` · Due ${a.dueDate}`:""}</div>
                </div>
                {a.dueDate&&new Date(a.dueDate)<today&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:DS.error.bg,color:DS.error.text,border:`1px solid ${DS.error.border}`,fontFamily:"'Inter',sans-serif"}}>Overdue</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {onTrack.length>0&&(
        <div style={{...S.card}}>
          <SectionHdr>On Track</SectionHdr>
          {(()=>{
            const upcoming = onTrack.filter(r=>r.daysToNext!==null&&r.daysToNext>=0&&r.daysToNext<=7);
            const needsScheduling = onTrack.filter(r=>r.daysToNext===null);
            const beyond = onTrack.filter(r=>r.daysToNext!==null&&r.daysToNext>7);
            return (<>
              {upcoming.length>0&&(<>
                <div style={{fontSize:10,fontWeight:600,color:DS.success.text,textTransform:"uppercase",letterSpacing:"0.07em",padding:"6px 0 4px"}}>Upcoming meetings (next 7 days)</div>
                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                  {upcoming.map(r=><ResRow key={r.id} r={r} urgency="ok"/>)}
                </div>
              </>)}
              {needsScheduling.length>0&&(<>
                <div style={{fontSize:10,fontWeight:600,color:DS.warning.text,textTransform:"uppercase",letterSpacing:"0.07em",padding:"6px 0 4px"}}>Needs scheduling</div>
                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                  {needsScheduling.map(r=><ResRow key={r.id} r={r} urgency="warn"/>)}
                </div>
              </>)}
              {beyond.length>0&&(<>
                <div style={{fontSize:10,fontWeight:600,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",padding:"6px 0 4px"}}>Beyond 7 days</div>
                <div style={{display:"flex",flexDirection:"column",gap:6,opacity:0.6}}>
                  {beyond.map(r=><ResRow key={r.id} r={r} urgency="ok"/>)}
                </div>
              </>)}
            </>);
          })()}
        </div>
      )}

      {withStatus.length===0&&(
        <div style={{...S.card,textAlign:"center",padding:"48px 0"}}>
          <div style={{width:48,height:48,borderRadius:12,background:DS.blueTint,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
            <i className="bi bi-arrow-repeat" style={{fontSize:20,color:DS.blue}}></i>
          </div>
          <div style={{fontSize:14,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif",marginBottom:4}}>No follow-ups scheduled yet</div>
          <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Residents appear here after their Coordinator Assessment meeting is logged</div>
        </div>
      )}
    </div>
  );

  if(view==="followup") return (
    <div style={{display:"flex",flexDirection:"column",gap:0}}>
      <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:8,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
        <i className="bi bi-person-fill" style={{fontSize:16,color:DS.blue,flexShrink:0}}></i>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{selected?.name} · DOC# {selected?.doc}</div>
          <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{selected?.facility} · {selected?.dpLevel||"No DP level set"}</div>
        </div>
        <div>
          <div style={{fontSize:10,color:DS.textSec,marginBottom:2,fontFamily:"'Inter',sans-serif"}}>Meeting date</div>
          <input type="date" style={{...S.input,border:`1px solid ${DS.border}`,width:"auto",fontSize:12}} value={meetingForm.date||TODAY} onChange={e=>mset("date",e.target.value)}/>
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {selected?.actionItems?.filter(a=>a.status==="open").length>0&&(
          <div style={{...S.card}}>
            <SectionHdr>Action Item Review</SectionHdr>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {selected.actionItems.filter(a=>a.status==="open").map(a=>(
                <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:DS.pageBg,borderRadius:6,border:`1px solid ${DS.border}`}}>
                  <div onClick={()=>toggleAction(selected.id,a.id)}
                    style={{width:18,height:18,borderRadius:4,border:`2px solid ${DS.border}`,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",background:"white"}}></div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{a.description}</div>
                    {a.dueDate&&<div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Due: {a.dueDate}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{...S.card}}>
          <SectionHdr>Update DP Level (if changed)</SectionHdr>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
            {[{v:"DP-0",sub:"No issues"},{v:"DP-1",sub:"SA only"},{v:"DP-2",sub:"MH only"},{v:"DP-3",sub:"SA + MH"}].map(({v,sub})=>{
              const dc=dpColors[v]||{};
              const isCurrent = meetingForm.dpLevel?meetingForm.dpLevel===v:selected?.dpLevel===v;
              return (
                <button key={v} onClick={()=>mset("dpLevel",v)}
                  style={{padding:"10px 8px",borderRadius:6,cursor:"pointer",textAlign:"center",background:isCurrent?(dc.bg||DS.pageBg):"white",border:`2px solid ${isCurrent?(dc.border||DS.border):DS.border}`}}>
                  <div style={{fontSize:14,fontWeight:600,color:isCurrent?(dc.color||DS.textPrimary):DS.textSec,fontFamily:"'DM Sans',sans-serif"}}>{v}</div>
                  <div style={{fontSize:10,color:isCurrent?(dc.color||DS.textPrimary):DS.textSec,fontFamily:"'Inter',sans-serif"}}>{sub}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div style={{...S.card}}>
          <SectionHdr>Case Notes</SectionHdr>
          <textarea rows={4} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} placeholder="Notes from this meeting..." value={meetingForm.notes||""} onChange={e=>mset("notes",e.target.value)}/>
        </div>
        <div style={{...S.card}}>
          <SectionHdr>Next Meeting Date</SectionHdr>
          <input type="date" style={{...S.input,border:`1px solid ${DS.border}`,width:"auto"}} value={meetingForm.nextMeeting||""} onChange={e=>mset("nextMeeting",e.target.value)}/>
        </div>

        {/* Resident acknowledgement */}
        {selected.virtualInitials && (
          <div style={{...S.card,border:`1px solid ${acknowledged?DS.success.border:DS.border}`,background:acknowledged?DS.success.bg:"white"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}} onClick={()=>setAcknowledged(a=>!a)}>
              <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${acknowledged?DS.success.text:DS.border}`,background:acknowledged?DS.success.text:"white",flexShrink:0,marginTop:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {acknowledged&&<span style={{color:"white",fontSize:12,fontWeight:700}}>✓</span>}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>
                  Resident acknowledges these case notes are accurate
                </div>
                <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif",marginTop:2}}>
                  Initialed by: <span style={{fontWeight:700,letterSpacing:"0.1em"}}>{selected.virtualInitials.toUpperCase()}</span> · {TODAY}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:16,borderTop:`0.5px solid ${DS.border}`,marginTop:16}}>
        <button onClick={()=>{setView("list");setSelected(null);setMeetingForm({});setAcknowledged(false);}} style={{...S.btnGhost}}>Cancel</button>
        <button onClick={saveFollowup} style={{...S.btn}}>Save Follow-up</button>
      </div>
    </div>
  );

  return null;
}

function ArchivedResidents({ residents }) {
  const [selected, setSelected] = useState(null);
  const [printSelectOpen, setPrintSelectOpen] = useState(false);
  const [printChecks, setPrintChecks] = useState({});
  const [archiveFilter, setArchiveFilter] = useState("discharged");
  const currentYear = new Date().getFullYear();

  const withdrawn = residents.filter(r => r.status === "withdrawn")
    .sort((a,b) => (b.withdrawnDate||"").localeCompare(a.withdrawnDate||""));

  const discharged = residents
    .filter(r => r.status === "discharged")
    .map(r => ({
      ...r,
      dischargeYear: r.actualDischargeDate ? new Date(r.actualDischargeDate).getFullYear() : null,
    }));

  const currentYearResidents = discharged
    .filter(r => r.dischargeYear === currentYear)
    .sort((a,b) => (b.actualDischargeDate||"").localeCompare(a.actualDischargeDate||""));

  const previousYearResidents = discharged
    .filter(r => r.dischargeYear !== currentYear)
    .sort((a,b) => {
      const lastA = (a.name||"").split(" ").pop();
      const lastB = (b.name||"").split(" ").pop();
      return lastA.localeCompare(lastB);
    });

  const allResidents = [...currentYearResidents, ...previousYearResidents];

  const DISCHARGE_TYPE_LABELS = {
    planned: "Planned Exit",
    voluntary: "Voluntary Discharge",
    violation: "P&P Violation",
    abscond: "Abscond",
    extension: "Extension",
  };

  const DISCHARGE_TYPE_COLORS = {
    planned: "#059669",
    voluntary: "#1B5EBE",
    violation: "#7A4F00",
    abscond: "#8B1A1A",
    extension: "#7c3aed",
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* Filter chips */}
      <div style={{display:"flex",gap:8}}>
        {[{id:"discharged",label:`Discharged (${allResidents.length})`},{id:"withdrawn",label:`Withdrawn (${withdrawn.length})`}].map(f=>(
          <button key={f.id} onClick={()=>setArchiveFilter(f.id)}
            style={{fontSize:11,padding:"4px 12px",borderRadius:16,cursor:"pointer",fontWeight:600,
              background:archiveFilter===f.id?(f.id==="withdrawn"?"#7A4F00":DS.blue):"white",
              border:`1px solid ${archiveFilter===f.id?(f.id==="withdrawn"?"#7A4F00":DS.blue):DS.border}`,
              color:archiveFilter===f.id?"white":DS.textSec}}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Withdrawn view */}
      {archiveFilter==="withdrawn"&&(
        <>
          <div style={{fontSize:13,color:DS.textSec}}>{withdrawn.length} resident{withdrawn.length!==1?"s":""} withdrawn before arrival</div>
          {withdrawn.length===0 ? (
            <div style={{...S.card,textAlign:"center",padding:"48px 0",color:DS.textSec}}>
              <div style={{fontSize:32,marginBottom:8}}>↩</div>
              <div style={{fontSize:14}}>No withdrawn residents</div>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {withdrawn.map(r=>(
                <div key={r.id} onClick={()=>setSelected(r)}
                  style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:"white",border:`0.5px solid ${DS.border}`,borderRadius:8,cursor:"pointer"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="#7A4F00"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=DS.border}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{r.name} <span style={{fontSize:11,color:DS.textSec,fontWeight:400}}>· DOC# {r.doc}</span></div>
                    <div style={{fontSize:11,color:DS.textSec,marginTop:2}}>{r.facility} · Withdrawn {r.withdrawnDate||"—"}</div>
                  </div>
                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:"#FFF3CD",color:"#7A4F00",border:"1px solid #F5C200",fontWeight:600}}>Withdrawn</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Discharged view */}
      {archiveFilter==="discharged"&&(
        <>
          <div style={{fontSize:13,color:DS.textSec}}>
            {currentYearResidents.length} residents discharged in {currentYear} · {previousYearResidents.length} from previous years
          </div>

      {allResidents.length === 0 ? (
        <div style={{...S.card,textAlign:"center",padding:"48px 0",color:DS.textSec}}>
          <div style={{fontSize:32,marginBottom:8}}>🗂</div>
          <div style={{fontSize:14}}>No discharged residents yet</div>
        </div>
      ) : (
        <>
          {currentYearResidents.length > 0 && (
            <div>
              <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${DS.border}`}}>{currentYear} — sorted by discharge date</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {currentYearResidents.map(r=>(
                  <div key={r.id} onClick={()=>setSelected(r)}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:"white",border:`0.5px solid ${DS.border}`,borderRadius:8,cursor:"pointer"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=DS.blue}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=DS.border}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{r.name} <span style={{fontSize:11,color:DS.textSec,fontWeight:400}}>· DOC# {r.doc}</span></div>
                      <div style={{fontSize:11,color:DS.textSec,marginTop:2}}>{r.facility} · Discharged {r.actualDischargeDate||"—"}</div>
                    </div>
                    {r.dischargeType && (
                      <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:500,background:`rgba(${r.dischargeType==="abscond"?"220,38,38":r.dischargeType==="violation"?"217,119,6":r.dischargeType==="voluntary"?"56,189,248":r.dischargeType==="extension"?"124,58,237":"5,150,105"},0.15)`,border:`1px solid ${DISCHARGE_TYPE_COLORS[r.dischargeType]||DS.border}`,color:DISCHARGE_TYPE_COLORS[r.dischargeType]||"#4A5568"}}>
                        {DISCHARGE_TYPE_LABELS[r.dischargeType]||r.dischargeType}
                      </span>
                    )}
                    <div style={{display:"flex",gap:6,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                      <button onClick={e=>{e.stopPropagation();setSelected(r);setPrintChecks({});setPrintSelectOpen(true);}}
                        style={{fontSize:11,padding:"5px 12px",borderRadius:7,cursor:"pointer",background:"white",border:`1px solid ${DS.border}`,color:DS.textSec,flexShrink:0}}>
                        Print Selected
                      </button>
                      <button onClick={e=>{e.stopPropagation();setSelected(r);}}
                        style={{fontSize:11,padding:"5px 12px",borderRadius:7,cursor:"pointer",background:DS.blue,border:"none",color:"white",flexShrink:0}}>
                        ⬇ Package
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {previousYearResidents.length > 0 && (
            <div>
              <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${DS.border}`}}>Previous years — sorted alphabetically</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {previousYearResidents.map(r=>(
                  <div key={r.id} onClick={()=>setSelected(r)}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:"white",border:`0.5px solid ${DS.border}`,borderRadius:8,cursor:"pointer"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=DS.blue}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=DS.border}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{r.name} <span style={{fontSize:11,color:DS.textSec,fontWeight:400}}>· DOC# {r.doc}</span></div>
                      <div style={{fontSize:11,color:DS.textSec,marginTop:2}}>{r.facility} · Discharged {r.actualDischargeDate||"—"} · {r.dischargeYear||"Year unknown"}</div>
                    </div>
                    {r.dischargeType && (
                      <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:500,background:`rgba(${r.dischargeType==="abscond"?"220,38,38":r.dischargeType==="violation"?"217,119,6":r.dischargeType==="voluntary"?"56,189,248":r.dischargeType==="extension"?"124,58,237":"5,150,105"},0.15)`,border:`1px solid ${DISCHARGE_TYPE_COLORS[r.dischargeType]||DS.border}`,color:DISCHARGE_TYPE_COLORS[r.dischargeType]||"#4A5568"}}>
                        {DISCHARGE_TYPE_LABELS[r.dischargeType]||r.dischargeType}
                      </span>
                    )}
                    <div style={{display:"flex",gap:6,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                      <button onClick={e=>{e.stopPropagation();setSelected(r);setPrintChecks({});setPrintSelectOpen(true);}}
                        style={{fontSize:11,padding:"5px 12px",borderRadius:7,cursor:"pointer",background:"white",border:`1px solid ${DS.border}`,color:DS.textSec,flexShrink:0}}>
                        Print Selected
                      </button>
                      <button onClick={e=>{e.stopPropagation();setSelected(r);}}
                        style={{fontSize:11,padding:"5px 12px",borderRadius:7,cursor:"pointer",background:DS.blue,border:"none",color:"white",flexShrink:0}}>
                        ⬇ Package
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Print Select Documents Modal */}
      {printSelectOpen && selected && (
        <div style={{position:"fixed",inset:0,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:20}}
          onClick={()=>setPrintSelectOpen(false)}>
          <div style={{background:"white",borderRadius:10,width:"100%",maxWidth:460,maxHeight:"88vh",display:"flex",flexDirection:"column",overflow:"hidden",border:`0.5px solid ${DS.border}`}}
            onClick={e=>e.stopPropagation()}>
            <div style={{padding:"12px 16px",background:DS.blue,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>Print Selected Documents</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>{displayName(selected.name)} · DOC# {selected.doc}</div>
              </div>
              <button onClick={()=>setPrintSelectOpen(false)} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:5,color:"white",fontSize:11,padding:"3px 10px",cursor:"pointer"}}>✕</button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"14px 16px",display:"flex",flexDirection:"column",gap:5}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Select documents to print</span>
                <button onClick={()=>{const all={};["intake","mh","dietary","property","lease","fees","release","sa","discharge","meetings","notes"].forEach(k=>all[k]=true);setPrintChecks(all);}} style={{fontSize:11,color:DS.blue,background:"none",border:"none",cursor:"pointer"}}>Select all</button>
              </div>
              {[
                {key:"intake",    label:"Notice of Arrival",             done:!!selected.intakeForms?.noticeOfArrival?.completedAt},
                {key:"mh",        label:"MH Screen",                     done:!!selected.intakeForms?.mhScreen?.completedAt},
                {key:"dietary",   label:"Dietary Needs",                  done:!!selected.intakeForms?.dietaryNeeds?.completedAt},
                {key:"property",  label:"Proof of Temporary Address",     done:!!selected.intakeForms?.propertyAgreement?.completedAt},
                {key:"lease",     label:"Lease Agreement",                done:!!selected.intakeForms?.leaseAgreement?.completedAt},
                {key:"fees",      label:"Program Fees Agreement",         done:!!selected.intakeForms?.programFees?.completedAt},
                {key:"release",   label:"Release of Information",         done:!!selected.intakeForms?.releaseOfInfo?.completedAt},
                {key:"sa",        label:"SA Treatment Agreement",         done:!!selected.intakeForms?.saTreatment?.completedAt},
                {key:"discharge", label:"Discharge Meeting",              done:!!selected.intakeForms?.dischargeMeeting?.completedAt},
                {key:"meetings",  label:"Meeting History",                done:!!(selected.meetings?.length)},
                {key:"notes",     label:"Case Notes",                     done:!!(selected.caseNotes?.length)},
              ].map(item=>(
                <div key={item.key} onClick={()=>setPrintChecks(c=>({...c,[item.key]:!c[item.key]}))}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:6,cursor:"pointer",
                    background:printChecks[item.key]?DS.blueTint:"white",
                    border:`0.5px solid ${printChecks[item.key]?DS.blueBorder:DS.border}`}}>
                  <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${printChecks[item.key]?DS.blue:DS.border}`,background:printChecks[item.key]?DS.blue:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {printChecks[item.key]&&<i className="bi bi-check" style={{fontSize:10,color:"white"}}></i>}
                  </div>
                  <span style={{fontSize:12,color:printChecks[item.key]?DS.blue:DS.textPrimary,flex:1,fontFamily:"'Inter',sans-serif"}}>{item.label}</span>
                  <span style={{fontSize:10,padding:"2px 7px",borderRadius:8,background:item.done?DS.success.bg:DS.pageBg,color:item.done?DS.success.text:DS.textSec,border:`1px solid ${item.done?DS.success.border:DS.border}`}}>{item.done?"Complete":"Not completed"}</span>
                </div>
              ))}
            </div>
            <div style={{padding:"10px 14px",borderTop:`0.5px solid ${DS.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
              <span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{Object.values(printChecks).filter(Boolean).length} selected</span>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setPrintSelectOpen(false)} style={{padding:"7px 14px",fontSize:12,borderRadius:6,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer"}}>Cancel</button>
                <button onClick={()=>{
                  const selected_keys = Object.keys(printChecks).filter(k=>printChecks[k]);
                  if(!selected_keys.length) return alert("Please select at least one document.");
                  const win = window.open("","_blank");
                  win.document.write(`<html><head><title>Documents — ${selected.name}</title><style>body{font-family:Arial,sans-serif;padding:24px;color:#1A2B45;} h1{font-size:18px;margin-bottom:4px;} .sub{font-size:13px;color:#4A5568;margin-bottom:24px;} .section{margin-bottom:20px;padding:16px;border:1px solid #D8DCE3;border-radius:8px;} .section h2{font-size:13px;font-weight:bold;margin:0 0 8px;} .field{font-size:12px;margin:4px 0;} .label{color:#4A5568;} @media print{button{display:none;}}</style></head><body>`);
                  win.document.write(`<h1>${selected.name}</h1><div class="sub">DOC# ${selected.doc} · ${selected.facility} · Discharged ${selected.actualDischargeDate||"—"}</div>`);
                  if(printChecks.intake&&selected.intakeForms?.noticeOfArrival){const f=selected.intakeForms.noticeOfArrival;win.document.write(`<div class="section"><h2>Notice of Arrival</h2><div class="field"><span class="label">Arrival from:</span> ${f.arrivalFrom||"—"}</div><div class="field"><span class="label">P&P Officer:</span> ${f.ppOfficer||"—"}</div><div class="field"><span class="label">UA Result:</span> ${f.uaResult||"—"}</div></div>`);}
                  if(printChecks.meetings&&selected.meetings?.length){win.document.write(`<div class="section"><h2>Meeting History (${selected.meetings.length})</h2>${selected.meetings.map(m=>`<div class="field">${m.date} — ${m.type} — ${m.dpLevel||""}: ${m.notes||""}</div>`).join("")}</div>`);}
                  if(printChecks.notes&&selected.caseNotes?.length){win.document.write(`<div class="section"><h2>Case Notes (${selected.caseNotes.length})</h2>${selected.caseNotes.map(n=>`<div class="field">${n.date} [${n.author}]: ${n.note}</div>`).join("")}</div>`);}
                  win.document.write(`<br><button onclick="window.print()">Print</button></body></html>`);
                  win.document.close();
                  setTimeout(()=>win.print(), 500);
                }} style={{padding:"7px 16px",fontSize:12,borderRadius:6,border:"none",background:DS.blue,color:"white",cursor:"pointer",fontWeight:500}}>
                  🖨 Print Selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
      )}
    </div>
  );
}

// ── PC Dashboard ──────────────────────────────────────────────────────────────
function PCDashboard({ residents, onNavigate }) {
  const [recentMeetingsOpen, setRecentMeetingsOpen] = useState(true);
  const [meetingsThisWeekOpen, setMeetingsThisWeekOpen] = useState(true);
  const active = residents.filter(r=>r.status==="active");
  const houseA = active.filter(r=>r.facility==="Dorcus 1 - 801");
  const houseB = active.filter(r=>r.facility==="Dorcus 2 - 24th St");

  const PCStatCard = ({label,value,sub,color=DS.blue,max,onClick})=>(
    <div onClick={onClick} style={{...S.card,cursor:onClick?"pointer":"default"}}
      onMouseEnter={e=>{if(onClick)e.currentTarget.style.borderColor=DS.blue;}}
      onMouseLeave={e=>{if(onClick)e.currentTarget.style.borderColor=DS.border;}}>
      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8,fontFamily:"'Inter',sans-serif"}}>{label}</div>
      <div style={{fontSize:32,fontWeight:600,color,lineHeight:1,fontFamily:"'DM Sans',sans-serif"}}>{value}{max&&<span style={{fontSize:14,color:DS.textSec,fontWeight:400}}>/{max}</span>}</div>
      {sub&&<div style={{fontSize:11,color:DS.textSec,marginTop:4,fontFamily:"'Inter',sans-serif"}}>{sub}</div>}
    </div>
  );

  const upcomingEntries = residents
    .filter(r=>r.status==="pending_arrival"&&r.entryDate)
    .map(r=>({...r, daysUntilEntry: daysUntil(r.entryDate)}))
    .filter(r=>r.daysUntilEntry!==null&&r.daysUntilEntry>=0&&r.daysUntilEntry<=7)
    .sort((a,b)=>a.daysUntilEntry-b.daysUntilEntry);

  const upcomingExits = active
    .filter(r=>r.exitDate)
    .map(r=>({...r, daysLeft: daysUntil(r.exitDate)}))
    .filter(r=>r.daysLeft!==null&&r.daysLeft>=0&&r.daysLeft<=14)
    .sort((a,b)=>a.daysLeft-b.daysLeft);

  // Real scheduled meetings — residents with nextMeeting set in their latest meeting
  const today = new Date(); today.setHours(0,0,0,0);
  const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate()+7);
  const scheduledMeetings = active.flatMap(r=>{
    const meetings = r.meetings||[];
    if(meetings.length===0) return [];
    const latest = [...meetings].sort((a,b)=>String(b.createdAt).localeCompare(String(a.createdAt)))[0];
    if(!latest?.nextMeeting) return [];
    const d = new Date(latest.nextMeeting);
    if(isNaN(d)) return [];
    if(d>=today&&d<=weekEnd) return [{residentName:r.name,residentDoc:r.doc,date:latest.nextMeeting,type:latest.type}];
    return [];
  }).sort((a,b)=>a.date.localeCompare(b.date));

  // 30/60/90 day milestones due
  const milestoneDue = active.filter(r => {
    if (!r.entryDate) return false;
    const days = Math.floor((today - new Date(r.entryDate + "T00:00:00")) / 86400000);
    return days >= 30;
  }).map(r => {
    const days = Math.floor((today - new Date(r.entryDate + "T00:00:00")) / 86400000);
    const period = days >= 90 ? 90 : days >= 60 ? 60 : 30;
    const hasMilestoneLog = (r.meetings||[]).some(m => m.type === `milestone_${period}`);
    return hasMilestoneLog ? null : {...r, milestonePeriod: period, daysSinceEntry: days};
  }).filter(Boolean).sort((a,b) => a.name.localeCompare(b.name));

  // Open action items across all residents
  const openActions = active.flatMap(r=>
    (r.actionItems||[]).filter(a=>a.status==="open").map(a=>({...a,residentName:r.name,residentDoc:r.doc,residentId:r.id}))
  ).sort((a,b)=>(a.dueDate||"9999").localeCompare(b.dueDate||"9999"));

  // Residents with incomplete ID approaching discharge
  const incompleteIDApproaching = active
    .filter(r=>r.exitDate&&daysUntil(r.exitDate)!==null&&daysUntil(r.exitDate)<=14&&daysUntil(r.exitDate)>=0)
    .filter(r=>{
      const svc = r.services||{};
      return !svc.idCheck?.status||svc.idCheck.status!=="complete";
    });

  // Recent case meetings
  const recentMeetings = active
    .flatMap(r=>(r.meetings||[]).map(m=>({...m,residentName:r.name,residentDoc:r.doc})))
    .sort((a,b)=>String(b.createdAt).localeCompare(String(a.createdAt)))
    .slice(0,4);

  // DP breakdown
  const dpBreakdown = ["DP-0","DP-1","DP-2","DP-3"].map(dp=>({
    dp, count:active.filter(r=>r.dpLevel===dp).length
  }));
  const dpColors2 = {"DP-0":DS.success,"DP-1":DS.teal,"DP-2":DS.purple,"DP-3":DS.amber};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>

      {/* Stat row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <PCStatCard label="Dorcus 1" value={houseA.length} max={35} sub="of 35 capacity" color={DS.blue} onClick={()=>onNavigate("residents")}/>
        <PCStatCard label="Dorcus 2" value={houseB.length} max={35} sub="of 35 capacity" color={DS.darkYellow} onClick={()=>onNavigate("residents")}/>
        {false && <PCStatCard label="Meetings This Week" value={scheduledMeetings.length + upcomingEntries.filter(e=>e.daysUntilEntry<=7).length + milestoneDue.length} sub={`${scheduledMeetings.length} follow-ups · ${upcomingEntries.filter(e=>e.daysUntilEntry<=7).length} intakes · ${milestoneDue.length} reports due`} color={DS.warning.text}/>}
        <PCStatCard label="Upcoming Discharges" value={upcomingExits.length} sub="within 7 days" color={DS.error.text}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>

        {/* Left column */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>

          {/* Incoming residents */}
          <div style={{...S.card}}>
            <SectionHdr>Upcoming Arrivals — Next 7 Days</SectionHdr>
            {upcomingEntries.length===0
              ? <div style={{fontSize:12,color:DS.textSec,textAlign:"center",padding:"8px 0",fontFamily:"'Inter',sans-serif"}}>No pending arrivals in the next 7 days.</div>
              : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {upcomingEntries.map(r=>{
                    const isToday=r.daysUntilEntry===0;
                    return (
                      <div key={r.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",borderRadius:6,background:isToday?DS.success.bg:DS.warning.bg,border:`1px solid ${isToday?DS.success.border:DS.warning.border}`}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{r.name} <span style={{fontSize:11,color:DS.textSec,fontWeight:400}}>· DOC# {r.doc}</span></div>
                          <div style={{fontSize:11,color:DS.textSec,marginTop:1,fontFamily:"'Inter',sans-serif"}}>{r.facility} · Entry: {r.entryDate}</div>
                        </div>
                        <div style={{fontSize:13,fontWeight:600,color:isToday?DS.success.text:DS.warning.text,fontFamily:"'Inter',sans-serif"}}>{isToday?"Today":`${r.daysUntilEntry}d`}</div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>

          {/* Upcoming discharges */}
          <div style={{...S.card}}>
            <SectionHdr>Upcoming Discharges — Next 14 Days</SectionHdr>
            {upcomingExits.length===0
              ? <div style={{fontSize:12,color:DS.textSec,textAlign:"center",padding:"8px 0",fontFamily:"'Inter',sans-serif"}}>No exits scheduled in the next 14 days.</div>
              : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {upcomingExits.map(r=>{
                    const isToday=r.daysLeft===0;
                    return (
                      <div key={r.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",borderRadius:6,background:isToday?DS.warning.bg:DS.pageBg,border:`1px solid ${isToday?DS.warning.border:DS.border}`}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{r.name} <span style={{fontSize:11,color:DS.textSec,fontWeight:400}}>· DOC# {r.doc}</span></div>
                          <div style={{fontSize:11,color:DS.textSec,marginTop:1,fontFamily:"'Inter',sans-serif"}}>{r.facility} · Exit: {r.exitDate}</div>
                        </div>
                        <div style={{fontSize:13,fontWeight:600,color:isToday?DS.warning.text:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{isToday?"Today":`${r.daysLeft}d`}</div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>

          {/* Incomplete ID approaching discharge alert */}
          {incompleteIDApproaching.length>0&&(
            <div style={{...S.card,border:`1px solid ${DS.error.border}`,background:DS.error.bg}}>
              <SectionHdr>Incomplete ID — Discharge Approaching</SectionHdr>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {incompleteIDApproaching.map(r=>(
                  <div key={r.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:DS.white,borderRadius:6,border:`1px solid ${DS.border}`}}>
                    <span style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{r.name}</span>
                    <span style={{fontSize:11,color:DS.error.text,fontFamily:"'Inter',sans-serif"}}>{daysUntil(r.exitDate)}d remaining</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Right column */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>

          {/* Meetings this week removed */}

          {/* Open action items */}
          {openActions.length>0&&(
            <div style={{...S.card,border:`1px solid ${DS.warning.border}`}}>
              <SectionHdr>Open Action Items</SectionHdr>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {openActions.slice(0,5).map((a,i)=>(
                  <div key={i} style={{padding:"8px 10px",background:DS.pageBg,borderRadius:6,border:`1px solid ${DS.border}`}}>
                    <div style={{fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{a.description}</div>
                    <div style={{fontSize:10,color:DS.textSec,marginTop:2,fontFamily:"'Inter',sans-serif"}}>{a.residentName} · DOC# {a.residentDoc}{a.dueDate?` · Due ${a.dueDate}`:""}</div>
                  </div>
                ))}
                {openActions.length>5&&<div style={{fontSize:11,color:DS.textSec,textAlign:"center",fontFamily:"'Inter',sans-serif"}}>+{openActions.length-5} more</div>}
              </div>
            </div>
          )}

          {/* Recent case meetings - collapsible */}
          <div style={{...S.card}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}} onClick={()=>setRecentMeetingsOpen(p=>!p)}>
              <SectionHdr>Recent Case Meetings</SectionHdr>
              <i className={`bi bi-chevron-${recentMeetingsOpen?"up":"down"}`} style={{fontSize:14,color:DS.textSec}}/>
            </div>

            {recentMeetingsOpen && (recentMeetings.length===0
              ? <div style={{fontSize:12,color:DS.textSec,textAlign:"center",padding:"8px 0",fontFamily:"'Inter',sans-serif"}}>No meetings logged yet.</div>
              : <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {recentMeetings.map((m,i)=>(
                    <div key={i} style={{borderLeft:`2px solid ${DS.blue}`,paddingLeft:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                        <span style={{fontSize:11,fontWeight:600,color:DS.blue,fontFamily:"'Inter',sans-serif"}}>{m.residentName} <span style={{color:DS.textSec,fontWeight:400}}>· DOC# {m.residentDoc}</span></span>
                        <span style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{m.date}</span>
                      </div>
                      <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{m.type==="day1"?"Day 1 Intake":m.type==="day34"?"Coordinator Assessment":"Follow-up"}{m.dpLevel?` · ${m.dpLevel}`:""}</div>
                      {m.notes&&<p style={{fontSize:11,color:DS.textSec,margin:"2px 0 0",lineHeight:1.4,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",fontFamily:"'Inter',sans-serif"}}>{m.notes}</p>}
                    </div>
                  ))}
                </div>
            )}
          </div>

          {/* DP breakdown */}
          <div style={{...S.card}}>
            <SectionHdr>Disposition Level Breakdown</SectionHdr>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
              {dpBreakdown.map(({dp,count})=>{
                const dc = dpColors2[dp]||DS.neutral;
                return (
                  <div key={dp} style={{textAlign:"center",padding:"10px 6px",borderRadius:6,background:dc.bg,border:`1px solid ${dc.border}`}}>
                    <div style={{fontSize:16,fontWeight:600,color:dc.text,fontFamily:"'DM Sans',sans-serif"}}>{count}</div>
                    <div style={{fontSize:10,color:dc.text,marginTop:2,fontFamily:"'Inter',sans-serif"}}>{dp}</div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}


// ── Notifications System ──────────────────────────────────────────────────────

// ── DC1: Discharge Meeting Form ───────────────────────────────────────────────
function DCDischargeMeeting({ residents, currentUser, onSaveResident }) {
  const [section, setSection] = useState(0); // 0=select, 1-7=A-G
  const [residentName, setResidentName] = useState("");
  const [residentMatch, setResidentMatch] = useState(null);
  const [exitDate, setExitDate] = useState("");
  const [meetingDate, setMeetingDate] = useState(TODAY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Section A
  const [dischargeType, setDischargeType] = useState(""); // "d15" | "returning"
  const [dischargePhone, setDischargePhone] = useState("");
  const [leaseYN, setLeaseYN] = useState("");
  const [leaseApprovedYN, setLeaseApprovedYN] = useState("");
  const [forwardingAddress, setForwardingAddress] = useState("");
  const [returningCity, setReturningCity] = useState("");
  const [poNotifiedYN, setPoNotifiedYN] = useState("");
  const [physicalAddressYN, setPhysicalAddressYN] = useState("");
  const [proposedAddress, setProposedAddress] = useState("");

  // Section B
  const [employmentAck, setEmploymentAck] = useState(false);

  // Section C
  const [transport, setTransport] = useState("");

  // Section D
  const [compensationYN, setCompensationYN] = useState("");
  const [bankAccountYN, setBankAccountYN] = useState("");
  const [busMoneyYN, setBusMoneyYN] = useState("");

  // Section E
  const [personalItemsAck, setPersonalItemsAck] = useState(false);

  // Section F
  const [medTransferAck, setMedTransferAck] = useState(false);
  const [saTransferAck, setSaTransferAck] = useState(false);

  // Section G
  const [addressChecks, setAddressChecks] = useState({});
  const [addressAck, setAddressAck] = useState(false);

  const ADDRESS_ITEMS = [
    "Medicaid / Medicare",
    "Social Security Administration",
    "Current / Previous Employer (HR Dept)",
    "Post Office",
    "DMV",
    "Pharmacy (for prescribed medications)",
    "Department of Social Services",
    "Friends / Family",
  ];

  const nameMatches = residentName.length >= 2
    ? residents.filter(r => r.name.toLowerCase().includes(residentName.toLowerCase()) && r.status === "active")
    : [];

  const suggestedForDischarge = residents.filter(r => r.status==="active" && r.exitDate).map(r => {
    const days = Math.round((new Date(r.exitDate) - new Date(TODAY)) / 86400000);
    return {...r, daysToExit: days};
  }).filter(r => r.daysToExit >= 0 && r.daysToExit <= 14).sort((a,b)=>a.daysToExit-b.daysToExit);

  const isReturning = dischargeType === "returning";
  const isD15 = dischargeType === "d15";

  const TOTAL_SECTIONS = 7;
  const progress = section === 0 ? 0 : Math.round((section / TOTAL_SECTIONS) * 100);

  const SectionHdrDC = ({letter, title}) => (
    <div style={{padding:"10px 14px",background:DS.blue,display:"flex",alignItems:"center",gap:8}}>
      <div style={{width:24,height:24,borderRadius:6,background:"rgba(255,255,255,0.2)",color:"white",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontFamily:"'DM Sans',sans-serif"}}>{letter}</div>
      <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>{title}</span>
    </div>
  );

  const YNButtons = ({value, onChange}) => (
    <div style={{display:"flex",gap:6}}>
      {["Yes","No"].map(v=>(
        <button key={v} onClick={()=>onChange(v)}
          style={{padding:"6px 18px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:500,
            background:value===v?(v==="Yes"?DS.success.bg:DS.error.bg):"white",
            border:`1.5px solid ${value===v?(v==="Yes"?DS.success.border:DS.error.border):DS.border}`,
            color:value===v?(v==="Yes"?DS.success.text:DS.error.text):DS.textSec,
            fontFamily:"'Inter',sans-serif"}}>
          {v}
        </button>
      ))}
    </div>
  );

  const Ack = ({checked, onChange, children}) => (
    <div onClick={onChange} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",background:checked?DS.success.bg:DS.pageBg,borderRadius:6,border:`1px solid ${checked?DS.success.border:DS.border}`,cursor:"pointer"}}>
      <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${checked?DS.success.text:DS.border}`,background:checked?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
        {checked&&<i className="bi bi-check" style={{fontSize:10,color:"white"}}></i>}
      </div>
      <span style={{fontSize:12,color:checked?DS.success.text:DS.textPrimary,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>{children}</span>
    </div>
  );

  const Notice = ({children, warn}) => (
    <div style={{background:warn?DS.warning.bg:DS.blueTint,border:`1px solid ${warn?DS.warning.border:DS.blueBorder}`,borderRadius:6,padding:"10px 12px",fontSize:12,color:warn?DS.warning.text:DS.textPrimary,lineHeight:1.6,fontFamily:"'Inter',sans-serif"}}>
      {children}
    </div>
  );

  const ProgressBar = () => (
    <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:8,padding:"8px 14px",display:"flex",alignItems:"center",gap:10}}>
      <div style={{flex:1,height:4,background:DS.border,borderRadius:2,overflow:"hidden"}}>
        <div style={{width:`${progress}%`,height:"100%",background:progress===100?DS.success.text:DS.blue,borderRadius:2,transition:"width 0.3s"}}/>
      </div>
      <span style={{fontSize:10,color:progress===100?DS.success.text:DS.blue,whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>{progress}% complete</span>
    </div>
  );

  const ResidentBar = () => residentMatch ? (
    <div style={{...S.card,padding:"8px 14px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <div>
        <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(residentMatch.name)}</div>
        <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Exit: {exitDate} · Meeting: {meetingDate}</div>
      </div>
      <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{section} of {TOTAL_SECTIONS} sections</div>
    </div>
  ) : null;

  const NavButtons = ({onBack, onNext, nextLabel="Next →", nextDisabled=false}) => (
    <div style={{display:"flex",justifyContent:"space-between"}}>
      <button onClick={onBack} style={{...S.btnGhost,fontSize:12}}>← Back</button>
      <button onClick={onNext} disabled={nextDisabled} style={{...S.btn,opacity:nextDisabled?0.4:1}}>{nextLabel}</button>
    </div>
  );

  const handleSave = async () => {
    if(!residentMatch) return;
    setSaving(true);
    const formData = {
      completedAt: TODAY,
      completedBy: currentUser||"PC",
      meetingDate,
      exitDate,
      dischargeType,
      dischargePhone,
      // A
      ...(isD15 ? {leaseYN,leaseApprovedYN,forwardingAddress} : {returningCity,poNotifiedYN,physicalAddressYN,proposedAddress}),
      // B
      employmentAck,
      // C
      transport,
      // D (only for planned discharge)
      compensationYN, bankAccountYN,
      ...(isReturning ? {busMoneyYN} : {}),
      // E
      personalItemsAck,
      // F
      medTransferAck, saTransferAck,
      // G
      addressChecks, addressAck,
    };

    // Save to resident's intakeForms as dischargeMeeting
    const updated = { ...(residentMatch.intakeForms||{}), dischargeMeeting: formData };
    onSaveResident && onSaveResident({...residentMatch, intakeForms: updated});
    setSaving(false);
    setDone(true);
  };

  if(done) return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{...S.card,textAlign:"center",padding:"40px 24px"}}>
        <i className="bi bi-check-circle-fill" style={{fontSize:40,color:DS.success.text,display:"block",marginBottom:16}}></i>
        <div style={{fontSize:16,fontWeight:600,color:DS.textPrimary,marginBottom:8,fontFamily:"'DM Sans',sans-serif"}}>Discharge meeting saved</div>
        <div style={{fontSize:12,color:DS.textSec,marginBottom:24,fontFamily:"'Inter',sans-serif"}}>{displayName(residentMatch.name)} · Saved to Documents tab</div>
        <div style={{fontSize:11,color:DS.textSec,background:DS.pageBg,borderRadius:6,padding:"8px 14px",display:"inline-block",fontFamily:"'Inter',sans-serif"}}>
          Discharge status must be updated separately by admin.
        </div>
      </div>
      <button onClick={()=>{setSection(0);setResidentMatch(null);setResidentName("");setDone(false);}} style={{...S.btnGhost,alignSelf:"center"}}>Start another meeting</button>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* Section 0 — Resident Select */}
      {section===0&&(
        <>
          <div style={{...S.card,padding:0,overflow:"hidden"}}>
            <div style={{padding:"10px 14px",background:DS.blue}}>
              <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>Select Resident</span>
            </div>
            <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:10}}>
              <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:6,padding:"8px 12px",fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>
                Search for the resident you are conducting the discharge meeting with. This form saves to their Documents tab on completion.
              </div>
              <div style={{position:"relative"}}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Resident Name</div>
                {suggestedForDischarge.length>0&&!residentMatch&&(
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:10,fontWeight:600,color:DS.warning.text,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Suggested (exit within 14 days)</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {suggestedForDischarge.map(r=>(
                        <button key={r.id} onClick={()=>{setResidentName(r.name);setResidentMatch(r);setExitDate(r.exitDate||"");}}
                          style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:`1px solid ${DS.warning.border}`,background:DS.warning.bg,color:DS.warning.text,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          {r.name} ({r.daysToExit}d)
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="Search resident..." value={residentName} onChange={e=>{setResidentName(e.target.value);setResidentMatch(null);}}/>
                {nameMatches.length>0&&!residentMatch&&(
                  <div style={{position:"absolute",top:"calc(100%)",left:0,right:0,zIndex:100,background:"white",border:`1px solid ${DS.border}`,borderRadius:6,boxShadow:"0 4px 12px rgba(0,0,0,0.1)"}}>
                    {nameMatches.map(r=>(
                      <div key={r.id} onClick={()=>{setResidentMatch(r);setResidentName(r.name);setExitDate(r.exitDate||"");}}
                        style={{padding:"8px 12px",cursor:"pointer",fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif",borderBottom:`0.5px solid ${DS.border}`}}
                        onMouseEnter={e=>e.currentTarget.style.background=DS.pageBg}
                        onMouseLeave={e=>e.currentTarget.style.background="white"}>
                        {displayName(r.name)} <span style={{color:DS.textSec}}>· {shortFacility(r.facility)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Exit Date</div>
                  <input type="date" style={{...S.input,border:`1px solid ${DS.border}`}} value={exitDate} onChange={e=>setExitDate(e.target.value)}/>
                </div>
                <div>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Meeting Date</div>
                  <input type="date" style={{...S.input,border:`1px solid ${DS.border}`}} value={meetingDate} onChange={e=>setMeetingDate(e.target.value)}/>
                </div>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>PC Conducting Meeting</div>
                <input style={{...S.input,border:`1px solid ${DS.border}`}} value={currentUser||"PC"} readOnly/>
              </div>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <button onClick={()=>setSection(1)} disabled={!residentMatch||!exitDate} style={{...S.btn,opacity:(!residentMatch||!exitDate)?0.4:1}}>Start Meeting →</button>
          </div>
        </>
      )}

      {/* Sections 1-7 */}
      {section>0&&<>
        <ResidentBar/>
        <ProgressBar/>
      </>}

      {/* Section 1 — A. Reporting Instructions */}
      {section===1&&(
        <>
          <div style={{...S.card,padding:0,overflow:"hidden"}}>
            <SectionHdrDC letter="A" title="Reporting Instructions"/>
            <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Discharge Type</div>
                <div style={{display:"flex",gap:8}}>
                  {[{val:"d15",label:"Staying in District 15"},{val:"returning",label:"Returning to Reporting District"}].map(({val,label})=>(
                    <button key={val} onClick={()=>setDischargeType(val)} style={{flex:1,padding:"8px 10px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:500,
                      background:dischargeType===val?DS.blueTint:"white",
                      border:`1.5px solid ${dischargeType===val?DS.blue:DS.border}`,
                      color:dischargeType===val?DS.blue:DS.textSec,fontFamily:"'Inter',sans-serif"}}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {isD15&&(
                <div style={{background:DS.pageBg,borderRadius:8,padding:12,border:`0.5px solid ${DS.border}`,display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{fontSize:11,color:DS.blue,fontWeight:600,fontFamily:"'Inter',sans-serif"}}>Staying in District 15</div>
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Physical lease of at least 6 months?</div>
                    <YNButtons value={leaseYN} onChange={setLeaseYN}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Lease submitted and approved by PO?</div>
                    <YNButtons value={leaseApprovedYN} onChange={setLeaseApprovedYN}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Proposed forwarding address</div>
                    <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="Enter address..." value={forwardingAddress} onChange={e=>setForwardingAddress(e.target.value)}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Discharge phone number</div>
                    <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="(XXX) XXX-XXXX" value={dischargePhone} onChange={e=>setDischargePhone(e.target.value)}/>
                  </div>
                </div>
              )}

              {isReturning&&(
                <div style={{background:DS.pageBg,borderRadius:8,padding:12,border:`0.5px solid ${DS.border}`,display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{fontSize:11,color:DS.blue,fontWeight:600,fontFamily:"'Inter',sans-serif"}}>Returning to Reporting District</div>
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>City and district reporting to</div>
                    <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="e.g. Richmond, District 1..." value={returningCity} onChange={e=>setReturningCity(e.target.value)}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Has PO notified you of reporting instructions?</div>
                    <YNButtons value={poNotifiedYN} onChange={setPoNotifiedYN}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Do you have a physical address?</div>
                    <YNButtons value={physicalAddressYN} onChange={setPhysicalAddressYN}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Proposed address (or N/A)</div>
                    <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="Address or N/A..." value={proposedAddress} onChange={e=>setProposedAddress(e.target.value)}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Discharge phone number</div>
                    <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="(XXX) XXX-XXXX" value={dischargePhone} onChange={e=>setDischargePhone(e.target.value)}/>
                  </div>
                </div>
              )}
            </div>
          </div>
          <NavButtons onBack={()=>setSection(0)} onNext={()=>setSection(2)} nextDisabled={!dischargeType}/>
        </>
      )}

      {/* Section 2 — B. Employment */}
      {section===2&&(
        <>
          <div style={{...S.card,padding:0,overflow:"hidden"}}>
            <SectionHdrDC letter="B" title="Employment"/>
            <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:12}}>
              <Notice>
                <strong>If planning to reside in District 15</strong>, please provide The Dorcus Home with a letter of verification from your employer by <strong>Day 65</strong>. This letter should verify full-time employment in District 15.
              </Notice>
              <Ack checked={employmentAck} onChange={()=>setEmploymentAck(a=>!a)}>
                Resident has been informed of the Day 65 employment verification requirement and understands what is needed.
              </Ack>
            </div>
          </div>
          <NavButtons onBack={()=>setSection(1)} onNext={()=>setSection(3)} nextDisabled={!employmentAck}/>
        </>
      )}

      {/* Section 3 — C. Transportation */}
      {section===3&&(
        <>
          <div style={{...S.card,padding:0,overflow:"hidden"}}>
            <SectionHdrDC letter="C" title="Transportation"/>
            <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Means of transportation back to reporting city</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {["Friend / Family","Train","Bus","Other"].map(t=>(
                    <button key={t} onClick={()=>setTransport(t)}
                      style={{padding:"7px 14px",borderRadius:6,cursor:"pointer",fontSize:12,
                        background:transport===t?DS.blueTint:"white",
                        border:`1.5px solid ${transport===t?DS.blue:DS.border}`,
                        color:transport===t?DS.blue:DS.textSec,fontFamily:"'Inter',sans-serif"}}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <NavButtons onBack={()=>setSection(2)} onNext={()=>setSection(4)} nextDisabled={!transport}/>
        </>
      )}

      {/* Section 4 — D. Financial */}
      {section===4&&(
        <>
          <div style={{...S.card,padding:0,overflow:"hidden"}}>
            <SectionHdrDC letter="D" title="Financial"/>
            <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Are you up to date on your compensation?</div>
                <YNButtons value={compensationYN} onChange={setCompensationYN}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Do you have a bank account in your reporting district?</div>
                <YNButtons value={bankAccountYN} onChange={setBankAccountYN}/>
              </div>
              {isReturning&&(
                <div style={{background:DS.pageBg,borderRadius:8,padding:12,border:`0.5px solid ${DS.border}`}}>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Only if returning to reporting district</div>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Do you have money saved for a bus or train ticket?</div>
                  <YNButtons value={busMoneyYN} onChange={setBusMoneyYN}/>
                </div>
              )}
            </div>
          </div>
          <NavButtons onBack={()=>setSection(3)} onNext={()=>setSection(5)} nextDisabled={!compensationYN||!bankAccountYN||(isReturning&&!busMoneyYN)}/>
        </>
      )}

      {/* Section 5 — E. Personal Items */}
      {section===5&&(
        <>
          <div style={{...S.card,padding:0,overflow:"hidden"}}>
            <SectionHdrDC letter="E" title="Personal Items"/>
            <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:12}}>
              <Notice warn>
                If a friend or family member is <strong>not</strong> picking you up, we suggest shipping your items to a friend/family address or your proposed home address. <strong>The Dorcus Home will not keep your items after Day 90.</strong><br/><br/>
                These items include: clothes, shoes, TV, bikes, electronics, mail, important documentation, wallets, or anything else you own.
              </Notice>
              <Ack checked={personalItemsAck} onChange={()=>setPersonalItemsAck(a=>!a)}>
                Resident acknowledges that Dorcus Home will not store personal items after Day 90 and has made arrangements accordingly.
              </Ack>
            </div>
          </div>
          <NavButtons onBack={()=>setSection(4)} onNext={()=>setSection(6)} nextDisabled={!personalItemsAck}/>
        </>
      )}

      {/* Section 6 — F. Transfer of Services */}
      {section===6&&(
        <>
          <div style={{...S.card,padding:0,overflow:"hidden"}}>
            <SectionHdrDC letter="F" title="Transfer of Services"/>
            <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:12}}>
              <div style={{fontSize:11,fontWeight:600,color:DS.blue,fontFamily:"'Inter',sans-serif"}}>Medical</div>
              <Notice>If you were receiving medical treatment in Roanoke and are returning to your district, be sure to get this information transferred to a local doctor or facility in your area.</Notice>
              <Ack checked={medTransferAck} onChange={()=>setMedTransferAck(a=>!a)}>
                Resident has been informed about transferring medical treatment records and services.
              </Ack>
              <div style={{height:1,background:DS.border}}/>
              <div style={{fontSize:11,fontWeight:600,color:DS.blue,fontFamily:"'Inter',sans-serif"}}>Substance Abuse</div>
              <Notice>If you were in an active substance abuse program in Roanoke and are returning to your district, ask for a referral from your current program administrator to a local substance abuse program in your reporting district.</Notice>
              <Ack checked={saTransferAck} onChange={()=>setSaTransferAck(a=>!a)}>
                Resident has been informed about substance abuse treatment transfer and referral options.
              </Ack>
            </div>
          </div>
          <NavButtons onBack={()=>setSection(5)} onNext={()=>setSection(7)} nextDisabled={!medTransferAck||!saTransferAck}/>
        </>
      )}

      {/* Section 7 — G. Change of Address */}
      {section===7&&(
        <>
          <div style={{...S.card,padding:0,overflow:"hidden"}}>
            <SectionHdrDC letter="G" title="Change of Address"/>
            <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:12}}>
              <Notice warn>
                On Day 91 your address cannot be 801 Shenandoah Ave or 23 24th Street. If you do not have an address, you may use your emergency contact's address. <strong>Dorcus Home is not required or obligated to forward your mail.</strong><br/><br/>
                You are required to be off the premises by <strong>9:30 AM on your 90th day</strong> unless you have written approval from the Director.
              </Notice>
              <div style={{fontSize:11,fontWeight:500,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Please update your address with the following:</div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {ADDRESS_ITEMS.map(item=>(
                  <div key={item} onClick={()=>setAddressChecks(c=>({...c,[item]:!c[item]}))}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:6,cursor:"pointer",
                      background:addressChecks[item]?DS.success.bg:"white",
                      border:`0.5px solid ${addressChecks[item]?DS.success.border:DS.border}`}}>
                    <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${addressChecks[item]?DS.success.text:DS.border}`,background:addressChecks[item]?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {addressChecks[item]&&<i className="bi bi-check" style={{fontSize:9,color:"white"}}></i>}
                    </div>
                    <span style={{fontSize:12,color:addressChecks[item]?DS.success.text:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{item}</span>
                  </div>
                ))}
              </div>
              <Ack checked={addressAck} onChange={()=>setAddressAck(a=>!a)}>
                Resident acknowledges all change of address requirements and the Day 90 move-out policy.
              </Ack>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <button onClick={()=>setSection(6)} style={{...S.btnGhost,fontSize:12}}>← Back</button>
            <button onClick={handleSave} disabled={!addressAck||saving}
              style={{...S.btnCTA,opacity:(!addressAck||saving)?0.4:1}}>
              {saving?"Saving...":"Save Discharge Meeting →"}
            </button>
          </div>
        </>
      )}

    </div>
  );
}


function NotificationsModule({ residents, onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [prepareNotif, setPrepareNotif] = useState(null);
  const [sendNotes, setSendNotes] = useState("");
  const [additionalRecipients, setAdditionalRecipients] = useState("");
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [showBulkComposer, setShowBulkComposer] = useState(false);
  const [bulkTitle, setBulkTitle] = useState("");
  const [bulkBody, setBulkBody] = useState("");
  const [bulkRecipients, setBulkRecipients] = useState("all"); // "all" | "Dorcus 1 - 801" | "Dorcus 2 - 24th St"
  const [bulkSending, setBulkSending] = useState(false);

  useEffect(() => {
    const unsub = subscribeToNotifications(all => setNotifications(all.filter(n=>!n.cleared)));
    return unsub;
  }, []);

  // ── Compensation overdue check — fires once per day ───────────────────────
  useEffect(() => {
    if(!residents.length) return;
    const checkKey = `dorcus_comp_overdue_check_${TODAY}`;
    try { if(localStorage.getItem(checkKey)) return; } catch{}
    const twoWeeksAgo = daysFromToday(-14);
    const overdueResidents = residents.filter(r => {
      if(r.status !== "active") return false;
      if(!r.entryDate || r.entryDate > twoWeeksAgo) return false;
      if(!r.employed) return false;
      const lastComp = r.lastCompensationDate;
      if(lastComp && lastComp >= twoWeeksAgo) return false;
      return true;
    });
    if(overdueResidents.length > 0) {
      saveNotification({
        type:"compensation_overdue",
        title:`Compensation Overdue — ${overdueResidents.length} resident${overdueResidents.length>1?"s":""}`,
        body:`${overdueResidents.map(r=>displayName(r.name)).join(", ")} ${overdueResidents.length>1?"have":"has"} not had compensation collected in 2+ weeks.`,
        residentIds: overdueResidents.map(r=>r.id),
        read:false, actioned:false, createdAt:TODAY,
      });
      try { localStorage.setItem(checkKey,"true"); } catch{}
    }
  }, [residents]);

  // ── PC Meeting overdue alert — 14 days, fires once per day ───────────────
  useEffect(() => {
    if(!residents.length) return;
    const checkKey = `dorcus_meeting_overdue_${TODAY}`;
    try { if(localStorage.getItem(checkKey)) return; } catch{}
    const fourteenDaysAgo = daysFromToday(-14);
    const overdueResidents = residents.filter(r => {
      if(r.status !== "active") return false;
      const meetings = r.meetings||[];
      if(meetings.length === 0) {
        // No meetings at all — check if they've been here 14+ days
        return r.entryDate && r.entryDate <= fourteenDaysAgo;
      }
      const lastMeeting = [...meetings].sort((a,b)=>(b.date||"").localeCompare(a.date||""))[0];
      return (lastMeeting.date||"") <= fourteenDaysAgo;
    });
    if(overdueResidents.length > 0) {
      overdueResidents.forEach(r => {
        saveNotification({
          type:"meeting_overdue",
          title:`PC Meeting Overdue — ${displayName(r.name)}`,
          body:`No PC meeting in 14+ days. Last meeting: ${[...(r.meetings||[])].sort((a,b)=>b.date.localeCompare(a.date))[0]?.date||"Never"}`,
          residentId: r.id,
          residentName: r.name,
          read:false, actioned:false, createdAt:TODAY,
        });
      });
      try { localStorage.setItem(checkKey,"true"); } catch{}
    }
  }, [residents]);

  // ── Weekly summary notification — Sunday at 12pm ─────────────────────────
  useEffect(() => {
    if(!residents.length) return;
    const now = new Date(NOW_NY());
    const isSunday = now.getDay() === 0;
    const isAfterNoon = now.getHours() >= 12;
    if(!isSunday || !isAfterNoon) return;
    const weekKey = `dorcus_weekly_summary_${TODAY}`;
    try { if(localStorage.getItem(weekKey)) return; } catch{}
    // Build weekly summary per house
    const FACILITIES = ["Dorcus 1 - 801","Dorcus 2 - 24th St"];
    FACILITIES.forEach(fac => {
      const facResidents = residents.filter(r=>r.status==="active"&&r.facility===fac);
      const employed = facResidents.filter(r=>r.employed);
      const body = `${shortFacility(fac)} — ${employed.length} employed residents tracked this week. View Reports → Compensation Ledger for full details.`;
      saveNotification({
        type:"weekly_compensation_summary",
        title:`Weekly Summary — ${shortFacility(fac)}`,
        body,
        facility:fac,
        weekOf:TODAY,
        read:false, actioned:false, createdAt:TODAY,
      });
    });
    try { localStorage.setItem(weekKey,"true"); } catch{}
  }, [residents]);

  // ── Monthly summary notification — 1st of month ──────────────────────────
  useEffect(() => {
    if(!residents.length) return;
    const now = new Date(NOW_NY());
    const isFirstOfMonth = now.getDate() === 1;
    if(!isFirstOfMonth) return;
    const monthKey = `dorcus_monthly_summary_${TODAY}`;
    try { if(localStorage.getItem(monthKey)) return; } catch{}
    const prevMonth = new Date(now.getFullYear(), now.getMonth()-1, 1);
    const prevMonthStr = prevMonth.toLocaleString("en-US",{month:"long",year:"numeric"});
    const FACILITIES = ["Dorcus 1 - 801","Dorcus 2 - 24th St"];
    FACILITIES.forEach(fac => {
      const facResidents = residents.filter(r=>r.status==="active"&&r.facility===fac);
      const withMeds = facResidents.filter(r=>(r.medications||[]).length>0);
      saveNotification({
        type:"monthly_ua_med_summary",
        title:`Monthly Summary — ${shortFacility(fac)} — ${prevMonthStr}`,
        body:`${shortFacility(fac)} — ${facResidents.length} residents. ${withMeds.length} on medications. View Reports → UA/BAC Results and Medication Compliance for ${prevMonthStr} details.`,
        facility:fac,
        month:prevMonthStr,
        read:false, actioned:false, createdAt:TODAY,
      });
    });
    try { localStorage.setItem(monthKey,"true"); } catch{}
  }, [residents]);

  const [notifTab, setNotifTab] = React.useState("unread");
  const [hiddenNotifIds, setHiddenNotifIds] = React.useState([]);
  const thirtyDaysAgo = daysFromToday(-30);
  const unread = notifications.filter(n => !n.read);
  const displayedNotifications = notifTab === "unread"
    ? notifications.filter(n => !n.read)
    : notifications.filter(n => n.createdAt >= thirtyDaysAgo && !hiddenNotifIds.includes(n.id));

  const handleBulkSend = async () => {
    if (!bulkTitle.trim() || !bulkBody.trim()) return;
    setBulkSending(true);
    // All staff see the same notifications inbox — one notification covers everyone
    // If filtered by facility, tag it so facility-specific staff see it prominently
    await saveNotification({
      type: "bulk_message",
      title: bulkTitle.trim(),
      body: bulkBody.trim(),
      facility: bulkRecipients === "all" ? null : bulkRecipients,
      read: false,
      actioned: false,
      createdAt: TODAY,
      sentBy: "Admin",
    });
    auditAction("bulk_notification_sent", { title:bulkTitle.trim(), recipients:bulkRecipients });
    setBulkSending(false);
    setShowBulkComposer(false);
    setBulkTitle("");
    setBulkBody("");
    setBulkRecipients("all");
    setSuccessMsg(`Notification sent to ${bulkRecipients === "all" ? "all staff" : bulkRecipients + " staff"}.`);
    setTimeout(() => setSuccessMsg(""), 5000);
  };

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n=>!n.read).map(n=>n.id);
    await Promise.all(unreadIds.map(id => markNotificationRead(id)));
  };

  const clearRead = async () => {
    const readIds = notifications.filter(n=>n.read).map(n=>n.id);
    const { db } = await import("./firebase");
    const { doc, updateDoc } = await import("firebase/firestore");
    await Promise.all(readIds.map(id => updateDoc(doc(db,"notifications",id), { cleared:true })));
  };

  const markRead = async (id) => {
    await markNotificationRead(id);
  };

  const handleLogAsSent = async () => {
    if (!prepareNotif) return;
    setSending(true);
    const resident = residents.find(r => r.id === prepareNotif.residentId);
    const sentEntry = {
      noaSentDate: TODAY,
      noaSentTo: prepareNotif.districtContact || "District Personnel",
      noaSentBy: prepareNotif.assignedTo || "Admin",
      noaSentNotes: sendNotes,
    };
    // Update resident intake form record
    if (resident) {
      const updatedForms = {
        ...(resident.intakeForms||{}),
        noticeOfArrival: {
          ...(resident.intakeForms?.noticeOfArrival||{}),
          ...sentEntry,
        }
      };
      await saveResident({ ...resident, intakeForms: updatedForms });
    }
    // Mark notification as actioned
    await actionNotification(prepareNotif.id, sentEntry);
    // Create a resolved notification
    await saveNotification({
      type: "noa_sent",
      title: `Notice of Arrival sent — ${prepareNotif.residentName}`,
      body: `Logged by Admin · Sent to ${sentEntry.noaSentTo}`,
      residentId: prepareNotif.residentId,
      residentName: prepareNotif.residentName,
      read: true,
      actioned: true,
      createdAt: TODAY,
    });
    setSending(false);
    setPrepareNotif(null);
    setSendNotes("");
    setAdditionalRecipients("");
    setSuccessMsg(`Notice of Arrival logged as sent — ${prepareNotif.residentName}`);
    setTimeout(() => setSuccessMsg(""), 5000);
  };

  if (prepareNotif) {
    const resident = residents.find(r => r.id === prepareNotif.residentId);
    const noa = resident?.intakeForms?.noticeOfArrival || {};
    return (
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={()=>setPrepareNotif(null)} style={{...S.btnGhost,fontSize:11,padding:"4px 10px"}}><i className="bi bi-chevron-left" style={{fontSize:10}}></i> Dashboard</button>
          <div style={{fontSize:14,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Notice of Arrival — {prepareNotif.residentName}</div>
        </div>
        <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:6,padding:"8px 12px",fontSize:11,color:DS.blue,display:"flex",alignItems:"center",gap:8}}>
          <i className="bi bi-info-circle-fill" style={{fontSize:13,flexShrink:0}}></i>
          Day 1 intake completed by {prepareNotif.completedBy||"PC"} on {prepareNotif.completedDate||TODAY}. Review and log as sent to district personnel.
        </div>
        <div style={{...S.card}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <SectionHdr>Notice of Arrival Summary</SectionHdr>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {[
              {label:"Resident", val:prepareNotif.residentName},
              {label:"DOC#", val:resident?.doc||"—"},
              {label:"Arrived From", val:noa.arrivalFrom||resident?.referralInstitution||"—"},
              {label:"Date of Arrival", val:noa.arrivalDate||resident?.entryDate||"—"},
              {label:"P&P Officer", val:noa.ppOfficer||resident?.referredBy||"—"},
              {label:"Urine Screen", val:noa.uaResult?`${noa.uaResult} · ${noa.uaDate||TODAY}`:"—"},
              {label:"Classification", val:resident?.classification||"—"},
              {label:"Scheduled Release", val:resident?.releaseDate||"—"},
            ].map(({label,val})=>(
              <div key={label}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:2,fontFamily:"'Inter',sans-serif"}}>{label}</div>
                <div style={{fontSize:12,color:DS.textPrimary,fontWeight:500,fontFamily:"'Inter',sans-serif"}}>{val}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{...S.card}}>
          <SectionHdr>Send To</SectionHdr>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5,fontFamily:"'Inter',sans-serif"}}>District Contact</div>
              <div style={{background:DS.success.bg,border:`1px solid ${DS.success.border}`,borderRadius:6,padding:"8px 12px",display:"flex",alignItems:"center",gap:10}}>
                <i className="bi bi-person-check-fill" style={{fontSize:14,color:DS.success.text,flexShrink:0}}></i>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{prepareNotif.districtContact||`P&P District ${resident?.ppDistrict||resident?.sentencingDistrict||""}`}</div>
                  <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>From resident record</div>
                </div>
              </div>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Additional Recipients (optional)</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="Add email address..." value={additionalRecipients} onChange={e=>setAdditionalRecipients(e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Notes (optional)</div>
              <textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} placeholder="Any notes to accompany this notice..." value={sendNotes} onChange={e=>setSendNotes(e.target.value)}/>
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",paddingTop:8}}>
          <button onClick={()=>setPrepareNotif(null)} style={{...S.btnGhost}}>Cancel</button>
          {prepareNotif.type==="nod_ready" ? (
            <button onClick={async()=>{
              const resident = residents.find(r=>r.id===prepareNotif.residentId);
              const dm = resident?.intakeForms?.dischargeMeeting || {};
              const logoBase64 = await fetchLogoBase64();
              const html = generateNODDocument(resident||{name:prepareNotif.residentName}, dm, currentUserName, logoBase64);
              const win = window.open("","_blank");
              if(win){
                win.document.write(html);
                win.document.close();
                const img = win.document.querySelector("img");
                if(img){
                  img.onload = ()=>win.print();
                  img.onerror = ()=>win.print();
                  if(img.complete) win.print();
                } else { win.print(); }
              }
              markRead(prepareNotif.id);
              setPrepareNotif(null);
            }} style={{...S.btn,background:DS.error.text}}>
              <i className="bi bi-printer-fill" style={{fontSize:11,marginRight:4}}></i>
              Generate &amp; Print NOD
            </button>
          ) : (
            <button onClick={handleLogAsSent} disabled={sending} style={{...S.btn,opacity:sending?0.6:1}}>
              <i className="bi bi-check-lg" style={{fontSize:11,marginRight:4}}></i>
              {sending?"Saving...":"Log as Sent"}
            </button>
          )}
        </div>
      </div>
    );
  }

  const typeConfig = {
    noa_ready:      { icon:"bi-send-fill",              iconBg:"#E8F0FB",      iconColor:DS.blue,          badge:"Action needed",   badgeStyle:{background:DS.warning.bg,color:DS.warning.text,border:`1px solid ${DS.warning.border}`},   dotColor:DS.blue },
    nod_ready:      { icon:"bi-file-earmark-text-fill", iconBg:"#FDECEA",      iconColor:DS.error.text,    badge:"Generate NOD",    badgeStyle:{background:DS.error.bg,color:DS.error.text,border:`1px solid ${DS.error.border}`},         dotColor:DS.error.text },
    mh_referral:    { icon:"bi-clipboard2-pulse-fill",  iconBg:"#F0EEFB",      iconColor:DS.purple.text,   badge:"Review needed",   badgeStyle:{background:DS.purple.bg,color:DS.purple.text,border:`1px solid ${DS.purple.border}`},     dotColor:DS.purple.text },
    discharge_approaching: { icon:"bi-calendar-event-fill", iconBg:DS.warning.bg, iconColor:DS.warning.text, badge:null, dotColor:DS.yellow },
    noa_sent:       { icon:"bi-check-circle-fill",      iconBg:DS.success.bg,  iconColor:DS.success.text,  badge:null, dotColor:null },
    money_request:  { icon:"bi-cash-stack",             iconBg:DS.success.bg,  iconColor:DS.success.text,  badge:"Pending approval", badgeStyle:{background:DS.warning.bg,color:DS.warning.text,border:`1px solid ${DS.warning.border}`}, dotColor:DS.success.text },
    day_of_discharge: { icon:"bi-box-arrow-right",      iconBg:DS.warning.bg,  iconColor:DS.warning.text,  badge:"Resident discharged", badgeStyle:{background:DS.warning.bg,color:DS.warning.text,border:`1px solid ${DS.warning.border}`}, dotColor:DS.warning.text },
    worklog_weekly: { icon:"bi-briefcase-fill",         iconBg:DS.neutral.bg,  iconColor:DS.textSec,       badge:"Ready to download", badgeStyle:{background:DS.neutral.bg,color:DS.neutral.text,border:`1px solid ${DS.neutral.border}`}, dotColor:DS.textSec },
    bulk_message:   { icon:"bi-megaphone-fill",         iconBg:"#EEF2FF",      iconColor:"#4338CA",        badge:null, dotColor:"#4338CA" },
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {successMsg&&(
        <div style={{background:DS.success.bg,border:`1px solid ${DS.success.border}`,borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
          <i className="bi bi-check-circle-fill" style={{fontSize:16,color:DS.success.text}}></i>
          <span style={{fontSize:12,color:DS.success.text,fontFamily:"'Inter',sans-serif"}}>{successMsg}</span>
        </div>
      )}
      <div style={{...S.card,padding:0,overflow:"hidden"}}>
        <div style={{padding:"9px 12px",borderBottom:`0.5px solid ${DS.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:11,fontWeight:600,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>Notifications</span>
            {unread.length>0&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:DS.error.bg,color:DS.error.text,border:`1px solid ${DS.error.border}`,fontWeight:600}}>{unread.length} unread</span>}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {unread.length>0&&<button onClick={markAllRead} style={{fontSize:10,color:DS.blue,background:"transparent",border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Mark all read</button>}
            {notifTab==="all"&&displayedNotifications.some(n=>n.read)&&<button onClick={()=>setHiddenNotifIds(p=>[...p,...displayedNotifications.filter(n=>n.read).map(n=>n.id)])} style={{fontSize:10,color:DS.textSec,background:"transparent",border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Clear read</button>}
            <button onClick={()=>setShowBulkComposer(s=>!s)} style={{fontSize:10,padding:"4px 10px",borderRadius:6,border:`1px solid ${DS.blue}`,color:DS.blue,background:showBulkComposer?DS.blueTint:"transparent",cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:4}}>
              <i className="bi bi-megaphone-fill" style={{fontSize:9}}></i> Send Notification
            </button>
          </div>
        </div>
        <div style={{display:"flex",borderBottom:`0.5px solid ${DS.border}`}}>
          {[{id:"unread",label:`Unread (${unread.length})`},{id:"all",label:"Last 30 days"}].map(t=>(
            <button key={t.id} onClick={()=>setNotifTab(t.id)}
              style={{flex:1,padding:"8px 12px",fontSize:12,fontWeight:notifTab===t.id?600:400,
                color:notifTab===t.id?DS.blue:DS.textSec,background:"transparent",border:"none",
                borderBottom:`2px solid ${notifTab===t.id?DS.blue:"transparent"}`,
                cursor:"pointer",fontFamily:"'Inter',sans-serif",transition:"all 0.15s"}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Bulk composer panel */}
        {showBulkComposer&&(
          <div style={{padding:"12px 14px",borderBottom:`1px solid ${DS.border}`,background:"#F8F9FF"}}>
            <div style={{fontSize:11,fontWeight:600,color:DS.textPrimary,marginBottom:10,fontFamily:"'DM Sans',sans-serif"}}>Send Staff Notification</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Recipients</div>
                <div style={{display:"flex",gap:6}}>
                  {[{val:"all",label:"All Staff"},{val:"Dorcus 1 - 801",label:"D1 Staff"},{val:"Dorcus 2 - 24th St",label:"D2 Staff"}].map(opt=>(
                    <button key={opt.val} onClick={()=>setBulkRecipients(opt.val)}
                      style={{padding:"5px 12px",fontSize:11,borderRadius:6,cursor:"pointer",fontWeight:bulkRecipients===opt.val?600:400,
                        background:bulkRecipients===opt.val?DS.blue:"white",
                        border:`1px solid ${bulkRecipients===opt.val?DS.blue:DS.border}`,
                        color:bulkRecipients===opt.val?"white":DS.textSec}}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Title *</div>
                <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="e.g. Schedule Change — Friday" value={bulkTitle} onChange={e=>setBulkTitle(e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Message *</div>
                <textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical"}} placeholder="Type your message here..." value={bulkBody} onChange={e=>setBulkBody(e.target.value)}/>
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button onClick={()=>{setShowBulkComposer(false);setBulkTitle("");setBulkBody("");setBulkRecipients("all");}} style={{...S.btnGhost,fontSize:11}}>Cancel</button>
                <button onClick={handleBulkSend} disabled={!bulkTitle.trim()||!bulkBody.trim()||bulkSending}
                  style={{...S.btn,fontSize:11,opacity:(!bulkTitle.trim()||!bulkBody.trim()||bulkSending)?0.4:1}}>
                  <i className="bi bi-megaphone-fill" style={{fontSize:10,marginRight:4}}></i>
                  {bulkSending?"Sending...":"Send"}
                </button>
              </div>
            </div>
          </div>
        )}
        {displayedNotifications.length===0&&(
          <div style={{padding:"24px",textAlign:"center",fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{notifTab==="unread"?"No unread notifications":"No notifications in the last 30 days"}</div>
        )}
        {displayedNotifications.map(n=>{
          const cfg = typeConfig[n.type]||typeConfig.noa_sent;
          return (
            <div key={n.id} style={{display:"flex",gap:10,padding:"10px 12px",borderBottom:`0.5px solid ${DS.border}`,background:n.read?"white":"#F8FAFF",cursor:n.actioned?"default":"pointer"}}
              onClick={()=>!n.actioned&&markRead(n.id)}>
              <div style={{width:30,height:30,borderRadius:6,background:cfg.iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <i className={`bi ${cfg.icon}`} style={{fontSize:13,color:cfg.iconColor}}></i>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:n.read?400:500,color:n.read?DS.textSec:DS.textPrimary,marginBottom:2,lineHeight:1.3,fontFamily:"'Inter',sans-serif"}}>{n.title}</div>
                <div style={{fontSize:11,color:DS.textSec,lineHeight:1.3,fontFamily:"'Inter',sans-serif"}}>{n.body}</div>
                {!n.actioned&&cfg.badge&&(
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:5}}>
                    <span style={{fontSize:10,padding:"2px 7px",borderRadius:10,fontWeight:500,...cfg.badgeStyle}}>{cfg.badge}</span>
                    {n.type==="noa_ready"&&(
                      <button onClick={e=>{e.stopPropagation();setPrepareNotif(n);}} style={{fontSize:10,padding:"3px 10px",borderRadius:5,border:`1.5px solid ${DS.blue}`,color:DS.blue,background:"transparent",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        Prepare &amp; Send →
                      </button>
                    )}
                    {n.type==="nod_ready"&&(
                      <button onClick={e=>{e.stopPropagation();setPrepareNotif(n);}} style={{fontSize:10,padding:"3px 10px",borderRadius:5,border:`1.5px solid ${DS.error.text}`,color:DS.error.text,background:"transparent",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        Generate NOD →
                      </button>
                    )}
                    {n.type==="mh_referral"&&(
                      <button onClick={e=>{e.stopPropagation();onNavigate("residents");}} style={{fontSize:10,padding:"3px 10px",borderRadius:5,border:`1.5px solid ${DS.blue}`,color:DS.blue,background:"transparent",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        View profile →
                      </button>
                    )}
                    {n.type==="money_request"&&(
                      <button onClick={async e=>{
                        e.stopPropagation();
                        await actionNotification(n.id,{completedDate:TODAY,completedBy:"Admin"});
                        await saveNotification({
                          type:"noa_sent",
                          title:`Money Request Complete — ${n.residentName}`,
                          body:`Funds disbursed and confirmed · ${TODAY}`,
                          residentId:n.residentId,
                          residentName:n.residentName,
                          read:true,actioned:true,createdAt:TODAY,
                        });
                      }} style={{fontSize:10,padding:"3px 10px",borderRadius:5,border:`1.5px solid ${DS.success.text}`,color:DS.success.text,background:"transparent",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        Mark Complete →
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                <div style={{fontSize:10,color:DS.textSec,whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>{n.createdAt||"Today"}</div>
                {!n.read&&cfg.dotColor&&<div style={{width:7,height:7,borderRadius:"50%",background:cfg.dotColor}}></div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── D6 Quick Access — Customizable ───────────────────────────────────────────
const QA_ALL_LINKS = [
  { id:"residents",    label:"Residents",       icon:"bi-people-fill",           iconBg:"#E8F0FB", iconColor:"#1B5EBE" },
  { id:"inout",        label:"In / Out Board",  icon:"bi-door-open-fill",        iconBg:"#E6F4EC", iconColor:"#1A6B35" },
  { id:"med_log",      label:"Medication Log",  icon:"bi-capsule-pill",          iconBg:"#F0EEFB", iconColor:"#4A3AAA" },
  { id:"referrals",    label:"Referrals",       icon:"bi-send-fill",             iconBg:"#FFF3CD", iconColor:"#7A4F00" },
  { id:"ua_bac",       label:"Urine & BAC",        icon:"bi-droplet-half",          iconBg:"#E6F6F4", iconColor:"#0D5C54" },
  { id:"headcount",    label:"Headcount",       icon:"bi-hash",                  iconBg:"#FDECEA", iconColor:"#8B1A1A" },
  { id:"shift",        label:"Shift Reports",   icon:"bi-pencil-fill",           iconBg:"#FEF0E6", iconColor:"#7A3300" },
  { id:"daily_log",    label:"Daily Log",       icon:"bi-journal-text",          iconBg:"#E6F1FB", iconColor:"#0C447C" },
  { id:"pc_intake",    label:"Intake",          icon:"bi-clipboard2-check-fill", iconBg:"#E8F0FB", iconColor:"#1B5EBE" },
  { id:"pc_discharge", label:"Discharge",       icon:"bi-box-arrow-right",       iconBg:"#E6F4EC", iconColor:"#1A6B35" },
  { id:"reports",      label:"Reports",         icon:"bi-bar-chart-fill",        iconBg:"#F0F4FA", iconColor:"#4A5568" },
];
const QA_DEFAULT = ["residents","inout","med_log","referrals"];

function QuickAccessPanel({ onNavigate, uid, residents, outCount, nextMedCall, referrals, uaDueCount, lastHeadcount, lastShift, pendingIntake, exitingWeek }) {
  const [order, setOrder] = useState(() => {
    try { const s=localStorage.getItem("qa_order_"+uid); return s?JSON.parse(s):QA_DEFAULT; } catch{ return QA_DEFAULT; }
  });
  const [editMode, setEditMode] = useState(false);
  const [dragSrc, setDragSrc] = useState(null);

  useEffect(() => {
    try { localStorage.setItem("qa_order_"+uid, JSON.stringify(order)); } catch{}
    // Also save to Firestore
    if(uid) { import("firebase/firestore").then(({doc,updateDoc})=>{
      import("./firebase").then(({db})=>{ updateDoc(doc(db,"users",uid),{quickAccess:order}).catch(()=>{}); }).catch(()=>{});
    }).catch(()=>{}); }
  }, [order]);

  const getStat = (id) => {
    const active = (residents||[]).filter(r=>r.status==="active");
    switch(id) {
      case "residents":    return `${active.length} active`;
      case "inout":        return `${outCount||0} out now`;
      case "med_log":      return nextMedCall?`Next: ${nextMedCall.timeStr}`:"No calls today";
      case "referrals":    return `${(referrals||[]).filter(r=>!["archived","rejected","active"].includes(r.stage||r.status)).length} in pipeline`;
      case "ua_bac":       return `${uaDueCount||0} due today`;
      case "headcount":    {
        if(!lastHeadcount) return "Not done";
        const m = minutesSince(lastHeadcount.submittedAt);
        return m>=60?`Last: ${Math.floor(m/60)}h ${m%60}min ago`:`Last: ${m}min ago`;
      }
      case "shift":        return lastShift?`Last: ${(lastShift.shift||"").split(" ")[0]}`:"None today";
      case "pc_intake":    return `${pendingIntake||0} pending`;
      case "pc_discharge": return `${exitingWeek||0} this week`;
      case "reports":      return "";
      default: return "";
    }
  };

  const activeLinks = order.filter(id=>QA_ALL_LINKS.find(l=>l.id===id));
  const inactiveLinks = QA_ALL_LINKS.filter(l=>!order.includes(l.id));

  const toggleLink = (id) => {
    if(order.includes(id)) setOrder(o=>o.filter(x=>x!==id));
    else if(order.length<4) setOrder(o=>[...o,id]);
  };

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <span style={{fontSize:11,fontWeight:600,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:"'Inter',sans-serif"}}>Quick Access</span>
        <button onClick={()=>setEditMode(true)}
          style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer",display:"flex",alignItems:"center",gap:5,fontFamily:"'Inter',sans-serif"}}>
          <i className="bi bi-pencil" style={{fontSize:10}}></i> Customize
        </button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:`repeat(${Math.max(1,Math.min(activeLinks.length,4))},1fr)`,gap:10}}>
        {activeLinks.map(id=>{
          const link=QA_ALL_LINKS.find(l=>l.id===id); if(!link) return null;
          const stat=getStat(id);
          return (
            <button key={id} onClick={()=>onNavigate(id)}
              style={{...S.card,cursor:"pointer",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:6,padding:"14px 10px",transition:"border-color 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=DS.blue}
              onMouseLeave={e=>e.currentTarget.style.borderColor=DS.border}>
              <div style={{width:38,height:38,borderRadius:10,background:link.iconBg,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <i className={`bi ${link.icon}`} style={{fontSize:17,color:link.iconColor}}></i>
              </div>
              <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{link.label}</div>
              {stat&&<div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{stat}</div>}
            </button>
          );
        })}
      </div>
      {editMode&&(
        <div style={{position:"fixed",inset:0,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:20}} onClick={()=>setEditMode(false)}>
          <div style={{background:"white",borderRadius:10,width:"100%",maxWidth:440,maxHeight:"88vh",display:"flex",flexDirection:"column",overflow:"hidden",border:`0.5px solid ${DS.border}`}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:"12px 16px",background:DS.blue,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>Customize Quick Access</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>Choose up to 4 · drag to reorder</div>
              </div>
              <button onClick={()=>setEditMode(false)} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:5,color:"white",fontSize:11,padding:"3px 10px",cursor:"pointer"}}>✕</button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"14px 16px"}}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Active — drag to reorder</div>
              {activeLinks.length===0&&<div style={{fontSize:12,color:DS.textSec,padding:8,textAlign:"center"}}>No active slots</div>}
              {activeLinks.map((id,idx)=>{
                const link=QA_ALL_LINKS.find(l=>l.id===id); if(!link) return null;
                return (
                  <div key={id} draggable
                    onDragStart={e=>{setDragSrc(id);e.dataTransfer.effectAllowed="move";}}
                    onDragOver={e=>e.preventDefault()}
                    onDrop={e=>{e.preventDefault();if(!dragSrc||dragSrc===id)return;setOrder(o=>{const a=[...o];const si=a.indexOf(dragSrc),ti=a.indexOf(id);a.splice(si,1);a.splice(ti,0,dragSrc);return a;});setDragSrc(null);}}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:7,border:`0.5px solid ${DS.blueBorder}`,background:DS.blueTint,marginBottom:6,cursor:"grab"}}>
                    <i className="bi bi-grip-vertical" style={{color:DS.border,fontSize:14,flexShrink:0}}></i>
                    <div style={{width:28,height:28,borderRadius:7,background:link.iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <i className={`bi ${link.icon}`} style={{fontSize:13,color:link.iconColor}}></i>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{link.label}</div>
                      <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{getStat(id)||"—"}</div>
                    </div>
                    <span style={{fontSize:9,padding:"2px 7px",borderRadius:8,background:DS.blueTint,color:DS.blue,border:`1px solid ${DS.blueBorder}`}}>Slot {idx+1}</span>
                    <button onClick={()=>toggleLink(id)} style={{width:20,height:20,borderRadius:"50%",border:`1.5px solid ${DS.error.border}`,background:DS.error.bg,color:DS.error.text,fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>−</button>
                  </div>
                );
              })}
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",margin:"10px 0 6px",fontFamily:"'Inter',sans-serif"}}>Available to add</div>
              {inactiveLinks.map(link=>(
                <div key={link.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:7,border:`0.5px solid ${DS.border}`,background:DS.pageBg,marginBottom:6}}>
                  <div style={{width:14,flexShrink:0}}/>
                  <div style={{width:28,height:28,borderRadius:7,background:link.iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <i className={`bi ${link.icon}`} style={{fontSize:13,color:link.iconColor}}></i>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{link.label}</div>
                    <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{getStat(link.id)||"—"}</div>
                  </div>
                  <span style={{fontSize:9,padding:"2px 7px",borderRadius:8,background:DS.pageBg,color:DS.textSec,border:`1px solid ${DS.border}`}}>Not shown</span>
                  <button onClick={()=>toggleLink(link.id)} disabled={order.length>=4}
                    style={{width:20,height:20,borderRadius:"50%",border:`1.5px solid ${order.length>=4?DS.border:DS.success.border}`,background:order.length>=4?DS.pageBg:DS.success.bg,color:order.length>=4?DS.textSec:DS.success.text,fontSize:12,cursor:order.length>=4?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>+</button>
                </div>
              ))}
            </div>
            <div style={{padding:"10px 14px",borderTop:`0.5px solid ${DS.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
              <span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{order.length} of 4 slots used</span>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setEditMode(false)} style={{padding:"7px 14px",fontSize:12,borderRadius:6,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer"}}>Cancel</button>
                <button onClick={()=>setEditMode(false)} style={{padding:"7px 16px",fontSize:12,borderRadius:6,border:"none",background:DS.blue,color:"white",cursor:"pointer",fontWeight:500}}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ residents, referrals, onNavigate, abscondNotifications=[], uid=null }) {
  const active = residents.filter(r=>r.status==="active");
  const pendingArrivals = residents.filter(r=>r.status==="pending_arrival");
  const houseA = active.filter(r=>r.facility==="Dorcus 1 - 801");
  const houseB = active.filter(r=>r.facility==="Dorcus 2 - 24th St");

  const CAP_HOUSE = 35;
  const CAP_TOTAL = 70;

  // Health flag visibility settings
  const ALL_FLAGS = [
    {id:"mh1",    label:"MH-1 (Some Concerns)",       color:DS.purple.text, bg:DS.purple.bg},
    {id:"mh2",    label:"MH-2 (Active Treatment)",     color:DS.purple.text, bg:DS.purple.bg},
    {id:"sa",     label:"Substance Abuse History",      color:DS.teal.text,   bg:DS.teal.bg},
    {id:"gang",   label:"Gang Affiliated",              color:DS.amber.text,  bg:DS.amber.bg},
  ];
  const [hiddenFlags, setHiddenFlags] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dorcus_hidden_flags")||"[]"); } catch { return []; }
  });
  const [showFlagSettings, setShowFlagSettings] = useState(false);
  const toggleFlag = (id) => {
    const updated = hiddenFlags.includes(id) ? hiddenFlags.filter(f=>f!==id) : [...hiddenFlags, id];
    setHiddenFlags(updated);
    try { localStorage.setItem("dorcus_hidden_flags", JSON.stringify(updated)); } catch {}
  };
  const isFlagVisible = (id) => !hiddenFlags.includes(id);

  const withMH1 = active.filter(r=>r.mhLevel==="MH-1").length;
  const withMH2 = active.filter(r=>r.mhLevel==="MH-2").length;
  const withSA = active.filter(r=>r.substanceAbuse).length;
  const withGang = active.filter(r=>r.gangAffiliated).length;
  const employed = active.filter(r=>r.employed).length;
  const inTraining = active.filter(r=>r.trainingPrograms).length;

  // Quick access metrics
  const today = TODAY;
  const pctInOut = (() => {
    const inCount = active.filter(r=>r.inStatus==="in"||r.inStatus===null).length;
    return active.length>0 ? Math.round(inCount/active.length*100) : 0;
  })();

  // Classification breakdown
  const classBreakdown = ["Non-Violent","Violent","Sex Offender"].map(c=>({
    label:c, count:active.filter(r=>r.classification===c).length,
    ...classStyle[c]
  }));

  // Upcoming discharges (next 14 days)
  const upcomingDischarges = active
    .filter(r=>r.exitDate)
    .map(r=>({...r, daysLeft:daysUntil(r.exitDate)}))
    .filter(r=>r.daysLeft!==null && r.daysLeft<=14)
    .sort((a,b)=>a.daysLeft-b.daysLeft);

  // Stat card component with capacity toggle
  const StatCard = ({label, value, max, color=DS.blue, onClick, noToggle, cardKey}) => {
    const storageKey = `dorcus_capmode_${cardKey||label}`;
    const [mode, setMode] = useState(() => {
      try { return localStorage.getItem(storageKey)||"pct"; } catch { return "pct"; }
    });
    const setModeAndSave = (m) => { setMode(m); try { localStorage.setItem(storageKey, m); } catch {}; };
    const pct = max ? Math.round(value/max*100) : null;
    return (
      <div onClick={onClick} style={{...S.card,cursor:onClick?"pointer":"default",position:"relative"}}
        onMouseEnter={e=>{if(onClick)e.currentTarget.style.borderColor=DS.blue;}}
        onMouseLeave={e=>{if(onClick)e.currentTarget.style.borderColor=DS.border;}}>
        {max&&!noToggle&&(
          <div style={{position:"absolute",top:10,right:10,display:"flex",gap:2,background:"white",border:`0.5px solid ${DS.border}`,borderRadius:5,padding:2}}>
            <button onClick={e=>{e.stopPropagation();setModeAndSave("pct");}} style={{fontSize:9,padding:"2px 6px",borderRadius:3,border:"none",cursor:"pointer",background:mode==="pct"?DS.blue:"transparent",color:mode==="pct"?"white":DS.textSec,fontFamily:"'Inter',sans-serif",fontWeight:500}}>%</button>
            <button onClick={e=>{e.stopPropagation();setModeAndSave("beds");}} style={{fontSize:9,padding:"2px 6px",borderRadius:3,border:"none",cursor:"pointer",background:mode==="beds"?DS.blue:"transparent",color:mode==="beds"?"white":DS.textSec,fontFamily:"'Inter',sans-serif",fontWeight:500}}>beds</button>
          </div>
        )}
        <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>{label}</div>
        <div style={{fontSize:32,fontWeight:600,color,marginBottom:4,fontFamily:"'DM Sans',sans-serif"}}>{value}</div>
        {max&&<>
          <div style={{height:3,background:DS.border,borderRadius:2,overflow:"hidden",marginBottom:4}}>
            <div style={{width:`${Math.min(100,pct)}%`,height:"100%",background:color,borderRadius:2}}/>
          </div>
          <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>
            {mode==="pct" ? `${pct}% capacity` : `${value} of ${max} beds`}
          </div>
        </>}
      </div>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:24}}>

      {/* Abscond alerts */}
      {abscondNotifications.filter(n=>!n.resolved).map(n=>(
        <div key={n.id} style={{background:DS.error.bg,border:`1px solid ${DS.error.border}`,borderRadius:8,padding:"14px 16px",display:"flex",alignItems:"flex-start",gap:12}}>
          <i className="bi bi-exclamation-circle-fill" style={{fontSize:16,color:DS.error.text,flexShrink:0,marginTop:2}}></i>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:DS.error.text,marginBottom:4,fontFamily:"'DM Sans',sans-serif"}}>Abscond reported — action required</div>
            <div style={{fontSize:12,color:DS.error.text,marginBottom:6,fontFamily:"'Inter',sans-serif"}}>{n.residentName} · {n.facility} · Reported by {n.reportedBy} at {n.reportedAt}</div>
            <div style={{fontSize:11,color:DS.error.text,opacity:0.8,marginBottom:10,fontFamily:"'Inter',sans-serif"}}>You must notify Probation & Parole. Mark as notified once complete.</div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={async()=>{ await updateAbscondNotification(n.id,{ppNotified:true,resolved:true}); }} style={{...S.btn,fontSize:11,padding:"5px 12px"}}>Mark P&P Notified</button>
              <button onClick={async()=>{ await updateAbscondNotification(n.id,{resolved:true}); }} style={{...S.btnGhost,fontSize:11,padding:"5px 12px"}}>Dismiss</button>
            </div>
          </div>
        </div>
      ))}

      {/* Top stat row — D1, D2 (Pending Arrivals), D3 left (Dorcus 1), Dorcus 2 */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <StatCard label="Active Residents" cardKey="active" value={active.length} max={CAP_TOTAL} color={DS.success.text} onClick={()=>onNavigate("residents")}/>
        {(()=>{
          const d1Pending = pendingArrivals.filter(r=>r.facility==="Dorcus 1 - 801").length;
          const d2Pending = pendingArrivals.filter(r=>r.facility==="Dorcus 2 - 24th St").length;
          const [paExpanded, setPaExpanded] = useState(()=>{try{return localStorage.getItem("dorcus_pa_expanded")!=="false";}catch{return true;}});
          const togglePA = (e) => { e.stopPropagation(); const next=!paExpanded; setPaExpanded(next); try{localStorage.setItem("dorcus_pa_expanded",next);}catch{} };
          return (
            <div onClick={()=>onNavigate("residents")} onMouseEnter={e=>e.currentTarget.style.borderColor=DS.blue} onMouseLeave={e=>e.currentTarget.style.borderColor=DS.border}
              style={{...S.card,cursor:"pointer",position:"relative",transition:"border-color 0.15s"}}>
              <button onClick={togglePA} style={{position:"absolute",top:10,right:10,width:18,height:18,borderRadius:"50%",background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:DS.blue}}>
                {paExpanded?"▾":"▴"}
              </button>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Pending Arrivals</div>
              <div style={{fontSize:32,fontWeight:600,color:DS.warning.text,fontFamily:"'DM Sans',sans-serif",lineHeight:1,marginBottom:4}}>{pendingArrivals.length}</div>
              {paExpanded ? (
                <div style={{display:"flex",gap:8,marginTop:8}}>
                  {[{label:"Dorcus 1",val:d1Pending,color:DS.blue},{label:"Dorcus 2",val:d2Pending,color:DS.darkYellow}].map(({label,val,color})=>(
                    <div key={label} style={{flex:1,background:DS.pageBg,borderRadius:6,padding:"6px 8px",textAlign:"center"}}>
                      <div style={{fontSize:20,fontWeight:600,color,fontFamily:"'DM Sans',sans-serif"}}>{val}</div>
                      <div style={{fontSize:9,color:DS.textSec,fontFamily:"'Inter',sans-serif",marginTop:1}}>{label}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>
                  <span style={{color:DS.blue,fontWeight:600}}>{d1Pending}</span> D1 · <span style={{color:DS.darkYellow,fontWeight:600}}>{d2Pending}</span> D2
                </div>
              )}
            </div>
          );
        })()}
        <StatCard label="Dorcus 1" cardKey="d1" value={houseA.length} max={CAP_HOUSE} color={DS.blue}/>
        <StatCard label="Dorcus 2" cardKey="d2" value={houseB.length} max={CAP_HOUSE} color={DS.darkYellow}/>
      </div>

      {/* Notifications Module */}
      <NotificationsModule residents={residents} onNavigate={onNavigate}/>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>

        {/* Left column — D3: House Capacity + Health Flags + Employment */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>

          {/* House Capacity — moved to left (D3) */}
          {["Dorcus 1 - 801","Dorcus 2 - 24th St"].map(house=>{
            const hrs = active.filter(r=>r.facility===house);
            const pct = Math.round(hrs.length/CAP_HOUSE*100);
            const hColor = house.includes("1")?DS.blue:DS.darkYellow;
            const hBorder = house.includes("1")?DS.d1Border:DS.d2Border;
            return (
              <div key={house} style={{...S.card,borderLeft:`3px solid ${hBorder}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{shortFacility(house)}</div>
                    <div style={{fontSize:11,color:DS.textSec,marginTop:2,fontFamily:"'Inter',sans-serif"}}>{hrs.length} of {CAP_HOUSE} beds · {pct}%</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:28,fontWeight:600,color:hColor,fontFamily:"'DM Sans',sans-serif"}}>{hrs.length}<span style={{fontSize:14,color:DS.textSec,fontWeight:400}}>/{CAP_HOUSE}</span></div>
                  </div>
                </div>
                <div style={{height:5,background:DS.border,borderRadius:3,overflow:"hidden",marginTop:10}}>
                  <div style={{width:`${pct}%`,height:"100%",background:hColor,borderRadius:3}}/>
                </div>
              </div>
            );
          })}

          {/* Health & Safety Flags — D5 with visibility settings */}
          <div style={{...S.card}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:12,flex:1}}>
                <span style={{fontSize:10,fontWeight:600,letterSpacing:"0.1em",color:DS.blue,textTransform:"uppercase",whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>Health & Safety Flags</span>
                <div style={{flex:1,height:1,background:DS.border}}/>
              </div>
              <button onClick={()=>setShowFlagSettings(s=>!s)} style={{...S.btnGhost,fontSize:10,padding:"3px 8px",marginLeft:8,display:"flex",alignItems:"center",gap:4}}>
                <i className="bi bi-sliders" style={{fontSize:10}}></i> {showFlagSettings?"Done":"Edit"}
              </button>
            </div>
            {showFlagSettings&&(
              <div style={{background:DS.pageBg,borderRadius:6,padding:"10px 12px",marginBottom:12,display:"flex",flexDirection:"column",gap:6}}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Toggle visible flags</div>
                {ALL_FLAGS.map(f=>(
                  <div key={f.id} onClick={()=>toggleFlag(f.id)}
                    style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:5,cursor:"pointer",
                      background:isFlagVisible(f.id)?DS.blueTint:"transparent",
                      border:`1px solid ${isFlagVisible(f.id)?DS.blueBorder:DS.border}`}}>
                    <div style={{width:14,height:14,borderRadius:4,border:`2px solid ${isFlagVisible(f.id)?DS.blue:DS.border}`,background:isFlagVisible(f.id)?DS.blue:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {isFlagVisible(f.id)&&<span style={{color:DS.textPrimary,fontSize:9,fontWeight:700}}>✓</span>}
                    </div>
                    <span style={{fontSize:12,color:isFlagVisible(f.id)?DS.blue:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{f.label}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {[
                {id:"mh1",  label:"MH-1 (Some Concerns)",      value:withMH1, color:DS.purple.text, bg:DS.purple.bg},
                {id:"mh2",  label:"MH-2 (Active Treatment)",    value:withMH2, color:DS.purple.text, bg:DS.purple.bg},
                {id:"sa",   label:"Substance Abuse History",     value:withSA,  color:DS.teal.text,   bg:DS.teal.bg},
                {id:"gang", label:"Gang Affiliated",             value:withGang,color:DS.amber.text,  bg:DS.amber.bg},
              ].filter(f=>isFlagVisible(f.id)).map(({label,value,color,bg})=>(
                <div key={label} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:6,background:value>0?bg:DS.pageBg}}>
                  <span style={{fontSize:12,color:value>0?color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{label}</span>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:80,height:4,background:DS.border,borderRadius:2,overflow:"hidden"}}>
                      <div style={{width:active.length>0?`${value/active.length*100}%`:"0%",height:"100%",background:color,borderRadius:2}}/>
                    </div>
                    <span style={{fontSize:13,fontWeight:600,color:value>0?color:DS.textSec,minWidth:16,textAlign:"right",fontFamily:"'Inter',sans-serif"}}>{value}</span>
                  </div>
                </div>
              ))}
              {ALL_FLAGS.every(f=>!isFlagVisible(f.id))&&(
                <div style={{fontSize:12,color:DS.textSec,textAlign:"center",padding:"8px 0",fontFamily:"'Inter',sans-serif"}}>No flags visible — click Edit to configure</div>
              )}
            </div>
          </div>

          {/* Employment — D2 moved here, renamed */}
          <div style={{...S.card}}>
            <SectionHdr>Employment & Self Improvement</SectionHdr>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[
                {label:"Employed",       value:employed,              icon:"bi-briefcase-fill",   color:DS.success.text},
                {label:"In Program",     value:inTraining,            icon:"bi-mortarboard-fill", color:DS.blue},
                {label:"Not Employed",   value:active.length-employed,icon:"bi-dash",             color:DS.textSec},
                {label:"No Program",     value:active.length-inTraining,icon:"bi-dash",           color:DS.textSec},
              ].map(({label,value,icon,color})=>(
                <div key={label} style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:8,padding:12,textAlign:"center"}}>
                  <i className={`bi ${icon}`} style={{fontSize:18,color,marginBottom:4,display:"block"}}></i>
                  <div style={{fontSize:22,fontWeight:600,color,fontFamily:"'DM Sans',sans-serif"}}>{value}</div>
                  <div style={{fontSize:10,color:DS.textSec,marginTop:2,fontFamily:"'Inter',sans-serif"}}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column — D2: Classification + Upcoming Arrivals + Discharges */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>

          {/* Classification — moved to right (D2) */}
          <div style={{...S.card}}>
            <SectionHdr>Classification Breakdown</SectionHdr>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {[
                {label:"Non-Violent",  count:active.filter(r=>r.classification==="Non-Violent").length,  color:DS.success.text, bg:DS.success.bg,  border:DS.success.border},
                {label:"Violent",      count:active.filter(r=>r.classification==="Violent").length,       color:DS.error.text,  bg:DS.error.bg,     border:DS.error.border},
                {label:"Sex Offender", count:active.filter(r=>r.classification==="Sex Offender").length,  color:DS.purple.text,  bg:DS.purple.bg,    border:DS.purple.border},
              ].map(({label,count,bg,border,color})=>(
                <div key={label} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:6,background:count>0?bg:DS.pageBg,border:`1px solid ${count>0?border:DS.border}`}}>
                  <span style={{fontSize:12,color:count>0?color:DS.textSec,fontWeight:count>0?500:400,fontFamily:"'Inter',sans-serif"}}>{label}</span>
                  <span style={{fontSize:13,fontWeight:600,color:count>0?color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Arrivals */}
          {(()=>{
            const upcomingArrivals = residents
              .filter(r=>r.status==="pending_arrival"&&r.entryDate)
              .map(r=>({...r, daysUntilEntry: daysUntil(r.entryDate)}))
              .filter(r=>r.daysUntilEntry!==null && r.daysUntilEntry>=0 && r.daysUntilEntry<=7)
              .sort((a,b)=>a.daysUntilEntry-b.daysUntilEntry);
            return (
              <div style={{...S.card}}>
                <SectionHdr>Upcoming Arrivals — Next 7 Days</SectionHdr>
                {upcomingArrivals.length===0
                  ? <div style={{fontSize:12,color:DS.textSec,textAlign:"center",padding:"8px 0",fontFamily:"'Inter',sans-serif"}}>No pending arrivals in the next 14 days.</div>
                  : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {upcomingArrivals.map(r=>{
                        const isToday = r.daysUntilEntry===0;
                        return (
                          <div key={r.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",borderRadius:6,background:isToday?DS.success.bg:DS.warning.bg,border:`1px solid ${isToday?DS.success.border:DS.warning.border}`}}>
                            <div>
                              <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(r.name)}</div>
                              <div style={{fontSize:11,color:DS.textSec,marginTop:1,fontFamily:"'Inter',sans-serif"}}>{shortFacility(r.facility)} · Entry: {r.entryDate}</div>
                            </div>
                            <div style={{fontSize:13,fontWeight:600,color:isToday?DS.success.text:DS.warning.text,fontFamily:"'Inter',sans-serif"}}>{isToday?"Today":`${r.daysUntilEntry}d`}</div>
                          </div>
                        );
                      })}
                    </div>
                }
              </div>
            );
          })()}

          {/* Upcoming Discharges */}
          <div style={{...S.card}}>
            <SectionHdr>Upcoming Discharges — Next 14 Days</SectionHdr>
            {upcomingDischarges.length===0
              ? <div style={{fontSize:12,color:DS.textSec,textAlign:"center",padding:"8px 0",fontFamily:"'Inter',sans-serif"}}>No discharges scheduled in the next 7 days.</div>
              : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {upcomingDischarges.map(r=>{
                    const isOverdue = r.daysLeft<0;
                    const isToday = r.daysLeft===0;
                    const bg = isOverdue?DS.error.bg:isToday?DS.warning.bg:DS.pageBg;
                    const border = isOverdue?DS.error.border:isToday?DS.warning.border:DS.border;
                    const textColor = isOverdue?DS.error.text:isToday?DS.warning.text:DS.textSec;
                    return (
                      <div key={r.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",borderRadius:6,background:bg,border:`1px solid ${border}`}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(r.name)}</div>
                          <div style={{fontSize:11,color:DS.textSec,marginTop:1,fontFamily:"'Inter',sans-serif"}}>{shortFacility(r.facility)} · Exit: {r.exitDate}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:13,fontWeight:600,color:textColor,fontFamily:"'Inter',sans-serif"}}>{isOverdue?`${Math.abs(r.daysLeft)}d overdue`:isToday?"Today":`${r.daysLeft}d`}</div>
                          <div style={{fontSize:10,color:DS.textSec,marginTop:1,fontFamily:"'Inter',sans-serif"}}>
                            {((r.exitChecklist||[]).filter(Boolean).length)}/{EXIT_CHECKLIST_ITEMS.length} exit tasks
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>

        </div>
      </div>

      {/* Quick Access — customizable */}
      <QuickAccessPanel
        onNavigate={onNavigate}
        uid={uid}
        residents={residents}
        outCount={0}
        nextMedCall={getNextMedCall()}
        referrals={referrals}
        uaDueCount={0}
        lastHeadcount={null}
        lastShift={null}
        pendingIntake={residents.filter(r=>r.status==="active"&&!r.intakeForms?.noticeOfArrival?.completedAt).length}
        exitingWeek={residents.filter(r=>r.status==="active"&&r.exitDate&&r.exitDate>=TODAY&&r.exitDate<=daysFromToday(7)).length}
      />

    </div>
  );
}


// ── RA: Headcount ─────────────────────────────────────────────────────────────
const SHIFT_HEADCOUNT_TIMES = {
  "Morning (6AM–2PM)":    ["6:00 AM (shift change)", "8:00 AM", "10:00 AM", "12:00 PM"],
  "Afternoon (2PM–10PM)": ["2:00 PM (shift change)", "4:00 PM", "6:00 PM",  "8:00 PM"],
  "Overnight (10PM–6AM)": ["10:00 PM (shift change)","12:00 AM","2:00 AM",  "4:00 AM"],
};
function getCurrentShift() {
  const h=new Date().getHours();
  if(h>=6&&h<14) return "Morning (6AM–2PM)";
  if(h>=14&&h<22) return "Afternoon (2PM–10PM)";
  return "Overnight (10PM–6AM)";
}
function minutesSince(isoStr) { return Math.floor((Date.now()-new Date(isoStr).getTime())/60000); }

function Headcount({ residents, perms, raName: raNameProp }) {
  const myResidents=residents.filter(r=>r.status==="active"&&(perms.canSeeAllFacilities||r.facility===perms.assignedFacility))
    .sort((a,b) => {
      const ra = a.room ? parseInt(a.room)||a.room : null;
      const rb = b.room ? parseInt(b.room)||b.room : null;
      if (ra === null && rb === null) return (a.name||"").localeCompare(b.name||"");
      if (ra === null) return 1;
      if (rb === null) return -1;
      return typeof ra === "number" && typeof rb === "number" ? ra - rb : String(ra).localeCompare(String(rb));
    });
  const [logs,setLogs]=useState([]);
  const [active,setActive]=useState(null);
  const [raName,setRaName]=useState(raNameProp||"");
  const [shift,setShift]=useState(getCurrentShift);
  const [selectedSlot,setSelectedSlot]=useState("");
  const [checkDate,setCheckDate]=useState(TODAY);
  const [facFilter,setFacFilter]=useState("all");

  // Keep raName synced if prop changes (RA logs in after mount)
  useEffect(()=>{ if(raNameProp && !raName) setRaName(raNameProp); },[raNameProp]);

  useEffect(() => {
    const unsub = subscribeToHeadcounts(perms.assignedFacility, setLogs);
    return unsub;
  }, [perms.assignedFacility]);

  const lastLog=logs.length>0?logs[0]:null;
  const minSinceLast=lastLog?minutesSince(lastLog.submittedAt):null;
  const isDue=minSinceLast===null||minSinceLast>=120;
  const isOverdue=minSinceLast!==null&&minSinceLast>=150;

  const startHeadcount=()=>{
    if(!raName.trim()||!selectedSlot)return;
    const statuses={};
    myResidents.forEach(r=>{statuses[r.id]=null;});
    setActive({raName,shift,selectedSlot,checkDate,statuses});
  };
  const toggle=(id,val)=>setActive(a=>({...a,statuses:{...a.statuses,[id]:val}}));
  const markedCount=active?Object.values(active.statuses).filter(v=>v!==null).length:0;
  const inCount=active?Object.values(active.statuses).filter(v=>v===true).length:0;
  const outCount=active?Object.values(active.statuses).filter(v=>v===false).length:0;
  const allMarked=active&&markedCount===myResidents.length;

  const submit=async ()=>{
    if(!allMarked)return;
    const entry={
      raName:active.raName,shift:active.shift,selectedSlot:active.selectedSlot||"",checkDate:active.checkDate,
      submittedAt:new Date().toISOString(),submittedTime:timeNow(),
      facility:perms.assignedFacility||"All Facilities",
      statuses:{...active.statuses},
      residents:myResidents.map(r=>({id:r.id,name:r.name,facility:r.facility})),
      inCount:Object.values(active.statuses).filter(v=>v===true).length,
      outCount:Object.values(active.statuses).filter(v=>v===false).length,
      total:myResidents.length,
    };
    await saveHeadcount(entry);
    // Cross-check headcount vs latest In/Out status
    try {
      const mismatches = [];
      myResidents.forEach(r => {
        const hcStatus = entry.statuses[r.id];
        const ioOut = (r.signInOut?.status === "out");
        if (hcStatus === true && ioOut) mismatches.push(`${r.name} (HC:in, IO:out)`);
        if (hcStatus === false && !ioOut) mismatches.push(`${r.name} (HC:out, IO:in)`);
      });
      if (mismatches.length > 0) {
        saveNotification({
          type:"headcount_mismatch",
          title:`Headcount mismatch — ${entry.facility}`,
          body:`${mismatches.length} discrepancy(ies): ${mismatches.slice(0,3).join(", ")}${mismatches.length>3?"...":""}`,
          facility:entry.facility, mismatches, read:false, createdAt:TODAY
        });
      }
    } catch(e) { console.error("Mismatch check failed", e); }
    auditAction("headcount_submitted", { facility:entry.facility, shift:entry.shift, inCount:entry.inCount, outCount:entry.outCount });
    setActive(null);
  };

  const facilityResidents=active?myResidents.filter(r=>facFilter==="all"||r.facility===facFilter):[];

  return (
    <div>
      {/* Due/overdue banner */}
      {isDue&&!active&&(
        <div style={{background:isOverdue?DS.error.bg:DS.warning.bg,border:`1px solid ${isOverdue?DS.error.border:DS.warning.border}`,borderRadius:12,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:20}}>{isOverdue?"🚨":"⏰"}</span>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:isOverdue?DS.error.text:DS.warning.text}}>{isOverdue?"Headcount Overdue":"Headcount Due"}</div>
            <div style={{fontSize:11,color:isOverdue?DS.error.text:DS.warning.text,marginTop:2}}>
              {minSinceLast===null?"No headcount has been logged yet for this session.":`Last headcount was ${minSinceLast} minutes ago — required every 2 hours.`}
            </div>
          </div>
        </div>
      )}
      {!isDue&&!active&&lastLog&&(
        <div style={{background:DS.success.bg,border:`1px solid ${DS.success.border}`,borderRadius:12,padding:"10px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:16}}>✅</span>
          <span style={{fontSize:12,color:DS.success.text}}>Last headcount: <strong>{lastLog.submittedTime}</strong> by {lastLog.raName} — next due in ~{120-minSinceLast} min</span>
        </div>
      )}

      {/* Start form */}
      {!active&&(
        <div style={{...S.card,marginBottom:24}}>
          <SectionHdr>Start New Headcount</SectionHdr>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Your Name *</div>
              <input style={{...S.input}} placeholder="RA name..." value={raName} onChange={e=>setRaName(e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Shift</div>
              <select style={{...S.input}} value={shift} onChange={e=>{setShift(e.target.value);setSelectedSlot("");}}>
                {Object.keys(SHIFT_HEADCOUNT_TIMES).map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div style={{gridColumn:"span 2"}}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Which headcount are you doing? *</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {SHIFT_HEADCOUNT_TIMES[shift].map(t=>(
                  <button key={t} onClick={()=>setSelectedSlot(t)}
                    style={{padding:"6px 14px",fontSize:12,borderRadius:8,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                      background:selectedSlot===t?"#4f46e5":"white",
                      border:`1px solid ${selectedSlot===t?"#4f46e5":DS.border}`,
                      color:selectedSlot===t?"white":DS.textSec,fontWeight:selectedSlot===t?600:400}}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Date</div>
              <input type="date" style={{...S.input}} value={checkDate} onChange={e=>setCheckDate(e.target.value)}/>
            </div>
          </div>
          <button onClick={startHeadcount} disabled={!raName.trim()||!selectedSlot} style={{...S.btn,opacity:(raName.trim()&&selectedSlot)?1:0.4}}>
            Begin Headcount ({myResidents.length} residents)
          </button>
        </div>
      )}

      {/* Active headcount */}
      {active&&(
        <div>
          <div style={{...S.card,marginBottom:16,background:"rgba(79,70,229,0.08)",border:"1px solid rgba(79,70,229,0.3)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:DS.textPrimary}}>Headcount in Progress</div>
                <div style={{fontSize:11,color:DS.textSec,marginTop:2}}>{active.raName} · {active.shift} · {active.checkDate}</div>
              </div>
              <div style={{display:"flex",gap:20,alignItems:"center"}}>
                {[{label:"In",val:inCount,c:"#1A6B35"},{label:"Out",val:outCount,c:"#8B1A1A"},{label:"Unmarked",val:myResidents.length-inCount-outCount,c:"#4A5568"}].map(({label,val,c})=>(
                  <div key={label} style={{textAlign:"center"}}>
                    <div style={{fontSize:24,fontWeight:700,color:c}}>{val}</div>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase"}}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{marginTop:12}}>
              <div style={{height:6,background:DS.border,borderRadius:3,overflow:"hidden"}}>
                <div style={{width:`${(markedCount/myResidents.length)*100}%`,height:"100%",background:"#4f46e5",borderRadius:3,transition:"width 0.3s"}}/>
              </div>
              <div style={{fontSize:10,color:DS.textSec,marginTop:4}}>{markedCount} of {myResidents.length} marked</div>
            </div>
          </div>

          {perms.canSeeAllFacilities&&(
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              {/* Facility filter removed — scoped by raResidents prop */}
            </div>
          )}

          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:20}}>
            {facilityResidents.map(r=>{
              const val=active.statuses[r.id];
              return (
                <div key={r.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:12,border:`1px solid ${val===true?DS.success.border:val===false?DS.error.border:DS.border}`,background:val===true?DS.success.bg:val===false?DS.error.bg:"white",transition:"all 0.15s"}}>
                  {r.photoURL?<img src={r.photoURL} alt={r.name} style={{width:36,height:36,borderRadius:10,objectFit:"cover",border:"1px solid #B5D4F4",flexShrink:0}}/>:<div style={{width:36,height:36,borderRadius:10,background:"#E8F0FB",border:"1px solid #B5D4F4",display:"flex",alignItems:"center",justifyContent:"center",color:"#1B5EBE",fontWeight:700,fontSize:12,flexShrink:0}}>{initials(r.name)}</div>}
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{r.name}</div>
                    <div style={{fontSize:11,color:DS.textSec}}>{r.facility}</div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    {[{label:"IN",val:true,ac:DS.success.text,tc:"white",bg:DS.success.bg},{label:"OUT",val:false,ac:DS.warning.text,tc:"white",bg:DS.warning.bg}].map(({label,val:v,ac,tc,bg})=>(
                      <button key={label} onClick={()=>toggle(r.id,v)} style={{padding:"6px 20px",fontSize:12,fontWeight:700,borderRadius:8,cursor:"pointer",background:val===v?bg:"transparent",border:`2px solid ${val===v?ac:DS.border}`,color:val===v?tc:"#475569",transition:"all 0.15s"}}>{label}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={()=>setActive(null)} style={{...S.btnGhost}}>Cancel</button>
            <button onClick={submit} disabled={!allMarked} style={{...S.btnCTA,opacity:allMarked?1:0.4}}>
              Submit Headcount {allMarked?`(${inCount} in, ${outCount} out)`:`— ${myResidents.length-markedCount} remaining`}
            </button>
          </div>
        </div>
      )}

      {/* History log */}
      {logs.length>0&&!active&&(
        <div style={{marginTop:24}}>
          <SectionHdr>Headcount History</SectionHdr>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {logs.map(log=>(
              <details key={log.id} style={{...S.card,cursor:"pointer"}}>
                <summary style={{display:"flex",alignItems:"center",justifyContent:"space-between",listStyle:"none",cursor:"pointer",userSelect:"none"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:36,height:36,borderRadius:10,background:"#E8F0FB",border:"1px solid #B5D4F4",display:"flex",alignItems:"center",justifyContent:"center",color:"#1B5EBE",fontWeight:700,fontSize:11}}>
                      {(log.raName||"RA").split(" ").filter(Boolean).map(x=>x[0]).join("").toUpperCase().slice(0,2)}
                    </div>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{log.raName} <span style={{color:DS.textSec,fontWeight:400}}>· {log.shift}</span></div>
                      <div style={{fontSize:11,color:DS.textSec}}>{log.checkDate} at {log.submittedTime} · {log.facility}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:16,alignItems:"center"}}>
                    <span style={{fontSize:13,color:"#1A6B35",fontWeight:700}}>{log.inCount} in</span>
                    <span style={{fontSize:13,color:"#8B1A1A",fontWeight:700}}>{log.outCount} out</span>
                    <span style={{fontSize:11,color:DS.textSec}}>▾ expand</span>
                  </div>
                </summary>
                <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${DS.border}`,display:"flex",flexDirection:"column",gap:6}}>
                  {(log.residents||[]).map(r=>{
                    const wasIn=log.statuses?.[r.id];
                    return (
                      <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 10px",borderRadius:8,background:wasIn?DS.success.bg:DS.error.bg,border:`1px solid ${wasIn?DS.success.border:DS.error.border}`}}>
                        <div style={{width:6,height:6,borderRadius:"50%",background:wasIn?DS.success.text:DS.error.text,flexShrink:0}}/>
                        <span style={{fontSize:12,color:DS.textPrimary,flex:1}}>{r.name}</span>
                        <span style={{fontSize:11,fontWeight:700,color:wasIn?DS.success.text:DS.error.text}}>{wasIn?"IN":"OUT"}</span>
                      </div>
                    );
                  })}
                  {(!log.residents||log.residents.length===0)&&(
                    <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>No resident detail available for this log.</div>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── RA Home Screen ────────────────────────────────────────────────────────────
const MED_CALL_TIMES = [
  { label:"Morning",  hour:6,  minute:0  },
  { label:"Lunch",    hour:12, minute:0  },
  { label:"Evening",  hour:18, minute:0  },
  { label:"Evening",  hour:21, minute:0  },
];

function getCurrentCallSlot() {
  // Returns the slot name for the current med call window (within 1hr either side)
  const now = NOW_NY();
  const todayMins = now.getHours() * 60 + now.getMinutes();
  for (const call of MED_CALL_TIMES) {
    const callMins = call.hour * 60 + call.minute;
    if (Math.abs(callMins - todayMins) <= 60) return call.label;
  }
  return null; // Not currently in a call window
}
function getNextMedCall() {
  const now = new Date();
  const todayMins = now.getHours() * 60 + now.getMinutes();
  for (const call of MED_CALL_TIMES) {
    const callMins = call.hour * 60 + call.minute;
    if (callMins > todayMins) {
      const diff = callMins - todayMins;
      const h = Math.floor(diff / 60);
      const m = diff % 60;
      const timeStr = new Date(now.getFullYear(), now.getMonth(), now.getDate(), call.hour, call.minute)
        .toLocaleTimeString("en-US", {hour:"numeric", minute:"2-digit"});
      return { label: call.label, timeStr, h, m, diff };
    }
  }
  // All calls passed — next is tomorrow morning
  const tomorrowMins = (24 * 60 - todayMins) + MED_CALL_TIMES[0].hour * 60;
  const h = Math.floor(tomorrowMins / 60);
  const m = tomorrowMins % 60;
  return { label:"Morning", timeStr:"6:00 AM", h, m, diff: tomorrowMins, tomorrow: true };
}


// ── RA8: Money Request Modal ──────────────────────────────────────────────────
// ── Compensation Collection Form ─────────────────────────────────────────────
function CompensationModal({ residents, raName, onClose }) {
  const [stage, setStage] = useState(1);
  const [residentName, setResidentName] = useState("");
  const [residentMatch, setResidentMatch] = useState(null);
  const [weekData, setWeekData] = useState([]); // [{weekStr, sun, sat, hoursWorked, owed, selected, override, missed, missedReason}]
  const [selectedWeeks, setSelectedWeeks] = useState({});
  const [overrides, setOverrides] = useState({});
  const [missedWeeks, setMissedWeeks] = useState({});
  const [amountReceived, setAmountReceived] = useState("");
  const [residentAck, setResidentAck] = useState(false);
  const [confirmNo, setConfirmNo] = useState("");
  const [saving, setSaving] = useState(false);

  const nameMatches = residentName.length >= 2
    ? residents.filter(r=>r.name.toLowerCase().includes(residentName.toLowerCase())&&r.status==="active")
    : [];

  // Generate last 5 Sun-Sat weeks
  const generateWeeks = () => {
    const weeks = [];
    const now = NOW_NY();
    // Find last Sunday
    const day = now.getDay(); // 0=Sun
    const lastSun = new Date(now);
    lastSun.setDate(now.getDate() - day - 7); // go back to last Sunday (full week)
    for(let i=0; i<5; i++) {
      const sun = new Date(lastSun);
      sun.setDate(lastSun.getDate() - (i*7));
      const sat = new Date(sun);
      sat.setDate(sun.getDate() + 6);
      const sunStr = sun.toISOString().split("T")[0];
      const satStr = sat.toISOString().split("T")[0];
      const label = `${sun.toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${sat.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}`;
      weeks.push({ sunStr, satStr, label, hoursWorked:0, owed:0, paid:false });
    }
    return weeks;
  };

  const calcOwed = (hrs) => Math.min(50, Math.floor(hrs/8)*10);

  const loadResidentData = async (r) => {
    setResidentMatch(r);
    setResidentName(r.name);
    const weeks = generateWeeks();
    // Try to load work log hours from Firestore
    // For now set placeholder — real implementation reads work_log_end events
    const enriched = weeks.map(w => ({...w, hoursWorked:0, owed:0, paid:false}));
    setWeekData(enriched);
    // Pre-select all unpaid weeks
    const sel = {};
    enriched.forEach(w => { if(!w.paid) sel[w.sunStr] = true; });
    setSelectedWeeks(sel);
  };

  const totalOwed = weekData.reduce((sum,w) => {
    if(!selectedWeeks[w.sunStr] || missedWeeks[w.sunStr]) return sum;
    const amt = overrides[w.sunStr] !== undefined ? parseFloat(overrides[w.sunStr])||0 : calcOwed(w.hoursWorked);
    return sum + amt;
  }, 0);

  const handleSave = async () => {
    setSaving(true);
    // Generate unique 6-digit confirmation number
    const usedNos = JSON.parse(localStorage.getItem("dorcus_comp_nos")||"[]");
    let no;
    do { no = Math.floor(100000 + Math.random()*900000).toString(); } while(usedNos.includes(no));
    usedNos.push(no); localStorage.setItem("dorcus_comp_nos", JSON.stringify(usedNos.slice(-500)));
    setConfirmNo(no);

    await saveNotification({
      type:"compensation_collected",
      title:`Compensation Collected — ${residentMatch.name}`,
      body:`$${parseFloat(amountReceived).toFixed(2)} collected by ${raName} · Conf# ${no}`,
      residentId:residentMatch.id,
      residentName:residentMatch.name,
      amount:amountReceived,
      confirmNo:no,
      weeks:weekData.filter(w=>selectedWeeks[w.sunStr]).map(w=>w.label),
      missedWeeks:weekData.filter(w=>missedWeeks[w.sunStr]).map(w=>w.label),
      collectedBy:raName,
      date:TODAY,
      read:false, actioned:false, createdAt:TODAY,
    });
    // Also save to compensationLogs collection for ledger
    try {
      const { db } = await import("./firebase");
      const { collection, addDoc, serverTimestamp } = await import("firebase/firestore");
      await addDoc(collection(db,"compensationLogs"), {
        residentId: residentMatch.id,
        residentName: residentMatch.name,
        facility: residentMatch.facility,
        amount: parseFloat(amountReceived)||0,
        confirmNo: no,
        weeks: weekData.filter(w=>selectedWeeks[w.sunStr]).map(w=>w.label),
        missedWeeks: weekData.filter(w=>missedWeeks[w.sunStr]).map(w=>w.label),
        collectedBy: raName,
        date: TODAY,
        createdAt: serverTimestamp(),
      });
    } catch(e) { console.error("compensationLogs write failed:", e); }
    setSaving(false);
    setStage(4);
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:300,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"white",borderRadius:10,width:"100%",maxWidth:520,maxHeight:"92vh",display:"flex",flexDirection:"column",overflow:"hidden",border:`0.5px solid ${DS.border}`}}>
        <div style={{padding:"12px 16px",background:DS.blue,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div style={{fontSize:13,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>Compensation Collection</div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:5,color:"white",fontSize:11,padding:"3px 10px",cursor:"pointer"}}>✕ Close</button>
        </div>

        {/* Stage dots */}
        <div style={{display:"flex",borderBottom:`0.5px solid ${DS.border}`,background:DS.pageBg,flexShrink:0}}>
          {["Resident","Weeks","Collect","Receipt"].map((s,i)=>(
            <div key={s} style={{flex:1,padding:"5px 8px",fontSize:9,fontWeight:600,textAlign:"center",fontFamily:"'Inter',sans-serif",
              borderBottom:`2px solid ${stage===i+1?DS.blue:stage>i+1?DS.success.text:"transparent"}`,
              color:stage===i+1?DS.blue:stage>i+1?DS.success.text:DS.textSec}}>
              {stage>i+1?"✓ ":""}{s}
            </div>
          ))}
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"14px 16px",display:"flex",flexDirection:"column",gap:12}}>

          {/* Stage 1 — Resident */}
          {stage===1&&(
            <>
              <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:6,padding:"8px 12px",fontSize:12,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>
                Select the resident to collect compensation from. Balance is calculated from work logs.
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Select Resident</div>
                <div style={{border:`1px solid ${DS.border}`,borderRadius:8,overflow:"hidden",maxHeight:240,overflowY:"auto"}}>
                  {residents.filter(r=>r.status==="active").sort((a,b)=>a.name.localeCompare(b.name)).map(r=>(
                    <div key={r.id} onClick={()=>loadResidentData(r)}
                      style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",cursor:"pointer",
                        background:residentMatch?.id===r.id?DS.blueTint:"white",
                        borderBottom:`0.5px solid ${DS.border}`,
                        borderLeft:residentMatch?.id===r.id?`3px solid ${DS.blue}`:"3px solid transparent"}}
                      onMouseEnter={e=>{if(residentMatch?.id!==r.id)e.currentTarget.style.background=DS.pageBg;}}
                      onMouseLeave={e=>{if(residentMatch?.id!==r.id)e.currentTarget.style.background="white";}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500,color:residentMatch?.id===r.id?DS.blue:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(r.name)}</div>
                        <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{shortFacility(r.facility)} · Rm {r.room||"—"}</div>
                      </div>
                      {residentMatch?.id===r.id&&<i className="bi bi-check-circle-fill" style={{fontSize:16,color:DS.blue}}></i>}
                    </div>
                  ))}
                </div>
              </div>
              {residentMatch&&(
                <div style={{background:DS.pageBg,border:`0.5px solid ${DS.border}`,borderRadius:8,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(residentMatch.name)}</div>
                    <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>DOC# {residentMatch.doc} · {shortFacility(residentMatch.facility)}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Est. outstanding</div>
                    <div style={{fontSize:20,fontWeight:600,color:DS.error.text,fontFamily:"'DM Sans',sans-serif"}}>${totalOwed.toFixed(2)}</div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Stage 2 — Week selection */}
          {stage===2&&(
            <>
              <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Select weeks being paid. Override amounts if needed. Mark missed weeks.</div>
              {weekData.map(w=>{
                const isMissed = missedWeeks[w.sunStr];
                const isSelected = selectedWeeks[w.sunStr] && !isMissed;
                const amt = overrides[w.sunStr]!==undefined ? parseFloat(overrides[w.sunStr])||0 : calcOwed(w.hoursWorked);
                return (
                  <div key={w.sunStr} style={{border:`1px solid ${isMissed?DS.error.border:isSelected?DS.blueBorder:DS.border}`,borderRadius:8,padding:"10px 12px",background:isMissed?DS.error.bg:isSelected?DS.blueTint:"white"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:isMissed||isSelected?8:0}}>
                      <div onClick={()=>{if(!isMissed)setSelectedWeeks(s=>({...s,[w.sunStr]:!s[w.sunStr]}));}}
                        style={{width:18,height:18,borderRadius:4,border:`2px solid ${isSelected?DS.blue:DS.border}`,background:isSelected?DS.blue:"white",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
                        {isSelected&&<i className="bi bi-check" style={{fontSize:10,color:"white"}}></i>}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{w.label}</div>
                        <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{w.hoursWorked}hrs worked · ${calcOwed(w.hoursWorked).toFixed(2)} owed</div>
                      </div>
                      <button onClick={()=>setMissedWeeks(m=>({...m,[w.sunStr]:!m[w.sunStr]}))}
                        style={{fontSize:9,padding:"2px 8px",borderRadius:4,border:`1px solid ${isMissed?DS.error.border:DS.border}`,background:isMissed?DS.error.bg:"white",color:isMissed?DS.error.text:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        {isMissed?"✕ Missed":"Mark missed"}
                      </button>
                    </div>
                    {isSelected&&!isMissed&&(
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Override amount: $</span>
                        <input type="number" style={{...S.input,border:`1px solid ${DS.border}`,width:80,padding:"4px 8px",textAlign:"right"}}
                          placeholder={calcOwed(w.hoursWorked).toFixed(2)}
                          value={overrides[w.sunStr]||""}
                          onChange={e=>setOverrides(o=>({...o,[w.sunStr]:e.target.value}))}/>
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{background:DS.pageBg,borderRadius:8,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",border:`0.5px solid ${DS.border}`}}>
                <span style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Total collecting today</span>
                <span style={{fontSize:20,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>${totalOwed.toFixed(2)}</span>
              </div>
            </>
          )}

          {/* Stage 3 — Collect */}
          {stage===3&&(
            <>
              <div style={{background:DS.pageBg,border:`0.5px solid ${DS.border}`,borderRadius:8,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{displayName(residentMatch.name)}</div>
                  <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{weekData.filter(w=>selectedWeeks[w.sunStr]&&!missedWeeks[w.sunStr]).map(w=>w.label).join(", ")}</div>
                </div>
                <div style={{fontSize:22,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>${totalOwed.toFixed(2)}</div>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Payment Method</div>
                <div style={{background:DS.success.bg,border:`1px solid ${DS.success.border}`,borderRadius:6,padding:"8px 12px",fontSize:12,color:DS.success.text,fontWeight:500,fontFamily:"'Inter',sans-serif"}}>Cash only</div>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Amount Received ($)</div>
                <input type="number" style={{...S.input,border:`1px solid ${DS.border}`,fontSize:16}} placeholder={totalOwed.toFixed(2)} value={amountReceived} onChange={e=>setAmountReceived(e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Collected By</div>
                <input style={{...S.input,border:`1px solid ${DS.border}`}} value={raName||"RA"} readOnly/>
              </div>
              <div onClick={()=>setResidentAck(a=>!a)}
                style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",background:residentAck?DS.success.bg:DS.pageBg,borderRadius:6,border:`1px solid ${residentAck?DS.success.border:DS.border}`,cursor:"pointer"}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${residentAck?DS.success.text:DS.border}`,background:residentAck?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                  {residentAck&&<i className="bi bi-check" style={{fontSize:10,color:"white"}}></i>}
                </div>
                <span style={{fontSize:12,color:residentAck?DS.success.text:DS.textPrimary,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>
                  I, {residentMatch?.name||"_______"}, confirm I have paid the above compensation amount on {TODAY}.
                </span>
              </div>
            </>
          )}

          {/* Stage 4 — Receipt */}
          {stage===4&&(
            <div style={{border:"2px dashed #D8DCE3",borderRadius:8,padding:20,textAlign:"center"}}>
              <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Dorcus Home LLC</div>
              <div style={{fontSize:14,fontWeight:600,color:DS.textPrimary,marginBottom:2,fontFamily:"'DM Sans',sans-serif"}}>Compensation Receipt</div>
              <div style={{fontSize:11,color:DS.textSec,marginBottom:16,fontFamily:"'Inter',sans-serif"}}>{TODAY} · {timeNow()}</div>
              <div style={{height:1,background:DS.border,margin:"10px 0"}}/>
              <div style={{display:"flex",flexDirection:"column",gap:6,textAlign:"left",margin:"12px 0"}}>
                {[
                  ["Resident", displayName(residentMatch?.name||"")],
                  ["Weeks covered", weekData.filter(w=>selectedWeeks[w.sunStr]&&!missedWeeks[w.sunStr]).map(w=>w.label).join(", ")],
                  ["Payment", "Cash"],
                  ["Collected by", raName||"RA"],
                ].map(([label,val])=>(
                  <div key={label} style={{display:"flex",justifyContent:"space-between",fontSize:12,fontFamily:"'Inter',sans-serif",color:DS.textPrimary}}>
                    <span style={{color:DS.textSec}}>{label}</span>
                    <strong>{val}</strong>
                  </div>
                ))}
              </div>
              <div style={{height:1,background:DS.border,margin:"10px 0"}}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",margin:"12px 0"}}>
                <span style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>Total Collected</span>
                <span style={{fontSize:24,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>${parseFloat(amountReceived||totalOwed).toFixed(2)}</span>
              </div>
              <div style={{height:1,background:DS.border,margin:"10px 0"}}/>
              <div style={{marginTop:14}}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Confirmation Number</div>
                <div style={{fontSize:30,fontWeight:600,color:DS.blue,fontFamily:"'DM Sans',sans-serif",letterSpacing:"0.15em"}}>{confirmNo}</div>
                <div style={{fontSize:10,color:DS.textSec,marginTop:4,fontFamily:"'Inter',sans-serif"}}>Keep this number for your records</div>
              </div>
            </div>
          )}

        </div>

        <div style={{padding:"10px 14px",background:"white",borderTop:`0.5px solid ${DS.border}`,display:"flex",justifyContent:"flex-end",gap:8,flexShrink:0}}>
          {stage===1&&<>
            <button onClick={onClose} style={{...S.btnGhost,fontSize:11}}>Cancel</button>
            <button onClick={()=>setStage(2)} disabled={!residentMatch} style={{...S.btn,opacity:!residentMatch?0.4:1}}>View Weeks →</button>
          </>}
          {stage===2&&<>
            <button onClick={()=>setStage(1)} style={{...S.btnGhost,fontSize:11}}>← Back</button>
            <button onClick={()=>{setAmountReceived(totalOwed.toFixed(2));setStage(3);}} disabled={totalOwed===0} style={{...S.btn,opacity:totalOwed===0?0.4:1}}>Confirm Collection →</button>
          </>}
          {stage===3&&<>
            <button onClick={()=>setStage(2)} style={{...S.btnGhost,fontSize:11}}>← Back</button>
            <button onClick={handleSave} disabled={!amountReceived||!residentAck||saving}
              style={{...S.btnCTA,opacity:(!amountReceived||!residentAck)?0.4:1,fontFamily:"'Inter',sans-serif"}}>
              {saving?"Saving...":"Generate Receipt →"}
            </button>
          </>}
          {stage===4&&<>
            <button onClick={()=>window.print()} style={{...S.btn,display:"flex",alignItems:"center",gap:6}}><i className="bi bi-printer-fill" style={{fontSize:12}}></i> Print Receipt</button>
            <button onClick={onClose} style={{...S.btnGhost,fontSize:11}}>Done</button>
          </>}
        </div>
      </div>
    </div>
  );
}


function RAHome({ residents, perms, raName, onNavigate, abscondNotifications=[] }) {
  const [now, setNow] = useState(NOW_NY());
  const [headcounts, setHeadcounts] = useState([]);
  const [uaResults, setUAResults] = useState([]);
  const [quickName, setQuickName] = useState("");
  const [quickMatch, setQuickMatch] = useState(null);
  const [quickAction, setQuickAction] = useState("out");
  const [quickReason, setQuickReason] = useState(OUT_REASONS[0]);
  const [quickEst, setQuickEst] = useState("");
  const [quickNote, setQuickNote] = useState("");
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [statuses, setStatuses] = useState({});
  const [curfewPrompt, setCurfewPrompt] = useState(null);
  const [curfewNote, setCurfewNote] = useState("");
  const [hoursPrompt, setHoursPrompt] = useState(null);
  const [hoursWorked, setHoursWorked] = useState("");
  const [showMoneyRequest, setShowMoneyRequest] = useState(false);
  const [lateEntry, setLateEntry] = useState(null);
  const [lateTime, setLateTime] = useState("");
  const [lateReason, setLateReason] = useState("");

  const CURFEW_HOUR = 21;
  const CURFEW_MIN  = 30;

  const isPastCurfew = () => {
    const h = now.getHours(); const m = now.getMinutes();
    return h > CURFEW_HOUR || (h === CURFEW_HOUR && m >= CURFEW_MIN);
  };

  useEffect(() => {
    const t = setInterval(() => setNow(NOW_NY()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const unsub = subscribeToUAResults(TODAY, TODAY, perms.assignedFacility||null, results => {
      setUAResults(results.filter(r => r.date === TODAY));
    });
    return unsub;
  }, [perms.assignedFacility]);

  useEffect(() => {
    const unsub = subscribeToTodayInOutLogs(perms.assignedFacility, logs => {
      const latestByResident = {};
      logs.forEach(log => {
        const prev2 = latestByResident[log.residentId];
        if (!prev2) { latestByResident[log.residentId] = log; return; }
        const aS2 = log.createdAt?.seconds ?? null;
        const bS2 = prev2.createdAt?.seconds ?? null;
        if (aS2 !== null && bS2 !== null) {
          if (aS2 > bS2) latestByResident[log.residentId] = log;
          return;
        }
        const aT2 = log.time || "00:00";
        const bT2 = prev2.time || "00:00";
        if (aT2 > bT2 || (aT2 === bT2 && (log.id||"") > (prev2.id||""))) {
          latestByResident[log.residentId] = log;
        }
      });
      const statusMap = {};
      Object.entries(latestByResident).forEach(([id, log]) => {
        statusMap[id] = { status: log.eventType === "sign_out" ? "out" : "in", reason: log.reason, estReturn: log.estReturn, estReturnDate: log.estReturnDate||null, signOutDate: log.date||null, timeOut: log.time, note: log.note };
      });
      // Merge with existing state — don't wipe optimistic updates that haven't synced yet
      setStatuses(prev => ({...prev, ...statusMap}));
    });
    return unsub;
  }, [perms.assignedFacility]);

  useEffect(() => {
    const unsub = subscribeToHeadcounts(perms.assignedFacility, setHeadcounts);
    return unsub;
  }, [perms.assignedFacility]);

  // Sunday work log notification
  useEffect(() => {
    const day = new Date().getDay();
    if(day === 0) {
      const notifKey = `dorcus_worklog_notif_${TODAY}`;
      try {
        if(!localStorage.getItem(notifKey)) {
          saveNotification({ type:"worklog_weekly", title:"Weekly work logs complete", body:`Work movement logs for the week are ready for download · ${TODAY}`, residentId:null, read:false, actioned:false, createdAt:TODAY });
          localStorage.setItem(notifKey,"sent");
        }
      } catch {}
    }
  }, []);

  const getOverdueMinutes = (residentId) => {
    const s = statuses[residentId];
    if (!s || s.status !== "out") return 0;
    // Overnight detection: if signed out on a prior day, automatically overdue
    if (s.signOutDate && s.signOutDate < TODAY) {
      const daysOut = Math.floor((new Date(TODAY) - new Date(s.signOutDate)) / (1000*60*60*24));
      // If estReturn exists and estReturnDate is in future, not overdue yet
      if (s.estReturnDate && s.estReturnDate > TODAY) return 0;
      return Math.max(999, daysOut * 1440); // return large number indicating overdue (1440 min = 1 day)
    }
    if (s.estReturn) {
      const match = s.estReturn.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (match) {
        let h = parseInt(match[1]);
        const m = parseInt(match[2]);
        if (match[3]?.toUpperCase()==="PM" && h!==12) h+=12;
        if (match[3]?.toUpperCase()==="AM" && h===12) h=0;
        const etrTotalMins = h*60+m;
        const now = NOW_NY();
        const nowMins = now.getHours()*60+now.getMinutes();
        // If estReturnDate is today, compare normally
        // If estReturnDate is tomorrow (past midnight ETR), not overdue until that time tomorrow
        const etrDate = s.estReturnDate || TODAY;
        if (etrDate > TODAY) return 0; // ETR is tomorrow, not overdue yet
        if (etrDate < TODAY) {
          // ETR was yesterday - fully overdue, return large number
          return 999;
        }
        return nowMins > etrTotalMins ? nowMins - etrTotalMins : 0;
      }
    }
    return 0;
  };

  const myFacility = perms.assignedFacility;
  const CAP = 35;

  const inCount = residents.filter(r => (statuses[r.id]?.status || "in") === "in").length;
  const outCount = residents.filter(r => statuses[r.id]?.status === "out").length;
  const overdueResidents = residents.filter(r => getOverdueMinutes(r.id) > 0);

  const lastHeadcount = headcounts.length > 0 ? headcounts[0] : null;
  const minsSinceHC = lastHeadcount ? minutesSince(lastHeadcount.submittedAt) : null;
  const hcDue = minsSinceHC === null || minsSinceHC >= 150;
  const nextMedCall = getNextMedCall();
  const uaCompletedToday = uaResults.filter(r => (r.completedDate||r.date) === TODAY).length;

  const nameMatches = quickName.length >= 2
    ? residents.filter(r => r.name.toLowerCase().includes(quickName.toLowerCase()))
    : [];

  // Sorted: overdue → out → in
  const sortedResidents = [...residents].sort((a,b) => {
    const aScore = getOverdueMinutes(a.id) > 0 ? 0 : statuses[a.id]?.status === "out" ? 1 : 2;
    const bScore = getOverdueMinutes(b.id) > 0 ? 0 : statuses[b.id]?.status === "out" ? 1 : 2;
    return aScore - bScore;
  });

  const handleQuickSubmit = async () => {
    if (!quickMatch) return;
    const isOut = quickAction === "out";
    if (isOut) {
      const etrValid = quickEst && typeof quickEst === "string" && quickEst.includes(":");
      if (!etrValid) {
        alert("Estimated return time is required before signing out.");
        return;
      }
    }
    setQuickSubmitting(true);
    try {
      if (isOut) {
        const _qNow = NOW_NY(); const _qH = _qNow.getHours()*60+_qNow.getMinutes();
        const _qEtrParts = quickEst.match(/(\d+):(\d+)/);
        const _qEtrMins = _qEtrParts ? parseInt(_qEtrParts[1])*60+parseInt(_qEtrParts[2]) : 999;
        const _qEtrDate = _qEtrMins < _qH ? daysFromToday(1) : TODAY;
        await saveInOutEvent({ residentId:quickMatch.id, residentName:quickMatch.name, facility:quickMatch.facility, date:TODAY, eventType:"sign_out", reason:quickReason, estReturn:quickEst, estReturnDate:_qEtrDate, note:quickNote, time:timeNow() });
        setStatuses(p=>({...p,[quickMatch.id]:{...(p[quickMatch.id]||{}),status:"out",reason:quickReason,timeOut:timeNow(),estReturn:quickEst,timeIn:null,note:quickNote}}));
        if(quickReason === "Work") {
          await saveInOutEvent({ residentId:quickMatch.id, residentName:quickMatch.name, facility:quickMatch.facility, date:TODAY, eventType:"work_log_start", reason:"Work", time:timeNow() });
        }
      } else {
        const prev = statuses[quickMatch.id] || {};
        const overdueMins = getOverdueMinutes(quickMatch.id);
        const isWork = prev.reason === "Work";
        const pastCurfew = isPastCurfew() && !isWork;
        await saveInOutEvent({ residentId:quickMatch.id, residentName:quickMatch.name, facility:quickMatch.facility, date:TODAY, eventType:"sign_in", time:timeNow(), previousReason:prev.reason||null, timeWasOut:prev.timeOut||null });
        setStatuses(p=>({...p,[quickMatch.id]:{...(p[quickMatch.id]||{}),status:"in",reason:null,timeOut:null,estReturn:"",timeIn:timeNow()}}));

        // Work return — show hours prompt so staff can confirm hours worked
        if (isWork) {
          setHoursPrompt({ resident:quickMatch, timeOut:prev.timeOut||null });
          setQuickName(""); setQuickMatch(null); setQuickEst(""); setQuickNote(""); setQuickAction("out");
          setQuickSubmitting(false);
          return;
        }

        if (overdueMins > 0 || pastCurfew) {
          setCurfewPrompt({ resident:quickMatch, overdueMins, pastCurfew });
          setQuickName(""); setQuickMatch(null); setQuickEst(""); setQuickNote(""); setQuickAction("out");
          setQuickSubmitting(false);
          return;
        }
      }
      setQuickName(""); setQuickMatch(null); setQuickEst(""); setQuickNote(""); setQuickAction("out");
    } finally { setQuickSubmitting(false); }
  };

  const handleHoursSubmit = async () => {
    if(!hoursPrompt) return;
    await saveInOutEvent({ residentId:hoursPrompt.resident.id, residentName:hoursPrompt.resident.name, facility:hoursPrompt.resident.facility, date:TODAY, eventType:"work_log_end", hoursWorked:parseFloat(hoursWorked)||0, time:timeNow() });
    setHoursPrompt(null); setHoursWorked("");
  };

  const handleLateEntrySubmit = async () => {
    if(!lateEntry||!lateTime||!lateReason) return;
    if(lateEntry.action==="out"&&(!quickEst||!quickEst.includes(":"))) return;
    await saveInOutEvent({ residentId:lateEntry.resident.id, residentName:lateEntry.resident.name, facility:lateEntry.resident.facility, date:TODAY, eventType:lateEntry.action==="out"?"sign_out":"sign_in", reason:lateEntry.action==="out"?quickReason:null, estReturn:lateEntry.action==="out"?quickEst:"", time:lateTime, note:`[LATE ENTRY] ${lateReason}`, lateEntry:true });
    setStatuses(p=>({...p,[lateEntry.resident.id]:{...p[lateEntry.resident.id], status:lateEntry.action==="out"?"out":"in", reason:lateEntry.action==="out"?quickReason:null, timeOut:lateEntry.action==="out"?lateTime:null, timeIn:lateEntry.action==="in"?lateTime:null, estReturn:lateEntry.action==="out"?quickEst:"", _updated:Date.now()}}));
    setLateEntry(null); setLateTime(""); setLateReason(""); setQuickEst("");
  };

  const handleCurfewAction = async (action) => {
    if (!curfewPrompt) return;
    if (action === "note" && curfewNote.trim()) {
      await saveShiftNote({ date:TODAY, time:timeNow(), facility:curfewPrompt.resident.facility, raName, note:`[CURFEW/OVERDUE] ${curfewPrompt.resident.name} signed in late. ${curfewPrompt.overdueMins > 0 ? `Overdue by ${curfewPrompt.overdueMins} min.` : "Past curfew (9:30 PM)."} Note: ${curfewNote}`, type:"incident" });
    }
    setCurfewPrompt(null); setCurfewNote("");
  };

  const hColor = myFacility?.includes("1") ? DS.blue : DS.darkYellow;
  const hBorder = myFacility?.includes("1") ? DS.d1Border : DS.d2Border;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* Abscond banner */}
      {abscondNotifications.filter(n=>!n.resolved&&(!n.facility||n.facility===perms.assignedFacility)).map(n=>(
        <div key={n.id} style={{background:DS.error.bg,border:`1px solid ${DS.error.border}`,borderRadius:8,padding:"12px 16px",display:"flex",alignItems:"flex-start",gap:12}}>
          <i className="bi bi-exclamation-circle-fill" style={{fontSize:16,color:DS.error.text,flexShrink:0,marginTop:2}}></i>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:DS.error.text,marginBottom:4,fontFamily:"'DM Sans',sans-serif"}}>Abscond reported</div>
            <div style={{fontSize:12,color:DS.error.text,fontFamily:"'Inter',sans-serif"}}>{n.residentName} — reported at {n.reportedAt}</div>
          </div>
        </div>
      ))}

      {/* Overdue banner */}
      {overdueResidents.length > 0 && (
        <div style={{background:DS.error.bg,border:`1px solid ${DS.error.border}`,borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
          <i className="bi bi-clock-fill" style={{fontSize:14,color:DS.error.text,flexShrink:0}}></i>
          <div style={{fontSize:12,color:DS.error.text,fontFamily:"'Inter',sans-serif"}}>
            <strong>{overdueResidents.length} resident{overdueResidents.length!==1?"s":""} overdue</strong>
            {overdueResidents.map(r=>{
              const mins=getOverdueMinutes(r.id);
              const h=Math.floor(mins/60); const m=mins%60;
              const timeStr = h>0?`${h}h ${m}min`:`${m}min`;
              return <span key={r.id}> — {displayName(r.name)} · {timeStr}</span>;
            })}
          </div>
        </div>
      )}

      {/* House card + In/Out/Overdue stats */}
      <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:10,alignItems:"stretch"}}>
        <div style={{...S.card,borderLeft:`3px solid ${hBorder}`,minWidth:110}}>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>{shortFacility(myFacility)||"House"}</div>
          <div style={{fontSize:26,fontWeight:600,color:hColor,fontFamily:"'DM Sans',sans-serif"}}>{residents.length}<span style={{fontSize:13,color:DS.textSec,fontWeight:400}}>/{CAP}</span></div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          {[
            {label:"In",color:DS.success.text,value:inCount,bg:"white",border:DS.border},
            {label:"Out",color:DS.warning.text,value:outCount,bg:"white",border:DS.border},
            {label:"Overdue",color:DS.error.text,value:overdueResidents.length,bg:overdueResidents.length>0?DS.error.bg:"white",border:overdueResidents.length>0?DS.error.border:DS.border},
          ].map(({label,color,value,bg,border})=>(
            <div key={label} style={{...S.card,background:bg,border:`0.5px solid ${border}`,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:overdueResidents.length>0&&label==="Overdue"?DS.error.text:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>{label}</div>
              <div style={{fontSize:24,fontWeight:600,color,fontFamily:"'DM Sans',sans-serif"}}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Shift-end UA reminder banner */}
      {(()=>{
        const now = NOW_NY();
        const hr = now.getHours();
        const min = now.getMinutes();
        const minsIntoHour = hr*60 + min;
        const inShiftEndWindow = (
          (minsIntoHour >= 13*60+30 && minsIntoHour < 14*60) ||
          (minsIntoHour >= 21*60+30 && minsIntoHour < 22*60) ||
          (minsIntoHour >= 5*60+30 && minsIntoHour < 6*60)
        );
        const shiftLabel = minsIntoHour < 14*60 ? "Day" : minsIntoHour < 22*60 ? "Evening" : "Night";
        const showBanner = inShiftEndWindow && uaResults.length === 0;
        if (!showBanner) return null;
        return (
          <div style={{background:"#FFF3CD", border:"1px solid #F5C200", borderRadius:8, padding:"12px 16px", marginBottom:12, display:"flex", alignItems:"center", gap:10}}>
            <span style={{fontSize:18}}>⚠️</span>
            <div style={{flex:1}}>
              <div style={{fontSize:13, fontWeight:600, color:"#7A4F00", fontFamily:"'DM Sans',sans-serif"}}>No Urine Screen Logged This Shift</div>
              <div style={{fontSize:11, color:"#7A4F00", marginTop:2}}>{shiftLabel} shift ends soon. Complete at least one urine screen before signing off.</div>
            </div>
            <button onClick={()=>onNavigate && onNavigate("ua_bac")}
              style={{padding:"6px 14px", fontSize:11, fontWeight:600, borderRadius:6, border:"1px solid #7A4F00", background:"white", color:"#7A4F00", cursor:"pointer"}}>
              Go to UA/BAC →
            </button>
          </div>
        );
      })()}

      {/* RA Tasks */}
      {(()=>{
        const nowNY = NOW_NY();
        const shiftHour = nowNY.getHours();
        const isMorningShift = shiftHour >= 6 && shiftHour < 14;
        const isNoonPast = shiftHour >= 12;

        // Room checks + chores state (localStorage per day)
        const rcKey = `dorcus_room_checks_${TODAY}_${perms.assignedFacility}`;
        const choresKey = `dorcus_chores_${TODAY}_${perms.assignedFacility}`;
        const [roomChecksLogged, setRoomChecksLogged] = useState(()=>{try{return localStorage.getItem(rcKey)==="true";}catch{return false;}});
        const [choresLogged, setChoresLogged] = useState(()=>{try{return localStorage.getItem(choresKey)==="true";}catch{return false;}});
        const logRoomChecks = async () => { setRoomChecksLogged(true); try{localStorage.setItem(rcKey,"true");}catch{} await saveNotification({type:"room_checks_complete",title:"Room Checks Complete",body:`Room checks completed by ${raName} · ${TODAY}`,facility:perms.assignedFacility,read:false,actioned:false,createdAt:TODAY}); };
        const logChores = async () => { setChoresLogged(true); try{localStorage.setItem(choresKey,"true");}catch{} await saveNotification({type:"chores_complete",title:"Chores Complete",body:`Chores completed by ${raName} · ${TODAY}`,facility:perms.assignedFacility,read:false,actioned:false,createdAt:TODAY}); };
        const roomChecksOverdue = isMorningShift && isNoonPast && !roomChecksLogged;
        const choresOverdue = isMorningShift && isNoonPast && !choresLogged;

        // Fire drill — random once/month, stored in localStorage
        const drillKey = `dorcus_fire_drill_${TODAY.slice(0,7)}`; // YYYY-MM
        const [drillScheduled] = useState(()=>{
          try {
            const stored = localStorage.getItem(drillKey);
            if(stored) return JSON.parse(stored);
            // Schedule random day+time this month
            const now = NOW_NY();
            const daysInMonth = new Date(now.getFullYear(), now.getMonth()+1, 0).getDate();
            const day = Math.floor(Math.random()*daysInMonth)+1;
            const hour = Math.floor(Math.random()*15)+6; // 6am-9pm
            const scheduled = {day, hour, dateStr:`${TODAY.slice(0,7)}-${String(day).padStart(2,'0')}`};
            localStorage.setItem(drillKey, JSON.stringify(scheduled));
            return scheduled;
          } catch { return null; }
        });
        const isDrillDay = drillScheduled && TODAY === drillScheduled.dateStr && shiftHour >= drillScheduled.hour;
        const [drillLogged, setDrillLogged] = useState(()=>{try{return localStorage.getItem(`dorcus_drill_done_${TODAY}`)==="true";}catch{return false;}});
        const logDrill = async () => { setDrillLogged(true); try{localStorage.setItem(`dorcus_drill_done_${TODAY}`,"true");}catch{} await saveNotification({type:"fire_drill_complete",title:"Fire Drill Complete",body:`Fire drill completed by ${raName} · ${TODAY}`,facility:perms.assignedFacility,read:false,actioned:false,createdAt:TODAY}); };

        // UA names
        const uaTodayNames = residents.filter(r=>uaResults.some(ur=>ur.residentId===r.id && (ur.completedDate||ur.date)===TODAY)).map(r=>displayName(r.name));

        const tasks = [
          {label:"Headcount", icon:"bi-hash", page:"headcount",
           status:(()=>{ const fmtMins = m => m>=60?`${Math.floor(m/60)}h ${m%60}min ago`:`${m}min ago`; if(hcDue) return minsSinceHC===null?"Not done yet — due now":`Due now · Last: ${fmtMins(minsSinceHC)}`; const minsLeft=150-minsSinceHC; return `Done · ${fmtMins(minsSinceHC)} · next in ${minsLeft>=60?`${Math.floor(minsLeft/60)}h ${minsLeft%60}min`:`${minsLeft}min`}`; })(),
           bg:hcDue?DS.warning.bg:"white", border:hcDue?DS.warning.border:DS.border, color:hcDue?DS.warning.text:DS.success.text, iconColor:hcDue?DS.warning.text:DS.success.text},
          {label:"Med Call", icon:"bi-capsule-pill", page:"med_log",
           status:`${nextMedCall.label} — ${nextMedCall.timeStr}${(nextMedCall.h===0&&nextMedCall.m<=60&&!nextMedCall.tomorrow)?` · in ${nextMedCall.m}min`:""}${nextMedCall.tomorrow?" (tomorrow)":""}`,
           bg:"white", border:DS.border, color:DS.textSec, iconColor:DS.blue},
          {label:"Urine Screens", icon:"bi-droplet-half", page:"ua_bac",
           status:uaCompletedToday>0?`${uaCompletedToday} completed today`:"None completed yet · tap to view",
           subNames:uaCompletedToday>0?uaTodayNames:[],
           bg:uaCompletedToday>0?DS.success.bg:"white", border:uaCompletedToday>0?DS.success.border:DS.border, color:uaCompletedToday>0?DS.success.text:DS.textSec, iconColor:uaCompletedToday>0?DS.success.text:DS.warning.text},
          ...(isMorningShift?[
            {label:"Room Checks", icon:"bi-door-closed-fill", page:null, action:roomChecksLogged?null:()=>{if(window.confirm("Mark Room Checks as complete?"))logRoomChecks();},
             status:roomChecksLogged?"Completed ✓":roomChecksOverdue?"Overdue — log now":"Log when complete",
             bg:roomChecksLogged?DS.success.bg:roomChecksOverdue?DS.error.bg:"white",
             border:roomChecksLogged?DS.success.border:roomChecksOverdue?DS.error.border:DS.border,
             color:roomChecksLogged?DS.success.text:roomChecksOverdue?DS.error.text:DS.textSec,
             iconColor:roomChecksLogged?DS.success.text:roomChecksOverdue?DS.error.text:DS.blue},
            {label:"Chores", icon:"bi-stars", page:null, action:choresLogged?null:()=>{if(window.confirm("Mark Chores as complete?"))logChores();},
             status:choresLogged?"Completed ✓":choresOverdue?"Overdue — log now":"Log when complete",
             bg:choresLogged?DS.success.bg:choresOverdue?DS.error.bg:"white",
             border:choresLogged?DS.success.border:choresOverdue?DS.error.border:DS.border,
             color:choresLogged?DS.success.text:choresOverdue?DS.error.text:DS.textSec,
             iconColor:choresLogged?DS.success.text:choresOverdue?DS.error.text:"#F5C200"},
          ]:[]),
          ...(isDrillDay?[
            {label:"Fire Drill", icon:"bi-fire", page:null, action:drillLogged?null:logDrill,
             status:drillLogged?"Completed ✓":"Due today — log completion",
             bg:drillLogged?DS.success.bg:DS.error.bg, border:drillLogged?DS.success.border:DS.error.border,
             color:drillLogged?DS.success.text:DS.error.text, iconColor:drillLogged?DS.success.text:DS.error.text},
          ]:[]),
        ];

        return (
          <div style={{...S.card}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
              <span style={{fontSize:10,fontWeight:600,color:DS.blue,textTransform:"uppercase",letterSpacing:"0.1em",fontFamily:"'Inter',sans-serif"}}>RA Tasks</span>
              <div style={{flex:1,height:1,background:DS.border}}/>
            </div>
            {tasks.map(({label,icon,page,action,status,subNames,bg,border,color,iconColor})=>(
              <div key={label} onClick={()=>{if(page)onNavigate(page);else if(action)action();}}
                style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 12px",borderRadius:6,marginBottom:6,cursor:(page||action)?"pointer":"default",background:bg,border:`1px solid ${border}`,transition:"opacity 0.15s"}}
                onMouseEnter={e=>{if(page||action)e.currentTarget.style.opacity="0.85";}}
                onMouseLeave={e=>{e.currentTarget.style.opacity="1";}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <i className={`bi ${icon}`} style={{fontSize:16,color:iconColor,width:20,textAlign:"center"}}></i>
                  <div>
                    <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{label}</div>
                    <div style={{fontSize:11,color,fontFamily:"'Inter',sans-serif",marginTop:1}}>{status}</div>
                    {subNames&&subNames.length>0&&(
                      <div style={{fontSize:10,color,fontFamily:"'Inter',sans-serif",marginTop:2,opacity:0.8}}>{subNames.join(" · ")}</div>
                    )}
                  </div>
                </div>
                {(page||action)&&<i className="bi bi-chevron-right" style={{fontSize:11,color:DS.textSec}}></i>}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Quick sign in/out */}
      <div style={{...S.card}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <span style={{fontSize:10,fontWeight:600,color:DS.blue,textTransform:"uppercase",letterSpacing:"0.1em",fontFamily:"'Inter',sans-serif"}}>Quick Sign In / Out</span>
          <div style={{flex:1,height:1,background:DS.border}}/>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:quickMatch?12:0}}>
          <div style={{flex:1,position:"relative"}}>
            <input style={{...S.input,border:`1px solid ${DS.border}`,background:"white"}} placeholder="🔍  Search resident name..." value={quickName}
              onChange={e=>{setQuickName(e.target.value);setQuickMatch(null);}}/>
            {nameMatches.length > 0 && !quickMatch && (
              <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:100,background:"white",border:`1px solid ${DS.border}`,borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.12)"}}>
                {nameMatches.map(r=>{
                  const s=statuses[r.id];
                  const isOut=s?.status==="out";
                  return (
                    <div key={r.id} onClick={()=>{setQuickMatch(r);setQuickName(r.name);setQuickAction(isOut?"in":"out");}}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",cursor:"pointer",borderBottom:`0.5px solid ${DS.border}`}}
                      onMouseEnter={e=>e.currentTarget.style.background=DS.pageBg}
                      onMouseLeave={e=>e.currentTarget.style.background="white"}>
                      <div style={{width:7,height:7,borderRadius:"50%",background:isOut?DS.warning.text:DS.success.text,flexShrink:0}}/>
                      <span style={{fontSize:13,color:DS.textPrimary,flex:1,fontFamily:"'Inter',sans-serif"}}>{displayName(r.name)}</span>
                      <span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{isOut?"Out":"In"}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {quickMatch && (
            <div style={{display:"flex",gap:6}}>
              {["out","in"].map(a=>(
                <button key={a} onClick={()=>setQuickAction(a)}
                  style={{padding:"8px 14px",fontSize:12,fontWeight:600,borderRadius:8,cursor:"pointer",
                    background:quickAction===a?(a==="out"?DS.warning.bg:DS.success.bg):"transparent",
                    border:`1px solid ${quickAction===a?(a==="out"?DS.warning.border:DS.success.border):DS.border}`,
                    color:quickAction===a?(a==="out"?DS.warning.text:DS.success.text):DS.textSec,
                    fontFamily:"'Inter',sans-serif"}}>
                  Sign {a}
                </button>
              ))}
            </div>
          )}
        </div>
        {quickMatch && quickAction === "out" && (
          <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:10}}>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Reason</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {OUT_REASONS.map(r=>(
                  <button key={r} onClick={()=>setQuickReason(r)}
                    style={{padding:"5px 12px",fontSize:12,borderRadius:20,cursor:"pointer",
                      background:quickReason===r?DS.blueTint:"transparent",
                      border:`1px solid ${quickReason===r?DS.blue:DS.border}`,
                      color:quickReason===r?DS.blue:DS.textSec,
                      fontFamily:"'Inter',sans-serif"}}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Est. return *</div>
                <TimeInput value={quickEst} onChange={setQuickEst}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Note (optional)</div>
                <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="Add note..." value={quickNote} onChange={e=>setQuickNote(e.target.value)}/>
              </div>
            </div>
          </div>
        )}
        {quickMatch && (
          <div style={{display:"flex",gap:8,justifyContent:"space-between",marginTop:12,alignItems:"center"}}>
            <button onClick={()=>setLateEntry({resident:quickMatch,action:quickAction})} style={{...S.btnGhost,fontSize:11,display:"flex",alignItems:"center",gap:4}}>
              <i className="bi bi-clock-history" style={{fontSize:10}}></i> Late entry
            </button>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{setQuickMatch(null);setQuickName("");}} style={{...S.btnGhost,fontSize:12}}>Cancel</button>
              <button onClick={handleQuickSubmit}
                disabled={quickSubmitting||(quickAction==="out"&&(!quickEst||!quickEst.includes(":")))}
                style={{...S.btn,background:quickAction==="out"?DS.warning.text:DS.success.text,opacity:(quickSubmitting||(quickAction==="out"&&(!quickEst||!quickEst.includes(":"))))?0.4:1}}>
                {quickSubmitting?"...":quickAction==="out"?`Sign Out — ${displayName(quickMatch.name)}`:`Sign In — ${displayName(quickMatch.name)}`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Resident grid — original compact tile style */}
      <div style={{...S.card}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <span style={{fontSize:10,fontWeight:600,color:DS.blue,textTransform:"uppercase",letterSpacing:"0.1em",fontFamily:"'Inter',sans-serif"}}>Residents — {residents.length} total</span>
          <div style={{flex:1,height:1,background:DS.border}}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(148px,1fr))",gap:8}}>
          {sortedResidents.map(r=>{
            const s = statuses[r.id]||{status:"in"};
            const overdueMins = getOverdueMinutes(r.id);
            const isOverdue = overdueMins > 0;
            const isOut = s.status === "out";
            const tileBg = isOverdue?"#FBBFBF":isOut?"#FDE68A":"white";
            const tileBorder = isOverdue?DS.error.border:isOut?DS.warning.border:DS.border;
            const dotColor = isOverdue?DS.error.text:isOut?DS.warning.text:DS.success.text;
            const subColor = isOverdue?DS.error.text:isOut?DS.warning.text:DS.textSec;
            const sub = isOverdue
              ? `Overdue ${Math.floor(overdueMins/60)>0?`${Math.floor(overdueMins/60)}h `:""}${overdueMins%60}min`
              : isOut ? `Out · ${s.reason||""}` : "In building";
            return (
              <div key={r.id} style={{background:tileBg,border:`0.5px solid ${tileBorder}`,borderRadius:8,padding:"10px 12px"}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:dotColor,marginBottom:6}}/>
                <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{displayName(r.name)}</div>
                <div style={{fontSize:10,color:subColor,marginTop:2,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{sub}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hours worked prompt */}
      {hoursPrompt&&(
        <div style={{position:"fixed",inset:0,zIndex:300,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"white",borderRadius:10,padding:24,maxWidth:340,width:"100%",border:`0.5px solid ${DS.border}`}}>
            <div style={{fontSize:15,fontWeight:600,color:DS.textPrimary,marginBottom:6,fontFamily:"'DM Sans',sans-serif"}}>Hours worked?</div>
            <div style={{fontSize:12,color:DS.textSec,marginBottom:16,fontFamily:"'Inter',sans-serif"}}>{displayName(hoursPrompt.resident.name)} is signing in from Work.</div>
            <input type="number" min="0" max="24" step="0.5" style={{...S.input,border:`1px solid ${DS.border}`,fontSize:16,textAlign:"center",marginBottom:16}} placeholder="e.g. 8" value={hoursWorked} onChange={e=>setHoursWorked(e.target.value)}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setHoursPrompt(null)} style={{...S.btnGhost,fontSize:12}}>Skip</button>
              <button onClick={handleHoursSubmit} disabled={!hoursWorked} style={{...S.btn,opacity:!hoursWorked?0.5:1}}>Save Hours</button>
            </div>
          </div>
        </div>
      )}

      {/* Late entry modal */}
      {lateEntry&&(
        <div style={{position:"fixed",inset:0,zIndex:300,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"white",borderRadius:10,width:"100%",maxWidth:380,border:`0.5px solid ${DS.border}`,overflow:"hidden"}}>
            <div style={{padding:"12px 16px",background:DS.blue,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:13,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>Late Entry — {displayName(lateEntry.resident.name)}</div>
              <button onClick={()=>setLateEntry(null)} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:5,color:"white",fontSize:11,padding:"2px 8px",cursor:"pointer"}}>✕</button>
            </div>
            <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:12}}>
              <div style={{background:DS.warning.bg,border:`1px solid ${DS.warning.border}`,borderRadius:6,padding:"8px 12px",fontSize:11,color:DS.warning.text,fontFamily:"'Inter',sans-serif"}}>
                Use this to log a movement that already happened but wasn't recorded in real time.
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Movement type</div>
                <div style={{display:"flex",gap:6}}>
                  {["out","in"].map(a=>(
                    <button key={a} onClick={()=>setLateEntry(e=>({...e,action:a}))}
                      style={{flex:1,padding:"7px",borderRadius:6,cursor:"pointer",fontSize:12,
                        background:lateEntry.action===a?(a==="out"?DS.warning.bg:DS.success.bg):"white",
                        border:`1.5px solid ${lateEntry.action===a?(a==="out"?DS.warning.border:DS.success.border):DS.border}`,
                        color:lateEntry.action===a?(a==="out"?DS.warning.text:DS.success.text):DS.textSec,
                        fontFamily:"'Inter',sans-serif"}}>
                      Sign {a}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Actual time of movement</div>
                <TimeInput value={lateTime} onChange={setLateTime}/>
              </div>
              {lateEntry.action==="out"&&(
                <div>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Sign out reason</div>
                  <select style={{...S.input,border:`1px solid ${DS.border}`}} value={quickReason} onChange={e=>setQuickReason(e.target.value)}>
                    {["Leisure","Medical","Appointment","Work","P&P"].map(r=><option key={r}>{r}</option>)}
                  </select>
                </div>
              )}
              {lateEntry.action==="out"&&(
                <div>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Estimated return *</div>
                  <TimeInput value={quickEst} onChange={setQuickEst}/>
                </div>
              )}
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Reason not logged in real time *</div>
                <textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"none"}} placeholder="Required — explain why this entry was missed..." value={lateReason} onChange={e=>setLateReason(e.target.value)}/>
              </div>
            </div>
            <div style={{padding:"10px 16px",background:"white",borderTop:`0.5px solid ${DS.border}`,display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setLateEntry(null)} style={{...S.btnGhost,fontSize:11}}>Cancel</button>
              <button onClick={handleLateEntrySubmit} disabled={!lateTime||!lateReason||(lateEntry.action==="out"&&(!quickEst||!quickEst.includes(":")))} style={{...S.btn,opacity:(!lateTime||!lateReason||(lateEntry.action==="out"&&(!quickEst||!quickEst.includes(":"))))?0.5:1}}>Log Late Entry</button>
            </div>
          </div>
        </div>
      )}

      {/* Curfew prompt */}
      {curfewPrompt&&(
        <div style={{position:"fixed",inset:0,zIndex:300,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"white",borderRadius:10,padding:24,maxWidth:380,width:"100%",border:`0.5px solid ${DS.error.border}`}}>
            <div style={{fontSize:15,fontWeight:600,color:DS.error.text,marginBottom:8,fontFamily:"'DM Sans',sans-serif"}}>
              {curfewPrompt.overdueMins>0?"Overdue return":"Past curfew"} — {displayName(curfewPrompt.resident.name)}
            </div>
            <div style={{fontSize:12,color:DS.textSec,marginBottom:16,fontFamily:"'Inter',sans-serif"}}>
              {curfewPrompt.overdueMins>0?`Overdue by ${Math.floor(curfewPrompt.overdueMins/60)>0?`${Math.floor(curfewPrompt.overdueMins/60)}h ${curfewPrompt.overdueMins%60}min`:`${curfewPrompt.overdueMins}min`}.`:"Resident signed in after curfew (9:30 PM)."} Add a shift note.
            </div>
            <textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"none",marginBottom:12}} placeholder="Add note (required to log)..." value={curfewNote} onChange={e=>setCurfewNote(e.target.value)}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>handleCurfewAction("skip")} style={{...S.btnGhost,fontSize:12}}>Skip</button>
              <button onClick={()=>handleCurfewAction("note")} disabled={!curfewNote.trim()} style={{...S.btn,opacity:!curfewNote.trim()?0.5:1}}>Save Note</button>
            </div>
          </div>
        </div>
      )}

      {/* Money Request Modal */}
      {showMoneyRequest&&<CompensationModal residents={residents} raName={raName} onClose={()=>setShowMoneyRequest(false)}/>}

    </div>
  );
}

// ── Unscheduled Urine Test Testing ─────────────────────────────────────────────────────────
const FIVE_PANEL_SUBSTANCES = [
  "Marijuana (THC)",
  "Cocaine",
  "Opiates",
  "Amphetamines",
  "PCP",
];

const ADHOC_REASONS = [
  { id:"suspicion",  label:"Suspicious Behavior",       sub:"Behavioral indicators observed" },
  { id:"random",     label:"Random Selection",           sub:"Outside scheduled rotation" },
  { id:"incident",   label:"Post-Incident",              sub:"Following a reported incident" },
  { id:"curfew",     label:"Return Late from Curfew/Leisure", sub:"Resident returned past scheduled curfew" },
];

function AdHocUA({ residents, raName }) {
  const [residentName, setResidentName] = useState("");
  const [residentMatch, setResidentMatch] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState("");
  const [substance, setSubstance] = useState("");
  const [date, setDate] = useState(TODAY);
  const [time, setTime] = useState(timeNow());
  const [saving, setSaving] = useState(false);
  const [recentTests, setRecentTests] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const activeResidents = residents.filter(r=>r.status==="active");
  const nameMatches = residentName.length>1&&!residentMatch ? activeResidents.filter(r=>r.name.toLowerCase().includes(residentName.toLowerCase())) : [];

  useEffect(()=>{
    const unsub = subscribeToUAResults(null,null,null,all=>{
      setRecentTests(all.filter(r=>r.adHoc).sort((a,b)=>(b.date||"").localeCompare(a.date||"")).slice(0,10));
    });
    return unsub;
  },[]);

  const canSubmit = residentMatch && reason && result && (result==="pass"||(result==="fail"&&substance.trim()));

  const handleSubmit = async () => {
    if(!canSubmit) return;
    setSaving(true);
    try {
      let photoUrl = null;
      if (photoFile) {
        try { photoUrl = await uploadDocument(photoFile, `ua-results/${residentMatch.id}/${Date.now()}`); } catch(e) {}
      }
      await saveUAResult({
        residentId: residentMatch.id, residentName: residentMatch.name,
        facility: residentMatch.facility, date, completedDate: TODAY, time, result,
        substance: result==="fail"?substance:"",
        reason, notes, adHoc:true,testType:"unscheduled", reportedBy:raName,
        weekStart:TODAY, weekEnd:TODAY,
        ...(photoUrl ? { photoUrl } : {}),
      });
      if(result==="fail") {
        await saveNotification({
          type:"ua_fail_adhoc",
          title:`Unscheduled Urine Test Fail — ${residentMatch.name}`,
          body:`${displayName(residentMatch.name)} failed an Unscheduled urine test (${substance}). Reported by ${raName}. Reason: ${ADHOC_REASONS.find(r=>r.id===reason)?.label||reason}.`,
          residentId: residentMatch.id, residentName: residentMatch.name,
          read:false, actioned:false, createdAt:TODAY,
        });
      }
      // Reset
      setResidentName(""); setResidentMatch(null); setReason("");
      setNotes(""); setResult(""); setSubstance("");
      setDate(TODAY); setTime(timeNow());
      setPhotoFile(null); setPhotoPreview(null);
      alert(`Unscheduled Urine Test ${result} logged for ${displayName(residentMatch.name)}.`);
    } catch(e) { alert("Failed: "+e.message); }
    setSaving(false);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{background:DS.warning.bg,border:`1px solid ${DS.warning.border}`,borderRadius:8,padding:"10px 14px",fontSize:12,color:DS.warning.text,display:"flex",alignItems:"center",gap:8}}>
        <i className="bi bi-lightning-charge-fill"></i>
        <span><strong>Unscheduled Urine Test</strong> — Test any resident outside the scheduled rotation. Admin is notified on a fail result.</span>
      </div>

      {/* Step 1: Resident */}
      <div style={{...S.card,padding:0,overflow:"hidden"}}>
        <div style={{padding:"9px 14px",background:DS.darkBlue}}>
          <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em"}}>1 — Select Resident</span>
        </div>
        <div style={{padding:14}}>
          <div>
            <select style={{...S.input,border:`1px solid ${DS.border}`}}
              value={residentMatch?.id||""}
              onChange={e=>{
                const r=activeResidents.find(r=>r.id===e.target.value);
                setResidentMatch(r||null);
                setResidentName(r?displayName(r.name):"");
              }}>
              <option value="">Select resident...</option>
              {activeResidents.sort((a,b)=>displayName(a.name).localeCompare(displayName(b.name))).map(r=>(
                <option key={r.id} value={r.id}>{displayName(r.name)} — {shortFacility(r.facility)}</option>
              ))}
            </select>
          </div>
          {residentMatch&&(
            <div style={{marginTop:8,background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:8,padding:"8px 12px",display:"flex",alignItems:"center",gap:8}}>
              <i className="bi bi-person-check-fill" style={{color:DS.blue,fontSize:14}}></i>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:DS.blue}}>{displayName(residentMatch.name)}</div>
                <div style={{fontSize:11,color:DS.textSec}}>{shortFacility(residentMatch.facility)} · Rm {residentMatch.room||"—"}</div>
              </div>
              <button onClick={()=>{setResidentMatch(null);setResidentName("");}} style={{fontSize:11,color:DS.textSec,background:"none",border:"none",cursor:"pointer"}}>✕</button>
            </div>
          )}
        </div>
      </div>

      {/* Step 2: Reason */}
      <div style={{...S.card,padding:0,overflow:"hidden"}}>
        <div style={{padding:"9px 14px",background:DS.darkBlue}}>
          <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em"}}>2 — Reason for Test</span>
        </div>
        <div style={{padding:14,display:"flex",flexDirection:"column",gap:6}}>
          {ADHOC_REASONS.map(r=>(
            <div key={r.id} onClick={()=>setReason(r.id)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,cursor:"pointer",
                background:reason===r.id?DS.blueTint:"white",
                border:`1px solid ${reason===r.id?DS.blueBorder:DS.border}`}}>
              <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${reason===r.id?DS.blue:DS.border}`,background:reason===r.id?DS.blue:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {reason===r.id&&<div style={{width:6,height:6,borderRadius:"50%",background:"white"}}/>}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:reason===r.id?600:400,color:reason===r.id?DS.blue:DS.textPrimary}}>{r.label}</div>
                <div style={{fontSize:11,color:DS.textSec}}>{r.sub}</div>
              </div>
            </div>
          ))}
          <div style={{marginTop:4}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Additional Notes (optional)</div>
            <textarea rows={2} style={{...S.input,resize:"none",border:`1px solid ${DS.border}`}}
              placeholder="Any additional context..." value={notes} onChange={e=>setNotes(e.target.value)}/>
          </div>
        </div>
      </div>

      {/* Step 3: Result */}
      <div style={{...S.card,padding:0,overflow:"hidden"}}>
        <div style={{padding:"9px 14px",background:DS.darkBlue}}>
          <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em"}}>3 — Log Result</span>
        </div>
        <div style={{padding:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <button onClick={()=>setResult("pass")}
              style={{padding:"14px",fontSize:14,fontWeight:700,borderRadius:8,cursor:"pointer",
                background:result==="pass"?DS.success.bg:"white",
                border:`2px solid ${result==="pass"?DS.success.border:DS.border}`,
                color:result==="pass"?DS.success.text:DS.textSec}}>✓ Pass</button>
            <button onClick={()=>setResult("fail")}
              style={{padding:"14px",fontSize:14,fontWeight:700,borderRadius:8,cursor:"pointer",
                background:result==="fail"?DS.error.bg:"white",
                border:`2px solid ${result==="fail"?DS.error.border:DS.border}`,
                color:result==="fail"?DS.error.text:DS.textSec}}>✗ Fail</button>
          </div>
          {result==="fail"&&(
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,color:DS.error.text,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Substance Detected *</div>
              <select style={{...S.input,border:`1px solid ${DS.error.border}`}} value={substance} onChange={e=>setSubstance(e.target.value)}>
                <option value="">Select substance...</option>
                {FIVE_PANEL_SUBSTANCES.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Test Date</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} type="date" value={date} onChange={e=>setDate(e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Test Time</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} type="time" value={time} onChange={e=>setTime(e.target.value)}/>
            </div>
          </div>
        </div>
      </div>

      <div style={{...S.card,border:`1px solid ${DS.border}`}}>
        <div style={{fontSize:11,fontWeight:600,color:DS.textPrimary,marginBottom:8}}>📷 Photo of Test Result (optional)</div>
        {photoPreview ? (
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <img src={photoPreview} alt="Test result" style={{width:64,height:64,objectFit:"cover",borderRadius:8,border:`1px solid ${DS.border}`}}/>
            <button onClick={()=>{setPhotoFile(null);setPhotoPreview(null);}} style={{...S.btnGhost,fontSize:11}}>Remove</button>
          </div>
        ) : (
          <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:8,border:`1px dashed ${DS.border}`,cursor:"pointer",fontSize:12,color:DS.textSec,background:DS.pageBg}}>
            Take / Upload Photo
            <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;setPhotoFile(f);const r=new FileReader();r.onload=ev=>setPhotoPreview(ev.target.result);r.readAsDataURL(f);}}/>
          </label>
        )}
      </div>

      <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
        <button onClick={()=>{setResidentMatch(null);setResidentName("");setReason("");setNotes("");setResult("");setSubstance("");setPhotoFile(null);setPhotoPreview(null);}}
          style={{...S.btnGhost,fontSize:12}}>Clear</button>
        <button onClick={handleSubmit} disabled={!canSubmit||saving}
          style={{...S.btnCTA,fontSize:12,opacity:!canSubmit?0.4:1,background:result==="fail"?DS.error.text:result==="pass"?DS.success.text:DS.blue,border:"none"}}>
          {saving?"Saving...":"Save Result"}
        </button>
      </div>

      {/* Recent ad-hoc tests */}
      {recentTests.length>0&&(
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          <div style={{padding:"9px 14px",background:DS.blue}}>
            <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em"}}>Recent Unscheduled Urine Tests</span>
          </div>
          {recentTests.map((t,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:`0.5px solid ${DS.border}`,
              background:t.result==="pass"?DS.success.bg:DS.error.bg}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:500,color:t.result==="pass"?DS.success.text:DS.error.text}}>{displayName(t.residentName)}</div>
                <div style={{fontSize:11,color:DS.textSec}}>{t.date} · {t.time||""} · {ADHOC_REASONS.find(r=>r.id===t.reason)?.label||t.reason||"Ad-hoc"}</div>
                {t.substance&&<div style={{fontSize:11,color:DS.error.text}}>Substance: {t.substance}</div>}
              </div>
              {t.photoUrl&&<img src={t.photoUrl} alt="Test" onClick={()=>window.open(t.photoUrl,"_blank")} style={{width:36,height:36,objectFit:"cover",borderRadius:6,border:`1px solid ${DS.border}`,cursor:"pointer",flexShrink:0}} title="View test photo"/>}
              <Pill label={t.result==="pass"?"Pass":"Fail"} bg={t.result==="pass"?DS.success.bg:DS.error.bg} border={t.result==="pass"?DS.success.border:DS.error.border} color={t.result==="pass"?DS.success.text:DS.error.text}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UABACTesting({ residents, raName, perms }) {
  const [tab, setTab] = useState("ua");
  return (
    <div>
      <div style={{display:"flex",gap:4,marginBottom:20,background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:8,padding:3,width:"fit-content"}}>
        {[{id:"ua",label:"Daily Urine Tests"},{id:"adhoc",label:"⚡ Unscheduled Urine Test"},{id:"bac",label:"Breathalyzer (BAC)"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:"7px 16px",fontSize:13,fontWeight:tab===t.id?600:400,borderRadius:6,cursor:"pointer",
              background:tab===t.id?DS.blue:"transparent",border:"none",
              color:tab===t.id?"white":DS.textSec,transition:"all 0.15s",fontFamily:"'Inter',sans-serif"}}>
            {t.label}
          </button>
        ))}
      </div>
      {tab==="ua" && <UATracker residents={residents} perms={perms}/>}
      {tab==="adhoc" && <AdHocUA residents={residents} raName={raName}/>}
      {tab==="bac" && <BACTracker residents={residents} raName={raName} perms={perms}/>}
    </div>
  );
}

// ── Compensation Ledger Report ────────────────────────────────────────────────
const TableWrap = ({cols, rows, emptyMsg="No data for this period."}) => (
  <div style={{background:DS.white,border:`0.5px solid ${DS.border}`,borderRadius:8,overflow:"hidden",marginBottom:16}}>
    <div style={{display:"grid",gridTemplateColumns:`repeat(${cols.length},minmax(0,1fr))`,padding:"8px 12px",background:DS.blue,borderBottom:`0.5px solid ${DS.border}`}}>
      {cols.map(c=><div key={c.label} style={{fontSize:11,color:DS.textPrimary,fontWeight:500,fontFamily:"'Inter',sans-serif"}}>{c.label}</div>)}
    </div>
    {rows.length===0&&<div style={{textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13,fontFamily:"'Inter',sans-serif"}}>{emptyMsg}</div>}
    {rows.slice(0,100).map((r,i)=>(
      <div key={i} style={{display:"grid",gridTemplateColumns:`repeat(${cols.length},minmax(0,1fr))`,padding:"8px 12px",borderBottom:`0.5px solid ${DS.border}`,background:r._alert?DS.error.bg:i%2===0?DS.white:DS.pageBg}}>
        {cols.map(c=><div key={c.key} style={{fontSize:12,color:r._alertCol&&c.alert?DS.error.text:DS.textPrimary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>{r[c.key]||"—"}</div>)}
      </div>
    ))}
    {rows.length>100&&<div style={{textAlign:"center",padding:"8px 0",fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Showing first 100 of {rows.length} rows — export CSV for full data</div>}
  </div>
);

const CompStatCard = ({label,value,color=DS.blue}) => (
  <div style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:8,padding:"12px 14px"}}>
    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>{label}</div>
    <div style={{fontSize:20,fontWeight:600,color,fontFamily:"'DM Sans',sans-serif"}}>{value}</div>
  </div>
);

function CompensationLedgerReport() {
  const [compData, setCompData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    (async()=>{
      try {
        const { db } = await import("./firebase");
        const { collection, getDocs } = await import("firebase/firestore");
        const snap = await getDocs(collection(db,"compensationLogs"));
        const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
        docs.sort((a,b)=>(b.date||"").localeCompare(a.date||""));
        setCompData(docs);
      } catch(e) { console.error(e); }
      setLoading(false);
    })();
  },[]);

  if(loading) return <div style={{...S.card,textAlign:"center",padding:"40px 0",color:DS.textSec,fontSize:13}}>Loading compensation data...</div>;

  const totalAll = compData.reduce((s,l)=>s+(parseFloat(l.amount)||0),0);
  const byResident = compData.reduce((acc,l)=>{
    const rid = l.residentId||"unknown";
    const rname = l.residentName||"Unknown Resident";
    if(!acc[rid]) acc[rid]={residentId:rid,residentName:rname,facility:l.facility||"",total:0,collections:0};
    acc[rid].total += parseFloat(l.amount)||0;
    acc[rid].collections++;
    return acc;
  },{});
  const residentSummary = Object.values(byResident).sort((a,b)=>b.total-a.total);

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
        <CompStatCard label="Total Collected" value={`$${totalAll.toFixed(2)}`} color={DS.success.text}/>
        <CompStatCard label="Total Payments" value={compData.length} color={DS.blue}/>
        <CompStatCard label="Residents w/ Payments" value={residentSummary.length} color={DS.warning.text}/>
      </div>
      {residentSummary.length>0&&(
        <div style={{...S.card,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,marginBottom:10,fontFamily:"'DM Sans',sans-serif"}}>By Resident</div>
          {residentSummary.map(r=>(
            <div key={r.residentId} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:`0.5px solid ${DS.border}`}}>
              <div style={{flex:1,fontSize:13,color:DS.textPrimary}}>{displayName(r.residentName)}</div>
              <div style={{fontSize:11,color:DS.textSec}}>{shortFacility(r.facility)}</div>
              <div style={{fontSize:11,color:DS.textSec}}>{r.collections} payment{r.collections!==1?"s":""}</div>
              <div style={{fontSize:13,fontWeight:600,color:DS.success.text}}>${r.total.toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
      {compData.length===0?(
        <div style={{...S.card,textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>No compensation collected yet</div>
      ):(
        <TableWrap
          cols={[{label:"Resident",key:"_name"},{label:"Facility",key:"_fac"},{label:"Date",key:"date"},{label:"Amount",key:"_amt"},{label:"Conf#",key:"confirmNo"},{label:"Collected By",key:"collectedBy"}]}
          rows={compData.map(l=>({...l,_name:displayName(l.residentName),_fac:shortFacility(l.facility),_amt:`$${parseFloat(l.amount||0).toFixed(2)}`}))}
        />
      )}
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
        <button onClick={()=>exportCSV(compData.map(l=>({...l,residentName:displayName(l.residentName),amount:`$${parseFloat(l.amount||0).toFixed(2)}`})),["residentName","facility","date","amount","confirmNo","collectedBy"],"compensation-ledger")} style={{...S.btnSec,fontSize:12}}>⬇ CSV</button>
      </div>
    </div>
  );
}

// ── Reports Section ───────────────────────────────────────────────────────────

// ── RA Timesheets Report ──────────────────────────────────────────────────────
function StaffTimesheetsReport() {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [weekOf, setWeekOf] = React.useState((() => {
    const d = new Date(); const day = d.getDay();
    d.setDate(d.getDate() - day); return d.toISOString().split("T")[0];
  })());

  React.useEffect(() => {
    (async () => {
      try {
        const { db } = await import("./firebase");
        const { collection, query, where, getDocs, orderBy } = await import("firebase/firestore");
        const weekStart = new Date(weekOf + "T00:00:00");
        const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 6);
        const startStr = weekStart.toISOString().split("T")[0];
        const endStr = weekEnd.toISOString().split("T")[0];
        const q = query(collection(db,"staffShiftLogs"), where("date",">=",startStr), where("date","<=",endStr), orderBy("date"), orderBy("clockIn"));
        const snap = await getDocs(q);
        setLogs(snap.docs.map(d=>({id:d.id,...d.data()})));
      } catch(e){ console.error(e); }
      setLoading(false);
    })();
  }, [weekOf]);

  const calcHours = (clockIn, clockOut) => {
    if(!clockIn || !clockOut) return null;
    return (new Date(clockOut) - new Date(clockIn)) / (1000*60*60);
  };

  const byRA = logs.reduce((acc, log) => {
    if(!acc[log.raId]) acc[log.raId] = { raId:log.raId, raName:log.raName, facility:log.facility, days:{} };
    if(!acc[log.raId].days[log.date]) acc[log.raId].days[log.date] = 0;
    const h = calcHours(log.clockIn, log.clockOut);
    if(h !== null) acc[log.raId].days[log.date] += h;
    return acc;
  }, {});

  const raRows = Object.values(byRA).map(ra => {
    const totalHours = Object.values(ra.days).reduce((s,h)=>s+h, 0);
    const regularHours = Math.min(totalHours, 40);
    const overtimeHours = Math.max(0, totalHours - 40);
    const nameParts = (ra.raName||"").trim().split(" ");
    return { ...ra, totalHours, regularHours, overtimeHours,
      firstName: nameParts[0]||"", lastName: nameParts.slice(1).join(" ")||"" };
  });

  const openShifts = logs.filter(l => l.clockIn && !l.clockOut);

  const weekDays = Array.from({length:7},(_,i)=>{ const d=new Date(weekOf+"T00:00:00"); d.setDate(d.getDate()+i); return d.toISOString().split("T")[0]; });

  const exportGusto = () => {
    const headers = ["First Name","Last Name","Date","Regular Hours","Overtime Hours"];
    const rows = [];
    raRows.forEach(ra => {
      weekDays.forEach(date => {
        const dayHours = ra.days[date]||0;
        if(dayHours > 0) rows.push([ra.firstName, ra.lastName, date, dayHours.toFixed(2), "0.00"]);
      });
      if(ra.overtimeHours>0){
        const lastDay = weekDays[6];
        const existing = rows.find(r=>r[0]===ra.firstName&&r[1]===ra.lastName&&r[2]===lastDay);
        if(existing){ existing[3]=Math.min(ra.days[lastDay]||0,8).toFixed(2); existing[4]=ra.overtimeHours.toFixed(2); }
      }
    });
    const csv = [headers,...rows].map(r=>r.join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download=`gusto-timesheets-${weekOf}.csv`; a.click();
  };

  if(loading) return <div style={{padding:20,color:DS.textSec,fontFamily:"'Inter',sans-serif",fontSize:13}}>Loading...</div>;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif",marginBottom:3}}>Week of (Sunday)</div>
          <input type="date" value={weekOf} onChange={e=>{setWeekOf(e.target.value);setLoading(true);}}
            style={{...S.input,border:`1px solid ${DS.border}`,width:"auto"}}/>
        </div>
        <button onClick={exportGusto} style={{...S.btn,fontSize:12,marginTop:16}}>&darr; Export Gusto CSV</button>
      </div>

      {openShifts.length>0&&(
        <div style={{background:"#FFF3CD",border:"1px solid #F5C200",borderRadius:8,padding:"10px 14px"}}>
          <div style={{fontSize:12,fontWeight:600,color:"#7A4F00",fontFamily:"'DM Sans',sans-serif",marginBottom:4}}>&#9888; {openShifts.length} Open Shift{openShifts.length>1?"s":""}</div>
          {openShifts.map(s=>(
            <div key={s.id} style={{fontSize:12,color:"#7A4F00",fontFamily:"'Inter',sans-serif"}}>{s.raName} &middot; {s.date} &middot; Clocked in {s.clockInDisplay} &mdash; no clock-out recorded</div>
          ))}
        </div>
      )}

      {raRows.length===0 ? (
        <div style={{padding:"24px",textAlign:"center",color:DS.textSec,fontSize:13,fontFamily:"'Inter',sans-serif"}}>No shift logs found for this week.</div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {raRows.map(ra=>(
            <div key={ra.raId} style={{background:"white",border:`1px solid ${DS.border}`,borderRadius:8,padding:"12px 16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{ra.raName}</div>
                  <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{ra.facility}</div>
                </div>
                <div style={{display:"flex",gap:12,alignItems:"center"}}>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Regular</div>
                    <div style={{fontSize:15,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{ra.regularHours.toFixed(1)}h</div>
                  </div>
                  {ra.overtimeHours>0&&(
                    <div style={{textAlign:"right",background:"#FFF3CD",border:"1px solid #F5C200",borderRadius:6,padding:"4px 10px"}}>
                      <div style={{fontSize:11,color:"#7A4F00",fontFamily:"'Inter',sans-serif"}}>Overtime</div>
                      <div style={{fontSize:15,fontWeight:600,color:"#7A4F00",fontFamily:"'DM Sans',sans-serif"}}>{ra.overtimeHours.toFixed(1)}h</div>
                    </div>
                  )}
                </div>
              </div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {weekDays.map(date=>{
                  const h = ra.days[date]||0;
                  return (
                    <div key={date} style={{flex:1,minWidth:60,textAlign:"center",padding:"6px 4px",background:h>0?DS.blueTint:DS.pageBg,borderRadius:6,border:`1px solid ${h>0?DS.blueBorder:DS.border}`}}>
                      <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{new Date(date+"T12:00:00").toLocaleDateString("en-US",{weekday:"short"})}</div>
                      <div style={{fontSize:12,fontWeight:600,color:h>0?DS.blue:DS.textSec,fontFamily:"'DM Sans',sans-serif"}}>{h>0?h.toFixed(1)+"h":"--"}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function RCDashboard({ referrals, residents, onNavigate }) {
  const [editModal, setEditModal] = React.useState(null); // {type:'referral'|'pending', item}
  const [editEntryDate, setEditEntryDate] = React.useState("");
  const [editStatus, setEditStatus] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState("active");

  const today = new Date(TODAY + "T00:00:00");
  const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today); monthAgo.setDate(monthAgo.getDate() - 30);

  const toDate = s => s ? new Date(s + "T00:00:00") : null;

  // Pipeline stats
  const allRefs = referrals || [];
  const activeStages = ["received","under_review","approved","pending_arrival","waitlisted"];
  const activeRefs = allRefs.filter(r => activeStages.includes(r.stage||r.status||"received"));
  const newThisWeek = allRefs.filter(r => { const d = toDate(r.createdAt?.toDate ? toNYDateStr(r.createdAt.toDate()) : r.dateReceived); return d && d >= weekAgo; });
  const acceptedThisMonth = allRefs.filter(r => { const s = r.stage||r.status; if(s!=="accepted") return false; const d = toDate(r.updatedAt?.toDate ? toNYDateStr(r.updatedAt.toDate()) : r.dateReceived); return d && d >= monthAgo; });
  const rejectedThisMonth = allRefs.filter(r => { const s = r.stage||r.status; if(!["rejected","withdrawn"].includes(s)) return false; const d = toDate(r.dateReceived); return d && d >= monthAgo; });

  // Pending residents (status=pending, not yet active)
  const pendingResidents = (residents||[]).filter(r => r.status === "pending");

  const stageLabels = { received:"Received", under_review:"Under Review", approved:"Approved", pending_arrival:"Pending Arrival", accepted:"Active", waitlisted:"Waitlisted", rejected:"Rejected", withdrawn:"Withdrawn", archived:"Archived" };
  const stageBg = { received:"#E6F1FB", under_review:"#FFF3CD", approved:"#E6F4EC", pending_arrival:"#FFF3CD", accepted:"#E6F4EC", waitlisted:"#EEEDFE", rejected:"#FDECEA", withdrawn:"#F1EFE8", archived:"#F1EFE8" };
  const stageColor = { received:"#0C447C", under_review:"#854F0B", approved:"#1A6B35", pending_arrival:"#854F0B", accepted:"#1A6B35", waitlisted:"#3C3489", rejected:"#8B1A1A", withdrawn:"#4A5568", archived:"#4A5568" };

  const displayRefs = statusFilter === "active" ? activeRefs : statusFilter === "all" ? allRefs : allRefs.filter(r => (r.stage||r.status||"received") === statusFilter);

  const handleSaveEdit = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      const { doc, updateDoc, serverTimestamp } = await import("firebase/firestore");
      if (editModal.type === "referral") {
        await updateDoc(doc(db, "referrals", editModal.item.id), {
          stage: editStatus || editModal.item.stage,
          updatedAt: serverTimestamp(),
        });
      } else {
        const updates = {};
        if (editEntryDate) updates.entryDate = editEntryDate;
        if (editStatus) updates.status = editStatus;
        await updateDoc(doc(db, "residents", editModal.item.id), updates);
      }
      setEditModal(null);
    } catch(e) { console.error(e); }
    setSaving(false);
  };

  const RCStat = ({label, value, color=DS.blue, sub}) => (
    <div style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:8,padding:"12px 16px"}}>
      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>{label}</div>
      <div style={{fontSize:24,fontWeight:600,color,fontFamily:"'DM Sans',sans-serif"}}>{value}</div>
      {sub && <div style={{fontSize:10,color:DS.textSec,marginTop:2,fontFamily:"'Inter',sans-serif"}}>{sub}</div>}
    </div>
  );

  return (
    <div>
      {/* Stats row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
        <RCStat label="Active Pipeline" value={activeRefs.length} color={DS.blue}/>
        <RCStat label="New This Week" value={newThisWeek.length} color={"#854F0B"}/>
        <RCStat label="Accepted This Month" value={acceptedThisMonth.length} color={"#1A6B35"}/>
        <RCStat label="Pending Arrival" value={pendingResidents.length} sub="awaiting entry date" color={"#534AB7"}/>
      </div>

      {/* Pending residents */}
      {pendingResidents.length > 0 && (
        <div style={{background:"white",border:`1px solid #B5D4F4`,borderRadius:8,padding:"14px 16px",marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:10,fontFamily:"'DM Sans',sans-serif"}}>Pending Arrival</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {pendingResidents.map(r => (
              <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:DS.pageBg,borderRadius:6,border:`0.5px solid ${DS.border}`}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:500,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{r.name}</div>
                  <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>DOC# {r.doc||"—"} \xb7 {r.facility||"—"} \xb7 Entry: {r.entryDate||"Not set"}</div>
                </div>
                <button onClick={()=>{setEditModal({type:"pending",item:r});setEditEntryDate(r.entryDate||"");setEditStatus(r.status||"pending");}}
                  style={{padding:"4px 10px",fontSize:11,borderRadius:6,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Edit
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Referral pipeline */}
      <div style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:8,padding:"14px 16px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Referral Pipeline</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {[{id:"active",label:"Active"},{ id:"all",label:"All"},{id:"rejected",label:"Rejected"},{id:"withdrawn",label:"Withdrawn"}].map(f=>(
              <button key={f.id} onClick={()=>setStatusFilter(f.id)}
                style={{padding:"3px 10px",fontSize:11,borderRadius:99,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                  border:`1px solid ${statusFilter===f.id?DS.blue:DS.border}`,
                  background:statusFilter===f.id?DS.blue:"white",
                  color:statusFilter===f.id?"white":DS.textSec}}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {displayRefs.length === 0 ? (
          <div style={{textAlign:"center",padding:"24px 0",color:DS.textSec,fontSize:13}}>No referrals found</div>
        ) : (
          <div style={{border:`0.5px solid ${DS.border}`,borderRadius:6,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 110px 90px 80px",padding:"6px 12px",background:DS.pageBg,borderBottom:`0.5px solid ${DS.border}`}}>
              {["Applicant","Facility","Status",""].map(h=>(
                <div key={h} style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.06em",fontFamily:"'Inter',sans-serif"}}>{h}</div>
              ))}
            </div>
            {displayRefs.slice(0,30).map((r,i) => {
              const stage = r.stage||r.status||"received";
              return (
                <div key={r.id||i} style={{display:"grid",gridTemplateColumns:"1fr 110px 90px 80px",padding:"9px 12px",borderBottom:`0.5px solid ${DS.border}`,background:"white",alignItems:"center"}}
                  onMouseEnter={e=>e.currentTarget.style.background=DS.pageBg}
                  onMouseLeave={e=>e.currentTarget.style.background="white"}>
                  <div>
                    <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{r.name||r.applicantName||"—"}</div>
                    <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{r.dateReceived||""}{r.doc?` \xb7 DOC# ${r.doc}`:""}</div>
                  </div>
                  <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{r.facility||"—"}</div>
                  <div>
                    <span style={{fontSize:10,fontWeight:500,padding:"2px 7px",borderRadius:99,background:stageBg[stage]||"#F1EFE8",color:stageColor[stage]||"#4A5568",fontFamily:"'Inter',sans-serif"}}>
                      {stageLabels[stage]||stage}
                    </span>
                  </div>
                  <div style={{display:"flex",justifyContent:"flex-end"}}>
                    <button onClick={()=>{setEditModal({type:"referral",item:r});setEditStatus(stage);}}
                      style={{padding:"3px 8px",fontSize:11,borderRadius:6,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      Edit
                    </button>
                  </div>
                </div>
              );
            })}
            {displayRefs.length > 30 && (
              <div style={{padding:"8px 12px",fontSize:11,color:DS.textSec,textAlign:"center",fontFamily:"'Inter',sans-serif"}}>
                +{displayRefs.length-30} more \xb7 <button onClick={()=>onNavigate("referrals")} style={{background:"none",border:"none",color:DS.blue,cursor:"pointer",fontSize:11,fontFamily:"'Inter',sans-serif"}}>View full pipeline →</button>
              </div>
            )}
          </div>
        )}

        <div style={{marginTop:10,display:"flex",justifyContent:"flex-end"}}>
          <button onClick={()=>onNavigate("referrals")} style={{padding:"6px 14px",fontSize:12,borderRadius:6,border:`1px solid ${DS.blue}`,background:"white",color:DS.blue,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            Open Full Pipeline →
          </button>
        </div>
      </div>

      {/* Edit modal */}
      {editModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setEditModal(null)}>
          <div style={{background:"white",borderRadius:12,padding:24,width:360,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:600,color:DS.textPrimary,marginBottom:16,fontFamily:"'DM Sans',sans-serif"}}>
              {editModal.type==="pending"?"Edit Pending Resident":"Edit Referral"}
            </div>
            <div style={{fontSize:12,color:DS.textSec,marginBottom:16,fontFamily:"'Inter',sans-serif"}}>{editModal.item.name||editModal.item.applicantName}</div>

            {editModal.type === "pending" && (
              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Entry Date</div>
                <input type="date" value={editEntryDate} onChange={e=>setEditEntryDate(e.target.value)}
                  style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none",boxSizing:"border-box"}}/>
              </div>
            )}

            <div style={{marginBottom:20}}>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Status</div>
              <select value={editStatus} onChange={e=>setEditStatus(e.target.value)}
                style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none",boxSizing:"border-box"}}>
                {editModal.type==="referral"
                  ? ["received","under_review","approved","pending_arrival","accepted","waitlisted","rejected","withdrawn"].map(s=>(
                      <option key={s} value={s}>{stageLabels[s]}</option>
                    ))
                  : ["pending","active","withdrawn"].map(s=>(
                      <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>
                    ))
                }
              </select>
            </div>

            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setEditModal(null)}
                style={{padding:"7px 16px",fontSize:12,borderRadius:6,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                Cancel
              </button>
              <button onClick={handleSaveEdit} disabled={saving}
                style={{padding:"7px 16px",fontSize:12,borderRadius:6,border:"none",background:DS.blue,color:"white",cursor:"pointer",fontFamily:"'Inter',sans-serif",opacity:saving?0.6:1}}>
                {saving?"Saving...":"Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FireDrillReport({ facilityFilter, dateFrom, dateTo }) {
  const [drills, setDrills] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const StatCard = ({label,value,color}) => (
    <div style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:8,padding:"10px 14px"}}>
      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>{label}</div>
      <div style={{fontSize:22,fontWeight:600,color:color||DS.blue,fontFamily:"'DM Sans',sans-serif"}}>{value}</div>
    </div>
  );

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { collection, getDocs, query, orderBy } = await import("firebase/firestore");
        const snap = await getDocs(query(collection(db, "fireDrills"), orderBy("date","desc")));
        setDrills(snap.docs.map(d => ({id:d.id,...d.data()})));
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const filtered = drills.filter(d => {
    if (d.date < dateFrom || d.date > dateTo) return false;
    if (facilityFilter !== "all" && d.facility && !d.facility.includes(facilityFilter==="Dorcus 1 - 801"?"801":"24th")) return false;
    return true;
  });

  // Compliance check per month per facility per shift
  const SHIFTS = ["Day (6a-2p)", "Evening (2p-10p)", "Night (10p-6a)"];
  const facilities = facilityFilter === "all" ? ["Dorcus 1 - 801", "Dorcus 2 - 24th St"] : [facilityFilter];

  const getMonthKey = d => d.date ? d.date.substring(0,7) : "";
  const months = [...new Set(filtered.map(getMonthKey))].sort().reverse();

  const isWeekend = dateStr => {
    const d = new Date(dateStr + "T12:00:00");
    return d.getDay() === 0 || d.getDay() === 6;
  };

  const getShift = time => {
    if (!time) return "Unknown";
    const h = parseInt(time.split(":")[0]);
    if (h >= 6 && h < 14) return "Day (6a-2p)";
    if (h >= 14 && h < 22) return "Evening (2p-10p)";
    return "Night (10p-6a)";
  };

  const exportPDF = () => {
    const rows = filtered.map(d => `
      <tr>
        <td>${d.date}</td>
        <td>${d.time||"—"}</td>
        <td>${d.facility||"—"}</td>
        <td>${getShift(d.time)}</td>
        <td>${d.residentCount||"—"}</td>
        <td>${d.conductedBy||"—"}</td>
        <td>${d.duration||"—"}</td>
        <td>${isWeekend(d.date)?"Yes":"No"}</td>
      </tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Fire Drill Report</title>
<style>body{font-family:Arial,sans-serif;padding:24px;font-size:12px;}table{width:100%;border-collapse:collapse;}th{background:#1B5EBE;color:white;padding:6px 8px;text-align:left;font-size:11px;}td{padding:5px 8px;border-bottom:1px solid #eee;}</style>
</head><body>
<h2 style="margin:0 0 4px">Fire Drill Report</h2>
<p style="color:#666;margin:0 0 16px">${dateFrom} to ${dateTo} \xb7 Generated ${TODAY}</p>
<table><thead><tr><th>Date</th><th>Time</th><th>Facility</th><th>Shift</th><th>Residents</th><th>Conducted By</th><th>Duration</th><th>Weekend</th></tr></thead>
<tbody>${rows}</tbody></table></body></html>`;
    const w = window.open("","_blank"); w.document.write(html); w.document.close(); w.print();
  };

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
        <StatCard label="Total Drills" value={filtered.length} color={DS.blue}/>
        <StatCard label="Weekend Drills" value={filtered.filter(d=>isWeekend(d.date)).length} color={"#E85D20"}/>
        <StatCard label="Months Covered" value={months.length} color={DS.success?.text||"#1A6B35"}/>
      </div>

      {/* Compliance by month */}
      {months.length > 0 && (
        <div style={{marginBottom:16}}>
          {months.map(month => {
            const monthDrills = filtered.filter(d => getMonthKey(d) === month);
            const monthLabel = new Date(month+"-15").toLocaleDateString("en-US",{month:"long",year:"numeric"});
            return (
              <div key={month} style={{...S.card, marginBottom:8, padding:"12px 14px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{monthLabel}</div>
                  <div style={{display:"flex",gap:6}}>
                    {[{check: monthDrills.some(d=>isWeekend(d.date)), label:"Weekend \u2713"},
                      {check: SHIFTS.every(s=>monthDrills.some(d=>getShift(d.time)===s)), label:"All Shifts \u2713"}
                    ].map(({check,label})=>(
                      <span key={label} style={{fontSize:10,fontWeight:500,padding:"2px 8px",borderRadius:99,
                        background:check?"#E6F4EC":"#FDECEA",color:check?"#1A6B35":"#8B1A1A"}}>
                        {check ? label : label.replace("\u2713","\u2715")}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
                  {SHIFTS.map(shift => {
                    const shiftDrills = monthDrills.filter(d=>getShift(d.time)===shift);
                    return (
                      <div key={shift} style={{background:shiftDrills.length>0?"#E6F4EC":"#FDECEA",borderRadius:6,padding:"6px 10px",border:`1px solid ${shiftDrills.length>0?"#A8D5B5":"#F5C6C6"}`}}>
                        <div style={{fontSize:10,color:shiftDrills.length>0?"#1A6B35":"#8B1A1A",fontWeight:500,marginBottom:2}}>{shift}</div>
                        <div style={{fontSize:13,fontWeight:600,color:shiftDrills.length>0?"#1A6B35":"#8B1A1A"}}>{shiftDrills.length} drill{shiftDrills.length!==1?"s":""}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Drill log table */}
      {loading ? (
        <div style={{textAlign:"center",padding:"24px",color:DS.textSec}}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{...S.card,textAlign:"center",padding:"24px",color:DS.textSec}}>No fire drills found for this date range</div>
      ) : (
        <div style={{border:`0.5px solid ${DS.border}`,borderRadius:8,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"80px 70px 1fr 120px 70px 100px",padding:"6px 12px",background:DS.pageBg,borderBottom:`0.5px solid ${DS.border}`}}>
            {["Date","Time","Facility / Shift","Conducted By","Residents","Duration"].map(h=>(
              <div key={h} style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.06em",fontFamily:"'Inter',sans-serif"}}>{h}</div>
            ))}
          </div>
          {filtered.map((d,i) => (
            <div key={d.id||i} style={{display:"grid",gridTemplateColumns:"80px 70px 1fr 120px 70px 100px",padding:"8px 12px",borderBottom:`0.5px solid ${DS.border}`,background:i%2===0?"white":DS.pageBg,alignItems:"center"}}>
              <div style={{fontSize:12,color:DS.textPrimary}}>
                {d.date}
                {isWeekend(d.date) && <span style={{fontSize:9,fontWeight:600,marginLeft:4,padding:"1px 4px",borderRadius:3,background:"#FFF3CD",color:"#854F0B"}}>WKD</span>}
              </div>
              <div style={{fontSize:12,color:DS.textSec,whiteSpace:"nowrap"}}>{d.time||"—"}</div>
              <div>
                <div style={{fontSize:12,color:DS.textPrimary}}>{d.facility||"—"}</div>
                <div style={{fontSize:10,color:DS.textSec}}>{getShift(d.time)}</div>
              </div>
              <div style={{fontSize:12,color:DS.textPrimary}}>{d.conductedBy||"—"}</div>
              <div style={{fontSize:12,color:DS.textSec}}>{d.residentCount||"—"}</div>
              <div style={{fontSize:12,color:DS.textSec}}>{d.duration||"—"}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:12}}>
        <button onClick={exportPDF} style={{...S.btn,fontSize:12}}>\u2b07 PDF</button>
      </div>
    </div>
  );
}

function ProgressReportsSection({ residents, facilityFilter }) {
  const [selectedResident, setSelectedResident] = React.useState(null);
  const [reportPeriod, setReportPeriod] = React.useState(30);
  const [uaResults, setUAResults] = React.useState([]);
  const [bacResults, setBACResults] = React.useState([]);
  const [compensationLogs, setCompensationLogs] = React.useState([]);
  const [incidentReports, setIncidentReports] = React.useState([]);
  const [additionalComments, setAdditionalComments] = React.useState("");
  const [otherAgencies, setOtherAgencies] = React.useState("");
  const [progressGoals, setProgressGoals] = React.useState("");
  const [transitionPlan, setTransitionPlan] = React.useState("");
  const [servicesProvided, setServicesProvided] = React.useState("");
  const [caseManager, setCaseManager] = React.useState("");
  const [signature, setSignature] = React.useState("");
  const [signatureDate, setSignatureDate] = React.useState(TODAY);
  const [editName, setEditName] = React.useState("");
  const [editDoc, setEditDoc] = React.useState("");
  const [editEntryDate, setEditEntryDate] = React.useState("");
  const [editPPOfficer, setEditPPOfficer] = React.useState("");
  const [editDistrict, setEditDistrict] = React.useState("");
  const [editEmployed, setEditEmployed] = React.useState("");
  const [editEmployer, setEditEmployer] = React.useState("");
  const [editCommService, setEditCommService] = React.useState("");
  const [residentSearch, setResidentSearch] = React.useState("");
  const [showSearch, setShowSearch] = React.useState(false);

  const activeResidents = residents.filter(r =>
    r.status === "active" && r.entryDate &&
    (facilityFilter === "all" || r.facility === facilityFilter)
  ).sort((a,b) => {
    const fo = f => f.includes("801") ? 0 : 1;
    return fo(a.facility) - fo(b.facility) || a.name.localeCompare(b.name);
  });

  const getDaysSinceEntry = (r) => {
    if (!r.entryDate) return 0;
    const entry = new Date(r.entryDate + "T00:00:00");
    const today = new Date(TODAY + "T00:00:00");
    return Math.floor((today - entry) / 86400000);
  };

  const getMilestone = (r) => {
    const days = getDaysSinceEntry(r);
    if (days >= 90) return 90;
    if (days >= 60) return 60;
    if (days >= 30) return 30;
    return null;
  };

  const getPPOfficer = (r) => {
    try {
      const contacts = JSON.parse(localStorage.getItem("dorcus_district_contacts") || "[]");
      const match = contacts.find(c => c.facility && r.facility && c.facility === r.facility && c.title && c.title.toLowerCase().includes("p&p"))
        || contacts.find(c => c.district && r.sentencingDistrict && c.district.toLowerCase().includes((r.sentencingDistrict||"").toLowerCase()));
      return match ? match.name : r.ppOfficer || "—";
    } catch { return r.ppOfficer || "—"; }
  };

  const getPeriodEnd = (r, period) => {
    const d = new Date(r.entryDate + "T00:00:00");
    d.setDate(d.getDate() + period);
    return toNYDateStr(d);
  };

  React.useEffect(() => {
    if (!selectedResident) return;
    const r = selectedResident;
    const periodEnd = getPeriodEnd(r, reportPeriod);
    const u1 = subscribeToUAResults(null, null, null, data => {
      setUAResults(data.filter(x => x.residentId === r.id && x.date >= r.entryDate && x.date <= periodEnd));
    });
    const u2 = subscribeToBACResults(null, data => {
      setBACResults(data.filter(x => x.residentId === r.id && x.date >= r.entryDate && x.date <= periodEnd));
    });
    const loadExtra = async () => {
      try {
        const { collection, getDocs, query, where } = await import("firebase/firestore");
        const compSnap = await getDocs(query(collection(db,"compensationLogs"), where("residentId","==",r.id)));
        setCompensationLogs(compSnap.docs.map(d=>({id:d.id,...d.data()})).filter(x=>x.date>=r.entryDate&&x.date<=periodEnd));
        const incSnap = await getDocs(query(collection(db,"incidentReports"), where("residentId","==",r.id)));
        setIncidentReports(incSnap.docs.map(d=>({id:d.id,...d.data()})).filter(x=>x.date>=r.entryDate&&x.date<=periodEnd));
      } catch(e) { console.error(e); }
    };
    loadExtra();
    return () => { u1(); u2(); };
  }, [selectedResident, reportPeriod]);

  const handlePrint = () => {
    if (!selectedResident) return;
    const r = selectedResident;
    const period = reportPeriod;
    const ppOfficer = editPPOfficer||getPPOfficer(r);
    const periodEnd = getPeriodEnd(r, period);
    const monthLabel = new Date(periodEnd + "T00:00:00").toLocaleDateString("en-US",{month:"long",year:"numeric"});
    const uaCount = uaResults.length;
    const bacCount = bacResults.length;
    const uaPositive = [...uaResults,...bacResults].filter(x=>["Positive","Fail","fail","positive"].includes(x.result)).length;
    const uaNegative = (uaCount + bacCount) - uaPositive;
    const uaResultStr = uaPositive > 0 ? `${uaPositive} positive, ${uaNegative} negative` : (uaCount + bacCount) > 0 ? "All negative" : "None administered";
    const bacPositive = bacResults.filter(x=>["Positive","Fail","fail","positive"].includes(x.result)).length;
    const bacResultStr = bacPositive > 0 ? "Positive results noted" : bacCount > 0 ? "All negative" : "None administered";
    // Compensation is credits-only. Period filter based on report milestone period.
    const periodStart = new Date(r.entryDate||TODAY); periodStart.setDate(periodStart.getDate() + ((period - 30)));
    const periodEndDate = new Date(r.entryDate||TODAY); periodEndDate.setDate(periodEndDate.getDate() + period);
    const inPeriod = (x) => { if(!x.date) return false; const d = new Date(x.date); return d >= periodStart && d <= periodEndDate; };
    const sumCredits = (logs) => logs.reduce((s,x)=>s + (parseFloat(x.amount)||0), 0).toFixed(2);
    const moneySavedPeriod = sumCredits(compensationLogs.filter(inPeriod));
    const moneySavedTotal = sumCredits(compensationLogs);
    const incidentStr = incidentReports.length > 0 ? incidentReports.map(i=>`${i.date}: ${i.type||i.incidentType||"Incident"}`).join("; ") : "None";
    const printName = editName||r.name;
    const printDoc = editDoc||r.doc;
    const printDistrict = editDistrict||r.sentencingDistrict;
    const printEmployed = editEmployed||(r.employed?"Yes":"No");
    const printEmployer = editEmployer||r.employer||r.employmentNotes||"—";
    const printCommService = editCommService||r.communityServiceHours||"N/A";

    const html = `<!DOCTYPE html><html><head><title>CRP ${period}-Day Report \u2014 ${printName}</title>
<style>
body{font-family:Arial,sans-serif;font-size:12px;color:#1a2b45;padding:32px;max-width:800px;margin:0 auto;}
h2{font-size:13px;font-weight:700;margin:20px 0 8px;padding-bottom:4px;border-bottom:1.5px solid #1B5EBE;color:#1B5EBE;}
.hdr{border-bottom:2px solid #1a2b45;padding-bottom:12px;margin-bottom:20px;display:flex;justify-content:space-between;}
.logo{font-size:18px;font-weight:700;color:#1B5EBE;}
.g2{display:grid;grid-template-columns:1fr 1fr;gap:6px 24px;margin-bottom:8px;}
.g3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px 16px;margin-bottom:8px;}
.lbl{font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:#4A5568;}
.val{font-size:12px;font-weight:600;color:#1a2b45;border-bottom:1px solid #D8DCE3;padding-bottom:2px;min-height:18px;}
.ta{border:1px solid #D8DCE3;border-radius:4px;min-height:60px;padding:6px 8px;font-size:12px;color:#1a2b45;width:100%;box-sizing:border-box;white-space:pre-wrap;}
@media print{body{padding:16px;}}
</style></head><body>
<div class="hdr">
  <div><div class="logo">Dorcus Home</div><div style="font-size:11px;color:#4A5568;margin-top:2px;">Community Residential Program \u2014 ${period}-Day Report</div></div>
  <div style="text-align:right;font-size:11px;color:#4A5568;"><div>${r.facility}</div><div>Generated: ${TODAY}</div></div>
</div>
<h2>Section 1 \u2014 Resident Information</h2>
<div class="g2">
  <div><div class="lbl">Resident Name</div><div class="val">${printName}</div></div>
  <div><div class="lbl">DOC Number</div><div class="val">${printDoc||"—"}</div></div>
  <div><div class="lbl">Month of Report</div><div class="val">${monthLabel}</div></div>
  <div><div class="lbl">Entry Date</div><div class="val">${editEntryDate||r.entryDate||"—"}</div></div>
  <div><div class="lbl">Supervising P&P Officer</div><div class="val">${ppOfficer}</div></div>
  <div><div class="lbl">Sentencing District</div><div class="val">${printDistrict||"—"}</div></div>
</div>
<h2>Section 2 \u2014 Drug &amp; Alcohol Testing</h2>
<div class="g3">
  <div><div class="lbl">UA Tests This Period</div><div class="val">${uaCount}</div></div>
  <div><div class="lbl">Cumulative UA Tests</div><div class="val">${uaCount + bacCount}</div></div>
  <div><div class="lbl">Urine Screen Results</div><div class="val">${uaResultStr}</div></div>
  <div><div class="lbl">BAC Tests This Period</div><div class="val">${bacCount}</div></div>
  <div style="grid-column:span 2"><div class="lbl">Alcohol Test Results</div><div class="val">${bacResultStr}</div></div>
</div>
<h2>Section 3 \u2014 Employment &amp; Finances</h2>
<div class="g2">
  <div><div class="lbl">Employed</div><div class="val">${printEmployed}</div></div>
  <div><div class="lbl">Employer</div><div class="val">${printEmployer}</div></div>
  <div><div class="lbl">Money Saved This Period</div><div class="val">$${moneySavedPeriod}</div></div>
  <div><div class="lbl">Total Money Saved (Stay)</div><div class="val">$${moneySavedTotal}</div></div>
</div>
<h2>Section 4 \u2014 Community Service &amp; Disciplinary</h2>
<div class="g2">
  <div><div class="lbl">Community Service Hours (This Period)</div><div class="val">${printCommService}</div></div>
  <div><div class="lbl">Disciplinary Problems</div><div class="val">${incidentStr}</div></div>
</div>
<h2>Section 5 \u2014 Program Progress</h2>
<div style="margin-bottom:10px"><div class="lbl">Additional Comments</div><div class="ta">${additionalComments||""}</div></div>
<div style="margin-bottom:10px"><div class="lbl">Other Agencies / Organizations Involved</div><div class="ta">${otherAgencies||""}</div></div>
<div style="margin-bottom:10px"><div class="lbl">Progress Toward Objectives &amp; Goals (Treatment Plan)</div><div class="ta">${progressGoals||""}</div></div>
<div style="margin-bottom:10px"><div class="lbl">Transitional Discharge Plan</div><div class="ta">${transitionPlan||""}</div></div>
<div style="margin-bottom:10px"><div class="lbl">Services Provided</div><div class="ta">${servicesProvided||""}</div></div>
<div style="margin-bottom:10px"><div class="lbl">Case Manager / Program Coordinator</div><div class="val">${caseManager||""}</div></div>
<div style="display:grid;grid-template-columns:2fr 1fr;gap:32px;margin-top:32px;">
  <div><div style="border-bottom:1px solid #1a2b45;margin-top:40px"></div><div class="lbl">Signature: ${signature||""}</div></div>
  <div><div style="border-bottom:1px solid #1a2b45;margin-top:40px"></div><div class="lbl">Date: ${signatureDate||TODAY}</div></div>
</div>
</body></html>`;
    const win = window.open("","_blank");
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const facTag = f => f.includes("801") ? "D1" : f.includes("24th") ? "D2" : "";

  return (
    <div>
      <div style={{...S.card, marginBottom:16, padding:"14px 16px"}}>
        <div style={{display:"grid", gridTemplateColumns:"1fr auto", gap:12, alignItems:"end"}}>
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Resident</div>
            <div style={{position:"relative"}}>
              <input
                style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none",boxSizing:"border-box"}}
                placeholder="Search resident name..."
                value={residentSearch}
                onChange={e=>{setResidentSearch(e.target.value);setShowSearch(true);setSelectedResident(null);}}
                onFocus={()=>setShowSearch(true)}
                onFocus={()=>setShowSearch(true)}
              />
              {showSearch && residentSearch.length >= 1 && (
                <div style={{position:"absolute",top:"calc(100% + 2px)",left:0,right:0,zIndex:200,background:"white",border:`1px solid ${DS.border}`,borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.15)",maxHeight:200,overflowY:"auto"}}>
                  {activeResidents.filter(r=>r.name.toLowerCase().includes(residentSearch.toLowerCase())).map(r=>{
                    const milestone = getMilestone(r);
                    return (
                      <div key={r.id} onClick={()=>{setSelectedResident(r);setResidentSearch(r.name);setShowSearch(false);setReportPeriod(milestone||30);;setEditName(r.name||'');setEditDoc(r.doc||'');setEditEntryDate(r.entryDate||'');setEditPPOfficer(getPPOfficer(r));setEditDistrict(r.sentencingDistrict||'');setEditEmployed(r.employed?'Yes':'No');setEditEmployer(r.employer||r.employmentNotes||'');setEditCommService(r.communityServiceHours||'');}}
                        style={{padding:"8px 12px",cursor:"pointer",fontSize:12,color:DS.textPrimary,borderBottom:`1px solid ${DS.border}`,display:"flex",alignItems:"center",gap:8}}
                        onMouseEnter={e=>e.currentTarget.style.background="#F0F4FA"}
                        onMouseLeave={e=>e.currentTarget.style.background="white"}>
                        <span style={{flex:1}}>{r.name}</span>
                        <span style={{fontSize:10,color:DS.textSec}}>Day {getDaysSinceEntry(r)}</span>
                        <span style={{fontSize:10,fontWeight:500,padding:"1px 6px",borderRadius:99,background:r.facility.includes("801")?"#E6F1FB":"#FFF3CD",color:r.facility.includes("801")?"#0C447C":"#854F0B"}}>{facTag(r.facility)}</span>
                        {milestone && <span style={{fontSize:10,fontWeight:600,padding:"1px 6px",borderRadius:99,background:"#E6F4EC",color:"#1A6B35"}}>{milestone}-day due</span>}
                      </div>
                    );
                  })}
                  {activeResidents.filter(r=>r.name.toLowerCase().includes(residentSearch.toLowerCase())).length===0&&(
                    <div style={{padding:"8px 12px",fontSize:12,color:DS.textSec}}>No residents found</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Report Period</div>
            <div style={{display:"flex",border:`1px solid ${DS.border}`,borderRadius:6,overflow:"hidden"}}>
              {[30,60,90].map(p=>(
                <button key={p} onClick={()=>setReportPeriod(p)}
                  style={{padding:"7px 16px",fontSize:12,cursor:"pointer",border:"none",fontFamily:"'Inter',sans-serif",
                    background:reportPeriod===p?DS.blue:"white",color:reportPeriod===p?"white":DS.textSec,fontWeight:reportPeriod===p?600:400}}>
                  {p} Day
                </button>
              ))}
            </div>
          </div>
        </div>
        {activeResidents.filter(r=>getMilestone(r)).length > 0 && !selectedResident && (
          <div style={{marginTop:12,paddingTop:12,borderTop:`0.5px solid ${DS.border}`}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Reports Due</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {activeResidents.filter(r=>getMilestone(r)).map(r=>(
                <button key={r.id} onClick={()=>{setSelectedResident(r);setResidentSearch(r.name);setReportPeriod(getMilestone(r));;setEditName(r.name||'');setEditDoc(r.doc||'');setEditEntryDate(r.entryDate||'');setEditPPOfficer(getPPOfficer(r));setEditDistrict(r.sentencingDistrict||'');setEditEmployed(r.employed?'Yes':'No');setEditEmployer(r.employer||r.employmentNotes||'');setEditCommService(r.communityServiceHours||'');}}
                  style={{padding:"4px 10px",fontSize:11,borderRadius:99,border:`1px solid #1A6B35`,background:"#E6F4EC",color:"#1A6B35",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  {r.name} · {getMilestone(r)}-day
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedResident && (() => {
        const r = selectedResident;
        const period = reportPeriod;
        const periodEnd = getPeriodEnd(r, period);
        const monthLabel = new Date(periodEnd+"T00:00:00").toLocaleDateString("en-US",{month:"long",year:"numeric"});
        const ppOfficer = getPPOfficer(r);
        const uaCount = uaResults.length;
        const bacCount = bacResults.length;
        const uaPositive = [...uaResults,...bacResults].filter(x=>["Positive","Fail","fail","positive"].includes(x.result)).length;
        const uaNegative = (uaCount+bacCount)-uaPositive;
        const uaResultStr = uaPositive>0?`${uaPositive} positive, ${uaNegative} negative`:(uaCount+bacCount)>0?"All negative":"None administered";
        const bacPositive = bacResults.filter(x=>["Positive","Fail","fail","positive"].includes(x.result)).length;
        const bacResultStr = bacPositive>0?"Positive results noted":bacCount>0?"All negative":"None administered";
        const periodStart2 = new Date(r.entryDate||TODAY); periodStart2.setDate(periodStart2.getDate() + ((period - 30)));
        const periodEnd2 = new Date(r.entryDate||TODAY); periodEnd2.setDate(periodEnd2.getDate() + period);
        const inPeriod2 = (x) => { if(!x.date) return false; const d = new Date(x.date); return d >= periodStart2 && d <= periodEnd2; };
        const sumCredits2 = (logs) => logs.reduce((s,x)=>s + (parseFloat(x.amount)||0), 0).toFixed(2);
        const moneySavedPeriod = sumCredits2(compensationLogs.filter(inPeriod2));
        const moneySavedTotal = sumCredits2(compensationLogs);
        const incidentStr = incidentReports.length>0?incidentReports.map(i=>`${i.date}: ${i.type||i.incidentType||"Incident"}`).join("; "):"None";
        const EF = ({label,value,onChange,type="text"})=>(
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>{label}</div>
            <input type={type} value={value} onChange={e=>onChange(e.target.value)}
              style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"6px 8px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none",boxSizing:"border-box",fontFamily:"'Inter',sans-serif"}}/>
          </div>
        );
        const RF = ({label,value})=>(
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3,fontFamily:"'Inter',sans-serif"}}>{label}</div>
            <div style={{fontSize:12,fontWeight:500,color:DS.textPrimary,background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"6px 8px",minHeight:20,fontFamily:"'Inter',sans-serif"}}>{value||"—"}</div>
          </div>
        );
        return (<div>
          <div style={{...S.card,marginBottom:12,padding:"14px 16px"}}>
            <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:10,fontFamily:"'DM Sans',sans-serif"}}>Section 1 — Resident Information</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 24px"}}>
              <EF label="Resident Name" value={editName} onChange={setEditName}/>
              <EF label="DOC Number" value={editDoc} onChange={setEditDoc}/>
              <RF label="Month of Report" value={monthLabel}/>
              <EF label="Entry Date" value={editEntryDate} onChange={setEditEntryDate} type="date"/>
              <EF label="Supervising P&P Officer" value={editPPOfficer} onChange={setEditPPOfficer}/>
              <EF label="Sentencing District" value={editDistrict} onChange={setEditDistrict}/>
            </div>
          </div>
          <div style={{...S.card,marginBottom:12,padding:"14px 16px"}}>
            <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:10,fontFamily:"'DM Sans',sans-serif"}}>Section 2 — Drug & Alcohol Testing</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"10px 16px"}}>
              <RF label="Urine Screen Tests This Period" value={String(uaCount)}/>
              <RF label="Cumulative Urine Screens" value={String(uaCount+bacCount)}/>
              <RF label="Urine Screen Results" value={uaResultStr}/>
              <RF label="BAC Tests This Period" value={String(bacCount)}/>
              <div style={{gridColumn:"span 2"}}><RF label="Alcohol Test Results" value={bacResultStr}/></div>
            </div>
          </div>
          <div style={{...S.card,marginBottom:12,padding:"14px 16px"}}>
            <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:10,fontFamily:"'DM Sans',sans-serif"}}>Section 3 — Employment & Finances</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 24px"}}>
              <EF label="Employed" value={editEmployed} onChange={setEditEmployed}/>
              <EF label="Employer" value={editEmployer} onChange={setEditEmployer}/>
              <RF label="Money Saved This Period" value={`$${moneySavedPeriod}`}/>
              <RF label="Total Money Saved (Stay)" value={`$${moneySavedTotal}`}/>
            </div>
          </div>
          <div style={{...S.card,marginBottom:12,padding:"14px 16px"}}>
            <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:10,fontFamily:"'DM Sans',sans-serif"}}>Section 4 — Community Service & Disciplinary</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 24px"}}>
              <EF label="Community Service Hours (This Period)" value={editCommService} onChange={setEditCommService}/>
              <RF label="Disciplinary Problems" value={incidentStr}/>
            </div>
          </div>
          <div style={{...S.card, marginBottom:16, padding:"14px 16px"}}>
            <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:12,fontFamily:"'DM Sans',sans-serif"}}>Section 5 — Program Progress</div>
            {[
              ["Additional Comments", additionalComments, setAdditionalComments],
              ["Other Agencies / Organizations Involved", otherAgencies, setOtherAgencies],
              ["Progress Toward Objectives & Goals (Treatment Plan)", progressGoals, setProgressGoals],
              ["Transitional Discharge Plan", transitionPlan, setTransitionPlan],
              ["Services Provided", servicesProvided, setServicesProvided],
            ].map(([label, val, setter])=>(
              <div key={label} style={{marginBottom:10}}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>{label}</div>
                <textarea value={val} onChange={e=>setter(e.target.value)} rows={3}
                  style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none",boxSizing:"border-box",resize:"vertical",fontFamily:"'Inter',sans-serif"}}/>
              </div>
            ))}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginTop:4}}>
              {[["Case Manager / PC", caseManager, setCaseManager],["Signature (typed)", signature, setSignature]].map(([lbl,val,setter])=>(
                <div key={lbl}>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>{lbl}</div>
                  <input value={val} onChange={e=>setter(e.target.value)}
                    style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Date</div>
                <input type="date" value={signatureDate} onChange={e=>setSignatureDate(e.target.value)}
                  style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={handlePrint} style={{...S.btn,fontSize:12}}>Print / PDF</button>
          </div>
        </div>
        );
      })()}

      {!selectedResident && (
        <div style={{...S.card,textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>
          Select a resident above to generate their report
        </div>
      )}
    </div>
  );
}

const DEFAULT_SIDEBAR_ORDER = ["dashboard","residents","reports","daily_log","directory","staff","archived","referrals","ra_tools","pc_workflow"];

function ReportsSection({ residents, referrals }) {
  const [activeReport, setActiveReport] = useState("movement");
  const [inOutLogs, setInOutLogs] = useState([]);
  const [uaResults, setUAResults] = useState([]);
  const [bacResults, setBACResults] = useState([]);
  const [medLogs, setMedLogs] = useState([]);

  // Filters
  const [residentFilter, setResidentFilter] = useState("");
  const [facilityFilter, setFacilityFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth()-1); return toNYDateStr(d);
  });
  const [dateTo, setDateTo] = useState(TODAY);
  const [resultFilter, setResultFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("classification");
  const [demoFilters, setDemoFilters] = useState({ classification:"all", race:"all", district:"all", mhLevel:"all", gangAffiliated:"all" });
  const setDF = (k,v) => { setDemoFilters(f=>({...f,[k]:v})); setGenerated(false); };
  const [generated, setGenerated] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const u1 = subscribeToInOutLogs(null, setInOutLogs);
    const u2 = subscribeToUAResults(null, null, null, setUAResults);
    const u3 = subscribeToBACResults(null, setBACResults);
    const u4 = subscribeToMedicationLogs(null, null, setMedLogs);
    return () => { u1(); u2(); u3(); u4(); };
  }, []);

  const generate = () => { setLoading(true); setTimeout(()=>{setGenerated(true);setLoading(false);},400); };

  const filteredResident = residents.find(r => r.name.toLowerCase() === residentFilter.toLowerCase() && residentFilter);

  // ── Filter helpers ──────────────────────────────────────────────────────────
  const filterByDate = (items, key="date") => items.filter(item => {
    const val = item[key] || (item.createdAt?.seconds ? new Date(item.createdAt.seconds*1000).toLocaleDateString("en-CA",{timeZone:"America/New_York"}) : typeof item.createdAt === "string" ? item.createdAt.split("T")[0] : null);
    if (!val) return false;
    if (dateFrom && val < dateFrom) return false;
    if (dateTo && val > dateTo) return false;
    return true;
  });

  const filterByFacility = (items) => facilityFilter==="all" ? items : items.filter(i=>i.facility===facilityFilter);

  const filterByResident = (items, key="residentId") => {
    if (!residentFilter || !filteredResident) return items;
    return items.filter(i=>i[key]===filteredResident.id);
  };

  // ── House movement report data ──────────────────────────────────────────────────────────────────────────
  const houseMovementData = (() => {
    const parseTime = t => {
      const m=(t||"").match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if(!m) return 0;
      let h=parseInt(m[1]),min=parseInt(m[2]);
      if(m[3]?.toUpperCase()==="PM"&&h!==12) h+=12;
      if(m[3]?.toUpperCase()==="AM"&&h===12) h=0;
      return h*60+min;
    };
    let logs = filterByDate(inOutLogs);
    logs = filterByFacility(logs);
    const residentIds = [...new Set(logs.map(l=>l.residentId))];
    const byResident = residentIds.map(rid => {
      const resLogs = logs.filter(l=>l.residentId===rid);
      const res = residents.find(r=>r.id===rid);
      const signOuts = resLogs.filter(l=>l.eventType==="sign_out").sort((a,b)=>(a.date||"").localeCompare(b.date||"")||(a.time||"").localeCompare(b.time||""));
      const signIns  = resLogs.filter(l=>l.eventType==="sign_in").sort((a,b)=>(a.date||"").localeCompare(b.date||"")||(a.time||"").localeCompare(b.time||""));
      const paired = signOuts.map(out => {
        const outMins = parseTime(out.time);
        const matchIn = signIns.find(sin => {
          if(sin.date > out.date) return true;
          if(sin.date === out.date && parseTime(sin.time) > outMins) return true;
          return false;
        });
        let duration = null;
        if(matchIn) {
          const outTotal = (new Date(out.date+"T00:00:00")).getTime()/60000 + outMins;
          const inTotal  = (new Date(matchIn.date+"T00:00:00")).getTime()/60000 + parseTime(matchIn.time);
          const mins = Math.round(inTotal - outTotal);
          duration = mins>=60?`${Math.floor(mins/60)}h ${mins%60}m`:`${mins}m`;
        }
        return { out, matchIn, duration };
      });
      return { rid, name:res?.name||"Unknown", doc:res?.doc||"—", facility:res?.facility||"", paired };
    }).filter(r=>r.paired.length>0)
      .sort((a,b)=>{
        const fo = f=>f.includes("801")?0:1;
        return fo(a.facility)-fo(b.facility)||a.name.localeCompare(b.name);
      });
    const totalMovements = byResident.reduce((s,r)=>s+r.paired.length,0);
    const stillOut = byResident.reduce((s,r)=>s+r.paired.filter(p=>!p.matchIn).length,0);
    return { byResident, totalMovements, stillOut };
  })();

  // ── Movement report data ────────────────────────────────────────────────────
  const movementData = (() => {
    let data = filterByDate(inOutLogs);
    data = filterByFacility(data);
    if (residentFilter && filteredResident) data = data.filter(l=>l.residentId===filteredResident.id);
    data = data.map(l=>({...l, residentDoc: residents.find(r=>r.id===l.residentId)?.doc||"—"}));
    data.sort((a,b)=>(b.date||"").localeCompare(a.date||"")||(b.time||"").localeCompare(a.time||""));
    const overdueCount = data.filter(l=>l.eventType==="sign_out"&&l.overdue).length;
    const reasons = data.filter(l=>l.eventType==="sign_out").reduce((acc,l)=>{acc[l.reason||"Unknown"]=(acc[l.reason||"Unknown"]||0)+1;return acc;},{});
    const topReason = Object.entries(reasons).sort((a,b)=>b[1]-a[1])[0]?.[0]||"—";
    const uniqueResidents = new Set(data.map(l=>l.residentId)).size;
    return { data, overdueCount, topReason, uniqueResidents };
  })();

  // ── UA/BAC report data ──────────────────────────────────────────────────────
  const uaBACData = (() => {
    let ua = filterByDate(uaResults);
    let bac = filterByDate(bacResults);
    ua = filterByFacility(ua); bac = filterByFacility(bac);
    if (residentFilter && filteredResident) { ua=ua.filter(r=>r.residentId===filteredResident.id); bac=bac.filter(r=>r.residentId===filteredResident.id); }
    if (resultFilter!=="all") { ua=ua.filter(r=>r.result===resultFilter); bac=bac.filter(r=>r.result===resultFilter); }
    const combined = [
      ...ua.map(r=>({...r,type:"UA",residentDoc:residents.find(res=>res.id===r.residentId)?.doc||"—"})),
      ...bac.map(r=>({...r,type:"BAC",residentDoc:residents.find(res=>res.id===r.residentId)?.doc||"—"})),
    ].sort((a,b)=>(b.date||"").localeCompare(a.date||""));
    const passCount = combined.filter(r=>r.result==="pass"||r.result==="Pass").length;
    const failCount = combined.filter(r=>r.result==="fail"||r.result==="Fail").length;
    const uniqueResidents = new Set(combined.map(r=>r.residentId)).size;
    return { data:combined, passCount, failCount, uniqueResidents };
  })();

  // ── Medication report data ──────────────────────────────────────────────────
  const medData = (() => {
    let data = filterByDate(medLogs);
    data = filterByFacility(data);
    if (residentFilter && filteredResident) data = data.filter(l=>l.residentId===filteredResident.id);
    if (statusFilter!=="all") data = data.filter(l=>l.status===statusFilter);
    data = data.map(l=>({...l, residentDoc: residents.find(r=>r.id===l.residentId)?.doc||"—"}));
    data.sort((a,b)=>(b.date||"").localeCompare(a.date||""));
    const takenCount = data.filter(l=>l.status==="taken").length;
    const missedCount = data.filter(l=>l.status==="missed").length;
    const refusedCount = data.filter(l=>l.status==="refused").length;
    const total = data.length;
    const compliance = total>0?Math.round(takenCount/total*100):0;
    return { data, takenCount, missedCount, refusedCount, total, compliance };
  })();

  // ── Demographic report data ─────────────────────────────────────────────────
  const demoData = (() => {
    let data = residents.filter(r=>facilityFilter==="all"||r.facility===facilityFilter);
    if (statusFilter!=="all") data=data.filter(r=>r.status===statusFilter);
    if (demoFilters.classification!=="all") data=data.filter(r=>r.classification===demoFilters.classification);
    if (demoFilters.race!=="all") data=data.filter(r=>r.race===demoFilters.race);
    if (demoFilters.district!=="all") data=data.filter(r=>String(r.sentencingDistrict)===String(demoFilters.district));
    if (demoFilters.mhLevel!=="all") data=data.filter(r=>r.mhLevel===demoFilters.mhLevel);
    if (demoFilters.gangAffiliated!=="all") data=data.filter(r=>(r.gangAffiliated?"Yes":"No")===demoFilters.gangAffiliated);
    const grouped = data.reduce((acc,r)=>{
      const key = groupBy==="gangAffiliated"?(r.gangAffiliated?"Yes":"No"):(r[groupBy]||"Unknown");
      if(!acc[key])acc[key]=[];
      acc[key].push(r);
      return acc;
    },{});
    return { data, grouped, total:data.length };
  })();

  // ── Occupancy report data ───────────────────────────────────────────────────
  const occupancyData = (() => {
    let data = residents.filter(r=>{
      if (facilityFilter!=="all"&&r.facility!==facilityFilter) return false;
      if (r.entryDate && r.entryDate < dateFrom) return false;
      return true;
    });
    const avgDays = data.filter(r=>r.entryDate).reduce((sum,r)=>{
      const entry = new Date(r.entryDate);
      const exit = r.actualDischargeDate ? new Date(r.actualDischargeDate) : new Date();
      return sum + Math.max(0,Math.round((exit-entry)/86400000));
    },0) / (data.filter(r=>r.entryDate).length||1);
    const active = data.filter(r=>r.status==="active").length;
    const discharged = data.filter(r=>r.status==="discharged").length;
    return { data, avgDays:Math.round(avgDays), active, discharged };
  })();

  // ── CSV export ──────────────────────────────────────────────────────────────
  const exportCSV = (rows, headers, filename) => {
    const csv = [headers.join(","), ...rows.map(r=>headers.map(h=>JSON.stringify(r[h]||"")).join(","))].join("\n");
    const blob = new Blob([csv], {type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download=filename+".csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ── PDF export ──────────────────────────────────────────────────────────────
  const exportPDF = (title, rows, columns) => {
    const win = window.open("","_blank");
    const header = `<div style="font-family:Arial,sans-serif;padding:32px;max-width:900px;margin:0 auto;">
      <div style="border-bottom:2px solid #1e293b;padding-bottom:16px;margin-bottom:24px;">
        <div style="font-size:20px;font-weight:700;color:#111;">Dorcus Home</div>
        <div style="font-size:14px;color:#666;margin-top:4px;">${title}</div>
        <div style="font-size:12px;color:#999;margin-top:4px;">Date range: ${dateFrom} to ${dateTo} · Generated ${TODAY} by Admin</div>
      </div>`;
    const tableRows = rows.map(r=>`<tr>${columns.map(c=>`<td style="padding:8px 10px;border-bottom:1px solid #eee;font-size:12px;">${r[c.key]||"—"}</td>`).join("")}</tr>`).join("");
    const table = `<table style="width:100%;border-collapse:collapse;">
      <thead><tr>${columns.map(c=>`<th style="padding:8px 10px;background:#f5f5f5;font-size:11px;text-align:left;text-transform:uppercase;letter-spacing:0.05em;border-bottom:2px solid #ddd;">${c.label}</th>`).join("")}</tr></thead>
      <tbody>${tableRows}</tbody></table>`;
    win.document.write(`<!DOCTYPE html><html><body>${header}${table}</div></body></html>`);
    win.document.close();
    win.print();
  };

  const REPORT_TYPES = [
    {id:"movement",    icon:"🚶", label:"Movement Log"},
    {id:"house_movement",icon:"🏠", label:"Daily Movement (by House)"},
    {id:"ua_bac",      icon:"🧪", label:"Urine Screen / BAC"},
    {id:"medication",  icon:"💊", label:"Medication"},
    {id:"demographic", icon:"👥", label:"Demographics"},
    {id:"occupancy",   icon:"🏠", label:"Occupancy"},
    {id:"pipeline",    icon:"📋", label:"Pipeline"},
    {id:"compensation",icon:"💰", label:"Compensation"},
    {id:"ra_timesheets",icon:"🕐",label:"Staff Timesheets"},
    {id:"progress_reports",icon:"📄",label:"30/60/90 Day Reports"},
    {id:"fire_drills",icon:"🔥",label:"Fire Drill Log"},
  ];

  const SF = {
    label: { fontSize:10, color:DS.textSec, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4, fontFamily:"'Inter',sans-serif" },
    input: { background:DS.pageBg, border:`1px solid ${DS.border}`, borderRadius:6, padding:"7px 10px", fontSize:12, color:DS.textPrimary, width:"100%", outline:"none", boxSizing:"border-box", fontFamily:"'Inter',sans-serif" },
  };

  const StatCard = ({label,value,color=DS.blue}) => (
    <div style={{...S.card}}>
      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>{label}</div>
      <div style={{fontSize:24,fontWeight:600,color,fontFamily:"'DM Sans',sans-serif"}}>{value}</div>
    </div>
  );

  // TableWrap defined at module level above CompensationLedgerReport

  return (
    <div>
      {/* Report type selector */}
      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
        {REPORT_TYPES.map(r=>(
          <button key={r.id} onClick={()=>{setActiveReport(r.id);setGenerated(false);}}
            style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",fontSize:12,fontWeight:500,borderRadius:6,cursor:"pointer",
              background:activeReport===r.id?DS.blue:DS.white,
              border:`1px solid ${activeReport===r.id?DS.blue:DS.border}`,
              color:activeReport===r.id?"white":DS.textSec,
              fontFamily:"'Inter',sans-serif",transition:"all 0.15s"}}>
            <span style={{fontSize:14}}>{r.icon}</span>{r.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      {activeReport!=="ra_timesheets" && <div style={{background:DS.white,border:`0.5px solid ${DS.border}`,borderRadius:8,padding:"14px 16px",marginBottom:20,position:"relative",zIndex:20}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12,marginBottom:12,overflow:"visible"}}>
          {activeReport!=="demographic"&&activeReport!=="occupancy"&&activeReport!=="ra_timesheets"&&(
            <div style={{position:"relative",zIndex:30}}>
              <div style={SF.label}>Resident (optional)</div>
              <input style={SF.input} placeholder="Type name..." value={residentFilter}
                onChange={e=>{setResidentFilter(e.target.value);setGenerated(false);}}
                autoComplete="off"/>
              {residentFilter.length >= 2 && !filteredResident && (
                <div style={{position:"absolute",top:"calc(100% + 2px)",left:0,right:0,zIndex:200,background:"white",border:`1px solid ${DS.border}`,borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.4)",maxHeight:180,overflowY:"auto"}}>
                  {residents.filter(r=>r.name.toLowerCase().includes(residentFilter.toLowerCase())).slice(0,8).map(r=>(
                    <div key={r.id} onClick={()=>{setResidentFilter(r.name);setGenerated(false);}}
                      style={{padding:"8px 12px",cursor:"pointer",fontSize:12,color:DS.textPrimary,borderBottom:"1px solid rgba(51,65,85,0.5)"}}
                      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      {r.name} <span style={{fontSize:10,color:DS.textSec}}>· {r.facility}</span>
                    </div>
                  ))}
                  {residents.filter(r=>r.name.toLowerCase().includes(residentFilter.toLowerCase())).length === 0 && (
                    <div style={{padding:"8px 12px",fontSize:12,color:DS.textSec}}>No residents found</div>
                  )}
                </div>
              )}
            </div>
          )}
          <div>
            <div style={SF.label}>Facility</div>
            <select style={SF.input} value={facilityFilter} onChange={e=>{setFacilityFilter(e.target.value);setGenerated(false);}}>
              <option value="all">All Facilities</option>
              {FACILITIES.map(f=><option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          {activeReport!=="demographic"&&(
            <>
              <div>
                <div style={SF.label}>Date from</div>
                <input type="date" style={SF.input} value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setGenerated(false);}}/>
              </div>
              <div>
                <div style={SF.label}>Date to</div>
                <input type="date" style={SF.input} value={dateTo} onChange={e=>{setDateTo(e.target.value);setGenerated(false);}}/>
              </div>
            </>
          )}
          {activeReport==="ua_bac"&&(
            <div>
              <div style={SF.label}>Result</div>
              <select style={SF.input} value={resultFilter} onChange={e=>{setResultFilter(e.target.value);setGenerated(false);}}>
                <option value="all">All</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
              </select>
            </div>
          )}
          {activeReport==="medication"&&(
            <div>
              <div style={SF.label}>Status</div>
              <select style={SF.input} value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setGenerated(false);}}>
                <option value="all">All</option>
                {["taken","missed","refused","absent"].map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
          )}
          {activeReport==="demographic"&&(
            <>
              <div>
                <div style={SF.label}>Resident status</div>
                <select style={SF.input} value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setGenerated(false);}}>
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="discharged">Discharged</option>
                </select>
              </div>
              <div>
                <div style={SF.label}>Classification</div>
                <select style={SF.input} value={demoFilters.classification} onChange={e=>setDF("classification",e.target.value)}>
                  <option value="all">All</option>
                  {["Non-Violent","Violent","Sex Offender"].map(v=><option key={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <div style={SF.label}>Race</div>
                <select style={SF.input} value={demoFilters.race} onChange={e=>setDF("race",e.target.value)}>
                  <option value="all">All</option>
                  {RACE_OPTIONS.map(v=><option key={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <div style={SF.label}>P&P District</div>
                <select style={SF.input} value={demoFilters.district} onChange={e=>setDF("district",e.target.value)}>
                  <option value="all">All</option>
                  {VA_DISTRICTS.map(d=><option key={d.n} value={String(d.n)}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <div style={SF.label}>MH Level</div>
                <select style={SF.input} value={demoFilters.mhLevel} onChange={e=>setDF("mhLevel",e.target.value)}>
                  <option value="all">All</option>
                  {["MH-0","MH-1","MH-2"].map(v=><option key={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <div style={SF.label}>Gang Affiliated</div>
                <select style={SF.input} value={demoFilters.gangAffiliated} onChange={e=>setDF("gangAffiliated",e.target.value)}>
                  <option value="all">All</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <div style={SF.label}>Group by</div>
                <select style={SF.input} value={groupBy} onChange={e=>{setGroupBy(e.target.value);setGenerated(false);}}>
                  {[["classification","Classification"],["race","Race"],["sentencingDistrict","District"],["mhLevel","MH Level"],["gangAffiliated","Gang Affiliated"],["status","Status"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
        <div style={{display:"flex",justifyContent:"flex-end"}}>
          <button onClick={generate} disabled={loading}
            style={{...S.btn,opacity:loading?0.5:1}}>
            {loading?"Generating...":"Generate Report"}
          </button>
        </div>
      </div>}

      {/* Report output */}
      {generated && activeReport==="movement" && (
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:10,marginBottom:16}}>
            <StatCard label="Total movements" value={movementData.data.length} color={DS.blue}/>
            <StatCard label="Unique residents" value={movementData.uniqueResidents} color={DS.purple.text}/>
            <StatCard label="Overdue incidents" value={movementData.overdueCount} color={DS.error.text}/>
            <StatCard label="Top reason" value={movementData.topReason} color={DS.warning.text}/>
          </div>
          <TableWrap
            cols={[{label:"Resident",key:"residentName"},{label:"DOC#",key:"residentDoc"},{label:"Date",key:"date"},{label:"Event",key:"eventType"},{label:"Time",key:"time"},{label:"Reason",key:"reason"},{label:"RA",key:"raName"||"administeredBy"},{label:"Notes",key:"note",alert:true}]}
            rows={movementData.data.map(l=>({...l,eventType:l.eventType==="sign_out"?"Sign out":"Sign in",_alertCol:l.overdue?"#8B1A1A":null}))}
          />
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>exportCSV(movementData.data,["residentName","date","eventType","time","reason","note"],"movement-log")} style={{...S.btnSec,fontSize:12}}>⬇ CSV</button>
            <button onClick={()=>exportPDF("Movement Log Report",movementData.data,[{label:"Resident",key:"residentName"},{label:"Date",key:"date"},{label:"Event",key:"eventType"},{label:"Time",key:"time"},{label:"Reason",key:"reason"},{label:"RA",key:"raName"},{label:"Notes",key:"note"}])} style={{...S.btn,fontSize:12}}>⬇ PDF</button>
          </div>
        </div>
      )}

      {generated && activeReport==="house_movement" && (() => {
        const RC = {Work:{bg:"#E6F1FB",color:"#0C447C"},Medical:{bg:"#EEEDFE",color:"#3C3489"},Leisure:{bg:"#EAF3DE",color:"#3B6D11"},"P&P":{bg:"#FAEEDA",color:"#854F0B"},Appointment:{bg:"#E1F5EE",color:"#085041"},Personal:{bg:"#F1EFE8",color:"#5F5E5A"}};
        const rc = r=>RC[r]||{bg:"#F1EFE8",color:"#5F5E5A"};
        const facTag = f=>f.includes("801")?"D1":f.includes("24th")?"D2":"";
        const exportPDF = () => {
          const win = window.open("","_blank");
          let rows = "";
          houseMovementData.byResident.forEach(r=>{
            rows += `<tr style="background:#E8F0FB;"><td colspan="6" style="padding:7px 10px;font-size:12px;font-weight:600;">${r.name}&nbsp;<span style="font-size:10px;font-weight:400;color:#666;">DOC# ${r.doc} · ${facTag(r.facility)}</span></td></tr>`;
            if(!r.paired.length) { rows += `<tr><td colspan="6" style="padding:6px 10px;font-size:11px;color:#999;">No movements</td></tr>`; }
            else r.paired.forEach(p=>{ rows += `<tr><td style="padding:5px 8px;border-bottom:1px solid #eee;font-size:11px;">${p.out.date}</td><td style="padding:5px 8px;border-bottom:1px solid #eee;font-size:11px;white-space:nowrap">${p.out.time}</td><td style="padding:5px 8px;border-bottom:1px solid #eee;font-size:11px;">${p.out.reason||"Out"}</td><td style="padding:5px 8px;border-bottom:1px solid #eee;font-size:11px;">${p.out.estReturn||"--"}</td><td style="padding:5px 8px;border-bottom:1px solid #eee;font-size:11px;">${p.matchIn?p.matchIn.time:"Still out"}</td><td style="padding:5px 8px;border-bottom:1px solid #eee;font-size:11px;">${p.duration||"--"}</td></tr>`; });
          });
          const html = `<html><head><title>Daily Movement Report</title><style>body{font-family:Arial,sans-serif;padding:24px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:6px 8px;background:#1B5EBE;color:white;font-size:11px;}</style></head><body><h2 style="margin:0 0 4px;font-size:16px;">Daily Movement Report \u2014 By House</h2><p style="color:#666;margin:0 0 16px;font-size:12px;">${dateFrom===dateTo?dateFrom:dateFrom+" to "+dateTo} · ${facilityFilter==="all"?"All Facilities":facilityFilter} · Generated ${TODAY}</p><table><thead><tr><th>Date</th><th>Out</th><th>Reason</th><th>ETR</th><th>Return</th><th>Duration</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
          win.document.write(html); win.document.close(); win.print();
        };
        const exportCSV = () => {
          const rows = [];
          houseMovementData.byResident.forEach(r=>{ r.paired.forEach(p=>{ rows.push({Resident:r.name,"DOC#":r.doc,Facility:facTag(r.facility),Date:p.out.date,"Out Time":p.out.time,Reason:p.out.reason||"",ETR:p.out.estReturn||"","Return Time":p.matchIn?p.matchIn.time:"Still out",Duration:p.duration||""}); }); });
          const hdrs = ["Resident","DOC#","Facility","Date","Out Time","Reason","ETR","Return Time","Duration"];
          const csv = [hdrs.join(","),...rows.map(r=>hdrs.map(h=>JSON.stringify(r[h]||"")).join(","))].join("\n");
          const blob = new Blob([csv],{type:"text/csv"});
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href=url; a.download="house-movement-report.csv"; a.click();
          URL.revokeObjectURL(url);
        };
        return (
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,minmax(0,1fr))",gap:10,marginBottom:16}}>
              <StatCard label="Total movements" value={houseMovementData.totalMovements} color={DS.blue}/>
              <StatCard label="Residents" value={houseMovementData.byResident.length} color={"#534AB7"}/>
              <StatCard label="Still out" value={houseMovementData.stillOut} color={"#854F0B"}/>
            </div>
            {houseMovementData.byResident.length===0?(
              <div style={{...S.card,textAlign:"center",padding:"32px 0",color:DS.textSec,fontSize:13}}>No movements found for this date range</div>
            ):(
              <div style={{border:`0.5px solid ${DS.border}`,borderRadius:8,overflow:"hidden"}}>
                {houseMovementData.byResident.map((r,ri)=>(
                  <div key={r.rid}>
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:DS.pageBg,borderBottom:`0.5px solid ${DS.border}`}}>
                      <span style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{r.name}</span>
                      <span style={{fontSize:10,color:DS.textSec}}>DOC# {r.doc}</span>
                      <span style={{fontSize:10,fontWeight:500,padding:"1px 6px",borderRadius:99,background:r.facility.includes("801")?"#E6F1FB":"#FFF3CD",color:r.facility.includes("801")?"#0C447C":"#854F0B"}}>{facTag(r.facility)}</span>
                      <span style={{marginLeft:"auto",fontSize:11,color:DS.textSec}}>{r.paired.length} movement{r.paired.length!==1?"s":""}</span>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"80px 72px 90px 72px 80px 72px",padding:"5px 14px",background:"#F8FAFD",borderBottom:`0.5px solid ${DS.border}`}}>
                      {["Date","Out","Reason","ETR","Return","Duration"].map(h=>(
                        <span key={h} style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.05em"}}>{h}</span>
                      ))}
                    </div>
                    {r.paired.map((p,i)=>(
                      <div key={i} style={{display:"grid",gridTemplateColumns:"80px 72px 90px 72px 80px 72px",padding:"7px 14px",borderBottom:`0.5px solid ${DS.border}`,background:!p.matchIn?"#FFFBEB":"white",alignItems:"center"}}>
                        <span style={{fontSize:12,color:DS.textPrimary}}>{new Date(p.out.date+"T12:00:00").toLocaleDateString("en-US",{month:"numeric",day:"numeric"})}</span>
                        <span style={{fontSize:12,color:DS.textPrimary,whiteSpace:"nowrap"}}>{p.out.time}</span>
                        <span style={{fontSize:10,fontWeight:500,padding:"2px 7px",borderRadius:99,background:rc(p.out.reason).bg,color:rc(p.out.reason).color,display:"inline-block",whiteSpace:"nowrap"}}>{p.out.reason||"Out"}</span>
                        <span style={{fontSize:11,color:DS.textSec,whiteSpace:"nowrap"}}>{p.out.estReturn||"--"}</span>
                        {p.matchIn?<span style={{fontSize:12,color:DS.textPrimary,whiteSpace:"nowrap"}}>{p.matchIn.time}</span>:<span style={{fontSize:10,fontWeight:500,padding:"2px 7px",borderRadius:99,background:"#FAEEDA",color:"#854F0B"}}>Still out</span>}
                        <span style={{fontSize:11,color:p.matchIn?DS.textSec:"#854F0B"}}>{p.duration||"--"}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
              <button onClick={exportCSV} style={{...S.btnSec,fontSize:12}}>\u2b07 CSV</button>
              <button onClick={exportPDF} style={{...S.btn,fontSize:12}}>\u2b07 PDF</button>
            </div>
          </div>
        );
      })()}

      {generated && activeReport==="ua_bac" && (
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:10,marginBottom:16}}>
            <StatCard label="Total tests" value={uaBACData.data.length} color={DS.blue}/>
            <StatCard label="Passed" value={uaBACData.passCount} color={DS.success.text}/>
            <StatCard label="Failed" value={uaBACData.failCount} color={DS.error.text}/>
            <StatCard label="Unique residents" value={uaBACData.uniqueResidents} color={DS.purple.text}/>
          </div>
          <TableWrap
            cols={[{label:"Resident",key:"residentName"},{label:"DOC#",key:"residentDoc"},{label:"Date",key:"date"},{label:"Time",key:"time"},{label:"Type",key:"type"},{label:"Result",key:"result"},{label:"BAC",key:"bac"},{label:"Substance",key:"substance"},{label:"By",key:"administeredBy"}]}
            rows={uaBACData.data}
          />
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>exportCSV(uaBACData.data,["residentName","date","time","type","result","bac","substance","administeredBy"],"ua-bac-results")} style={{...S.btnSec,fontSize:12}}>⬇ CSV</button>
            <button onClick={()=>exportPDF("UA / BAC Results Report",uaBACData.data,[{label:"Resident",key:"residentName"},{label:"Date",key:"date"},{label:"Type",key:"type"},{label:"Result",key:"result"},{label:"BAC",key:"bac"},{label:"Substance",key:"substance"},{label:"By",key:"administeredBy"}])} style={{...S.btn,fontSize:12}}>⬇ PDF</button>
          </div>
        </div>
      )}

      {generated && activeReport==="medication" && (
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:10,marginBottom:16}}>
            <StatCard label="Compliance rate" value={`${medData.compliance}%`} color={medData.compliance>=80?DS.success.text:medData.compliance>=60?DS.warning.text:DS.error.text}/>
            <StatCard label="Taken" value={medData.takenCount} color={DS.success.text}/>
            <StatCard label="Missed" value={medData.missedCount} color="#7A4F00"/>
            <StatCard label="Refused" value={medData.refusedCount} color={DS.error.text}/>
          </div>
          <TableWrap
            cols={[{label:"Resident",key:"residentName"},{label:"DOC#",key:"residentDoc"},{label:"Date",key:"date"},{label:"Medication",key:"medication"},{label:"Slot",key:"slot"},{label:"Status",key:"status"},{label:"Time",key:"time"},{label:"Facility",key:"facility"}]}
            rows={medData.data}
          />
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>exportCSV(medData.data,["residentName","date","medication","slot","status","time","facility"],"medication-compliance")} style={{...S.btnSec,fontSize:12}}>⬇ CSV</button>
            <button onClick={()=>exportPDF("Medication Compliance Report",medData.data,[{label:"Resident",key:"residentName"},{label:"Date",key:"date"},{label:"Medication",key:"medication"},{label:"Slot",key:"slot"},{label:"Status",key:"status"},{label:"Time",key:"time"}])} style={{...S.btn,fontSize:12}}>⬇ PDF</button>
          </div>
        </div>
      )}

      {generated && activeReport==="demographic" && (
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,minmax(0,1fr))",gap:10,marginBottom:16}}>
            <StatCard label="Total residents" value={demoData.total} color={DS.blue}/>
            <StatCard label="Groups found" value={Object.keys(demoData.grouped).length} color={DS.purple.text}/>
            <StatCard label="Filtered by" value={groupBy.replace(/([A-Z])/g," $1").trim()} color={DS.warning.text}/>
          </div>
          {Object.entries(demoData.grouped).sort((a,b)=>b[1].length-a[1].length).map(([key,group])=>(
            <div key={key} style={{marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{key||"Unknown"}</div>
                <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{group.length} resident{group.length!==1?"s":""} · {Math.round(group.length/demoData.total*100)}%</div>
                <div style={{flex:1,height:4,background:DS.border,borderRadius:2,overflow:"hidden"}}>
                  <div style={{width:`${Math.round(group.length/demoData.total*100)}%`,height:"100%",background:DS.blue,borderRadius:2}}/>
                </div>
              </div>
              <TableWrap
                cols={[{label:"Name",key:"name"},{label:"DOC#",key:"doc"},{label:"Facility",key:"facility"},{label:"Classification",key:"classification"},{label:"District",key:"sentencingDistrict"},{label:"Status",key:"status"}]}
                rows={group}
              />
            </div>
          ))}
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>exportCSV(demoData.data,["name","doc","facility","classification","race","sentencingDistrict","mhLevel","gangAffiliated","status"],"demographic-report")} style={{...S.btnSec,fontSize:12}}>⬇ CSV</button>
            <button onClick={()=>exportPDF("Demographic Report",demoData.data,[{label:"Name",key:"name"},{label:"DOC#",key:"doc"},{label:"Facility",key:"facility"},{label:"Classification",key:"classification"},{label:"Race",key:"race"},{label:"District",key:"sentencingDistrict"},{label:"MH Level",key:"mhLevel"},{label:"Status",key:"status"}])} style={{...S.btn,fontSize:12}}>⬇ PDF</button>
          </div>
        </div>
      )}

      {generated && activeReport==="occupancy" && (
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:10,marginBottom:16}}>
            <StatCard label="Total residents" value={occupancyData.data.length} color={DS.blue}/>
            <StatCard label="Currently active" value={occupancyData.active} color={DS.success.text}/>
            <StatCard label="Discharged" value={occupancyData.discharged} color={DS.neutral.text}/>
            <StatCard label="Avg. days in program" value={occupancyData.avgDays} color={DS.warning.text}/>
          </div>
          <TableWrap
            cols={[{label:"Resident",key:"name"},{label:"Facility",key:"facility"},{label:"Entry date",key:"entryDate"},{label:"Planned exit",key:"exitDate"},{label:"Actual discharge",key:"actualDischargeDate"},{label:"Days",key:"_days"},{label:"Status",key:"status"}]}
            rows={occupancyData.data.map(r=>{
              const days = r.entryDate ? Math.round((new Date(r.actualDischargeDate||TODAY)-new Date(r.entryDate))/86400000) : null;
              return {...r, _days:days||"—"};
            })}
          />
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>exportCSV(occupancyData.data.map(r=>({...r,_days:r.entryDate?Math.round((new Date(r.actualDischargeDate||TODAY)-new Date(r.entryDate))/86400000):""})),["name","facility","entryDate","exitDate","actualDischargeDate","_days","status"],"occupancy-report")} style={{...S.btnSec,fontSize:12}}>⬇ CSV</button>
            <button onClick={()=>exportPDF("Occupancy / Length of Stay Report",occupancyData.data,[{label:"Resident",key:"name"},{label:"Facility",key:"facility"},{label:"Entry",key:"entryDate"},{label:"Planned Exit",key:"exitDate"},{label:"Discharge",key:"actualDischargeDate"},{label:"Status",key:"status"}])} style={{...S.btn,fontSize:12}}>⬇ PDF</button>
          </div>
        </div>
      )}

      {generated && activeReport==="pipeline" && (()=>{
        const allReferrals = (referrals||[]);
        const stages = ["received","under_review","approved","pending_arrival","accepted","waitlisted","rejected","withdrawn","archived"];
        const stageLabels = { received:"Received", under_review:"Under Review", approved:"Approved", pending_arrival:"Pending Arrival", accepted:"Active", waitlisted:"Waitlisted", rejected:"Rejected", withdrawn:"Withdrawn", archived:"Archived" };
        const stageColors = { received:DS.blue, under_review:DS.warning.text, approved:DS.success.text, pending_arrival:DS.warning.text, accepted:DS.success.text, waitlisted:DS.purple.text, rejected:DS.error.text, withdrawn:DS.textSec, archived:DS.textSec };
        // Calculate days in current stage and total days in pipeline
        const enriched = allReferrals.map(r => {
          const stage = r.stage||r.status||"received";
          const toDateStr = v => { if(!v) return null; if(typeof v==="string") return v; if(v?.toDate) return toNYDateStr(v.toDate()); if(v?.seconds) return toNYDateStr(new Date(v.seconds*1000)); return null; };
          const receivedDate = toDateStr(r.receivedDate)||toDateStr(r.createdAt)||toDateStr(r.submittedDate)||TODAY;
          const totalDays = receivedDate ? Math.floor((new Date(NOW_NY())-new Date(receivedDate))/(1000*60*60*24)) : 0;
          return { ...r, stage, receivedDate, totalDays };
        }).sort((a,b)=>b.totalDays-a.totalDays);
        // Stage summary
        const stageSummary = stages.map(s => ({
          stage:s, label:stageLabels[s],
          count:enriched.filter(r=>r.stage===s).length,
          avgDays:Math.round(enriched.filter(r=>r.stage===s).reduce((sum,r)=>sum+r.totalDays,0)/(enriched.filter(r=>r.stage===s).length||1)),
          color:stageColors[s],
        })).filter(s=>s.count>0);
        const active = enriched.filter(r=>!["rejected","archived"].includes(r.stage));
        const avgTotal = active.length ? Math.round(active.reduce((s,r)=>s+r.totalDays,0)/active.length) : 0;
        const oldest = active.reduce((a,b)=>a.totalDays>b.totalDays?a:b, active[0]||{});
        return (
          <div>
            {/* Summary cards */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
              <StatCard label="Total referrals" value={enriched.length} color={DS.blue}/>
              <StatCard label="Active in pipeline" value={active.length} color={DS.success.text}/>
              <StatCard label="Avg. days in pipeline" value={avgTotal+"d"} color={DS.warning.text}/>
              <StatCard label="Oldest active" value={oldest?.totalDays?oldest.totalDays+"d":"—"} color={DS.error.text}/>
            </div>
            {/* Stage breakdown */}
            <div style={{...S.card,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,marginBottom:10,fontFamily:"'DM Sans',sans-serif"}}>Time by Stage</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {stageSummary.map(s=>(
                  <div key={s.stage} style={{display:"flex",alignItems:"center",gap:12,padding:"7px 0",borderBottom:`0.5px solid ${DS.border}`}}>
                    <div style={{width:90,fontSize:11,fontWeight:600,color:s.color,fontFamily:"'Inter',sans-serif"}}>{s.label}</div>
                    <div style={{flex:1,height:6,background:DS.pageBg,borderRadius:3,overflow:"hidden"}}>
                      <div style={{width:`${Math.min(100,s.avgDays/90*100)}%`,height:"100%",background:s.color,borderRadius:3}}/>
                    </div>
                    <div style={{fontSize:11,color:DS.textSec,width:60,textAlign:"right"}}>{s.count} referral{s.count!==1?"s":""}</div>
                    <div style={{fontSize:11,fontWeight:600,color:s.color,width:50,textAlign:"right"}}>{s.avgDays}d avg</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Individual referral table */}
            <TableWrap
              cols={[{label:"Name",key:"name"},{label:"DOC#",key:"doc"},{label:"Stage",key:"_stageLabel"},{label:"Classification",key:"classification"},{label:"Facility",key:"facility"},{label:"Received",key:"receivedDate"},{label:"Days in Pipeline",key:"totalDays"}]}
              rows={enriched.map(r=>({...r,_stageLabel:stageLabels[r.stage]||r.stage}))}
            />
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
              <button onClick={()=>exportCSV(enriched.map(r=>({...r,_stageLabel:stageLabels[r.stage]||r.stage})),["name","doc","_stageLabel","classification","facility","receivedDate","totalDays"],"pipeline-report")} style={{...S.btnSec,fontSize:12}}>⬇ CSV</button>
              <button onClick={()=>exportPDF("Referral Pipeline Report",enriched.map(r=>({...r,_stageLabel:stageLabels[r.stage]||r.stage})),[{label:"Name",key:"name"},{label:"DOC#",key:"doc"},{label:"Stage",key:"_stageLabel"},{label:"Classification",key:"classification"},{label:"Facility",key:"facility"},{label:"Received",key:"receivedDate"},{label:"Days",key:"totalDays"}])} style={{...S.btn,fontSize:12}}>⬇ Print / PDF</button>
            </div>
          </div>
        );
      })()}

      {generated && activeReport==="compensation" && <CompensationLedgerReport/>}
      {generated && activeReport==="ra_timesheets" && <StaffTimesheetsReport/>}
      {generated && activeReport==="progress_reports" && <ProgressReportsSection residents={residents} facilityFilter={facilityFilter} />}
      {generated && activeReport==="fire_drills" && <FireDrillReport facilityFilter={facilityFilter} dateFrom={dateFrom} dateTo={dateTo} />}
      {false && (()=>{
        const [compData, setCompData] = React.useState([]);
        React.useEffect(()=>{
          (async()=>{
            const { db } = await import("./firebase");
            const { collection, getDocs } = await import("firebase/firestore");
            const allSnap = await getDocs(collection(db,"compensationLogs"));
            const snap = { docs: allSnap.docs.filter(d=>d.data().residentId===residentId).sort((a,b)=>(b.data().date||"").localeCompare(a.data().date||"")) };
            setCompData(snap.docs.map(d=>({id:d.id,...d.data()})));
          })();
        },[]);
        const totalAll = compData.reduce((s,l)=>s+(parseFloat(l.amount)||0),0);
        const byResident = compData.reduce((acc,l)=>{
          if(!acc[l.residentId]) acc[l.residentId]={residentId:l.residentId,residentName:l.residentName,facility:l.facility,total:0,collections:0};
          acc[l.residentId].total += parseFloat(l.amount)||0;
          acc[l.residentId].collections++;
          return acc;
        },{});
        const residentSummary = Object.values(byResident).sort((a,b)=>b.total-a.total);
        return (
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
              <StatCard label="Total Collected" value={`$${totalAll.toFixed(2)}`} color={DS.success.text}/>
              <StatCard label="Total Payments" value={compData.length} color={DS.blue}/>
              <StatCard label="Residents w/ Payments" value={residentSummary.length} color={DS.warning.text}/>
            </div>
            <div style={{...S.card,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,marginBottom:10,fontFamily:"'DM Sans',sans-serif"}}>By Resident</div>
              {residentSummary.map(r=>(
                <div key={r.residentId} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:`0.5px solid ${DS.border}`}}>
                  <div style={{flex:1,fontSize:13,color:DS.textPrimary}}>{displayName(r.residentName)}</div>
                  <div style={{fontSize:11,color:DS.textSec}}>{shortFacility(r.facility)}</div>
                  <div style={{fontSize:11,color:DS.textSec}}>{r.collections} payment{r.collections!==1?"s":""}</div>
                  <div style={{fontSize:13,fontWeight:600,color:DS.success.text}}>${r.total.toFixed(2)}</div>
                </div>
              ))}
            </div>
            <TableWrap
              cols={[{label:"Resident",key:"residentName"},{label:"Facility",key:"facility"},{label:"Date",key:"date"},{label:"Amount",key:"_amt"},{label:"Conf#",key:"confirmNo"},{label:"Collected By",key:"collectedBy"}]}
              rows={compData.map(l=>({...l,residentName:displayName(l.residentName),_amt:`$${parseFloat(l.amount||0).toFixed(2)}`}))}
            />
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
              <button onClick={()=>exportCSV(compData.map(l=>({...l,residentName:displayName(l.residentName),amount:`$${parseFloat(l.amount||0).toFixed(2)}`})),["residentName","facility","date","amount","confirmNo","collectedBy","weeks"],"compensation-ledger")} style={{...S.btnSec,fontSize:12}}>⬇ CSV</button>
            </div>
          </div>
        );
      })()}

      {!generated && (
        <div style={{...S.card,textAlign:"center",padding:"60px 0"}}>
          <div style={{width:48,height:48,borderRadius:12,background:DS.blueTint,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
            <i className="bi bi-bar-chart-fill" style={{fontSize:20,color:DS.blue}}></i>
          </div>
          <div style={{fontSize:14,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif",marginBottom:4}}>Set your filters and click Generate Report</div>
          <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Reports can be exported as PDF or CSV</div>
        </div>
      )}
    </div>
  );
}

// ── District Directory ────────────────────────────────────────────────────────
function DistrictDirectory() {
  const [contacts, setContacts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dorcus_district_contacts")||"[]"); } catch { return []; }
  });
  const [editing, setEditing] = useState(null); // null | "new" | contact id
  const [form, setForm] = useState({name:"",title:"",district:"",email:"",phone:"",facility:""});
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = () => {
    let updated;
    if (editing === "new") {
      updated = [...contacts, {...form, id: Date.now()}];
    } else {
      updated = contacts.map(c => c.id === editing ? {...form, id:editing} : c);
    }
    setContacts(updated);
    localStorage.setItem("dorcus_district_contacts", JSON.stringify(updated));
    setEditing(null);
    setForm({name:"",title:"",district:"",email:"",phone:""});
  };

  const remove = (id) => {
    const updated = contacts.filter(c => c.id !== id);
    setContacts(updated);
    localStorage.setItem("dorcus_district_contacts", JSON.stringify(updated));
  };

  const startEdit = (c) => {
    setForm({name:c.name,title:c.title,district:c.district,email:c.email,phone:c.phone||"",facility:c.facility||""});
    setEditing(c.id);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={{fontSize:13,color:DS.textSec}}>{contacts.length} contact{contacts.length!==1?"s":""} in directory</div>
        <button onClick={()=>{setForm({name:"",title:"",district:"",email:"",phone:""});setEditing("new");}} style={{...S.btn}}>+ Add Contact</button>
      </div>

      {/* Add/Edit form */}
      {editing && (
        <div style={{...S.card,marginBottom:20,border:`1px solid ${DS.border}`}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,marginBottom:16}}>{editing==="new"?"New Contact":"Edit Contact"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            {[{k:"name",l:"Full Name *"},{k:"title",l:"Title / Role"},{k:"district",l:"District *"},{k:"email",l:"Email *"},{k:"phone",l:"Phone (optional)"}].map(({k,l})=>(
              <div key={k}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                <input style={{...S.input}} value={form[k]} onChange={e=>setF(k,e.target.value)}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Facility</div>
              <select style={{...S.input}} value={form.facility} onChange={e=>setF("facility",e.target.value)}>
                <option value="">All Facilities</option>
                <option value="Dorcus 1 - 801">Dorcus 1 - 801</option>
                <option value="Dorcus 2 - 24th St">Dorcus 2 - 24th St</option>
              </select>
            </div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>setEditing(null)} style={{...S.btnGhost}}>Cancel</button>
            <button onClick={save} disabled={!form.name||!form.district||!form.email} style={{...S.btn,opacity:(!form.name||!form.district||!form.email)?0.4:1}}>Save Contact</button>
          </div>
        </div>
      )}

      {/* Contact list */}
      {contacts.length === 0 ? (
        <div style={{...S.card,textAlign:"center",padding:"48px 0",color:DS.textSec}}>
          <div style={{fontSize:32,marginBottom:8}}>📇</div>
          <div style={{fontSize:14,marginBottom:4}}>No contacts yet</div>
          <div style={{fontSize:12}}>Add district personnel to use in acceptance and denial communications.</div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {contacts.sort((a,b)=>a.district.localeCompare(b.district)).map(c=>(
            <div key={c.id} style={{...S.card,display:"flex",alignItems:"center",gap:14}}>
              <div style={{width:40,height:40,borderRadius:10,background:DS.border,border:"1px solid #334155",display:"flex",alignItems:"center",justifyContent:"center",color:DS.textSec,fontSize:14,flexShrink:0}}>📋</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{c.name}</div>
                <div style={{fontSize:11,color:DS.textSec}}>{c.title} · District {c.district}{c.facility?` · ${c.facility}`:""}</div>
                <div style={{fontSize:11,color:"#4f46e5"}}>{c.email}{c.phone&&` · ${c.phone}`}</div>
              </div>
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>startEdit(c)} style={{...S.btnGhost,fontSize:11,padding:"4px 10px"}}>Edit</button>
                <button onClick={()=>remove(c.id)} style={{...S.btnGhost,fontSize:11,padding:"4px 10px",color:"#8B1A1A",borderColor:"#8B1A1A"}}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Referral Pipeline ─────────────────────────────────────────────────────────
const REFERRAL_STAGES = {
  received:    { label:"Received",      color:DS.neutral.text,  bg:DS.neutral.bg,  border:DS.neutral.border,  dot:DS.textSec },
  under_review:{ label:"Under Review",  color:DS.warning.text,  bg:DS.warning.bg,  border:DS.warning.border,  dot:DS.yellow },
  accepted:    { label:"Accepted",      color:DS.success.text,  bg:DS.success.bg,  border:DS.success.border,  dot:DS.success.border },
  rejected:    { label:"Rejected",      color:DS.error.text,    bg:DS.error.bg,    border:DS.error.border,    dot:DS.error.border },
  withdrawn:   { label:"Withdrawn",     color:DS.textSec,       bg:DS.neutral.bg,  border:DS.neutral.border,  dot:"#94a3b8" },
  waitlisted:  { label:"Waitlisted",    color:DS.purple.text,   bg:DS.purple.bg,   border:DS.purple.border,   dot:DS.purple.border },
  active:      { label:"Active Resident",color:DS.blue,         bg:DS.blueTint,    border:DS.blueBorder,      dot:DS.blue },
};

const emptyReferral = {
  stage:"received", submittedDate:TODAY,
  // Identity
  name:"", doc:"", dob:"", race:"", sentencingDistrict:"",
  classification:"Non-Violent", gangAffiliated:false,
  mhLevel:"MH-0", mentalHealth:false, mentalHealthNotes:"",
  // Referral source
  referredBy:"", counselorEmail:"", referralInstitution:"",
  referralDate:TODAY,
  // Legal
  currentOffenses:[], priorOffenses:[],
  detainer:false, icot:false, priorDorcus:"",
  noContact:false, noContactList:"",
  // Substance
  substanceAbuse:false, substanceAbuseNotes:"",
  // Physical
  physicalHealth:false, physicalHealthNotes:"",
  // Docs checklist
  checklist:Array(13).fill(false),
  // Decision
  decisionDate:"", decisionBy:"", decisionNotes:"",
  communicationLog:[],
};

function ReferralPipeline({ referrals, setReferrals, perms, currentUser }) {
  const [loading, setLoading] = useState(false);
  const [districtContacts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dorcus_district_contacts")||"[]"); } catch { return []; }
  });

  const [view, setView] = useState(perms?.role==="rc" ? "coordinator" : "pipeline");
  const [selected, setSelected] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [newForm, setNewForm] = useState({...emptyReferral});
  const [commType, setCommType] = useState("acceptance");
  const [commBody, setCommBody] = useState("");
  const [commRecipients, setCommRecipients] = useState([]);
  const [commSending, setCommSending] = useState(false);
  const [commSent, setCommSent] = useState(false);
  const setNF = (k,v) => setNewForm(f=>({...f,[k]:v}));

  const addReferral = async () => {
    const r = {...newForm, createdAt:TODAY, createdBy:currentUser};
    await saveReferral(r);
    setNewForm({...emptyReferral});
    setView("pipeline");
  };

  const updateStage = async (id, stage, notes="") => {
    const updated = { stage, decisionDate:TODAY, decisionBy:currentUser, decisionNotes:notes };
    await saveReferral({ id, ...updated });
    if (selected?.id === id) setSelected(prev => ({...prev, ...updated}));
    auditAction("referral_stage_updated", { referralId:id, referralName:selected?.name, stage, notes });
  };

  const [confirmAccept, setConfirmAccept] = useState(false);
  const [acceptEntryDate, setAcceptEntryDate] = useState("");
  const [confirmArchiveReferral, setConfirmArchiveReferral] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectOther, setRejectOther] = useState("");
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawInitiator, setWithdrawInitiator] = useState("");
  const [withdrawNotes, setWithdrawNotes] = useState("");

  const REJECTION_REASONS = [
    "Lack of Bedspace",
    "MH Level Exceeds MH-2",
    "SVP Designation",
    "Institutional Infraction / Behavioral History",
    "Applicant Requires Services Beyond Program Scope",
    "Insufficient Capacity for Full Program Duration",
    "Other",
  ];

  const handleReject = async () => {
    const notes = rejectReason === "Other" ? rejectOther : rejectReason;
    if (!notes) return;
    await updateStage(selected.id, "rejected", notes);
    openComm({...selected, stage:"rejected", decisionNotes:notes}, "rejection");
    setShowRejectModal(false);
    setRejectReason("");
    setRejectOther("");
  };

  const handleWithdraw = async () => {
    if (!withdrawInitiator) return;
    const notes = withdrawInitiator + (withdrawNotes ? ` — ${withdrawNotes}` : "");
    await updateStage(selected.id, "withdrawn", notes);
    auditAction("referral_withdrawn", { referralId:selected.id, referralName:selected.name, initiator:withdrawInitiator, notes:withdrawNotes });
    setShowWithdrawModal(false);
    setWithdrawInitiator("");
    setWithdrawNotes("");
  };

  const convertToResident = async () => {
    if (!selected) return;
    // Default: Sex Offenders → Dorcus 2, everyone else → Dorcus 1
    const facility = selected.classification === "Sex Offender" ? "Dorcus 2 - 24th St" : "Dorcus 1 - 801";
    const entryDate = acceptEntryDate || "";
    const exitDate = entryDate ? add90Days(entryDate) : "";
    const resident = {
      name: selected.name || "",
      doc: selected.doc || "",
      dob: selected.dob || "",
      race: selected.race || "",
      sentencingDistrict: selected.sentencingDistrict || "",
      classification: selected.classification || "Non-Violent",
      gangAffiliated: selected.gangAffiliated || false,
      mhLevel: selected.mhLevel || "MH-0",
      mentalHealth: selected.mentalHealth || false,
      mentalHealthNotes: selected.mentalHealthNotes || "",
      physicalHealth: selected.physicalHealth || false,
      physicalHealthNotes: selected.physicalHealthNotes || "",
      substanceAbuse: selected.substanceAbuse || false,
      substanceAbuseNotes: selected.substanceAbuseNotes || "",
      currentOffenses: selected.currentOffenses || [],
      priorOffenses: selected.priorOffenses || [],
      detainer: selected.detainer || false,
      icot: selected.icot || false,
      priorDorcus: selected.priorDorcus || "",
      noContact: selected.noContact || false,
      noContactList: selected.noContactList || "",
      referredBy: selected.referredBy || "",
      referralSource: selected.referralSource || "institution",
      referralInstitution: selected.referralInstitution || "",
      ppDistrict: selected.ppDistrict || "",
      referralDate: selected.referralDate || TODAY,
      checklist: selected.checklist || Array(13).fill(false),
      facility,
      room: "",
      entryDate,
      exitDate,
      actualDischargeDate: "",
      releaseDate: "",
      employed: false,
      employer: "",
      trainingDecisionPoints: false,
      trainingAngerManagement: false,
      trainingParenting: false,
      trainingNotes: "",
      medications: [],
      exitChecklist: Array(11).fill(false),
      dischargeNotes: "",
      status: "pending_arrival",
      inStatus: null,
      caseNotes: [],
      referralId: selected.id,
    };
    await saveResident(resident);
    await updateStage(selected.id, "active");
    await saveReferral({ id: selected.id, carriedOver: true });
    auditAction("referral_accepted", { referralId:selected.id, referralName:selected.name, facility:resident.facility });
    setConfirmAccept(false);
    openComm({...selected, stage:"accepted"}, "acceptance");
  };

  const openComm = (referral, type) => {
    setSelected(referral);
    setCommType(type);
    const name = referral.name || "the applicant";
    const isAccept = type === "acceptance";
    setCommBody(isAccept
      ? `Dear ${referral.referredBy||"Case Counselor"},\n\nWe are pleased to inform you that ${name} (DOC# ${referral.doc||"—"}) has been accepted into the Dorcus Step-Down Residential Program.\n\n${referral.entryDate?`Their scheduled entry date is ${referral.entryDate}.\n\n`:""}Please coordinate with our staff to confirm all required documentation has been submitted.\n\nPlease respond within the next 48 hours to confirm receipt of this communication.\n\nThank you for your referral.\n\nSincerely,\nDorcus Step-Down Residential Program\ncontact@dorcushomeva.org`
      : `Dear ${referral.referredBy||"Case Counselor"},\n\nThank you for submitting a referral for ${name} (DOC# ${referral.doc||"—"}). After careful review, we are unable to accept this individual into the Dorcus Step-Down Residential Program at this time.\n\nReason: ${referral.decisionNotes||"Please contact us for more information."}\n\nPlease respond within the next 48 hours to confirm receipt of this communication.\n\nWe appreciate your continued partnership.\n\nSincerely,\nDorcus Step-Down Residential Program\ncontact@dorcushomeva.org`
    );
    const recipients = [];
    if (referral.counselorEmail) recipients.push(referral.counselorEmail);
    setCommRecipients(recipients);
    setCommSent(false);
    setView("communicate");
  };

  const sendComm = async () => {
    setCommSending(true);
    setTimeout(async () => {
      const log = {
        date: TODAY, type: commType, sentBy: currentUser,
        recipients: commRecipients, body: commBody,
      };
      const updatedLog = [...(selected.communicationLog||[]), log];
      await saveReferral({ id: selected.id, communicationLog: updatedLog });
      setSelected(prev => ({...prev, communicationLog: updatedLog}));
      setCommSending(false);
      setCommSent(true);
    }, 1200);
  };

  const filtered = (stageFilter==="all" ? referrals : referrals.filter(r=>r.stage===stageFilter))
    .filter(r => !r.carriedOver && !r.archived);
  const counts = Object.fromEntries(Object.keys(REFERRAL_STAGES).map(s=>[s, referrals.filter(r=>r.stage===s).length]));

  // ── Pipeline view ──────────────────────────────────────────────────────────
  if (view === "coordinator") {
    const activeReferrals = referrals.filter(r => !["archived","rejected","active"].includes(r.stage||r.status));
    const weekAgo = (() => { const d = new Date(NOW_NY()); d.setDate(d.getDate()-7); return d.toISOString().split("T")[0]; })();
    const newThisWeek = activeReferrals.filter(r => r.createdAt >= weekAgo);
    const oldest = activeReferrals.length > 0 ? activeReferrals.reduce((a,b) => (a.createdAt||"9") < (b.createdAt||"9") ? a : b) : null;
    const oldestDays = oldest?.createdAt ? Math.floor((new Date(NOW_NY()) - new Date(oldest.createdAt)) / (1000*60*60*24)) : 0;
    return (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Coordinator Dashboard</div>
          <button onClick={()=>setView("pipeline")} style={{...S.btnGhost,fontSize:12}}>{perms?.role==="rc"?"View Full Pipeline →":"← Full Pipeline"}</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
          <div style={{...S.card}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>New Referrals</div>
            <div style={{fontSize:36,fontWeight:600,color:DS.blue,fontFamily:"'DM Sans',sans-serif",lineHeight:1,marginBottom:4}}>{newThisWeek.length}</div>
            <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>received this week</div>
          </div>
          <div style={{...S.card}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Active in Pipeline</div>
            <div style={{fontSize:36,fontWeight:600,color:DS.darkYellow,fontFamily:"'DM Sans',sans-serif",lineHeight:1,marginBottom:4}}>{activeReferrals.length}</div>
            <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>across all stages</div>
          </div>
          <div style={{...S.card}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>Oldest Referral</div>
            <div style={{fontSize:36,fontWeight:600,color:oldestDays>30?DS.error.text:DS.textPrimary,fontFamily:"'DM Sans',sans-serif",lineHeight:1,marginBottom:4}}>{oldestDays}</div>
            <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}><strong>days</strong>{oldest.name?` · ${oldest.name}`:""}</div>
          </div>
        </div>
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          <div style={{padding:"10px 14px",background:DS.blue}}>
            <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>New This Week</span>
          </div>
          {newThisWeek.length===0 ? (
            <div style={{padding:"20px 14px",fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif",textAlign:"center"}}>No new referrals this week</div>
          ) : newThisWeek.map(r => {
            const days = r.createdAt ? Math.floor((new Date(NOW_NY())-new Date(r.createdAt))/(1000*60*60*24)) : 0;
            const stage = REFERRAL_STAGES[r.stage]||REFERRAL_STAGES["received"];
            return (
              <div key={r.id} onClick={()=>{setSelected(r);setView("detail");}} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderBottom:`0.5px solid ${DS.border}`,cursor:"pointer"}}
                onMouseEnter={e=>e.currentTarget.style.background=DS.pageBg} onMouseLeave={e=>e.currentTarget.style.background="white"}>
                <div>
                  <div style={{fontSize:13,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{r.name}</div>
                  <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Received {r.createdAt} · {r.institution||""} · District {r.district||""}</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:stage.bg,color:stage.color,border:`1px solid ${stage.border}`,fontFamily:"'Inter',sans-serif"}}>{stage.label}</span>
                  <span style={{fontSize:13,fontWeight:600,color:stage.color,fontFamily:"'DM Sans',sans-serif"}}>{days}d</span>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          <div style={{padding:"10px 14px",background:DS.blue,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>All Active</span>
            <span style={{fontSize:10,color:"rgba(255,255,255,0.7)"}}>{activeReferrals.length} total</span>
          </div>
          {activeReferrals.sort((a,b)=>(a.createdAt||"")>(b.createdAt||"")? 1:-1).slice(0,8).map(r => {
            const days = r.createdAt ? Math.floor((new Date(NOW_NY())-new Date(r.createdAt))/(1000*60*60*24)) : 0;
            const stage = REFERRAL_STAGES[r.stage]||REFERRAL_STAGES["received"];
            return (
              <div key={r.id} onClick={()=>{setSelected(r);setView("detail");}} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 14px",borderBottom:`0.5px solid ${DS.border}`,cursor:"pointer"}}
                onMouseEnter={e=>e.currentTarget.style.background=DS.pageBg} onMouseLeave={e=>e.currentTarget.style.background="white"}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:stage.bg,color:stage.color,border:`1px solid ${stage.border}`,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>{stage.label}</span>
                  <span style={{fontSize:12,fontWeight:500,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{r.name}</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>District {r.district||""}</span>
                  <span style={{fontSize:12,fontWeight:600,color:days>30?DS.error.text:DS.textSec,fontFamily:"'DM Sans',sans-serif"}}>{days}d</span>
                </div>
              </div>
            );
          })}
          {activeReferrals.length>8&&<div style={{padding:"8px 14px",background:DS.pageBg,fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>+ {activeReferrals.length-8} more</span>
            <button onClick={()=>setView("pipeline")} style={{...S.btnGhost,fontSize:11,padding:"3px 10px"}}>View all →</button>
          </div>}
        </div>
      </div>
    );
  }

  if (view === "pipeline") return (
    <div>
      {/* Stage summary */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
        {Object.entries(REFERRAL_STAGES).map(([key,s])=>(
          <div key={key} onClick={()=>setStageFilter(stageFilter===key?"all":key)}
            style={{...S.card,cursor:"pointer",border:`1px solid ${stageFilter===key?s.border:DS.border}`,background:stageFilter===key?s.bg:DS.white,transition:"all 0.15s"}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:s.dot}}/>
              <span style={{fontSize:11,color:s.color,fontWeight:500,fontFamily:"'Inter',sans-serif"}}>{s.label}</span>
            </div>
            <div style={{fontSize:24,fontWeight:600,color:stageFilter===key?s.color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{counts[key]||0}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <button onClick={()=>setView("coordinator")} style={{...S.btnGhost,fontSize:12,display:"flex",alignItems:"center",gap:6}}><i className="bi bi-speedometer2"></i> Coordinator View</button>
        <button onClick={()=>{setNewForm({...emptyReferral});setView("new");}} style={{...S.btnCTA,display:"flex",alignItems:"center",gap:6}}><i className="bi bi-plus-lg"></i> New Referral</button>
      </div>

      {/* Referral list */}
      {filtered.length === 0 ? (
        <div style={{...S.card,textAlign:"center",padding:"48px 0"}}>
          <div style={{width:48,height:48,borderRadius:12,background:DS.blueTint,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
            <i className="bi bi-clipboard-fill" style={{fontSize:20,color:DS.blue}}></i>
          </div>
          <div style={{fontSize:14,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif",marginBottom:4}}>{stageFilter==="all"?"No referrals yet":"No referrals in this stage"}</div>
          <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Referrals submitted via the public form will appear here</div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.sort((a,b)=>b.id-a.id).map(r=>{
            const st = REFERRAL_STAGES[r.stage]||REFERRAL_STAGES.received;
            return (
              <div key={r.id} onClick={()=>{setSelected(r);setView("detail");}}
                style={{...S.card,cursor:"pointer",display:"flex",alignItems:"center",gap:14,transition:"border-color 0.15s"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=DS.blue}
                onMouseLeave={e=>e.currentTarget.style.borderColor=DS.border}>
                {r.photoURL?<img src={r.photoURL} alt={r.name} style={{width:40,height:40,borderRadius:8,objectFit:"cover",border:`1px solid ${DS.blueBorder}`,flexShrink:0}}/>:<div style={{width:40,height:40,borderRadius:8,background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,display:"flex",alignItems:"center",justifyContent:"center",color:DS.blue,fontWeight:600,fontSize:12,flexShrink:0,fontFamily:"'DM Sans',sans-serif"}}>{initials(r.name||"?")}</div>}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>{r.name||"Unnamed"}</div>
                  <div style={{fontSize:11,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>
                    DOC# {r.doc||"—"} · {r.referralInstitution||"—"} · Submitted {r.submittedDate}
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                  <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,fontWeight:500,background:st.bg,border:`1px solid ${st.border}`,color:st.color,fontFamily:"'Inter',sans-serif"}}>{st.label}</span>
                  {r.classification==="Violent"
                    ? <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,fontWeight:500,background:DS.error.bg,color:DS.error.text,border:`1px solid ${DS.error.border}`,fontFamily:"'Inter',sans-serif"}}>Violent</span>
                    : r.classification==="Sex Offender"
                    ? <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,fontWeight:500,background:DS.purple.bg,border:`1px solid ${DS.purple.border}`,color:DS.purple.text,fontFamily:"'Inter',sans-serif"}}>Sex Offender</span>
                    : <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,fontWeight:500,background:DS.success.bg,border:`1px solid ${DS.success.border}`,color:DS.success.text,fontFamily:"'Inter',sans-serif"}}>Non-Violent</span>
                  }
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── New referral form ──────────────────────────────────────────────────────
  if (view === "new") return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24}}>
        <button onClick={()=>setView("pipeline")} style={{...S.btnGhost,fontSize:12}}>← Back</button>
        <h2 style={{margin:0,fontSize:18,fontWeight:700,color:DS.textPrimary}}>New Referral Application</h2>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:20}}>

        {/* Referral source */}
        <div style={{...S.card}}>
          <SectionHdr>Referral Source</SectionHdr>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {[{k:"referredBy",l:"Case Counselor Name *"},{k:"counselorEmail",l:"Case Counselor Email *",type:"email"},{k:"referralInstitution",l:"Institution / Facility *"},{k:"referralDate",l:"Referral Date",type:"date"}].map(({k,l,type})=>(
              <div key={k}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                <input type={type||"text"} style={{...S.input}} value={newForm[k]} onChange={e=>setNF(k,e.target.value)}/>
              </div>
            ))}
          </div>
        </div>

        {/* Demographics */}
        <div style={{...S.card}}>
          <SectionHdr>Applicant Demographics</SectionHdr>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {[{k:"name",l:"Full Name *"},{k:"doc",l:"DOC Number *"},{k:"dob",l:"Date of Birth",type:"date"},{k:"race",l:"Race"}].map(({k,l,type})=>(
              <div key={k}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                <input type={type||"text"} style={{...S.input}} value={newForm[k]} onChange={e=>setNF(k,e.target.value)}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Sentencing District</div>
              <DistrictSelect value={newForm.sentencingDistrict} onChange={v=>setNF("sentencingDistrict",v)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Classification</div>
              <select style={{...S.input}} value={newForm.classification} onChange={e=>setNF("classification",e.target.value)}>
                {["Non-Violent","Violent","Sex Offender"].map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Gang Affiliated</div>
              <div style={{display:"flex",gap:8}}>
                {[true,false].map(v=>(
                  <button key={String(v)} onClick={()=>setNF("gangAffiliated",v)} style={{flex:1,padding:"7px",fontSize:12,borderRadius:8,cursor:"pointer",background:newForm.gangAffiliated===v?(v?"#2a1a0c":DS.border):"transparent",border:`1px solid ${newForm.gangAffiliated===v?(v?"#f97316":"#475569"):DS.border}`,color:newForm.gangAffiliated===v?(v?"#fdba74":"#e2e8f0"):"#64748b",fontWeight:newForm.gangAffiliated===v?600:400}}>
                    {v?"Yes":"No"}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* MH Status */}
        <div style={{...S.card}}>
          <SectionHdr>Mental Health Status</SectionHdr>
          <div style={{display:"flex",gap:8}}>
            {[{v:"MH-0",sub:"No concerns"},{v:"MH-1",sub:"Some concerns"},{v:"MH-2",sub:"Active treatment"}].map(({v,sub})=>{
              const active = newForm.mhLevel===v;
              const bg = v==="MH-0"?DS.border:"#2d1a4a";
              const border = v==="MH-0"?"#475569":v==="MH-1"?"#7c3aed":"#a855f7";
              const color = v==="MH-0"?"#64748b":v==="MH-1"?"#4A3AAA":"#d8b4fe";
              return (
                <button key={v} onClick={()=>{setNF("mhLevel",v);setNF("mentalHealth",v!=="MH-0");}} style={{flex:1,padding:"10px 6px",borderRadius:10,cursor:"pointer",background:active?bg:"transparent",border:`2px solid ${active?border:DS.border}`,color:active?color:DS.textSec,textAlign:"center"}}>
                  <div style={{fontSize:13,fontWeight:700}}>{v}</div>
                  <div style={{fontSize:10,marginTop:2}}>{sub}</div>
                </button>
              );
            })}
          </div>
          {newForm.mentalHealth&&(
            <div style={{marginTop:10}}>
              <textarea rows={2} style={{...S.input,resize:"none"}} placeholder="MH notes..." value={newForm.mentalHealthNotes} onChange={e=>setNF("mentalHealthNotes",e.target.value)}/>
            </div>
          )}
        </div>

        {/* Legal */}
        <div style={{...S.card}}>
          <SectionHdr>Legal Background</SectionHdr>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <OffenseSelector label="Current Offenses *" value={Array.isArray(newForm.currentOffenses)?newForm.currentOffenses:[]} onChange={v=>setNF("currentOffenses",v)}/>
            <OffenseSelector label="Prior Offenses" value={Array.isArray(newForm.priorOffenses)?newForm.priorOffenses:[]} onChange={v=>setNF("priorOffenses",v)}/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              {[{k:"detainer",l:"Detainer?"},{k:"icot",l:"ICOT in Progress?"},{k:"noContact",l:"No-Contact Order?"}].map(({k,l})=>(
                <div key={k}>
                  <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{l}</div>
                  <div style={{display:"flex",gap:6}}>
                    {[true,false].map(v=><button key={String(v)} onClick={()=>setNF(k,v)} style={{flex:1,padding:"6px",fontSize:12,borderRadius:8,cursor:"pointer",background:newForm[k]===v?(v?DS.error.bg:DS.success.bg):"transparent",border:`1px solid ${newForm[k]===v?(v?DS.error.border:DS.success.border):DS.border}`,color:newForm[k]===v?(v?DS.error.text:DS.success.text):DS.textSec,fontWeight:newForm[k]===v?600:400}}>{v?"Yes":"No"}</button>)}
                  </div>
                </div>
              ))}
            </div>
            {newForm.noContact&&(
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>No-Contact List</div>
                <textarea rows={2} style={{...S.input,resize:"none"}} value={newForm.noContactList} onChange={e=>setNF("noContactList",e.target.value)}/>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Prior Dorcus?</div>
                <input style={{...S.input}} placeholder="No / Yes — Year — Outcome" value={newForm.priorDorcus} onChange={e=>setNF("priorDorcus",e.target.value)}/>
              </div>
            </div>
          </div>
        </div>

        {/* Substance / Physical */}
        <div style={{...S.card}}>
          <SectionHdr>Health Background</SectionHdr>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {[{k:"substanceAbuse",nk:"substanceAbuseNotes",l:"Substance Abuse History"},{k:"physicalHealth",nk:"physicalHealthNotes",l:"Physical Health Concerns"}].map(({k,nk,l})=>(
              <div key={k}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:newForm[k]?8:0}}>
                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                    <input type="checkbox" checked={newForm[k]} onChange={e=>setNF(k,e.target.checked)}/>
                    <span style={{fontSize:13,color:DS.textPrimary}}>{l}</span>
                  </label>
                </div>
                {newForm[k]&&<textarea rows={2} style={{...S.input,resize:"none"}} placeholder="Details..." value={newForm[nk]} onChange={e=>setNF(nk,e.target.value)}/>}
              </div>
            ))}
          </div>
        </div>

        {/* Document checklist */}
        <div style={{...S.card}}>
          <SectionHdr>Document Checklist</SectionHdr>
          <div style={{fontSize:11,color:DS.textSec,marginBottom:10}}>Check off documents received and upload files</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {CHECKLIST_ITEMS.map((item,i)=>(
              <div key={i} style={{borderRadius:8,border:`1px solid ${newForm.checklist[i]?DS.success.border:DS.border}`,background:newForm.checklist[i]?DS.success.bg:"white",overflow:"hidden"}}>
                <div onClick={()=>{const c=[...newForm.checklist];c[i]=!c[i];setNF("checklist",c);}}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",cursor:"pointer"}}>
                  <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${newForm.checklist[i]?DS.success.text:DS.border}`,background:newForm.checklist[i]?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {newForm.checklist[i]&&<span style={{color:"white",fontSize:10,fontWeight:700}}>✓</span>}
                  </div>
                  <span style={{fontSize:12,color:newForm.checklist[i]?DS.success.text:DS.textPrimary,flex:1}}>
                    {item.label}{item.required&&<span style={{color:DS.error.text,marginLeft:2,fontSize:10}}>*</span>}
                  </span>
                </div>
                <div style={{padding:"0 12px 8px 12px"}}>
                  <DocumentUploader entityType="referrals" entityId={newForm.tempId||null} checklistIndex={i} itemLabel={item.label} canUpload={true}/>
                </div>
              </div>
            ))}
          </div>
          <div style={{marginTop:10,fontSize:11,color:DS.textSec}}>
            {newForm.checklist.filter(Boolean).length}/{CHECKLIST_ITEMS.length} documents received
          </div>
        </div>

        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button onClick={()=>setView("pipeline")} style={{...S.btnGhost}}>Cancel</button>
          <button onClick={addReferral} disabled={!newForm.name||!newForm.doc} style={{...S.btn,opacity:(!newForm.name||!newForm.doc)?0.4:1}}>Submit Referral</button>
        </div>
      </div>
    </div>
  );

  // ── Referral detail view ───────────────────────────────────────────────────
  if (view === "detail" && selected) {
    const st = REFERRAL_STAGES[selected.stage]||REFERRAL_STAGES.received;
    const cs = classStyle[selected.classification]||classStyle["Non-Violent"];
    return (
      <div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <button onClick={()=>setView("pipeline")} style={{...S.btnGhost,fontSize:12}}>← Back</button>
          <div style={{flex:1}}>
            <h2 style={{margin:0,fontSize:18,fontWeight:700,color:DS.textPrimary}}>{selected.name}</h2>
            <div style={{fontSize:11,color:DS.textSec,marginTop:2}}>DOC# {selected.doc} · Submitted {selected.submittedDate}</div>
          </div>
          <Pill label={st.label} bg={st.bg} border={st.dot} color={st.color}/>
        </div>

        {/* Archive referral — admin and RC */}
        {(perms.canManageReferrals || perms.canSeeAllFacilities) && !confirmArchiveReferral && (
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}>
            <button onClick={()=>setConfirmArchiveReferral(true)}
              style={{fontSize:11,padding:"5px 12px",borderRadius:8,cursor:"pointer",background:"transparent",border:"1px solid #dc2626",color:"#8B1A1A"}}>
              Archive Referral
            </button>
          </div>
        )}
        {confirmArchiveReferral && (
          <div style={{...S.card,background:DS.error.bg,border:`1px solid ${DS.error.border}`,marginBottom:16}}>
            <div style={{fontSize:13,color:DS.error.text,marginBottom:10,fontFamily:"'Inter',sans-serif"}}>
              Archive <strong>{selected.name}</strong>? This referral will be hidden from the pipeline but kept in records.
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setConfirmArchiveReferral(false)} style={{...S.btnGhost,fontSize:11}}>Cancel</button>
              <button onClick={async()=>{
                await saveReferral({id:selected.id, archived:true, archivedAt:TODAY});
                auditAction("referral_archived", { referralId:selected.id, referralName:selected.name });
                setConfirmArchiveReferral(false);
                setSelected(null);
              }} style={{fontSize:11,padding:"5px 14px",borderRadius:8,cursor:"pointer",background:DS.error.text,border:"none",color:"white",fontWeight:600}}>
                Confirm Archive
              </button>
            </div>
          </div>
        )}

        {/* Stage actions — show for all non-terminal stages */}
        {(()=>{const s=selected.stage||selected.status;return s!=="withdrawn"&&s!=="archived"&&s!=="rejected"&&s!=="active";})() && (
          <div style={{...S.card,marginBottom:20,border:`1px solid ${DS.border}`}}>
            <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,marginBottom:12}}>Update Stage</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {(selected.stage||selected.status)!=="under_review"&&<button onClick={()=>updateStage(selected.id,"under_review")} style={{padding:"8px 16px",fontSize:12,fontWeight:600,borderRadius:8,cursor:"pointer",background:DS.warning.bg,border:`1px solid ${DS.warning.border}`,color:DS.warning.text}}>Mark Under Review</button>}
              <button onClick={()=>setConfirmAccept(true)} style={{padding:"8px 16px",fontSize:12,fontWeight:600,borderRadius:8,cursor:"pointer",background:DS.success.bg,border:`1px solid ${DS.success.border}`,color:DS.success.text}}>✓ Accept</button>
              {(selected.stage||selected.status)!=="waitlisted"&&<button onClick={()=>updateStage(selected.id,"waitlisted")} style={{padding:"8px 16px",fontSize:12,fontWeight:600,borderRadius:8,cursor:"pointer",background:DS.purple.bg,border:`1px solid ${DS.purple.border}`,color:DS.purple.text}}>Waitlist</button>}
              <button onClick={()=>setShowWithdrawModal(true)} style={{...S.btn,background:DS.neutral.bg,border:`1px solid ${DS.neutral.border}`,color:DS.textSec,fontSize:12}}>↩ Withdraw</button>
              <button onClick={()=>setShowRejectModal(true)} style={{...S.btn,background:DS.error.bg,border:`1px solid ${DS.error.border}`,color:DS.error.text,fontSize:12}}>✗ Reject</button>

              {/* Withdraw modal */}
              {showWithdrawModal&&(
                <div style={{position:"fixed",inset:0,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(26,43,69,0.5)",padding:24}}>
                  <div style={{background:DS.white,borderRadius:8,width:"100%",maxWidth:440,overflow:"hidden",border:`0.5px solid ${DS.border}`}}>
                    <div style={{background:"#475569",padding:"12px 16px"}}>
                      <div style={{fontSize:15,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>Withdraw Application</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontFamily:"'Inter',sans-serif"}}>{selected?.name} · DOC# {selected?.doc}</div>
                    </div>
                    <div style={{padding:16}}>
                      <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8,fontFamily:"'Inter',sans-serif"}}>Withdrawal Initiated By</div>
                      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
                        {["Counselor / Referring Institution","Applicant (self-withdrawal)","Program — Mutual Agreement","Other"].map(opt=>(
                          <div key={opt} onClick={()=>setWithdrawInitiator(opt)}
                            style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:6,cursor:"pointer",border:`1px solid ${withdrawInitiator===opt?DS.blue:DS.border}`,background:withdrawInitiator===opt?DS.blueTint:DS.white}}>
                            <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${withdrawInitiator===opt?DS.blue:DS.border}`,background:withdrawInitiator===opt?DS.blue:"white",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                              {withdrawInitiator===opt&&<div style={{width:6,height:6,borderRadius:"50%",background:"white"}}/>}
                            </div>
                            <span style={{fontSize:13,color:withdrawInitiator===opt?DS.blue:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{opt}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4,fontFamily:"'Inter',sans-serif"}}>Notes (optional)</div>
                      <textarea rows={2} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical",marginBottom:14}} placeholder="Additional context..." value={withdrawNotes} onChange={e=>setWithdrawNotes(e.target.value)}/>
                      <div style={{fontSize:11,color:DS.textSec,fontStyle:"italic",marginBottom:16,fontFamily:"'Inter',sans-serif"}}>This is distinct from a rejection — no adverse decision is recorded.</div>
                      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                        <button onClick={()=>{setShowWithdrawModal(false);setWithdrawInitiator("");setWithdrawNotes("");}} style={{...S.btnGhost}}>Cancel</button>
                        <button onClick={handleWithdraw} disabled={!withdrawInitiator}
                          style={{...S.btn,background:withdrawInitiator?"#475569":"#9ca3af",border:"none",opacity:!withdrawInitiator?0.5:1}}>
                          Confirm Withdrawal
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Rejection reason modal */}
              {showRejectModal&&(
                <div style={{position:"fixed",inset:0,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(26,43,69,0.5)",padding:24}}>
                  <div style={{background:DS.white,borderRadius:8,width:"100%",maxWidth:440,overflow:"hidden",border:`0.5px solid ${DS.border}`}}>
                    <div style={{background:DS.blue,padding:"12px 16px"}}>
                      <div style={{fontSize:15,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>Reject Referral</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontFamily:"'Inter',sans-serif"}}>{selected?.name} · DOC# {selected?.doc}</div>
                    </div>
                    <div style={{padding:16}}>
                      <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8,fontFamily:"'Inter',sans-serif"}}>Reason for Rejection (Internal Only)</div>
                      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                        {REJECTION_REASONS.map(r=>(
                          <div key={r} onClick={()=>setRejectReason(r)}
                            style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:6,cursor:"pointer",border:`1px solid ${rejectReason===r?DS.blue:DS.border}`,background:rejectReason===r?DS.blueTint:DS.white}}>
                            <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${rejectReason===r?DS.blue:DS.border}`,background:rejectReason===r?DS.blue:"white",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                              {rejectReason===r&&<div style={{width:6,height:6,borderRadius:"50%",background:"white"}}/>}
                            </div>
                            <span style={{fontSize:13,color:rejectReason===r?DS.blue:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>{r}</span>
                          </div>
                        ))}
                      </div>
                      {rejectReason==="Other"&&(
                        <textarea rows={3} style={{...S.input,border:`1px solid ${DS.border}`,resize:"vertical",marginBottom:12}} placeholder="Describe reason..." value={rejectOther} onChange={e=>setRejectOther(e.target.value)}/>
                      )}
                      <div style={{fontSize:11,color:DS.textSec,fontStyle:"italic",marginBottom:16,fontFamily:"'Inter',sans-serif"}}>This reason is stored internally and not included in the counselor communication.</div>
                      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                        <button onClick={()=>{setShowRejectModal(false);setRejectReason("");setRejectOther("");}} style={{...S.btnGhost}}>Cancel</button>
                        <button onClick={handleReject} disabled={!rejectReason||(rejectReason==="Other"&&!rejectOther)}
                          style={{...S.btn,background:rejectReason?DS.error.text:"#9ca3af",border:"none",opacity:(!rejectReason||(rejectReason==="Other"&&!rejectOther))?0.5:1}}>
                          Confirm Rejection
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Accept confirmation modal */}
        {confirmAccept && (
          <div style={{position:"fixed",inset:0,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(26,43,69,0.5)",padding:24}}>
            <div style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:10,padding:24,maxWidth:460,width:"100%",boxShadow:"0 8px 32px rgba(27,94,190,0.15)"}}>
              <div style={{fontSize:18,fontWeight:700,color:DS.textPrimary,marginBottom:8}}>Accept Referral?</div>
              <p style={{fontSize:13,color:DS.textSec,marginBottom:16,lineHeight:1.6}}>
                This will create a Pending Arrival profile for <strong style={{color:DS.textPrimary}}>{selected.name}</strong>.
              </p>
              <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12,color:DS.textSec}}>
                ℹ Will be assigned to <strong style={{color:DS.textPrimary}}>{selected.classification==="Sex Offender"?"Dorcus 2 – 24th St":"Dorcus 1 – 801"}</strong> by default. You can change this in their profile.
              </div>
              <div style={{marginBottom:16}}>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Entry / Start Date</div>
                <input type="date" style={{...S.input}} value={acceptEntryDate} onChange={e=>setAcceptEntryDate(e.target.value)}
                  placeholder="Set entry date..."/>
                {acceptEntryDate&&<div style={{fontSize:11,color:DS.success.text,marginTop:4}}>Planned exit: {add90Days(acceptEntryDate)}</div>}
                {!acceptEntryDate&&<div style={{fontSize:11,color:DS.textSec,marginTop:4}}>Leave blank to set later in the resident's profile</div>}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button onClick={()=>{setConfirmAccept(false);setAcceptEntryDate("");}} style={{...S.btnGhost}}>Cancel</button>
                <button onClick={convertToResident} style={{...S.btnCTA}}>✓ Confirm & Create Resident</button>
              </div>
            </div>
          </div>
        )}

        {/* Communication buttons for accepted/rejected */}
        {(()=>{const s=selected.stage||selected.status;return s==="accepted"||s==="rejected";})() && (
          <div style={{display:"flex",gap:8,marginBottom:20}}>
            <button onClick={()=>openComm(selected, (selected.stage||selected.status)==="accepted"?"acceptance":"rejection")} style={{...S.btn,fontSize:12}}>📧 Send Communication</button>
          </div>
        )}

        {/* Details grid */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
          <div style={{...S.card}}>
            <SectionHdr>Referral Source</SectionHdr>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <InfoRow label="Case Counselor" value={selected.referredBy}/>
              <InfoRow label="Counselor Email" value={selected.counselorEmail}/>
              <InfoRow label="Referring From" value={selected.referralSource==="pp"?"Probation & Parole":"Institution"}/>
              {selected.referralSource==="pp"
                ? <InfoRow label="P&P District" value={VA_DISTRICTS.find(d=>String(d.n)===String(selected.ppDistrict))?.label||selected.ppDistrict}/>
                : <InfoRow label="Institution" value={selected.referralInstitution}/>
              }
              <InfoRow label="Referral Date" value={selected.referralDate}/>
            </div>
          </div>
          <div style={{...S.card}}>
            <SectionHdr>Demographics</SectionHdr>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <InfoRow label="DOC#" value={selected.doc}/>
              <InfoRow label="Date of Birth" value={selected.dob}/>
              <InfoRow label="Race" value={selected.race}/>
              <InfoRow label="Sentencing District" value={VA_DISTRICTS.find(d=>String(d.n)===String(selected.sentencingDistrict))?.label||selected.sentencingDistrict}/>
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                <span style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em"}}>Classification</span>
                <div style={{display:"flex",gap:6,marginTop:2,flexWrap:"wrap"}}>
                  <Pill label={selected.classification} bg={cs.bg} border={cs.border} color={cs.color}/>
                  {selected.gangAffiliated&&<Pill label="Gang Affiliated" bg="#2a1a0c" border="#f97316" color="#fdba74"/>}
                  <Pill label={selected.mhLevel||"MH-0"} bg="rgba(124,58,237,0.2)" border="#7c3aed" color="#4A3AAA"/>
                </div>
              </div>
            </div>
          </div>
          <div style={{...S.card}}>
            <SectionHdr>Legal</SectionHdr>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Current Offenses</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {(Array.isArray(selected.currentOffenses)?selected.currentOffenses:(selected.currentOffenses?[selected.currentOffenses]:[])).map((o,i)=><Pill key={i} label={o} bg="white" border={DS.border} color={DS.textPrimary}/>)}
                  {(!selected.currentOffenses||selected.currentOffenses.length===0)&&<span style={{fontSize:13,color:DS.textSec}}>—</span>}
                </div>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Prior Offenses</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {(Array.isArray(selected.priorOffenses)?selected.priorOffenses:(selected.priorOffenses?[selected.priorOffenses]:[])).map((o,i)=><Pill key={i} label={o} bg="white" border={DS.border} color={DS.textPrimary}/>)}
                  {(!selected.priorOffenses||selected.priorOffenses.length===0)&&<span style={{fontSize:13,color:DS.textSec}}>—</span>}
                </div>
              </div>
              {selected.detainer&&<InfoRow label="Detainer" value="Yes" alert/>}
              {selected.icot&&<InfoRow label="ICOT" value="In Process" alert/>}
              {selected.noContact&&<InfoRow label="No-Contact" value={selected.noContactList} alert/>}
              {selected.priorDorcus&&<InfoRow label="Prior Dorcus" value={selected.priorDorcus}/>}
            </div>
          </div>
          <div style={{...S.card}}>
            <SectionHdr>Document Checklist</SectionHdr>
            {(()=>{
              const checklist = selected.checklist||Array(CHECKLIST_ITEMS.length).fill(false);
              const isPP = selected.referralSource==="pp";
              const needsMH = selected.mhLevel==="MH-1"||selected.mhLevel==="MH-2";
              const submitted = checklist.filter(Boolean).length;
              const total = CHECKLIST_ITEMS.filter((_,i)=>!(CHECKLIST_ITEMS[i].condition==="pp"&&!isPP)&&!(CHECKLIST_ITEMS[i].condition==="mh"&&!needsMH)).length;
              const missingRequired = CHECKLIST_ITEMS.filter((item,i)=>item.required&&!checklist[i]&&!(item.condition==="pp"&&!isPP)&&!(item.condition==="mh"&&!needsMH));
              const pct = total?Math.round(submitted/total*100):0;
              return (<>
                {/* Progress */}
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <div style={{flex:1,height:4,background:DS.border,borderRadius:2,overflow:"hidden"}}>
                    <div style={{width:`${pct}%`,height:"100%",background:missingRequired.length===0?DS.success.text:DS.blue,borderRadius:2}}/>
                  </div>
                  <span style={{fontSize:10,color:DS.textSec,whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>{submitted}/{total} received</span>
                </div>
                {/* Missing required callout */}
                {missingRequired.length>0&&(
                  <div style={{background:DS.error.bg,border:`1px solid ${DS.error.border}`,borderRadius:6,padding:"7px 10px",marginBottom:8}}>
                    <div style={{fontSize:10,fontWeight:600,color:DS.error.text,marginBottom:3,fontFamily:"'Inter',sans-serif"}}>Missing — contact counselor:</div>
                    {missingRequired.map(item=>(
                      <div key={item.label} style={{fontSize:10,color:DS.error.text,fontFamily:"'Inter',sans-serif"}}>• {item.label}</div>
                    ))}
                  </div>
                )}
                {/* Doc rows */}
                <div style={{display:"flex",flexDirection:"column",gap:3,maxHeight:220,overflowY:"auto"}}>
                  {CHECKLIST_ITEMS.map((item,i)=>{
                    const isNA = (item.condition==="pp"&&!isPP)||(item.condition==="mh"&&!needsMH);
                    const isSub = checklist[i];
                    const isMissReq = !isSub&&!isNA&&item.required;
                    const isOptMiss = !isSub&&!isNA&&!item.required;
                    let bg="white",border=DS.border,iconBg=DS.border,icon="○",labelColor=DS.textSec;
                    if(isNA){bg=DS.pageBg;iconBg="#9CA3AF";icon="—";labelColor="#9CA3AF";}
                    else if(isSub){bg=DS.success.bg;border=DS.success.border;iconBg=DS.success.text;icon="✓";labelColor=DS.success.text;}
                    else if(isMissReq){bg=DS.error.bg;border=DS.error.border;iconBg=DS.error.text;icon="!";labelColor=DS.error.text;}
                    return (
                      <div key={i} style={{display:"flex",alignItems:"center",gap:7,padding:"6px 8px",borderRadius:5,background:bg,border:`0.5px solid ${border}`,opacity:isNA?0.5:1}}>
                        <div style={{width:15,height:15,borderRadius:3,background:iconBg,color:"white",fontSize:8,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{icon}</div>
                        <span style={{fontSize:11,color:labelColor,flex:1,fontFamily:"'Inter',sans-serif",lineHeight:1.3}}>
                          {item.label}{item.required&&!isNA&&<span style={{color:DS.error.text,marginLeft:2,fontSize:9}}>*</span>}
                          {isNA&&item.condition&&<span style={{fontSize:9,color:"#9CA3AF",marginLeft:5,fontStyle:"italic"}}>({item.condition==="pp"?"P&P only":"MH-1/2 only"})</span>}
                        </span>
                        {isSub&&<button onClick={async()=>{
                          try{
                            const allFiles=await getDocumentsForEntity("referrals",selected.id);
                            const match=allFiles.find(f=>f.path&&f.path.includes(`/${i}/`));
                            if(match){window.open(match.url,"_blank","width=900,height=700,scrollbars=yes");}
                            else{alert("File not found. It may have been uploaded differently.");}
                          }catch(e){alert("Could not load file: "+e.message);}
                        }} style={{fontSize:9,padding:"2px 7px",borderRadius:3,border:`1px solid ${DS.blueBorder}`,background:DS.blueTint,color:DS.blue,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif"}}>View</button>}
                        {isMissReq&&<span style={{fontSize:9,color:DS.error.text,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap",fontWeight:600}}>Not submitted</span>}
                        {isOptMiss&&<span style={{fontSize:9,color:"#9CA3AF",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>Not submitted</span>}
                      </div>
                    );
                  })}
                </div>
              </>);
            })()}
          </div>
        </div>

        {/* Decision record */}
        {selected.decisionDate&&(
          <div style={{...S.card,marginBottom:16,border:`1px solid ${st.dot}`}}>
            <SectionHdr>Decision Record</SectionHdr>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
              <InfoRow label="Decision" value={st.label}/>
              <InfoRow label="Date" value={selected.decisionDate}/>
              <InfoRow label="By" value={selected.decisionBy}/>
              {selected.decisionNotes&&<InfoRow label="Notes" value={selected.decisionNotes}/>}
            </div>
          </div>
        )}

        {/* Communication log */}
        {selected.communicationLog&&selected.communicationLog.length>0&&(
          <div style={{...S.card}}>
            <SectionHdr>Communication Log</SectionHdr>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {selected.communicationLog.map((c,i)=>(
                <div key={i} style={{borderLeft:"2px solid #4f46e5",paddingLeft:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                    <span style={{fontSize:11,fontWeight:600,color:"#a5b4fc",textTransform:"capitalize"}}>{c.type} email</span>
                    <span style={{fontSize:10,color:DS.textSec}}>{c.date} · {c.sentBy}</span>
                  </div>
                  <div style={{fontSize:11,color:DS.textSec}}>To: {c.recipients.join(", ")}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Communication draft view ───────────────────────────────────────────────
  if (view === "communicate" && selected) {
    const isAccept = commType === "acceptance";
    return (
      <div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <button onClick={()=>setView("detail")} style={{...S.btnGhost,fontSize:12}}>← Back</button>
          <h2 style={{margin:0,fontSize:18,fontWeight:700,color:DS.textPrimary}}>
            {isAccept?"Acceptance":"Rejection"} Communication — {selected.name}
          </h2>
        </div>

        {commSent ? (
          <div style={{...S.card,textAlign:"center",padding:"48px 0",border:"1px solid #059669"}}>
            <div style={{fontSize:40,marginBottom:12}}>✅</div>
            <div style={{fontSize:16,fontWeight:700,color:"#1A6B35",marginBottom:8}}>Communication Sent</div>
            <div style={{fontSize:12,color:DS.textSec,marginBottom:20}}>Sent to: {commRecipients.join(", ")}</div>
            <button onClick={()=>setView("detail")} style={{...S.btn}}>Back to Referral</button>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {/* Recipients */}
            <div style={{...S.card}}>
              <SectionHdr>Recipients</SectionHdr>
              <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:360,overflowY:"auto",paddingRight:4}}>
                {/* Counselor */}
                {selected.counselorEmail&&(
                  <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                    <input type="checkbox" checked={commRecipients.includes(selected.counselorEmail)}
                      onChange={e=>setCommRecipients(p=>e.target.checked?[...p,selected.counselorEmail]:p.filter(r=>r!==selected.counselorEmail))}/>
                    <div>
                      <div style={{fontSize:13,color:DS.textPrimary}}>{selected.referredBy||"Case Counselor"}</div>
                      <div style={{fontSize:11,color:DS.textSec}}>{selected.counselorEmail}</div>
                    </div>
                  </label>
                )}
                {/* District contacts */}
                {districtContacts.length > 0 && (
                  <>
                    <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginTop:4}}>District Personnel</div>
                    {districtContacts.map(c=>(
                      <label key={c.id} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                        <input type="checkbox" checked={commRecipients.includes(c.email)}
                          onChange={e=>setCommRecipients(p=>e.target.checked?[...p,c.email]:p.filter(r=>r!==c.email))}/>
                        <div>
                          <div style={{fontSize:13,color:DS.textPrimary}}>{c.name} — {c.title}</div>
                          <div style={{fontSize:11,color:DS.textSec}}>District {c.district} · {c.email}</div>
                        </div>
                      </label>
                    ))}
                  </>
                )}
                {districtContacts.length === 0 && (
                  <div style={{fontSize:11,color:DS.textSec}}>No district contacts in directory. Add them in Admin → District Directory.</div>
                )}
              </div>
            </div>

            {/* Email body */}
            <div style={{...S.card}}>
              <SectionHdr>Email Body</SectionHdr>
              <textarea rows={14} style={{...S.input,resize:"vertical",fontFamily:"inherit",lineHeight:1.6}} value={commBody} onChange={e=>setCommBody(e.target.value)}/>
              <div style={{fontSize:11,color:DS.textSec,marginTop:6}}>Edit as needed before sending.</div>
            </div>

            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setView("detail")} style={{...S.btnGhost}}>Cancel</button>
              <button onClick={sendComm} disabled={commSending||commRecipients.length===0}
                style={{...S.btn,opacity:(commSending||commRecipients.length===0)?0.5:1,background:isAccept?"#059669":"#dc2626"}}>
                {commSending?"Sending...":commRecipients.length===0?"Select Recipients":"📧 Send Email"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

// ── Auth-wired Main App ───────────────────────────────────────────────────────
import { auth, getOrCreateUserProfile, getUserProfile, subscribeToResidents, saveResident, logOut, isHouseComputerAccount, facilityForHouseAccount, subscribeToReferrals, saveReferral, deleteReferral, saveInOutEvent, subscribeToTodayInOutLogs, subscribeToInOutLogs, saveMedicationLog, subscribeToMedicationLogs, saveUAResult, subscribeToUAResults, subscribeToResidentUAHistory, saveShiftNote, subscribeToShiftNotes, saveHeadcount, subscribeToHeadcounts, saveBACResult, subscribeToBACResults, saveDischargeRecord, subscribeToDischargeRecords, saveAbscondNotification, updateAbscondNotification, subscribeToAbscondNotifications, logAuditEvent, saveUnlockPin, verifyUnlockPin, saveResidentDocument, deleteResidentDocument, subscribeToResidentDocuments, saveNotification, subscribeToNotifications, markNotificationRead, actionNotification, createRAStaff, updateRAStaff, deleteRAStaff, subscribeToRAStaff } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import LoginScreen from "./LoginScreen";
import RAPinScreen from "./RAPinScreen";
import PendingScreen from "./PendingScreen";
import StaffManager from "./StaffManager";

// Module-level audit ref — set by App, used by child components
let _auditFn = null;
const auditAction = (action, details={}) => { if (_auditFn) _auditFn(action, details); };

// Keyboard listener for PIN entry
function KeyboardPinListener({ onDigit, onBackspace }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key >= "0" && e.key <= "9") onDigit(e.key);
      if (e.key === "Backspace") onBackspace();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onDigit, onBackspace]);
  return null;
}

// ── RA Forms Page ─────────────────────────────────────────────────────────────

// ── DC2: RA Day of Discharge Form ────────────────────────────────────────────
function RADayOfDischargeForm({ residents, raName, onClose }) {
  const [residentName, setResidentName] = useState("");
  const [residentMatch, setResidentMatch] = useState(null);
  const [checks, setChecks] = useState({ room:false, meds:false, signoff:false });
  const [propertyChecks, setPropertyChecks] = useState({});
  const [propertyNotes, setPropertyNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const ITEMS = [
    { key:"room",    label:"Room inspected and cleared (including linens returned)" },
    { key:"meds",    label:"Medication supply reconciled" },
    { key:"signoff", label:"Resident signed exit acknowledgment" },
  ];

  const allChecked = Object.values(checks).every(Boolean);
  const nameMatches = residentName.length >= 2
    ? residents.filter(r=>r.name.toLowerCase().includes(residentName.toLowerCase())&&r.status==="active")
    : [];

  const handleSubmit = async () => {
    if(!residentMatch||!allChecked) return;
    setSaving(true);
    await saveNotification({
      type:"day_of_discharge",
      title:`Day of Discharge Complete — ${residentMatch.name}`,
      body:`RA checklist completed by ${raName||"RA"} on ${TODAY}. Property return checklist completed. Resident has been fully discharged.`,
      residentId:residentMatch.id,
      residentName:residentMatch.name,
      completedBy:raName||"RA",
      completedDate:TODAY,
      propertyChecks,
      read:false,
      actioned:false,
      createdAt:TODAY,
    });
    setSaving(false);
    setDone(true);
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:300,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"white",borderRadius:10,width:"100%",maxWidth:520,maxHeight:"95vh",display:"flex",flexDirection:"column",overflow:"hidden",border:`0.5px solid ${DS.border}`}}>
        <div style={{padding:"12px 16px",background:DS.blue,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div style={{fontSize:13,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>Day of Discharge Checklist</div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:5,color:"white",fontSize:11,padding:"3px 10px",cursor:"pointer"}}>✕ Close</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"16px 18px",display:"flex",flexDirection:"column",gap:16}}>
          {done ? (
            <div style={{textAlign:"center",padding:"32px 0"}}>
              <i className="bi bi-check-circle-fill" style={{fontSize:40,color:DS.success.text,marginBottom:16,display:"block"}}></i>
              <div style={{fontSize:15,fontWeight:600,color:DS.textPrimary,marginBottom:8,fontFamily:"'DM Sans',sans-serif"}}>Discharge checklist complete</div>
              <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Admin has been notified that {residentMatch?.name} has been fully discharged.</div>
            </div>
          ) : (
            <>
              <div style={{background:DS.warning.bg,border:`1px solid ${DS.warning.border}`,borderRadius:6,padding:"8px 12px",fontSize:11,color:DS.warning.text,fontFamily:"'Inter',sans-serif"}}>
                Complete all checklist items before submitting. Admin will be notified on completion.
              </div>
              <div style={{...S.card,padding:0,overflow:"visible"}}>
                <div style={{padding:"10px 14px",background:DS.blue,borderRadius:"8px 8px 0 0"}}>
                  <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>Resident</span>
                </div>
                <div style={{padding:"16px"}}>
                  <select style={{...S.input,border:`1px solid ${DS.border}`,padding:"12px 14px",fontSize:13,width:"100%"}} value={residentMatch?.id||""} onChange={e=>{const r=residents.find(x=>x.id===e.target.value);if(r){setResidentMatch(r);setResidentName(r.name);}else{setResidentMatch(null);setResidentName("");}}}>
                    <option value="">Select resident...</option>
                    {residents.filter(r=>r.status==="active").map(r=>(
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{...S.card,padding:0,overflow:"hidden"}}>
                <div style={{padding:"9px 12px",borderBottom:`0.5px solid ${DS.border}`}}>
                  <span style={{fontSize:11,fontWeight:600,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:"'Inter',sans-serif"}}>Discharge Checklist</span>
                </div>
                <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:10}}>
                  {ITEMS.map(item=>(
                    <div key={item.key} onClick={()=>setChecks(c=>({...c,[item.key]:!c[item.key]}))}
                      style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 14px",background:checks[item.key]?DS.success.bg:DS.pageBg,borderRadius:6,border:`1px solid ${checks[item.key]?DS.success.border:DS.border}`,cursor:"pointer"}}>
                      <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${checks[item.key]?DS.success.text:DS.border}`,background:checks[item.key]?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                        {checks[item.key]&&<i className="bi bi-check" style={{fontSize:10,color:"white"}}></i>}
                      </div>
                      <span style={{fontSize:12,color:checks[item.key]?DS.success.text:DS.textPrimary,fontFamily:"'Inter',sans-serif",lineHeight:1.5}}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Property Return Checklist */}
              <div style={{...S.card,padding:0,overflow:"hidden"}}>
                <div style={{padding:"9px 12px",background:DS.warning.text}}>
                  <span style={{fontSize:11,fontWeight:600,color:"white",textTransform:"uppercase",letterSpacing:"0.07em"}}>Property Return Checklist</span>
                </div>
                <div style={{padding:"14px",display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{fontSize:11,color:DS.textSec,marginBottom:8,fontFamily:"'Inter',sans-serif"}}>Check each item as it is returned to the resident. Add notes if an item is missing or damaged.</div>
                  <div style={{maxHeight:340,overflowY:"auto",display:"flex",flexDirection:"column",gap:12,paddingRight:6}}>
                  {PROPERTY_RETURN_ITEMS.map(item=>(
                    <div key={item.id}>
                      <div onClick={()=>setPropertyChecks(c=>({...c,[item.id]:!c[item.id]}))}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:propertyChecks[item.id]?DS.success.bg:DS.pageBg,borderRadius:6,border:`1px solid ${propertyChecks[item.id]?DS.success.border:DS.border}`,cursor:"pointer"}}>
                        <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${propertyChecks[item.id]?DS.success.text:DS.border}`,background:propertyChecks[item.id]?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          {propertyChecks[item.id]&&<i className="bi bi-check" style={{fontSize:9,color:"white"}}></i>}
                        </div>
                        <span style={{fontSize:12,color:propertyChecks[item.id]?DS.success.text:DS.textPrimary,flex:1,fontFamily:"'Inter',sans-serif"}}>{item.label}</span>
                      </div>
                      <input style={{...S.input,fontSize:11,padding:"5px 8px",marginTop:6,border:`1px solid ${DS.border}`}}
                        placeholder="Notes (optional — e.g. not found, damaged...)"
                        value={propertyNotes[item.id]||""}
                        onChange={e=>setPropertyNotes(n=>({...n,[item.id]:e.target.value}))}/>
                    </div>
                  ))}
                  <div style={{fontSize:11,color:DS.textSec,textAlign:"right",fontFamily:"'Inter',sans-serif",marginTop:6}}>
                    {Object.values(propertyChecks).filter(Boolean).length} of {PROPERTY_RETURN_ITEMS.length} items returned
                  </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div style={{padding:"10px 14px",background:"white",borderTop:`0.5px solid ${DS.border}`,display:"flex",justifyContent:"flex-end",gap:8,flexShrink:0}}>
          {done ? (
            <button onClick={onClose} style={{...S.btn}}>Done</button>
          ) : (
            <>
              <button onClick={onClose} style={{...S.btnGhost,fontSize:11}}>Cancel</button>
              <button onClick={handleSubmit} disabled={!residentMatch||!allChecked||saving}
                style={{...S.btn,opacity:(!residentMatch||!allChecked)?0.5:1}}>
                {saving?"Submitting...":"Submit & Notify Admin"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Incident Report Form ──────────────────────────────────────────────────────
const INCIDENT_CATEGORIES = [
  "Policy Violation","Altercation","Medical Event","Contraband",
  "Unauthorized Absence","Property Damage","Visitor Incident","Other",
];

function IncidentReportForm({ residents, raName, onClose }) {
  const [stage, setStage] = useState(1);
  const [residentId, setResidentId] = useState("");
  const [residentName, setResidentName] = useState("");
  const [residentMatch, setResidentMatch] = useState(null);
  const [category, setCategory] = useState("");
  const [narrative, setNarrative] = useState("");
  const [location, setLocation] = useState("");
  const [injuryOrDamage, setInjuryOrDamage] = useState("No");
  const [actionTaken, setActionTaken] = useState("");
  const [otherResidents, setOtherResidents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const activeResidents = residents.filter(r=>r.status==="active");
  const nameMatches = residentName.length>1 ? activeResidents.filter(r=>r.name.toLowerCase().includes(residentName.toLowerCase())) : [];

  const handleSubmit = async () => {
    if(!residentMatch||!category||!narrative.trim()) return;
    setSaving(true);
    try {
      const { db } = await import("./firebase");
      const { collection, addDoc, serverTimestamp } = await import("firebase/firestore");
      const ref = await addDoc(collection(db,"incidentReports"), {
        residentId: residentMatch.id,
        residentName: residentMatch.name,
        facility: residentMatch.facility,
        category, narrative, location, injuryOrDamage, actionTaken,
        otherResidentsInvolved: otherResidents,
        reportedBy: raName, reportedAt: new Date().toISOString(), date: TODAY,
        createdAt: serverTimestamp(),
      });
      await saveNotification({
        type:"incident_report",
        title:`Incident Report — ${residentMatch.name}`,
        body:`${category} reported by ${raName}. ${narrative.slice(0,80)}${narrative.length>80?"...":""}`,
        residentId: residentMatch.id, residentName: residentMatch.name,
        read:false, actioned:false, createdAt:TODAY,
      });
      setSubmitted(true);
    } catch(e) { alert("Failed to submit: "+e.message); }
    setSaving(false);
  };

  if(submitted) return (
    <div style={{position:"fixed",inset:0,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:20}}>
      <div style={{background:"white",borderRadius:10,padding:32,maxWidth:400,width:"100%",textAlign:"center",border:`0.5px solid ${DS.border}`}}>
        <div style={{fontSize:36,marginBottom:12}}>✅</div>
        <div style={{fontSize:16,fontWeight:600,color:DS.textPrimary,marginBottom:8,fontFamily:"'DM Sans',sans-serif"}}>Incident Report Submitted</div>
        <div style={{fontSize:13,color:DS.textSec,marginBottom:20}}>Admin has been notified. The report has been logged for {displayName(residentMatch.name)}.</div>
        <button onClick={onClose} style={{...S.btnCTA,width:"100%"}}>Done</button>
      </div>
    </div>
  );

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:20}}>
      <div style={{background:"white",borderRadius:10,width:"100%",maxWidth:500,maxHeight:"92vh",display:"flex",flexDirection:"column",overflow:"hidden",border:`0.5px solid ${DS.border}`}}>
        <div style={{padding:"12px 16px",background:DS.error.text,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontSize:14,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>Incident Report</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>Step {stage} of 3 · {raName}</div>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:5,color:"white",fontSize:11,padding:"3px 10px",cursor:"pointer"}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:16,display:"flex",flexDirection:"column",gap:12}}>
          {stage===1&&(
            <>
              <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Which resident is this report about?</div>
              <select style={{...S.input,border:`1px solid ${DS.border}`}}
                value={residentMatch?.id||""}
                onChange={e=>{
                  const r=activeResidents.find(r=>r.id===e.target.value);
                  setResidentMatch(r||null);
                  setResidentName(r?displayName(r.name):"");
                }}>
                <option value="">Select resident...</option>
                {activeResidents.sort((a,b)=>displayName(a.name).localeCompare(displayName(b.name))).map(r=>(
                  <option key={r.id} value={r.id}>{displayName(r.name)} — {shortFacility(r.facility)}</option>
                ))}
              </select>
              {residentMatch&&(
                <div style={{background:DS.error.bg,border:`1px solid ${DS.error.border}`,borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:10,marginTop:6}}>
                  <i className="bi bi-person-fill" style={{color:DS.error.text,fontSize:16}}></i>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:DS.error.text}}>{displayName(residentMatch.name)}</div>
                    <div style={{fontSize:11,color:DS.textSec}}>{shortFacility(residentMatch.facility)} · Rm {residentMatch.room||"—"}</div>
                  </div>
                </div>
              )}
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Incident Category *</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {INCIDENT_CATEGORIES.map(c=>(
                    <button key={c} onClick={()=>setCategory(c)}
                      style={{padding:"6px 12px",fontSize:12,borderRadius:8,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                        background:category===c?DS.error.bg:"white",
                        border:`1px solid ${category===c?DS.error.border:DS.border}`,
                        color:category===c?DS.error.text:DS.textPrimary,
                        fontWeight:category===c?600:400}}>{c}</button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Other Residents Involved (optional)</div>
                <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:160,overflowY:"auto"}}>
                  {activeResidents.filter(r=>r.id!==residentMatch?.id).sort((a,b)=>displayName(a.name).localeCompare(displayName(b.name))).map(r=>(
                    <div key={r.id} onClick={()=>setOtherResidents(o=>o.includes(r.id)?o.filter(x=>x!==r.id):[...o,r.id])}
                      style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:6,cursor:"pointer",
                        background:otherResidents.includes(r.id)?DS.warning.bg:"white",
                        border:`0.5px solid ${otherResidents.includes(r.id)?DS.warning.border:DS.border}`}}>
                      <div style={{width:15,height:15,borderRadius:3,border:`2px solid ${otherResidents.includes(r.id)?DS.warning.text:DS.border}`,background:otherResidents.includes(r.id)?DS.warning.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {otherResidents.includes(r.id)&&<i className="bi bi-check" style={{fontSize:9,color:"white"}}></i>}
                      </div>
                      <span style={{fontSize:12,color:otherResidents.includes(r.id)?DS.warning.text:DS.textPrimary}}>{displayName(r.name)}</span>
                      <span style={{fontSize:10,color:DS.textSec,marginLeft:"auto"}}>{shortFacility(r.facility)}</span>
                    </div>
                  ))}
                </div>
                {otherResidents.length>0&&<div style={{fontSize:11,color:DS.warning.text,marginTop:4}}>{otherResidents.length} other resident{otherResidents.length>1?"s":""} selected</div>}
              </div>
            </>
          )}
          {stage===2&&(
            <>
              <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>Describe the incident</div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Location</div>
                <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="e.g. Common area, Room 3, Front entrance..." value={location} onChange={e=>setLocation(e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Incident Narrative *</div>
                <textarea rows={6} style={{...S.input,resize:"none",border:`1px solid ${DS.border}`}}
                  placeholder="Describe what happened in detail — time, what was observed, who was involved, sequence of events..."
                  value={narrative} onChange={e=>setNarrative(e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Injury or Property Damage?</div>
                <div style={{display:"flex",gap:8}}>
                  {["Yes","No"].map(v=>(
                    <button key={v} onClick={()=>setInjuryOrDamage(v)}
                      style={{flex:1,padding:"8px",fontSize:12,borderRadius:8,cursor:"pointer",
                        background:injuryOrDamage===v?(v==="Yes"?DS.error.bg:DS.success.bg):"white",
                        border:`1px solid ${injuryOrDamage===v?(v==="Yes"?DS.error.border:DS.success.border):DS.border}`,
                        color:injuryOrDamage===v?(v==="Yes"?DS.error.text:DS.success.text):DS.textPrimary,fontWeight:600}}>{v}</button>
                  ))}
                </div>
              </div>
            </>
          )}
          {stage===3&&(
            <>
              <div style={{fontSize:12,fontWeight:600,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif"}}>What action was taken?</div>
              <div style={{background:DS.pageBg,border:`0.5px solid ${DS.border}`,borderRadius:8,padding:"10px 14px"}}>
                <div style={{fontSize:12,color:DS.textSec,marginBottom:4}}>Incident summary:</div>
                <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary}}>{displayName(residentMatch?.name)} · {category}</div>
                <div style={{fontSize:12,color:DS.textSec,marginTop:2}}>{narrative.slice(0,100)}{narrative.length>100?"...":""}</div>
              </div>
              <div>
                <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Action Taken</div>
                <textarea rows={4} style={{...S.input,resize:"none",border:`1px solid ${DS.border}`}}
                  placeholder="e.g. Verbal warning issued, Resident separated, Medical contacted, P&P notified..."
                  value={actionTaken} onChange={e=>setActionTaken(e.target.value)}/>
              </div>
              <div style={{background:DS.warning.bg,border:`1px solid ${DS.warning.border}`,borderRadius:8,padding:"8px 12px",fontSize:12,color:DS.warning.text}}>
                <i className="bi bi-bell-fill" style={{marginRight:6}}></i>
                Submitting this report will send an admin notification.
              </div>
            </>
          )}
        </div>
        <div style={{padding:"10px 16px",borderTop:`0.5px solid ${DS.border}`,display:"flex",justifyContent:"space-between",flexShrink:0}}>
          <button onClick={stage===1?onClose:()=>setStage(s=>s-1)} style={{...S.btnGhost,fontSize:12}}>
            {stage===1?"Cancel":"← Back"}
          </button>
          {stage<3?(
            <button onClick={()=>setStage(s=>s+1)}
              disabled={stage===1&&(!residentMatch||!category)}
              style={{...S.btnCTA,fontSize:12,opacity:(stage===1&&(!residentMatch||!category))?0.4:1}}>
              Continue →
            </button>
          ):(
            <button onClick={handleSubmit} disabled={!narrative.trim()||saving}
              style={{background:DS.error.text,color:"white",border:"none",borderRadius:6,padding:"8px 16px",fontSize:12,fontWeight:600,cursor:"pointer",opacity:(!narrative.trim()||saving)?0.4:1}}>
              {saving?"Submitting...":"Submit Report"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Fire Drill Log Form ────────────────────────────────────────────────────────
function FireDrillLogForm({ raName, onClose, perms, residents }) {
  const [participantIds, setParticipantIds] = React.useState([]);
  const facResidents = (residents||[]).filter(r => r.status === "active" && (!perms?.assignedFacility || r.facility === perms.assignedFacility));
  const toggleParticipant = (id) => setParticipantIds(p => p.includes(id) ? p.filter(x=>x!==id) : [...p, id]);
  const [date, setDate] = useState(TODAY);
  const [time, setTime] = useState(timeNow());
  const [conductedBy, setConductedBy] = useState(raName||"");
  const [residentCount, setResidentCount] = useState("");
  const [shift, setShift] = useState("Day (6a-2p)");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if(!date||!time||!conductedBy.trim()||!residentCount) return;
    setSaving(true);
    try {
      const { db } = await import("./firebase");
      const { collection, addDoc, serverTimestamp } = await import("firebase/firestore");
      await addDoc(collection(db,"fireDrills"), {
        date, time, conductedBy: conductedBy.trim(),
        residentCount: participantIds.length || parseInt(residentCount)||0, shift,
        participantIds, participantNames: facResidents.filter(r=>participantIds.includes(r.id)).map(r=>r.name),
        facility: perms?.assignedFacility||"",
        duration: duration.trim(), notes: notes.trim(),
        reportedBy: raName, createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch(e) { alert("Failed to submit: "+e.message); }
    setSaving(false);
  };

  if(submitted) return (
    <div style={{position:"fixed",inset:0,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:20}}>
      <div style={{background:"white",borderRadius:10,padding:32,maxWidth:400,width:"100%",textAlign:"center",border:`0.5px solid ${DS.border}`}}>
        <div style={{fontSize:36,marginBottom:12}}>🔥</div>
        <div style={{fontSize:16,fontWeight:600,color:DS.textPrimary,marginBottom:8,fontFamily:"'DM Sans',sans-serif"}}>Fire Drill Logged</div>
        <div style={{fontSize:13,color:DS.textSec,marginBottom:20}}>Fire drill on {date} at {time} has been recorded.</div>
        <button onClick={onClose} style={{...S.btnCTA,width:"100%"}}>Done</button>
      </div>
    </div>
  );

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(26,43,69,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:20}}>
      <div style={{background:"white",borderRadius:10,width:"100%",maxWidth:440,display:"flex",flexDirection:"column",overflow:"hidden",border:`0.5px solid ${DS.border}`}}>
        <div style={{padding:"12px 16px",background:"#7A3300",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontSize:14,fontWeight:600,color:"white",fontFamily:"'DM Sans',sans-serif"}}>🔥 Fire Drill Log</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>Record completion of fire drill</div>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:5,color:"white",fontSize:11,padding:"3px 10px",cursor:"pointer"}}>✕</button>
        </div>
        <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Date *</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} type="date" value={date} onChange={e=>setDate(e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Time *</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} type="time" value={time} onChange={e=>setTime(e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Conducted By *</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="Staff name..." value={conductedBy} onChange={e=>setConductedBy(e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Residents Present *</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} type="number" min="0" placeholder="e.g. 10" value={residentCount} onChange={e=>setResidentCount(e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Shift</div>
              <select value={shift} onChange={e=>setShift(e.target.value)}
                style={{background:DS.pageBg,border:`1px solid ${DS.border}`,borderRadius:6,padding:"7px 10px",fontSize:12,color:DS.textPrimary,width:"100%",outline:"none"}}>
                {["Day (6a-2p)","Evening (2p-10p)","Night (10p-6a)"].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Evacuation Time</div>
              <input style={{...S.input,border:`1px solid ${DS.border}`}} placeholder="e.g. 3 min 15 sec" value={duration} onChange={e=>setDuration(e.target.value)}/>
            </div>
          </div>
          <div>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Notes</div>
            <textarea rows={3} style={{...S.input,resize:"none",border:`1px solid ${DS.border}`}}
              placeholder="Any issues, residents absent, observations..."
              value={notes} onChange={e=>setNotes(e.target.value)}/>
          </div>
          <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${DS.border}`,padding:"12px 16px"}}>
            <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Residents Who Participated ({participantIds.length})</div>
            <div style={{maxHeight:180,overflowY:"auto",border:`1px solid ${DS.border}`,borderRadius:6,padding:6,background:DS.pageBg}}>
              {facResidents.length === 0 ? (
                <div style={{fontSize:11,color:DS.textSec,padding:8,textAlign:"center"}}>No active residents at this facility</div>
              ) : facResidents.map(r => (
                <label key={r.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 6px",cursor:"pointer",borderRadius:4,background:participantIds.includes(r.id)?"#E6F4EC":"transparent"}}>
                  <input type="checkbox" checked={participantIds.includes(r.id)} onChange={()=>toggleParticipant(r.id)}/>
                  <span style={{fontSize:12,color:DS.textPrimary}}>{r.name}</span>
                </label>
              ))}
            </div>
            <div style={{display:"flex",gap:6,marginTop:6}}>
              <button onClick={()=>setParticipantIds(facResidents.map(r=>r.id))}
                style={{padding:"3px 10px",fontSize:10,borderRadius:4,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer"}}>
                Select all
              </button>
              <button onClick={()=>setParticipantIds([])}
                style={{padding:"3px 10px",fontSize:10,borderRadius:4,border:`1px solid ${DS.border}`,background:"white",color:DS.textSec,cursor:"pointer"}}>
                Clear
              </button>
            </div>
          </div>
        </div>
        <div style={{padding:"10px 16px",borderTop:`0.5px solid ${DS.border}`,display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{...S.btnGhost,fontSize:12}}>Cancel</button>
          <button onClick={handleSubmit} disabled={!date||!time||!conductedBy.trim()||!residentCount||saving}
            style={{background:"#7A3300",color:"white",border:"none",borderRadius:6,padding:"8px 16px",fontSize:12,fontWeight:600,cursor:"pointer",opacity:(!date||!time||!conductedBy.trim()||!residentCount)?0.4:1}}>
            {saving?"Saving...":"Log Fire Drill"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RAFormsPage({ residents, raName, perms }) {
  const [showMoneyRequest, setShowMoneyRequest] = useState(false);
  const [showDayOfDischarge, setShowDayOfDischarge] = useState(false);
  const [showIncidentReport, setShowIncidentReport] = useState(false);
  const [showFireDrill, setShowFireDrill] = useState(false);

  const FORMS = [
    {
      id:"money_request",
      label:"Compensation Collection",
      icon:"bi-cash-stack",
      description:"Collect weekly compensation from a resident. Auto-calculates from work logs.",
      color:DS.success.text, bg:DS.success.bg, border:DS.success.border,
    },
    {
      id:"day_of_discharge",
      label:"Day of Discharge",
      icon:"bi-box-arrow-right",
      description:"Complete the RA discharge checklist when a resident is leaving. Notifies admin.",
      color:DS.warning.text, bg:DS.warning.bg, border:DS.warning.border,
    },
    {
      id:"incident_report",
      label:"Incident Report",
      icon:"bi-exclamation-triangle-fill",
      description:"Log a policy violation, altercation, medical event, or other incident. Notifies admin.",
      color:DS.error.text, bg:DS.error.bg, border:DS.error.border,
    },
    {
      id:"fire_drill",
      label:"Fire Drill Log",
      icon:"bi-fire",
      description:"Log completion of a fire drill — date, time, staff who conducted, and resident count.",
      color:"#7A3300", bg:"#FEF0E6", border:"#F5B885",
    },
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>
        Select a form to complete. Additional forms will be added as they become available.
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {FORMS.map(f=>(
          <button key={f.id} onClick={()=>{if(f.id==="money_request")setShowMoneyRequest(true);if(f.id==="day_of_discharge")setShowDayOfDischarge(true);if(f.id==="incident_report")setShowIncidentReport(true);if(f.id==="fire_drill")setShowFireDrill(true);}}
            style={{...S.card,cursor:"pointer",display:"flex",alignItems:"center",gap:14,textAlign:"left",transition:"border-color 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=DS.blue}
            onMouseLeave={e=>e.currentTarget.style.borderColor=DS.border}>
            <div style={{width:44,height:44,borderRadius:10,background:f.bg,border:`1px solid ${f.border}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <i className={`bi ${f.icon}`} style={{fontSize:20,color:f.color}}></i>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:600,color:DS.textPrimary,marginBottom:2,fontFamily:"'DM Sans',sans-serif"}}>{f.label}</div>
              <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>{f.description}</div>
            </div>
            <i className="bi bi-chevron-right" style={{fontSize:13,color:DS.textSec,flexShrink:0}}></i>
          </button>
        ))}
      </div>
      {showMoneyRequest&&<CompensationModal residents={residents} raName={raName} onClose={()=>setShowMoneyRequest(false)}/>}
      {showDayOfDischarge&&<RADayOfDischargeForm residents={residents} raName={raName} onClose={()=>setShowDayOfDischarge(false)}/>}
      {showIncidentReport&&<IncidentReportForm residents={residents} raName={raName} onClose={()=>setShowIncidentReport(false)}/>}
      {showFireDrill&&<FireDrillLogForm raName={raName} onClose={()=>setShowFireDrill(false)} perms={perms} residents={residents}/>}
    </div>
  );
}


// ── RA Notifications View ──────────────────────────────────────────────────────
function RANotificationsView({ facility }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeToNotifications(all => {
      // Show bulk messages (all facilities or matching facility) + facility-specific alerts
      const relevant = all.filter(n => {
        if (n.type === "bulk_message") {
          return !n.facility || n.facility === facility;
        }
        // Auto-triggers relevant to RA: UA fail, abscond
        return ["ua_fail_adhoc","abscond_alert","abscond_resolved"].includes(n.type) &&
          (!n.facility || n.facility === facility);
      });
      setNotifications(relevant);
      setLoading(false);
    });
    return () => unsub && unsub();
  }, [facility]);

  const markRead = async (id) => {
    const { db } = await import("./firebase");
    const { doc, updateDoc } = await import("firebase/firestore");
    await updateDoc(doc(db,"notifications",id), { read:true });
  };

  const unread = notifications.filter(n => !n.read);

  if (loading) return <div style={{...S.card,textAlign:"center",padding:"40px 0",color:DS.textSec,fontSize:13}}>Loading notifications...</div>;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {unread.length > 0 && (
        <div style={{display:"flex",justifyContent:"flex-end"}}>
          <button onClick={()=>unread.forEach(n=>markRead(n.id))} style={{...S.btnGhost,fontSize:11}}>
            Mark all read
          </button>
        </div>
      )}
      {notifications.length === 0 ? (
        <div style={{...S.card,textAlign:"center",padding:"48px 0",color:DS.textSec}}>
          <i className="bi bi-bell-slash" style={{fontSize:28,display:"block",marginBottom:8}}></i>
          <div style={{fontSize:13}}>No notifications yet</div>
        </div>
      ) : (
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          {notifications.map((n,i) => (
            <div key={n.id} onClick={()=>!n.read&&markRead(n.id)} style={{
              padding:"12px 14px",
              borderBottom:i<notifications.length-1?`0.5px solid ${DS.border}`:"none",
              background:n.read?"white":DS.blueTint,
              cursor:n.read?"default":"pointer",
              display:"flex",gap:12,alignItems:"flex-start"
            }}>
              <div style={{width:36,height:36,borderRadius:8,background:n.type==="bulk_message"?"#EEF2FF":DS.warning.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <i className={n.type==="bulk_message"?"bi bi-megaphone-fill":"bi bi-exclamation-triangle-fill"}
                  style={{fontSize:15,color:n.type==="bulk_message"?"#4338CA":DS.warning.text}}></i>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                  <div style={{fontSize:13,fontWeight:n.read?400:600,color:DS.textPrimary}}>{n.title}</div>
                  {!n.read&&<div style={{width:6,height:6,borderRadius:"50%",background:DS.blue,flexShrink:0}}/>}
                </div>
                <div style={{fontSize:12,color:DS.textSec,lineHeight:1.4}}>{n.body}</div>
                <div style={{fontSize:10,color:DS.textSec,marginTop:4}}>{n.createdAt}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Staff Manager ─────────────────────────────────────────────────────────────

export default function App() {
  // ── Hostname-based redirect for apply subdomain ─────────
  if (window.location.hostname === "apply.dorcushomeva.org" && window.location.pathname === "/") {
    window.location.replace("/apply");
    return null;
  }

  // ── Public routes — bypass auth entirely ────────────────
  if (window.location.pathname === "/apply" || window.location.pathname === "/apply/") {
    return <ReferralApp />;
  }

  // ── Auth state ──────────────────────────────────────────
  const [authState, setAuthState] = useState("loading"); // loading | login | raPin | pending | app
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [activeRA, setActiveRA] = useState(null); // RA logged in via PIN on house computer

  // ── App state ───────────────────────────────────────────
  const [page, setPage] = useState(() => {
    try { return localStorage.getItem("dorcus_page") || "dashboard"; } catch { return "dashboard"; }
  });

  const setPageAndSave = (p) => {
    setPage(p);
    try { localStorage.setItem("dorcus_page", p); } catch {}
  };
  const [residents, setResidents] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [abscondNotifications, setAbscondNotifications] = useState([]);
  const [raToolsOpen, setRAToolsOpen] = useState(false);
  const [pcWorkflowOpen, setPCWorkflowOpen] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showIntake, setShowIntake] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [facFilter, setFacFilter] = useState("all");
  const [previewRole, setPreviewRole] = useState(null);
  const [sidebarEditOpen, setSidebarEditOpen] = useState(false);
  const [sidebarOrder, setSidebarOrder] = useState(null);

  useEffect(() => {
    if (userProfile?.role !== "admin") return;
    const saved = localStorage.getItem(`dorcus_sidebar_order_${auth.currentUser?.uid}`);
    if (saved) { try { setSidebarOrder(JSON.parse(saved)); } catch {} }
  }, [userProfile?.role]);

  const saveSidebarOrder = (order) => {
    setSidebarOrder(order);
    try { localStorage.setItem(`dorcus_sidebar_order_${auth.currentUser?.uid}`, JSON.stringify(order)); } catch {}
  };

  // ── Soft lock state ─────────────────────────────────────
  const [isLocked, setIsLocked] = useState(false);
  const [lockPin, setLockPin] = useState("");
  const [lockError, setLockError] = useState("");
  const [lockPinInput, setLockPinInput] = useState("");
  const [showSetPin, setShowSetPin] = useState(false);
  const [setPinValue, setSetPinValue] = useState("");
  const [setPinConfirm, setSetPinConfirm] = useState("");
  const [setPinError, setSetPinError] = useState("");
  const inactivityTimer = useRef(null);

  // Audit helper — available throughout App
  const audit = useCallback((action, details={}) => {
    const role = activeRA ? (activeRA.facility?.includes("1")?"ra_a":"ra_b") : userProfile?.role;
    const userName = activeRA ? activeRA.name : (userProfile?.displayName || userProfile?.email || "Unknown");
    logAuditEvent({ action, userName, role, ...details, timestamp: new Date().toISOString() });
  }, [activeRA, userProfile]);

  // Wire to module-level so child components can use it
  _auditFn = audit;

  // Inactivity timeout — 25min for RAs, 45min for others
  const getTimeoutMs = useCallback(() => {
    const role = activeRA ? "ra" : userProfile?.role;
    return (role === "ra" || role === "ra_a" || role === "ra_b") ? 25*60*1000 : 45*60*1000;
  }, [activeRA, userProfile]);

  const resetTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (authState !== "app") return;
    inactivityTimer.current = setTimeout(() => {
      setIsLocked(true);
      setLockPinInput("");
      setLockError("");
      audit("session_locked", { reason: "inactivity" });
    }, getTimeoutMs());
  }, [authState, getTimeoutMs, audit]);

  useEffect(() => {
    if (authState !== "app") return;
    const events = ["mousemove","keydown","mousedown","touchstart","scroll"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive:true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [authState, resetTimer]);

  const handleUnlock = async () => {
    const role = activeRA ? "ra" : userProfile?.role;
    const isRA = role === "ra" || role === "ra_a" || role === "ra_b";
    if (isRA) {
      // RAs use their existing PIN from raStaff
      if (lockPinInput === activeRA?.pin || lockPinInput === lockPin) {
        setIsLocked(false); setLockPinInput(""); setLockError("");
        audit("session_unlocked");
        resetTimer();
      } else {
        setLockError("Incorrect PIN");
      }
    } else {
      // Admin/PC/RC use their unlock PIN stored in Firestore
      const result = await verifyUnlockPin(firebaseUser?.uid, lockPinInput);
      if (result === "valid") {
        setIsLocked(false); setLockPinInput(""); setLockError("");
        audit("session_unlocked");
        resetTimer();
      } else if (result === "no_pin") {
        // No PIN set yet — save this as their new PIN
        await saveUnlockPin(firebaseUser?.uid, lockPinInput);
        setLockPin(lockPinInput);
        setIsLocked(false); setLockPinInput(""); setLockError("");
        audit("unlock_pin_set");
        resetTimer();
      } else {
        setLockError("Incorrect PIN"); setLockPinInput("");
      }
    }
  };

  // ── Listen to Firebase auth ─────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setAuthState("login");
        setFirebaseUser(null);
        setUserProfile(null);
        setActiveRA(null);
        return;
      }
      setFirebaseUser(fbUser);
      const profile = await getOrCreateUserProfile(fbUser);
      setUserProfile(profile);

      if (profile.role === "house_computer") {
        // House computer — show RA PIN screen
        setAuthState("raPin");
      } else if (profile.role === "pending") {
        setAuthState("pending");
      } else {
        setAuthState("app");
      }
    });
    return unsub;
  }, []);

  // ── Subscribe to residents from Firestore ───────────────
  useEffect(() => {
    if (authState !== "app") return;
    const unsub = subscribeToResidents(data => {
      setResidents(data.filter(r => !r.archived));
    });
    return unsub;
  }, [authState]);

  // ── Subscribe to referrals from Firestore ───────────────
  useEffect(() => {
    if (authState !== "app") return;
    const unsub = subscribeToReferrals(setReferrals);
    return unsub;
  }, [authState]);

  // ── Subscribe to abscond notifications ──────────────────
  useEffect(() => {
    if (authState !== "app") return;
    const unsub = subscribeToAbscondNotifications(setAbscondNotifications);
    return unsub;
  }, [authState]);

  // ── Snap to valid page when profile loads ───────────────
  useEffect(() => {
    if (!userProfile && !activeRA) return;
    const role = activeRA ? (activeRA.facility?.includes("A") ? "ra_a" : "ra_b") : userProfile?.role;
    const p = PERMISSIONS[role];
    if (!p) return;
    if (!p.canSeeAllFacilities && ["dashboard", "residents"].includes(page)) setPageAndSave("inout");
    if (p.canSeeAllFacilities && page === "headcount") setPageAndSave("dashboard");
  }, [userProfile, activeRA]);

  // ── Auth flow handlers ──────────────────────────────────
  const handleLoginSuccess = async (fbUser) => {
    setFirebaseUser(fbUser);
    const profile = await getOrCreateUserProfile(fbUser);
    setUserProfile(profile);
    if (profile.role === "house_computer") {
      setAuthState("raPin");
    } else if (profile.role === "pending") {
      setAuthState("pending");
    } else {
      setAuthState("app");
      logAuditEvent({ action:"login", userName:fbUser.displayName||fbUser.email, role:profile.role, timestamp:new Date().toISOString() });
      if (profile.role === "pm" || profile.role === "rc") {
        setTimeout(() => setStaffClockModal("login"), 800);
      }
    }
  };

  const handleRALogin = (ra) => {
    setActiveRA(ra);
    setAuthState("app");
    setPageAndSave("inout");
    logAuditEvent({ action:"login", userName:ra.name, role:"ra", facility:ra.facility, timestamp:new Date().toISOString() });
    // RA time tracking: clock in
    (async()=>{
      try {
        const { db } = await import("./firebase");
        const { collection, addDoc, query, where, getDocs, updateDoc, doc } = await import("firebase/firestore");
        const now = new Date();
        // Close any open shift for this RA first
        const openQ = query(collection(db,"staffShiftLogs"), where("raId","==",ra.id), where("clockOut","==",null));
        const openSnap = await getDocs(openQ);
        openSnap.forEach(async(d)=>{ await updateDoc(doc(db,"staffShiftLogs",d.id),{clockOut:now.toISOString(),clockOutDisplay:now.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",timeZone:"America/New_York"})}); });
        // Clock in
        await addDoc(collection(db,"staffShiftLogs"),{
          raId:ra.id, raName:ra.name, facility:ra.facility,
          date:now.toLocaleDateString("en-CA",{timeZone:"America/New_York"}),
          clockIn:now.toISOString(),
          clockInDisplay:now.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",timeZone:"America/New_York"}),
          clockOut:null, clockOutDisplay:null,
        });
      } catch(e){ console.error("RA clock-in error",e); }
    })();
  };

  const [raShiftModal, setRaShiftModal] = React.useState(false);
  const [staffClockModal, setStaffClockModal] = React.useState(false); // PM/RC clock-in prompt
  const [staffShiftActive, setStaffShiftActive] = React.useState(false); // is PM/RC shift open
  const [staffShiftDocId, setStaffShiftDocId] = React.useState(null); // Firestore doc id for open shift


  const staffClockIn = async () => {
    try {
      const { db } = await import("./firebase");
      const { collection, addDoc } = await import("firebase/firestore");
      const now = new Date();
      const docRef = await addDoc(collection(db,"staffShiftLogs"),{
        staffId: firebaseUser?.uid||"",
        staffName: currentUserName,
        role: userProfile?.role||"",
        facility: userProfile?.facility||"",
        date: toNYDateStr(now),
        clockIn: now.toISOString(),
        clockInDisplay: now.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",timeZone:"America/New_York"}),
        clockOut: null, clockOutDisplay: null,
      });
      setStaffShiftDocId(docRef.id);
      setStaffShiftActive(true);
    } catch(e){ console.error("Staff clock-in error",e); }
    setStaffClockModal(false);
  };

  const staffClockOut = async () => {
    try {
      const { db } = await import("./firebase");
      const { doc, updateDoc } = await import("firebase/firestore");
      const now = new Date();
      if (staffShiftDocId) {
        await updateDoc(doc(db,"staffShiftLogs",staffShiftDocId),{
          clockOut: now.toISOString(),
          clockOutDisplay: now.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",timeZone:"America/New_York"}),
        });
      }
      setStaffShiftActive(false);
      setStaffShiftDocId(null);
    } catch(e){ console.error("Staff clock-out error",e); }
    setStaffClockModal(false);
  };

  const handleSwitchRA = () => {
    // RA time tracking: clock out current RA
    if(activeRA){
      (async()=>{
        try {
          const { db } = await import("./firebase");
          const { collection, query, where, getDocs, updateDoc, doc } = await import("firebase/firestore");
          const now = new Date();
          const openQ = query(collection(db,"staffShiftLogs"), where("raId","==",activeRA.id), where("clockOut","==",null));
          const openSnap = await getDocs(openQ);
          openSnap.forEach(async(d)=>{ await updateDoc(doc(db,"staffShiftLogs",d.id),{clockOut:now.toISOString(),clockOutDisplay:now.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",timeZone:"America/New_York"})}); });
        } catch(e){ console.error("RA clock-out error",e); }
      })();
    }
    setActiveRA(null);
    setAuthState("raPin");
    setPageAndSave("inout");
  };

  const handleSignOut = async () => {
    if (staffShiftActive && (userProfile?.role==="pm"||userProfile?.role==="rc")) {
      setStaffClockModal("logout");
      return;
    }
    await doSignOut();
  };
  const doSignOut = async () => {
    audit("logout");
    await logOut();
    setActiveRA(null);
    setAuthState("login");
    setFirebaseUser(null);
    setUserProfile(null);
    setStaffShiftActive(false);
    setStaffShiftDocId(null);
  };

  const handleSaveResident = async (updated) => {
    const existing = residents.find(r=>r.id===updated.id);
    // Log changed fields
    if (existing) {
      const changedFields = Object.keys(updated).filter(k => {
        try { return JSON.stringify(updated[k]) !== JSON.stringify(existing[k]); }
        catch { return false; }
      }).filter(k => !["createdAt","updatedAt"].includes(k));
      if (changedFields.length > 0) {
        const changes = Object.fromEntries(changedFields.map(k=>([k,{from:existing[k],to:updated[k]}])));
        audit("resident_edited", { residentId:updated.id, residentName:updated.name, fields:changedFields, changes });
      }
    } else {
      audit("resident_created", { residentName:updated.name });
    }
    await saveResident(updated);
    setSelected(null);
  };

  const handleArchiveResident = async (resident) => {
    audit("resident_archived", { residentId:resident.id, residentName:resident.name });
    await saveResident({ ...resident, archived: true, archivedAt: TODAY });
    setSelected(null);
  };

  const handleAddResident = async (newRes) => {
    audit("resident_created", { residentName:newRes.name });
    await saveResident(newRes);
  };

  // ── Auth screens ────────────────────────────────────────
  if (authState === "loading") {
    return (
      <div style={{ minHeight:"100vh", background:"#020817", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🏠</div>
          <div style={{ fontSize:13, color:DS.textSec }}>Loading...</div>
        </div>
      </div>
    );
  }
  if (authState === "login") return <LoginScreen onLogin={handleLoginSuccess} />;
  if (authState === "raPin") return <RAPinScreen facility={userProfile?.facility} onRALogin={handleRALogin} onSwitchAccount={handleSignOut} />;
  if (authState === "pending") return <PendingScreen user={firebaseUser} />;

  // ── Derive role from active RA or Google profile ────────
  const isHouseComputer = userProfile?.role === "house_computer";
  const realRole = activeRA
    ? (activeRA.facility?.includes("801") ? "ra_a" : "ra_b")
    : (userProfile?.role || "pending");

  // Admins can preview other roles for testing
  const role = (realRole === "admin" && previewRole) ? previewRole : realRole;

  const perms = PERMISSIONS[role] || PERMISSIONS.ra_a;

  // Facility for preview roles
  const previewFacility = previewRole === "ra_a" ? "Dorcus 1 - 801" : previewRole === "ra_b" ? "Dorcus 2 - 24th St" : null;

  const effectivePerms = {
    ...perms,
    role,
    assignedFacility: activeRA
      ? activeRA.facility
      : previewFacility
        ? previewFacility
        : ((role === "ra_a" || role === "ra_b") ? userProfile?.facility : perms.assignedFacility),
  };

  // Ensure page is valid for current role
  let safePage = page;
  if (!effectivePerms.canSeeAllFacilities && !effectivePerms.canViewResidents && ["dashboard","residents"].includes(page)) safePage = "ra_home";
  else if (!effectivePerms.canSeeAllFacilities && effectivePerms.canViewResidents && page === "dashboard") safePage = "ra_home";
  else if (effectivePerms.canSeeAllFacilities && !effectivePerms.canUseRATools && page === "ra_home") safePage = "dashboard";
  else if (!effectivePerms.canUseRATools && ["ra_home","ra_notifications","inout","med_log","ua_bac","shift","headcount","ra_forms","daily_ops"].includes(page)) safePage = "dashboard";
  else if (!["admin","pm"].includes(role) && ["pc_intake","pc_followups","pc_discharge","pc_progress_reports"].includes(page)) safePage = "dashboard";
  if (role === "rc" && !["dashboard","referrals"].includes(safePage)) safePage = "dashboard";
  else if (!effectivePerms.canManageReferrals && page === "referrals") safePage = "dashboard";
  else if (role !== "admin" && ["directory","reports"].includes(page)) safePage = "dashboard";

  // Current user display name for stamping on logs
  const currentUserName = activeRA ? activeRA.name : (userProfile?.displayName || userProfile?.email || "Staff");

  const visible = residents.filter(r => {
    if (!effectivePerms.canSeeAllFacilities) { if (r.status !== "active") return false; if (r.facility !== effectivePerms.assignedFacility) return false; }
    if (!effectivePerms.canViewApplicants && r.status === "applicant") return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (facFilter !== "all" && r.facility !== facFilter) return false;
    if (search && !r.name?.toLowerCase().includes(search.toLowerCase()) && !r.doc?.includes(search)) return false;
    return true;
  }).sort((a,b) => {
    const facA = FACILITIES.indexOf(a.facility); const facB = FACILITIES.indexOf(b.facility);
    if (facA !== facB) return facA - facB;
    const lastA = (a.name||"").split(" ").pop(); const lastB = (b.name||"").split(" ").pop();
    return lastA.localeCompare(lastB);
  });

  const counts = {
    all: residents.filter(r => effectivePerms.canSeeAllFacilities || (r.facility === effectivePerms.assignedFacility && r.status === "active")).length,
    active: residents.filter(r => r.status === "active" && (effectivePerms.canSeeAllFacilities || r.facility === effectivePerms.assignedFacility)).length,
    pending_arrival: effectivePerms.canViewApplicants ? residents.filter(r => r.status === "pending_arrival").length : 0,
    applicant: effectivePerms.canViewApplicants ? (referrals||[]).filter(r=>r.stage!=="active"&&r.stage!=="rejected").length : 0,
    discharged: effectivePerms.canSeeAllFacilities ? residents.filter(r => r.status === "discharged").length : 0,
  };

  const NAV_ITEMS = [
    { id:"dashboard",    label:"Dashboard",            icon:"bi-speedometer" },
    { id:"residents",    label:"Residents",            icon:"bi-people-fill" },
    { id:"reports",      label:"Reports",              icon:"bi-bar-chart-fill" },
    { id:"daily_log",    label:"Daily Log",            icon:"bi-journal-bookmark-fill" },
    { id:"directory",    label:"P&P Directory",        icon:"bi-book-fill" },
    { id:"staff",        label:"DH Staff",             icon:"bi-person-fill" },
    { id:"archived",     label:"Archived Residents",   icon:"bi-archive-fill" },
    { id:"referrals",    label:"Referral Pipeline",    icon:"bi-clipboard-fill" },
    { id:"ra_home_top",  label:"RA Home",              icon:"bi-house-fill",             raTop:true },
    { id:"ra_tools",     label:"RA Tools",              isGroup:true },
    { id:"ra_home_grp",  label:"RA Home",              icon:"bi-house-fill",             inGroup:true },
    { id:"ra_notifications", label:"Notifications",    icon:"bi-bell-fill",              inGroup:true },
    { id:"inout",        label:"In/Out Board",         icon:"bi-door-open-fill",          inGroup:true },
    { id:"med_log",      label:"Medication Log",       icon:"bi-capsule-pill",            inGroup:true },
    { id:"ua_bac",       label:"Urine Screen & BAC Testing",  icon:"bi-droplet-half",            inGroup:true },
    { id:"shift",        label:"Shift Reports",        icon:"bi-pencil-fill",             inGroup:true },
    { id:"headcount",    label:"Headcount Log",        icon:"bi-hash",                    inGroup:true },
    { id:"ra_forms",     label:"RA Forms",             icon:"bi-file-earmark-text-fill",  inGroup:true },
    { id:"daily_log",    label:"Daily Log",            icon:"bi-journal-bookmark-fill",   inGroup:true },
    { id:"pc_workflow",  label:"PC Workflow",           isPCGroup:true },
    { id:"pc_intake",    label:"Intake & Assessments",  icon:"bi-clipboard2-check-fill",  inPCGroup:true },
    { id:"pc_followups", label:"Resident Follow-ups",  icon:"bi-arrow-repeat",           inPCGroup:true },
    { id:"pc_progress_reports", label:"30/60/90 Day Reports", icon:"bi-file-earmark-bar-graph-fill", inPCGroup:true },
    { id:"pc_discharge", label:"Discharge Meeting",    icon:"bi-box-arrow-right",        inPCGroup:true },
    { id:"pc_intake_flat",    label:"Intake & Assessments",  icon:"bi-clipboard2-check-fill",  pmOnly:true },
    { id:"pc_followups_flat", label:"Resident Follow-ups",   icon:"bi-arrow-repeat",           pmOnly:true },
    { id:"pc_progress_flat",  label:"30/60/90 Day Reports",  icon:"bi-file-earmark-bar-graph-fill", pmOnly:true },
    { id:"pc_discharge_flat", label:"Discharge Meeting",     icon:"bi-box-arrow-right",        pmOnly:true },
  ];

  const visibleNav = NAV_ITEMS.filter(item => {
    if (item.id === "dashboard" && !effectivePerms.canSeeAllFacilities && role !== "rc") return false;
    if (item.raOnly && !effectivePerms.canUseRATools) return false;
    if (item.isGroup && !effectivePerms.canUseRATools) return false;
    if (item.inGroup && !effectivePerms.canUseRATools) return false;
    if (item.id === "staff" && role !== "admin") return false;
    if (item.id === "directory" && role !== "admin") return false;
    if (item.id === "reports" && role !== "admin") return false;
    if (item.id === "archived" && role !== "admin") return false;
    if (item.id === "referrals" && !effectivePerms.canManageReferrals) return false;
    if (item.id === "residents" && !effectivePerms.canViewResidents) return false;
    if (item.id === "residents" && role === "rc") return false;
    if ((item.isPCGroup || item.inPCGroup) && role !== "admin") return false;
    if (item.inPCGroup && !pcWorkflowOpen) return false;
    if (item.pmOnly && role !== "pm") return false;
    if (item.raTop && !effectivePerms.canUseRATools) return false;
    if (item.raTop && role !== "ra_a" && role !== "ra_b") return false;
    if (item.id === "ra_home_grp" && !effectivePerms.canSeeAllFacilities) return false;
    if (item.id === "daily_log" && !item.inGroup && !item.inPCGroup && !item.pmOnly && (role === "pm" || role === "rc" || role === "ra_a" || role === "ra_b")) return false;
    if (item.id === "daily_log" && item.inGroup && role === "admin") return false;
    return true;
  });

  const raResidents = residents.filter(r => r.status === "active" && (effectivePerms.canSeeAllFacilities || r.facility === effectivePerms.assignedFacility));

  const pageTitle = { dashboard:role==="pm"?"Prog. Coordinator Home":role==="rc"?"Referrals Home":"Admin Home", referrals:"Referral Pipeline", residents:"Resident Management", daily_log:"Daily Operations Log",
      reports:"Reports", ra_home:"RA Home", ra_notifications:"Notifications", inout:"In/Out Board", med_log:"Medication Log", ua_bac:"Urine Screen & BAC Testing", shift:"Shift Reports", headcount:"Headcount Log", ra_forms:"RA Forms", staff:"DH Staff", directory:"P&P Directory", archived:"Archived Residents", pc_intake:"Intake & Assessments", pc_followups:"Resident Follow-ups", pc_progress_reports:"30/60/90 Day Reports", pc_discharge:"Discharge Meeting", daily_ops:"Daily Operations Log" };
  const pageSubtitle = { dashboard:"At-a-glance overview across both facilities", referrals:"Track applications from received through active resident", residents:"Manage profiles & documents", reports:"Generate on-demand reports — Admin only", ra_home:`${effectivePerms.assignedFacility||"House"} · Live status`, ra_notifications:"Messages and alerts from admin", inout:"Track resident location status in real time", med_log:`${TODAY} · Log daily medication observations`, ua_bac:"urine testing schedule · Ad-hoc breathalyzer", shift:"Log end-of-shift summaries and incident notes", headcount:"Required at shift change and every 2 hours", ra_forms:"Submit and manage RA forms", staff:"Manage RA accounts and Google account roles", directory:"Manage district personnel contacts for communications", archived:"All discharged residents · sorted by discharge date", pc_intake:"Intake and orientation forms for new residents", pc_followups:"Scheduled follow-ups and open action items", pc_progress_reports:"Generate 30, 60, and 90-day CRP reports for residents", pc_discharge:"Conduct and record resident discharge meetings", daily_ops:"Chronological log of all facility events" };

  return (
    <div style={{ minHeight:"100vh", background:"#F0F4FA", fontFamily:"'Inter','Segoe UI',sans-serif", display:"flex", flexDirection:"column" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Inter:wght@400;500&family=Caveat:wght@600&display=swap" rel="stylesheet"/>
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css"/>

      {/* Soft lock screen */}
      {isLocked && (
        <div style={{position:"fixed",inset:0,zIndex:9999,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",background:"rgba(26,43,69,0.7)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:"white",border:"1px solid #D8DCE3",borderRadius:12,padding:36,maxWidth:360,width:"100%",textAlign:"center"}}>
            <KeyboardPinListener
              pinLength={lockPinInput.length}
              onDigit={(d)=>{
                if(lockPinInput.length<4){
                  const next=lockPinInput+d;
                  setLockPinInput(next);
                  setLockError("");
                  if(next.length===4){
                    setTimeout(async()=>{
                      const role=activeRA?"ra":userProfile?.role;
                      const isRA=role==="ra"||role==="ra_a"||role==="ra_b";
                      if(isRA){
                        if(next===activeRA?.pin||next===lockPin){setIsLocked(false);setLockPinInput("");setLockError("");audit("session_unlocked");resetTimer();}
                        else{setLockError("Incorrect PIN");setLockPinInput("");}
                      } else {
                        const result=await verifyUnlockPin(firebaseUser?.uid,next);
                        if(result==="valid"){setIsLocked(false);setLockPinInput("");setLockError("");audit("session_unlocked");resetTimer();}
                        else if(result==="no_pin"){await saveUnlockPin(firebaseUser?.uid,next);setLockPin(next);setIsLocked(false);setLockPinInput("");setLockError("");audit("unlock_pin_set");resetTimer();}
                        else{setLockError("Incorrect PIN");setLockPinInput("");}
                      }
                    },100);
                  }
                }
              }}
              onBackspace={()=>setLockPinInput(p=>p.slice(0,-1))}
            />
            <div style={{width:48,height:48,borderRadius:12,background:"#1B5EBE",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
              <i className="bi bi-shield-lock-fill" style={{fontSize:22,color:DS.textPrimary}}></i>
            </div>
            <div style={{fontSize:18,fontWeight:600,color:"#1A2B45",marginBottom:6,fontFamily:"'DM Sans',sans-serif"}}>Session Locked</div>
            <div style={{fontSize:13,color:"#4A5568",marginBottom:24}}>
              {activeRA ? "Enter your PIN to continue" : lockPin ? "Enter your PIN to continue" : "Set a 4-digit unlock PIN to continue"}
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center",marginBottom:16}}>
              {[0,1,2,3].map(i=>(
                <div key={i} style={{width:14,height:14,borderRadius:"50%",background:lockPinInput.length>i?"#1B5EBE":"#E8F0FB",border:`2px solid ${lockPinInput.length>i?"#1B5EBE":"#B5D4F4"}`,transition:"background 0.15s"}}/>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:16}}>
              {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((k,i)=>(
                <button key={i} onClick={()=>{
                  if(k==="⌫") setLockPinInput(p=>p.slice(0,-1));
                  else if(k!==""&&lockPinInput.length<4) {
                    const next = lockPinInput+String(k);
                    setLockPinInput(next);
                    setLockError("");
                    if(next.length===4) {
                      // auto-submit
                      setTimeout(async()=>{
                        const role = activeRA?"ra":userProfile?.role;
                        const isRA = role==="ra"||role==="ra_a"||role==="ra_b";
                        if(isRA) {
                          if(next===activeRA?.pin||next===lockPin) { setIsLocked(false);setLockPinInput("");setLockError("");audit("session_unlocked");resetTimer(); }
                          else { setLockError("Incorrect PIN");setLockPinInput(""); }
                        } else {
                          const result = await verifyUnlockPin(firebaseUser?.uid, next);
                          if(result==="valid") { setIsLocked(false);setLockPinInput("");setLockError("");audit("session_unlocked");resetTimer(); }
                          else if(result==="no_pin") { await saveUnlockPin(firebaseUser?.uid,next);setLockPin(next);setIsLocked(false);setLockPinInput("");setLockError("");audit("unlock_pin_set");resetTimer(); }
                          else { setLockError("Incorrect PIN");setLockPinInput(""); }
                        }
                      },100);
                    }
                  }
                }}
                  style={{padding:"14px",fontSize:k==="⌫"?18:16,fontWeight:500,borderRadius:8,cursor:k===""?"default":"pointer",background:k===""?"transparent":"#F0F4FA",border:`1px solid ${k===""?"transparent":"#D8DCE3"}`,color:k===""?"transparent":"#1A2B45",fontFamily:"'Inter',sans-serif"}}>
                  {k}
                </button>
              ))}
            </div>
            {lockError&&<div style={{fontSize:12,color:"#8B1A1A",marginBottom:8}}>{lockError}</div>}
            {!activeRA&&(
              <div style={{fontSize:11,color:"#4A5568",marginTop:8}}>
                {lockPin?"PIN set — enter to unlock":"Your PIN will be saved for future sessions."}
              </div>
            )}
            <button onClick={()=>{audit("session_logout_from_lock");logOut();}} style={{marginTop:16,fontSize:11,color:"#4A5568",background:"transparent",border:"none",cursor:"pointer",textDecoration:"underline"}}>
              Sign out instead
            </button>
          </div>
        </div>
      )}

      {/* Admin role preview bar */}
      {realRole === "admin" && (
        <div style={{ background:"#1B5EBE", borderBottom:"2px solid #F5C200", padding:"6px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"rgba(255,255,255,0.8)", fontFamily:"'Inter',sans-serif" }}>
            <i className="bi bi-eye-fill" style={{fontSize:13,color:"#F5C200"}}></i>
            <span style={{color:"rgba(255,255,255,0.6)"}}>Previewing as:</span>
            <span style={{color:DS.textPrimary,fontWeight:500}}>{ROLES[role]?.label || role}</span>
            {previewRole && <span style={{ fontSize:11, color:"rgba(255,255,255,0.75)" }}>· Testing mode — data is real</span>}
          </div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {Object.entries(ROLES).map(([key, val]) => {
              const isActive = role === key;
              return (
                <button key={key} onClick={() => {
                  setPreviewRole(key === "admin" ? null : key);
                  setStatusFilter("all");
                  setFacFilter("all");
                  setSelected(null);
                  if (key === "admin" || key === "pm" || key === "rc") setPageAndSave("dashboard");
                  else if (key === "ra_a" || key === "ra_b") setPageAndSave("ra_home");
                  else setPageAndSave("dashboard");
                }} style={{
                  padding:"3px 12px", fontSize:11, borderRadius:6, cursor:"pointer", fontWeight:isActive?600:400,
                  background:isActive?"white":"rgba(255,255,255,0.1)",
                  color:isActive?"#1B5EBE":"rgba(255,255,255,0.8)",
                  border:`1px solid ${isActive?"white":"rgba(255,255,255,0.2)"}`,
                  fontFamily:"'Inter',sans-serif",
                }}>
                  {val.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Global abscond banner — pinned top, above everything */}
      {abscondNotifications.filter(n=>!n.resolved).map(n=>(
        <div key={n.id} style={{background:DS.error.text,padding:"10px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:28,height:28,borderRadius:6,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <i className="bi bi-exclamation-triangle-fill" style={{fontSize:14,color:DS.textPrimary}}></i>
            </div>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:DS.textPrimary,fontFamily:"'Inter',sans-serif"}}>Abscond Alert — {n.residentName} · DOC# {n.residentDoc||"—"}</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontFamily:"'Inter',sans-serif"}}>{n.facility} · Reported {n.date} {!n.ppNotified&&"· P&P not yet notified"}</div>
            </div>
          </div>
          <button onClick={()=>setPageAndSave("residents")} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",color:DS.textPrimary,borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
            View record →
          </button>
        </div>
      ))}

      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>

        {/* Sidebar */}
        <div style={{ width:220, background:"#1B5EBE", display:"flex", flexDirection:"column", flexShrink:0, overflowY:"auto" }}>
          <div style={{ padding:"16px 14px 14px", borderBottom:"1px solid rgba(255,255,255,0.1)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:8, background:"#F5C200", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <i className="bi bi-house-fill" style={{fontSize:18,color:"#1B5EBE"}}></i>
              </div>
              <div>
                <div style={{ fontSize:15, fontWeight:600, color:"white", fontFamily:"'DM Sans',sans-serif", lineHeight:1.2 }}>Dorcus Home</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.65)", fontFamily:"'Inter',sans-serif" }}>Management System</div>
              </div>
            </div>
          </div>
          <div style={{ padding:"10px 8px", flex:1 }}>
            {visibleNav.map(item => {
              const NAV_ICONS = {
                dashboard:"bi-speedometer", residents:"bi-people-fill", referrals:"bi-clipboard-fill",
                reports:"bi-bar-chart-fill", archived:"bi-archive-fill", staff:"bi-person-fill",
                directory:"bi-book-fill", ra_home:"bi-house-fill", ra_home_grp:"bi-house-fill", ra_home_top:"bi-house-fill", inout:"bi-door-open-fill",
                med_log:"bi-capsule-pill", ua_bac:"bi-droplet-half", shift:"bi-pencil-fill",
                headcount:"bi-hash", ra_forms:"bi-file-earmark-text-fill",
                pc_intake:"bi-clipboard2-check-fill", pc_followups:"bi-arrow-repeat", pc_progress_reports:"bi-file-earmark-bar-graph-fill", pc_discharge:"bi-box-arrow-right",
              };
              if (item.pmOnly) {
                const realId = item.id.replace("_flat","").replace("pc_progress","pc_progress_reports");
                const isActive = safePage === realId;
                const iconMap = {pc_intake_flat:"bi-clipboard2-check-fill",pc_followups_flat:"bi-arrow-repeat",pc_progress_flat:"bi-file-earmark-bar-graph-fill",pc_discharge_flat:"bi-box-arrow-right"};
                const labelMap = {pc_intake_flat:"Intake & Assessments",pc_followups_flat:"Resident Follow-ups",pc_progress_flat:"30/60/90 Day Reports",pc_discharge_flat:"Discharge Meeting"};
                return (
                  <button key={item.id} onClick={()=>setPageAndSave(realId)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:isActive?"white":"transparent",border:"none",cursor:"pointer",textAlign:"left",width:"100%",borderRadius:6,marginBottom:2,transition:"background 0.15s"}}
                    onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background="rgba(255,255,255,0.1)";}}
                    onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent";}}>
                    <i className={`bi ${iconMap[item.id]||"bi-circle-fill"}`} style={{fontSize:16,color:isActive?"#1B5EBE":"rgba(255,255,255,0.9)",width:20,textAlign:"center"}}></i>
                    <span style={{fontSize:13,fontWeight:isActive?500:400,color:isActive?"#1A2B45":"rgba(255,255,255,0.9)",fontFamily:"'Inter',sans-serif"}}>{labelMap[item.id]||item.label}</span>
                  </button>
                );
              }
              if (item.isPCGroup) {
                return (
                  <button key={item.id} onClick={()=>setPCWorkflowOpen(o=>!o)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"transparent",border:"none",cursor:"pointer",textAlign:"left",width:"100%",borderRadius:6,marginBottom:2}}>
                    <i className="bi bi-person-lines-fill" style={{fontSize:16,color:"rgba(255,255,255,0.5)",width:20,textAlign:"center"}}></i>
                    <span style={{fontSize:13,fontWeight:500,color:"rgba(255,255,255,0.8)",flex:1,fontFamily:"'Inter',sans-serif"}}>PC Workflow</span>
                    <i className={`bi bi-chevron-${pcWorkflowOpen?"up":"down"}`} style={{fontSize:10,color:"rgba(255,255,255,0.7)"}}></i>
                  </button>
                );
              }
              if (item.inPCGroup && !pcWorkflowOpen) return null;
              if (item.inPCGroup) {
                const isActive = safePage === item.id;
                const iconMap = {pc_intake:"bi-clipboard2-check-fill",pc_followups:"bi-arrow-repeat",pc_progress_reports:"bi-file-earmark-bar-graph-fill",pc_discharge:"bi-box-arrow-right"};
                return (
                  <button key={item.id} onClick={()=>setPageAndSave(item.id)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px 7px 22px",background:isActive?"white":"transparent",border:"none",cursor:"pointer",textAlign:"left",width:"100%",borderRadius:6,marginBottom:2,transition:"background 0.15s"}}
                    onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background="rgba(255,255,255,0.1)";}}
                    onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent";}}>
                    <i className={`bi ${iconMap[item.id]||"bi-circle-fill"}`} style={{fontSize:14,color:isActive?"#1B5EBE":"rgba(255,255,255,0.9)",width:16,textAlign:"center"}}></i>
                    <span style={{fontSize:12,fontWeight:isActive?500:400,color:isActive?"#1A2B45":"rgba(255,255,255,0.9)",fontFamily:"'Inter',sans-serif"}}>{item.label}</span>
                  </button>
                );
              }
              if (item.isGroup) {
                if (!effectivePerms.canSeeAllFacilities) return null;
                return (
                  <button key={item.id} onClick={()=>setRAToolsOpen(o=>!o)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"transparent",border:"none",cursor:"pointer",textAlign:"left",width:"100%",borderRadius:6,marginBottom:2}}>
                    <i className="bi bi-tools" style={{fontSize:16,color:"rgba(255,255,255,0.5)",width:20,textAlign:"center"}}></i>
                    <span style={{fontSize:13,fontWeight:500,color:"rgba(255,255,255,0.8)",flex:1,fontFamily:"'Inter',sans-serif"}}>RA Tools</span>
                    <i className={`bi bi-chevron-${raToolsOpen?"up":"down"}`} style={{fontSize:10,color:"rgba(255,255,255,0.7)"}}></i>
                  </button>
                );
              }
              if (item.inGroup && effectivePerms.canSeeAllFacilities && !raToolsOpen) return null;
              if (item.inGroup && !effectivePerms.canSeeAllFacilities && !effectivePerms.canUseRATools) return null;
              const navId = (item.id === "ra_home_grp" || item.id === "ra_home_top") ? "ra_home" : item.id;
              const isActive = safePage === navId;
              const indent = item.inGroup && effectivePerms.canSeeAllFacilities;
              const iconClass = NAV_ICONS[navId] || NAV_ICONS[item.id] || "bi-circle-fill";
              return (
                <button key={item.id} onClick={() => setPageAndSave(navId)}
                  style={{display:"flex",alignItems:"center",gap:10,padding:`8px ${indent?"20px":"10px"}`,background:isActive?"white":"transparent",border:"none",cursor:"pointer",textAlign:"left",width:"100%",borderRadius:6,marginBottom:2,transition:"background 0.15s"}}
                  onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background="rgba(255,255,255,0.1)";}}
                  onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent";}}>
                  <i className={`bi ${iconClass}`} style={{fontSize:16,color:isActive?"#1B5EBE":"rgba(255,255,255,0.9)",width:20,textAlign:"center"}}></i>
                  <span style={{fontSize:13,fontWeight:isActive?500:400,color:isActive?"#1A2B45":"rgba(255,255,255,0.9)",fontFamily:"'Inter',sans-serif"}}>{item.id==="dashboard"?(role==="pm"?"PC Home":role==="rc"?"Referrals Home":"Admin Home"):item.label}</span>
                </button>
              );
            })}
          </div>
          {role==="admin" && (
            <button onClick={()=>setSidebarEditOpen(o=>!o)}
              style={{margin:"4px 8px",padding:"4px 8px",borderRadius:6,border:"1px solid rgba(255,255,255,0.2)",background:"transparent",color:"rgba(255,255,255,0.55)",fontSize:10,cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontFamily:"'Inter',sans-serif",width:"calc(100% - 16px)"}}>
              <i className="bi bi-layout-sidebar" style={{fontSize:11}}></i> Customize sidebar
            </button>
          )}
          <div style={{ padding:"12px 14px", borderTop:"1px solid rgba(255,255,255,0.1)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <div style={{ width:30, height:30, borderRadius:"50%", background:"#E8F0FB", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <i className="bi bi-person-fill" style={{fontSize:15,color:"#1B5EBE"}}></i>
              </div>
              <div style={{flex:1,overflow:"hidden"}}>
                <div style={{ fontSize:12, color:DS.textPrimary, fontWeight:500, fontFamily:"'Inter',sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{currentUserName}</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.7)", fontFamily:"'Inter',sans-serif" }}>{ROLES[role]?.label || role}</div>
              </div>
            </div>
            {isHouseComputer && activeRA ? (
              <button onClick={()=>setRaShiftModal(true)} style={{ width:"100%", padding:"6px", background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:6, color:DS.textPrimary, fontSize:11, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                Switch / End Shift
              </button>
            ) : null}
            {(role==="pm"||role==="rc")&&!staffShiftActive&&(
              <button onClick={()=>setStaffClockModal("login")} style={{width:"100%",padding:"6px",marginBottom:4,background:"transparent",border:"1px solid #F5C200",borderRadius:6,color:"#F5C200",fontSize:11,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Start Shift</button>
            )}
            {(role==="pm"||role==="rc")&&staffShiftActive&&(
              <button onClick={()=>setStaffClockModal("logout")} style={{width:"100%",padding:"6px",marginBottom:4,background:"transparent",border:"1px solid #1A6B35",borderRadius:6,color:"#E6F4EC",fontSize:11,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>● Shift Active</button>
            )}
            <button onClick={handleSignOut} style={{ width:"100%", padding:"6px", background:"transparent", border:"1px solid rgba(255,255,255,0.2)", borderRadius:6, color:"rgba(255,255,255,0.7)", fontSize:11, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                Sign Out
              </button>
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", background:DS.pageBg }}>
          <div style={{ background:"white", borderBottom:"0.5px solid #D8DCE3", padding:"0 28px", display:"flex", alignItems:"center", justifyContent:"space-between", height:52, flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", height:"100%", borderBottom:"2px solid #F5C200", gap:10 }}>
              {safePage!=="dashboard" && safePage!=="ra_home" && (
                <button onClick={()=>setPageAndSave(role==="pm"?"dashboard":role==="admin"?"dashboard":role==="rc"?"referrals":"ra_home")}
                  style={{background:"none",border:"none",cursor:"pointer",padding:"4px 6px",borderRadius:6,display:"flex",alignItems:"center",gap:4,color:DS.textSec}}
                  onMouseEnter={e=>e.currentTarget.style.background=DS.pageBg}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <i className="bi bi-arrow-left" style={{fontSize:14}}/>
                </button>
              )}
              <div>
                <h1 style={{ fontSize:16, fontWeight:600, color:"#1A2B45", margin:0, fontFamily:"'DM Sans',sans-serif" }}>{pageTitle[safePage]}</h1>
                <p style={{ fontSize:11, color:"#4A5568", margin:0, fontFamily:"'Inter',sans-serif" }}>{pageSubtitle[safePage]}</p>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:32,height:32,borderRadius:"50%",background:"#E8F0FB",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <i className="bi bi-person-fill" style={{fontSize:16,color:"#1B5EBE"}}></i>
              </div>
            </div>
          </div>

          <div style={{ padding:24, flex:1, background:DS.pageBg }}>
            {safePage==="dashboard" && role==="pm" && <PCDashboard residents={residents} onNavigate={setPageAndSave}/>}
            {safePage==="dashboard" && role==="rc" && <RCDashboard referrals={referrals} residents={residents} onNavigate={setPageAndSave}/>}
            {safePage==="dashboard" && role!=="pm" && role!=="rc" && <Dashboard residents={residents} referrals={referrals} onNavigate={setPageAndSave} abscondNotifications={abscondNotifications} uid={auth.currentUser?.uid}/>}
            {safePage==="staff" && role==="admin" && <StaffManager/>}
            {safePage==="residents" && (
              <div>
                {!effectivePerms.canSeeAllFacilities && (
                  <div style={{ background:DS.blueTint, border:`1px solid ${DS.blueBorder}`, borderRadius:8, padding:"12px 16px", marginBottom:20, display:"flex", gap:10, alignItems:"center" }}>
                    <i className="bi bi-building-fill" style={{fontSize:16,color:DS.blue,flexShrink:0}}></i>
                    <div><div style={{ fontSize:13, fontWeight:600, color:DS.blue, fontFamily:"'DM Sans',sans-serif" }}>Showing {effectivePerms.assignedFacility} only</div><div style={{ fontSize:11, color:DS.textSec, marginTop:2, fontFamily:"'Inter',sans-serif" }}>Contact a Program Coordinator for cross-facility access.</div></div>
                  </div>
                )}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:24 }}>
                  {[
                    {label:"Active Residents", value:counts.active, max:70, c:DS.success.text, bar:DS.success.border},
                    {label:effectivePerms.canViewApplicants?"Referrals":"Your House", value:effectivePerms.canViewApplicants?counts.applicant:counts.active, max:effectivePerms.canViewApplicants?20:35, c:DS.warning.text, bar:DS.yellow},
                    {label:"Dorcus 1", value:residents.filter(r=>r.facility==="Dorcus 1 - 801"&&r.status==="active").length, max:35, c:DS.blue, bar:DS.blue},
                    {label:"Dorcus 2", value:residents.filter(r=>r.facility==="Dorcus 2 - 24th St"&&r.status==="active").length, max:35, c:DS.darkYellow, bar:DS.darkYellow},
                  ].map(({label,value,max,c,bar})=>(
                    <div key={label} style={{...S.card}}>
                      <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>{label}</div>
                      <div style={{fontSize:28,fontWeight:600,color:c,marginBottom:8,fontFamily:"'DM Sans',sans-serif"}}>{value}</div>
                      <div style={{height:4,background:DS.border,borderRadius:2,overflow:"hidden",marginBottom:4}}><div style={{width:`${Math.min(100,value/max*100)}%`,height:"100%",background:bar,borderRadius:2}}/></div>
                      <div style={{fontSize:10,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>of {max} capacity</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
                  <div style={{ flex:1, minWidth:200, position:"relative" }}>
                    <i className="bi bi-search" style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:DS.textSec, fontSize:13 }}></i>
                    <input style={{...S.input, paddingLeft:32, border:`1px solid ${DS.border}`}} placeholder="Search by name or DOC#..." value={search} onChange={e=>setSearch(e.target.value)}/>
                  </div>
                  {effectivePerms.canSeeAllFacilities && <select style={{...S.input,width:"auto",border:`1px solid ${DS.border}`}} value={facFilter} onChange={e=>setFacFilter(e.target.value)}><option value="all">All Facilities</option>{FACILITIES.map(f=><option key={f}>{f}</option>)}</select>}
                  {effectivePerms.canCreateReferral && <button onClick={()=>setShowIntake(true)} style={{...S.btnCTA,display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}><i className="bi bi-plus-lg"></i> New Referral</button>}
                </div>
                <div style={{ display:"flex", gap:4, marginBottom:16, background:DS.pageBg, border:`1px solid ${DS.border}`, borderRadius:8, padding:4, width:"fit-content" }}>
                  {[{key:"active",label:"Active"},{key:"pending_arrival",label:"Pending Arrival"},{key:"discharged",label:"Discharged"}].map(({key,label})=>(
                    <button key={key} onClick={()=>setStatusFilter(key)} style={{padding:"6px 16px",fontSize:12,fontWeight:statusFilter===key?500:400,borderRadius:6,cursor:"pointer",background:statusFilter===key?DS.blue:"transparent",border:"none",color:statusFilter===key?"white":DS.textSec,fontFamily:"'Inter',sans-serif"}}>
                      {label} <span style={{opacity:0.6,fontSize:10}}>{counts[key]}</span>
                    </button>
                  ))}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12 }}>
                  {visible.map(r=><ResidentCard key={r.id} resident={r} onClick={(res)=>{setSelected(res);audit("resident_viewed",{residentId:res.id,residentName:res.name});}} perms={effectivePerms}/>)}
                  {visible.length===0 && (
                    <div style={{gridColumn:"1/-1",textAlign:"center",padding:"60px 0"}}>
                      <div style={{width:48,height:48,borderRadius:12,background:DS.blueTint,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
                        <i className="bi bi-search" style={{fontSize:20,color:DS.blue}}></i>
                      </div>
                      <div style={{fontSize:14,fontWeight:500,color:DS.textPrimary,fontFamily:"'DM Sans',sans-serif",marginBottom:4}}>No residents found</div>
                      <div style={{fontSize:12,color:DS.textSec,fontFamily:"'Inter',sans-serif"}}>Try adjusting your search or filters</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {["inout","med_log","ua_bac","shift","headcount","daily_ops"].includes(safePage) && <OverdueBanner residents={raResidents}/>}
            {safePage==="inout" && <InOutBoard residents={raResidents} perms={effectivePerms} raName={currentUserName}/>}
            {safePage==="med_log" && <MAR residents={raResidents} perms={effectivePerms}/>}
            {safePage==="ua_bac" && <UABACTesting residents={raResidents} raName={currentUserName} perms={effectivePerms}/>}
            {safePage==="shift" && <ShiftNotes raName={currentUserName}/>}
            {safePage==="headcount" && <Headcount residents={residents} perms={effectivePerms} raName={currentUserName}/>}
            {safePage==="ra_forms" && <RAFormsPage residents={raResidents} raName={currentUserName} perms={effectivePerms}/>}
            {safePage==="ra_home" && effectivePerms.canUseRATools && <RAHome residents={raResidents} perms={effectivePerms} raName={currentUserName} onNavigate={setPageAndSave} abscondNotifications={abscondNotifications}/>}
            {safePage==="ra_notifications" && effectivePerms.canUseRATools && <RANotificationsView facility={effectivePerms.assignedFacility}/>}
            {safePage==="referrals" && effectivePerms.canManageReferrals && <ReferralPipeline referrals={referrals} setReferrals={setReferrals} perms={effectivePerms} currentUser={currentUserName}/>}
            {safePage==="directory" && role==="admin" && <DistrictDirectory/>}
            {(safePage==="daily_log"||safePage==="daily_ops") && (role==="admin"||role==="ra_a"||role==="ra_b") && <DailyOpsLog residents={residents} role={role} facility={userProfile?.facility||""}/>}
            {safePage==="reports" && role==="admin" && <ReportsSection residents={residents} referrals={referrals}/>}
            {safePage==="archived" && role==="admin" && <ArchivedResidents residents={residents}/>}
            {safePage==="pc_intake" && (role==="pm"||role==="admin") && <PCIntake residents={residents} onSaveResident={handleSaveResident} currentUser={currentUserName}/>}
            {safePage==="pc_progress_reports" && (role==="pm"||role==="admin") && <ProgressReportsSection residents={residents} facilityFilter="all" />}
            {safePage==="pc_followups" && (role==="pm"||role==="admin") && <PCFollowups residents={residents} onSaveResident={handleSaveResident} currentUser={currentUserName}/>}
            {safePage==="pc_discharge" && (role==="pm"||role==="admin") && <DCDischargeMeeting residents={residents} currentUser={currentUserName} onSaveResident={handleSaveResident}/>}
          </div>
        </div>
      </div>

      

      {/* Staff Clock Modal — PM/RC shift start/end */}
      {staffClockModal && (userProfile?.role==="pm"||userProfile?.role==="rc") && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setStaffClockModal(false)}>
          <div style={{background:"white",borderRadius:12,padding:24,width:300,boxShadow:"0 4px 24px rgba(0,0,0,0.15)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:600,color:"#1A2B45",fontFamily:"'DM Sans',sans-serif",marginBottom:6}}>
              {staffClockModal==="logout"?"End your shift?":"Start your shift?"}
            </div>
            <div style={{fontSize:12,color:"#4A5568",fontFamily:"'Inter',sans-serif",marginBottom:20,lineHeight:1.5}}>
              {staffClockModal==="logout"
                ? "You have an open shift. Clock out before signing out?"
                : "Clock in to start tracking your hours for today."}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {staffClockModal==="logout" ? (<>
                <button onClick={async()=>{await staffClockOut();await doSignOut();}}
                  style={{padding:"10px 14px",borderRadius:8,border:"none",background:"#1B5EBE",color:"white",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  End shift &amp; sign out
                </button>
                <button onClick={async()=>{ await doSignOut(); }}
                  style={{padding:"10px 14px",borderRadius:8,border:"1.5px solid #D8DCE3",background:"white",color:"#1A2B45",fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Sign out without ending shift
                </button>
              </>) : (<>
                <button onClick={staffClockIn}
                  style={{padding:"10px 14px",borderRadius:8,border:"none",background:"#1B5EBE",color:"white",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Start shift
                </button>
                <button onClick={()=>setStaffClockModal(false)}
                  style={{padding:"10px 14px",borderRadius:8,border:"1.5px solid #D8DCE3",background:"white",color:"#1A2B45",fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Skip for now
                </button>
              </>)}
            </div>
          </div>
        </div>
      )}

      {/* RA Shift Modal */}
      {raShiftModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setRaShiftModal(false)}>
          <div style={{background:"white",borderRadius:12,padding:24,width:300,boxShadow:"0 4px 24px rgba(0,0,0,0.15)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:600,color:"#1A2B45",fontFamily:"'DM Sans',sans-serif",marginBottom:6}}>End of shift</div>
            <div style={{fontSize:12,color:"#4A5568",fontFamily:"'Inter',sans-serif",marginBottom:20,lineHeight:1.5}}>
              <strong>{currentUserName}</strong> is currently clocked in. What would you like to do?
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <button onClick={()=>{setRaShiftModal(false);handleSwitchRA();}}
                style={{padding:"10px 14px",borderRadius:8,border:"1.5px solid #1B5EBE",background:"#E8F0FB",color:"#1B5EBE",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",textAlign:"left"}}>
                <div style={{fontSize:13,fontWeight:600}}>Switch RA</div>
                <div style={{fontSize:11,fontWeight:400,color:"#4A5568",marginTop:2}}>Clock out {currentUserName} and clock in a new RA</div>
              </button>
              <button onClick={()=>{setRaShiftModal(false);handleSwitchRA();}}
                style={{padding:"10px 14px",borderRadius:8,border:"1.5px solid #D8DCE3",background:"white",color:"#1A2B45",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",textAlign:"left"}}>
                <div style={{fontSize:13,fontWeight:600}}>End shift only</div>
                <div style={{fontSize:11,fontWeight:400,color:"#4A5568",marginTop:2}}>Clock out and return to PIN screen</div>
              </button>
              <button onClick={()=>setRaShiftModal(false)}
                style={{padding:"8px 14px",borderRadius:8,border:"1px solid #D8DCE3",background:"transparent",color:"#4A5568",fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {sidebarEditOpen && role==="admin" && (
        <div style={{position:"fixed",inset:0,zIndex:500,display:"flex"}} onClick={()=>setSidebarEditOpen(false)}>
          <div style={{width:220,background:"#1B5EBE",padding:"12px 8px",display:"flex",flexDirection:"column",gap:6,boxShadow:"4px 0 20px rgba(0,0,0,0.3)",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:10,fontWeight:600,color:"rgba(255,255,255,0.6)",textTransform:"uppercase",letterSpacing:"0.07em",padding:"2px 6px",marginBottom:2}}>Reorder Sidebar</div>
            {(sidebarOrder||DEFAULT_SIDEBAR_ORDER).map((id,i,arr)=>{
              const lbls = {dashboard:"Admin Home",residents:"Residents",reports:"Reports",daily_log:"Daily Log",directory:"P&P Directory",staff:"DH Staff",archived:"Archived Residents",referrals:"Referral Pipeline",ra_tools:"RA Tools",pc_workflow:"PC Workflow"};
              const ics = {dashboard:"bi-speedometer",residents:"bi-people-fill",reports:"bi-bar-chart-fill",daily_log:"bi-journal-bookmark-fill",directory:"bi-book-fill",staff:"bi-person-fill",archived:"bi-archive-fill",referrals:"bi-clipboard-fill",ra_tools:"bi-tools",pc_workflow:"bi-person-lines-fill"};
              return (
                <div key={id} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 6px",background:"rgba(255,255,255,0.08)",borderRadius:5}}>
                  <i className={`bi ${ics[id]||"bi-circle-fill"}`} style={{fontSize:12,color:"rgba(255,255,255,0.6)",width:14,textAlign:"center",flexShrink:0}}></i>
                  <span style={{flex:1,fontSize:11,color:"rgba(255,255,255,0.85)",fontFamily:"'Inter',sans-serif"}}>{lbls[id]||id}</span>
                  <div style={{display:"flex",flexDirection:"column",gap:1}}>
                    <button disabled={i===0} onClick={()=>{const o=[...arr];[o[i-1],o[i]]=[o[i],o[i-1]];saveSidebarOrder(o);}}
                      style={{background:"none",border:"0.5px solid rgba(255,255,255,0.25)",borderRadius:3,width:16,height:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,0.6)",opacity:i===0?0.3:1,padding:0}}>
                      <i className="bi bi-chevron-up" style={{fontSize:8}}></i>
                    </button>
                    <button disabled={i===arr.length-1} onClick={()=>{const o=[...arr];[o[i],o[i+1]]=[o[i+1],o[i]];saveSidebarOrder(o);}}
                      style={{background:"none",border:"0.5px solid rgba(255,255,255,0.25)",borderRadius:3,width:16,height:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,0.6)",opacity:i===arr.length-1?0.3:1,padding:0}}>
                      <i className="bi bi-chevron-down" style={{fontSize:8}}></i>
                    </button>
                  </div>
                </div>
              );
            })}
            <button onClick={()=>saveSidebarOrder(DEFAULT_SIDEBAR_ORDER)}
              style={{marginTop:4,padding:"4px",background:"none",border:"1px solid rgba(255,255,255,0.2)",borderRadius:5,color:"rgba(255,255,255,0.55)",fontSize:10,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              Reset to default
            </button>
            <button onClick={()=>setSidebarEditOpen(false)}
              style={{padding:"4px",background:"white",border:"none",borderRadius:5,color:"#1B5EBE",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              Done
            </button>
          </div>
        </div>
      )}
      {selected && <ResidentProfile resident={selected} onClose={()=>setSelected(null)} onSave={handleSaveResident} onArchive={handleArchiveResident} onDischarge={handleSaveResident} perms={effectivePerms}/>}
      {showIntake && <IntakeModal onClose={()=>setShowIntake(false)} onSave={handleAddResident}/>}
    </div>
  );
}
